// src/catalog/schema.ts
var CATALOG_DOCUMENT_KINDS = [
  "foundation",
  "component",
  "pattern",
  "experiment",
  "governance"
];
var CATALOG_LIFECYCLES = [
  "Experimental",
  "Stable",
  "Deprecated"
];
var COMPONENT_CATEGORIES = [
  "actions",
  "forms",
  "navigation",
  "feedback",
  "overlays",
  "data-display",
  "page-structure"
];
var CATALOG_CATEGORIES = [
  "foundations",
  ...COMPONENT_CATEGORIES,
  "patterns",
  "experiments",
  "governance"
];
var FIXTURE_THEMES = ["light", "dark"];
var FIXTURE_DENSITIES = ["comfortable", "compact"];
var REQUIRED_COMPONENT_SECTION_KEYS = [
  "overview",
  "whenToUse",
  "whenNotToUse",
  "choosingBetween",
  "anatomy",
  "recommendedExample",
  "variants",
  "statesAndAdaptation",
  "behavior",
  "contentGuidance",
  "accessibility",
  "implementation",
  "apiReference",
  "relatedPatterns",
  "migration",
  "lifecycle"
];
var COMPONENT_PAGE_SECTION_KEYS = [
  "usage",
  "examples",
  "variantsAndStates",
  "behavior",
  "contentGuidance",
  "accessibility"
];
var CatalogValidationError = class extends Error {
  constructor(issues) {
    const details2 = issues.map((issue) => `${issue.path}: ${issue.message}`).join("\n");
    super(`Invalid Paper catalogue registry:
${details2}`);
    this.name = "CatalogValidationError";
    this.issues = issues;
  }
};
function defineCatalogDocument(document4) {
  return document4;
}
function defineCatalogFixture(fixture3) {
  return fixture3;
}

// src/catalog/validation-primitives.ts
var ID_PATTERN = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;
var ROUTE_PATTERN = /^\/[a-z0-9]+(?:[/-][a-z0-9]+)*$/;
function addIssue(issues, code, path, message) {
  issues.push({ code, path, message });
}
function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}
function hasDuplicate(values) {
  const seen = /* @__PURE__ */ new Set();
  return values.find((value) => {
    if (seen.has(value)) return true;
    seen.add(value);
    return false;
  });
}
function validateStringList(issues, value, path, { required = false } = {}) {
  if (!Array.isArray(value) || !value.every((item) => isNonEmptyString(item))) {
    addIssue(issues, "missing-metadata", path, "must be an array of non-empty strings");
    return;
  }
  if (required && value.length === 0) {
    addIssue(issues, "missing-metadata", path, "must contain at least one entry");
  }
  const duplicate = hasDuplicate(value);
  if (duplicate !== void 0) {
    addIssue(
      issues,
      "duplicate-reference",
      path,
      `contains duplicate entry "${duplicate}"`
    );
  }
}

// src/catalog/document-validation.ts
var DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
function isNonEmptyStringArray(value) {
  return Array.isArray(value) && value.length > 0 && value.every((item) => isNonEmptyString(item));
}
function isCalendarDate(value) {
  if (typeof value !== "string" || !DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
function categoryForKindIsValid(kind, category) {
  switch (kind) {
    case "foundation":
      return category === "foundations";
    case "component":
      return COMPONENT_CATEGORIES.includes(category);
    case "pattern":
      return category === "patterns";
    case "experiment":
      return category === "experiments";
    case "governance":
      return category === "governance";
  }
}
function expectedRoutePattern(document4) {
  switch (document4.kind) {
    case "foundation":
      return /^\/foundations\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
    case "component":
      return /^\/components\/(?:actions|forms|navigation|feedback|overlays|data-display|page-structure)\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
    case "pattern":
      return /^\/patterns\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
    case "experiment":
      return /^\/experiments\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
    case "governance":
      return /^\/(?:contributing|changelog)$/;
  }
}
function validateDocumentMetadata(document4, path, issues) {
  const strings = [
    [document4.name, "name"],
    [document4.summary, "summary"],
    [document4.sourcePath, "sourcePath"],
    [document4.packageVersion, "packageVersion"]
  ];
  strings.forEach(([value, key]) => {
    if (!isNonEmptyString(value)) {
      addIssue(issues, "missing-metadata", `${path}.${key}`, "must be a non-empty string");
    }
  });
  if (!isCalendarDate(document4.reviewDate)) {
    addIssue(
      issues,
      "invalid-date",
      `${path}.reviewDate`,
      "must be a real ISO calendar date (YYYY-MM-DD)"
    );
  }
  validateStringList(issues, document4.aliases, `${path}.aliases`);
  validateStringList(issues, document4.keywords, `${path}.keywords`, { required: true });
  validateStringList(issues, document4.fixtureIds, `${path}.fixtureIds`);
  validateStringList(issues, document4.behaviorTestIds, `${path}.behaviorTestIds`);
  if (!document4.guidance || typeof document4.guidance !== "object") {
    addIssue(issues, "missing-metadata", `${path}.guidance`, "is required");
  } else {
    validateStringList(issues, document4.guidance.whenToUse, `${path}.guidance.whenToUse`);
    validateStringList(
      issues,
      document4.guidance.whenNotToUse,
      `${path}.guidance.whenNotToUse`
    );
    validateStringList(issues, document4.guidance.content, `${path}.guidance.content`);
    validateStringList(
      issues,
      document4.guidance.commonMistakes,
      `${path}.guidance.commonMistakes`
    );
  }
  if (!document4.accessibility || typeof document4.accessibility !== "object") {
    addIssue(issues, "missing-metadata", `${path}.accessibility`, "is required");
  } else {
    validateStringList(
      issues,
      document4.accessibility.requirements,
      `${path}.accessibility.requirements`
    );
    validateStringList(
      issues,
      document4.accessibility.keyboard,
      `${path}.accessibility.keyboard`
    );
    validateStringList(
      issues,
      document4.accessibility.knownConstraints,
      `${path}.accessibility.knownConstraints`
    );
  }
  if (!document4.api || typeof document4.api !== "object") {
    addIssue(issues, "missing-metadata", `${path}.api`, "is required");
  } else {
    validateStringList(issues, document4.api.react, `${path}.api.react`);
    validateStringList(issues, document4.api.cssClasses, `${path}.api.cssClasses`);
    validateStringList(issues, document4.api.publicTypes, `${path}.api.publicTypes`);
    validateStringList(issues, document4.api.defaults, `${path}.api.defaults`);
    validateStringList(
      issues,
      document4.api.invalidCombinations,
      `${path}.api.invalidCombinations`
    );
  }
  if (!document4.dependencies || typeof document4.dependencies !== "object") {
    addIssue(issues, "missing-metadata", `${path}.dependencies`, "is required");
  } else {
    validateStringList(
      issues,
      document4.dependencies.documents,
      `${path}.dependencies.documents`
    );
    validateStringList(
      issues,
      document4.dependencies.packages,
      `${path}.dependencies.packages`
    );
  }
  if (!document4.migration || typeof document4.migration !== "object") {
    addIssue(issues, "missing-metadata", `${path}.migration`, "is required");
  } else {
    validateStringList(issues, document4.migration.legacy, `${path}.migration.legacy`);
    validateStringList(issues, document4.migration.notes, `${path}.migration.notes`);
  }
  if (!Array.isArray(document4.changelog)) {
    addIssue(issues, "missing-metadata", `${path}.changelog`, "must be an array");
  } else {
    document4.changelog.forEach((entry, entryIndex) => {
      const entryPath = `${path}.changelog[${entryIndex}]`;
      if (!isCalendarDate(entry?.date)) {
        addIssue(issues, "invalid-date", `${entryPath}.date`, "must be a real ISO date");
      }
      if (!isNonEmptyString(entry?.note)) {
        addIssue(issues, "missing-metadata", `${entryPath}.note`, "must be non-empty");
      }
    });
  }
}
function validateStableDocument(document4, path, issues) {
  if (document4.lifecycle !== "Stable") return;
  if (!Array.isArray(document4.fixtureIds) || document4.fixtureIds.length === 0) {
    addIssue(
      issues,
      "missing-stable-fixture",
      `${path}.fixtureIds`,
      "Stable documents must reference at least one deterministic fixture"
    );
  }
  if (!Array.isArray(document4.behaviorTestIds) || document4.behaviorTestIds.length === 0) {
    addIssue(
      issues,
      "missing-stable-behavior-test",
      `${path}.behaviorTestIds`,
      "Stable documents must identify rendered semantic or behavioral tests"
    );
  }
  if (!document4.accessibility || !isNonEmptyStringArray(document4.accessibility.requirements)) {
    addIssue(
      issues,
      "missing-stable-accessibility",
      `${path}.accessibility.requirements`,
      "Stable documents must state accessibility requirements"
    );
  }
  if (document4.kind !== "component") return;
  if (!document4.sections || typeof document4.sections.required !== "object") {
    REQUIRED_COMPONENT_SECTION_KEYS.forEach((key) => {
      addIssue(
        issues,
        "missing-stable-section",
        `${path}.sections.required.${key}`,
        `Stable component is missing required section "${key}"`
      );
    });
    return;
  }
  REQUIRED_COMPONENT_SECTION_KEYS.forEach((key) => {
    const section2 = document4.sections?.required[key];
    if (!section2 || !isNonEmptyString(section2.heading) || !isNonEmptyStringArray(section2.content)) {
      addIssue(
        issues,
        "missing-stable-section",
        `${path}.sections.required.${key}`,
        `Stable component section "${key}" needs a heading and content`
      );
    }
  });
  const pageSections = document4.sections.pageSections;
  if (!Array.isArray(pageSections) || pageSections.length === 0) {
    addIssue(
      issues,
      "missing-stable-section",
      `${path}.sections.pageSections`,
      "Stable component pages must opt into at least one useful public topic"
    );
  } else {
    const seenPageSections = /* @__PURE__ */ new Set();
    pageSections.forEach((key, index) => {
      if (!COMPONENT_PAGE_SECTION_KEYS.includes(key)) {
        addIssue(
          issues,
          "invalid-section",
          `${path}.sections.pageSections[${index}]`,
          `Unsupported public component topic "${key}"`
        );
      } else if (seenPageSections.has(key)) {
        addIssue(
          issues,
          "invalid-section",
          `${path}.sections.pageSections[${index}]`,
          `Public component topic "${key}" is duplicated`
        );
      }
      seenPageSections.add(key);
    });
  }
  Object.keys(document4.sections.required).forEach((key) => {
    if (!REQUIRED_COMPONENT_SECTION_KEYS.includes(key)) {
      addIssue(
        issues,
        "invalid-section",
        `${path}.sections.required.${key}`,
        "Component-specific sections belong in sections.optional"
      );
    }
  });
  let previousAfter = -1;
  document4.sections.optional?.forEach((section2, sectionIndex) => {
    const after = REQUIRED_COMPONENT_SECTION_KEYS.indexOf(section2.after);
    if (after < previousAfter) {
      addIssue(
        issues,
        "invalid-section",
        `${path}.sections.optional[${sectionIndex}].after`,
        "Optional sections must follow the required instructional sequence"
      );
    }
    previousAfter = after;
  });
}

// src/catalog/fixture-validation.ts
var FIXTURE_NON_DETERMINISM_PATTERNS = [
  [/\bMath\.random\s*\(/, "Math.random"],
  [/\bcrypto\.(?:randomUUID|getRandomValues)\s*\(/, "random crypto APIs"],
  [/\bDate\.now\s*\(/, "Date.now"],
  [/\bnew\s+Date\s*\(\s*\)/, "an unpinned Date"],
  [/\bperformance\.now\s*\(/, "performance.now"],
  [/\b(?:faker|Faker)\b/, "Faker"],
  [/\bfetch\s*\(/, "fetch"],
  [/\b(?:XMLHttpRequest|WebSocket|EventSource)\b/, "a network API"],
  [/\bnavigator\.onLine\b/, "network state"]
];
var FIXTURE_ROUTER_PATTERNS = [
  [/\buseRouter\s*\(/, "useRouter"],
  [/\b(?:next\/router|next\/navigation|react-router(?:-dom)?)\b/, "a router package"],
  [/\b(?:window|document)\.location\b/, "global location state"]
];
function validateFixtureInto(fixture3, path, issues) {
  if (!fixture3 || typeof fixture3 !== "object") {
    addIssue(issues, "invalid-fixture", path, "must be an object");
    return;
  }
  if (!isNonEmptyString(fixture3.id) || !ID_PATTERN.test(fixture3.id)) {
    addIssue(issues, "invalid-id", `${path}.id`, "must be a stable lowercase ID");
  }
  ["name", "description"].forEach((key) => {
    if (!isNonEmptyString(fixture3[key])) {
      addIssue(issues, "missing-metadata", `${path}.${key}`, "must be non-empty");
    }
  });
  validateStringList(issues, fixture3.tags, `${path}.tags`, { required: true });
  if (!Array.isArray(fixture3.themes) || fixture3.themes.length === 0 || fixture3.themes.some(
    (theme) => !FIXTURE_THEMES.includes(theme)
  )) {
    addIssue(issues, "invalid-fixture", `${path}.themes`, "contains an unsupported theme");
  } else if (hasDuplicate(fixture3.themes)) {
    addIssue(issues, "duplicate-reference", `${path}.themes`, "contains a duplicate theme");
  }
  if (!Array.isArray(fixture3.densities) || fixture3.densities.length === 0 || fixture3.densities.some(
    (density) => !FIXTURE_DENSITIES.includes(density)
  )) {
    addIssue(
      issues,
      "invalid-fixture",
      `${path}.densities`,
      "contains an unsupported density"
    );
  } else if (hasDuplicate(fixture3.densities)) {
    addIssue(
      issues,
      "duplicate-reference",
      `${path}.densities`,
      "contains a duplicate density"
    );
  }
  if (!Array.isArray(fixture3.viewports) || fixture3.viewports.length === 0) {
    addIssue(issues, "invalid-fixture", `${path}.viewports`, "must not be empty");
  } else {
    const viewportIds = [];
    fixture3.viewports.forEach((viewport, viewportIndex) => {
      const viewportPath = `${path}.viewports[${viewportIndex}]`;
      if (!isNonEmptyString(viewport?.id) || !ID_PATTERN.test(viewport.id)) {
        addIssue(issues, "invalid-id", `${viewportPath}.id`, "must be a stable lowercase ID");
      } else {
        viewportIds.push(viewport.id);
      }
      if (!isNonEmptyString(viewport?.label)) {
        addIssue(issues, "missing-metadata", `${viewportPath}.label`, "must be non-empty");
      }
      if (!Number.isInteger(viewport?.width) || viewport.width <= 0) {
        addIssue(issues, "invalid-fixture", `${viewportPath}.width`, "must be a positive integer");
      }
      if (!Number.isInteger(viewport?.height) || viewport.height <= 0) {
        addIssue(issues, "invalid-fixture", `${viewportPath}.height`, "must be a positive integer");
      }
    });
    const duplicateViewport = hasDuplicate(viewportIds);
    if (duplicateViewport) {
      addIssue(
        issues,
        "duplicate-reference",
        `${path}.viewports`,
        `contains duplicate viewport "${duplicateViewport}"`
      );
    }
  }
  if (fixture3.deterministic !== true) {
    addIssue(
      issues,
      "invalid-fixture-marker",
      `${path}.deterministic`,
      "must be explicitly marked true after deterministic review"
    );
  }
  if (typeof fixture3.render !== "function") {
    addIssue(issues, "invalid-fixture", `${path}.render`, "must be a render function");
    return;
  }
  const source = Function.prototype.toString.call(fixture3.render);
  FIXTURE_NON_DETERMINISM_PATTERNS.forEach(([pattern, label]) => {
    if (pattern.test(source)) {
      addIssue(
        issues,
        "non-deterministic-fixture",
        `${path}.render`,
        `must not read ${label}; pass fixed values through the fixture instead`
      );
    }
  });
  FIXTURE_ROUTER_PATTERNS.forEach(([pattern, label]) => {
    if (pattern.test(source)) {
      addIssue(
        issues,
        "router-dependent-fixture",
        `${path}.render`,
        `must not read ${label}; fixtures are router-neutral`
      );
    }
  });
}

// src/catalog/validation.ts
function validateCatalogFixture(fixture3) {
  const issues = [];
  validateFixtureInto(fixture3, "fixture", issues);
  return { valid: issues.length === 0, issues };
}
function validateCatalogRegistry(registry) {
  const issues = [];
  if (!registry || typeof registry !== "object") {
    return {
      valid: false,
      issues: [{ code: "invalid-registry", path: "registry", message: "must be an object" }]
    };
  }
  if (!Array.isArray(registry.documents) || !Array.isArray(registry.fixtures)) {
    return {
      valid: false,
      issues: [
        {
          code: "invalid-registry",
          path: "registry",
          message: "documents and fixtures must be arrays"
        }
      ]
    };
  }
  const documentIds = /* @__PURE__ */ new Map();
  const fixtureIds = /* @__PURE__ */ new Map();
  const routes = /* @__PURE__ */ new Map();
  const slugs = /* @__PURE__ */ new Map();
  const aliases = /* @__PURE__ */ new Map();
  registry.fixtures.forEach((fixture3, index) => {
    const path = `fixtures[${index}]`;
    validateFixtureInto(fixture3, path, issues);
    const previous = fixtureIds.get(fixture3?.id);
    if (previous !== void 0) {
      addIssue(
        issues,
        "duplicate-fixture-id",
        `${path}.id`,
        `duplicates fixtures[${previous}].id`
      );
    } else if (isNonEmptyString(fixture3?.id)) {
      fixtureIds.set(fixture3.id, index);
    }
  });
  registry.documents.forEach((document4, index) => {
    const path = `documents[${index}]`;
    if (!document4 || typeof document4 !== "object") {
      addIssue(issues, "invalid-document", path, "must be an object");
      return;
    }
    if (!isNonEmptyString(document4.id) || !ID_PATTERN.test(document4.id)) {
      addIssue(issues, "invalid-id", `${path}.id`, "must be a stable lowercase ID");
    } else {
      const previous = documentIds.get(document4.id);
      if (previous !== void 0) {
        addIssue(
          issues,
          "duplicate-document-id",
          `${path}.id`,
          `duplicates documents[${previous}].id`
        );
      } else {
        documentIds.set(document4.id, index);
      }
    }
    if (!CATALOG_DOCUMENT_KINDS.includes(document4.kind)) {
      addIssue(issues, "invalid-kind", `${path}.kind`, "contains an unsupported kind");
    }
    if (!CATALOG_LIFECYCLES.includes(document4.lifecycle)) {
      addIssue(
        issues,
        "invalid-lifecycle",
        `${path}.lifecycle`,
        "must be Experimental, Stable, or Deprecated"
      );
    }
    if (!CATALOG_CATEGORIES.includes(document4.category)) {
      addIssue(issues, "invalid-category", `${path}.category`, "is not a catalogue category");
    } else if (CATALOG_DOCUMENT_KINDS.includes(document4.kind) && !categoryForKindIsValid(document4.kind, document4.category)) {
      addIssue(
        issues,
        "invalid-category",
        `${path}.category`,
        `category "${document4.category}" is not valid for kind "${document4.kind}"`
      );
    }
    if (!isNonEmptyString(document4.route) || !ROUTE_PATTERN.test(document4.route) || CATALOG_DOCUMENT_KINDS.includes(document4.kind) && !expectedRoutePattern(document4).test(document4.route)) {
      addIssue(
        issues,
        "invalid-route",
        `${path}.route`,
        "does not match the canonical route for its kind and category"
      );
    } else {
      const previousRoute = routes.get(document4.route);
      if (previousRoute) {
        addIssue(
          issues,
          "duplicate-route",
          `${path}.route`,
          `duplicates canonical route owned by "${previousRoute}"`
        );
      } else {
        routes.set(document4.route, document4.id);
      }
      const routeParts = document4.route.split("/");
      const slug = routeParts[routeParts.length - 1] ?? "";
      const previousSlug = slugs.get(slug);
      if (previousSlug) {
        addIssue(
          issues,
          "duplicate-slug",
          `${path}.route`,
          `slug "${slug}" is already owned by "${previousSlug}"`
        );
      } else {
        slugs.set(slug, document4.id);
      }
    }
    validateDocumentMetadata(document4, path, issues);
    validateStableDocument(document4, path, issues);
    document4.aliases?.forEach((alias, aliasIndex) => {
      const aliasPath = `${path}.aliases[${aliasIndex}]`;
      if (!ROUTE_PATTERN.test(alias)) {
        addIssue(issues, "invalid-route", aliasPath, "must be an absolute normalized route");
      }
      const owner = aliases.get(alias) ?? routes.get(alias);
      if (owner) {
        addIssue(issues, "duplicate-alias", aliasPath, `route is already owned by "${owner}"`);
      } else {
        aliases.set(alias, document4.id);
      }
    });
  });
  registry.documents.forEach((document4, documentIndex) => {
    if (!document4 || typeof document4 !== "object") return;
    document4.fixtureIds?.forEach((fixtureId, fixtureIndex) => {
      if (!fixtureIds.has(fixtureId)) {
        addIssue(
          issues,
          "unknown-fixture-reference",
          `documents[${documentIndex}].fixtureIds[${fixtureIndex}]`,
          `fixture "${fixtureId}" is not registered`
        );
      }
    });
    document4.dependencies?.documents?.forEach(
      (documentId, dependencyIndex) => {
        if (!documentIds.has(documentId)) {
          addIssue(
            issues,
            "unknown-document-reference",
            `documents[${documentIndex}].dependencies.documents[${dependencyIndex}]`,
            `document "${documentId}" is not registered`
          );
        }
      }
    );
    if (document4.lifecycle === "Deprecated") {
      const replacementId = document4.migration?.replacementDocumentId;
      if (!isNonEmptyString(replacementId)) {
        addIssue(
          issues,
          "invalid-deprecation",
          `documents[${documentIndex}].migration.replacementDocumentId`,
          "Deprecated documents must name a replacement document"
        );
      } else if (replacementId === document4.id || !documentIds.has(replacementId)) {
        addIssue(
          issues,
          "unknown-document-reference",
          `documents[${documentIndex}].migration.replacementDocumentId`,
          "replacement must reference a different registered document"
        );
      }
      if (!isNonEmptyString(document4.migration?.removalGuidance)) {
        addIssue(
          issues,
          "invalid-deprecation",
          `documents[${documentIndex}].migration.removalGuidance`,
          "Deprecated documents must include planned removal guidance"
        );
      }
    }
  });
  aliases.forEach((owner, alias) => {
    const canonicalOwner = routes.get(alias);
    if (canonicalOwner && canonicalOwner !== owner) {
      addIssue(
        issues,
        "duplicate-alias",
        `documents[${documentIds.get(owner) ?? 0}].aliases`,
        `alias "${alias}" collides with canonical route owned by "${canonicalOwner}"`
      );
    }
  });
  const redirects = registry.redirects ?? [];
  if (!Array.isArray(redirects)) {
    addIssue(issues, "invalid-registry", "redirects", "must be an array when provided");
  } else {
    const redirectSources = /* @__PURE__ */ new Set();
    redirects.forEach((redirect, index) => {
      const path = `redirects[${index}]`;
      if (!redirect || typeof redirect !== "object") {
        addIssue(issues, "invalid-redirect", path, "must be an object");
        return;
      }
      if (!ROUTE_PATTERN.test(redirect.from) || !ROUTE_PATTERN.test(redirect.to)) {
        addIssue(issues, "invalid-redirect", path, "from and to must be normalized routes");
      }
      if (redirect.from === redirect.to) {
        addIssue(issues, "invalid-redirect", path, "must not redirect a route to itself");
      }
      if (routes.has(redirect.from) || aliases.has(redirect.from) || redirectSources.has(redirect.from)) {
        addIssue(issues, "duplicate-alias", `${path}.from`, "redirect source is already owned");
      }
      if (!routes.has(redirect.to)) {
        addIssue(
          issues,
          "unknown-document-reference",
          `${path}.to`,
          "redirect target must be a canonical document route"
        );
      }
      redirectSources.add(redirect.from);
    });
  }
  return { valid: issues.length === 0, issues };
}

// src/components/actions/Button/Button.tsx
import {
  forwardRef,
  useId
} from "react";
import { jsx, jsxs } from "react/jsx-runtime";
function buttonClassName({
  variant = "default",
  fullWidth = false,
  loading = false,
  className
} = {}) {
  return [
    "paper-button",
    `paper-button--${variant}`,
    fullWidth ? "paper-button--full-width" : "",
    loading ? "paper-button--loading" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
}
var Button = forwardRef(function Button2({
  variant = "default",
  fullWidth = false,
  loading = false,
  loadingLabel = "Working",
  leadingIcon,
  trailingIcon,
  className,
  children,
  disabled,
  type = "button",
  "aria-describedby": describedBy2,
  "aria-label": ariaLabel,
  ...props
}, ref) {
  const generatedId = useId();
  const loadingStatusId = `paper-button-status-${generatedId.replace(/:/gu, "")}`;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ...props,
      ref,
      type,
      disabled: disabled || loading,
      "aria-busy": loading || void 0,
      "aria-label": ariaLabel ?? (loading && typeof children === "string" ? children : void 0),
      "aria-describedby": [describedBy2, loading ? loadingStatusId : void 0].filter(Boolean).join(" ") || void 0,
      className: buttonClassName({ variant, fullWidth, loading, className }),
      children: [
        loading ? /* @__PURE__ */ jsx("span", { className: "paper-button__spinner", "aria-hidden": "true" }) : null,
        !loading && leadingIcon ? /* @__PURE__ */ jsx("span", { className: "paper-button__icon", "aria-hidden": "true", children: leadingIcon }) : null,
        /* @__PURE__ */ jsx("span", { className: "paper-button__label", children }),
        !loading && trailingIcon ? /* @__PURE__ */ jsx("span", { className: "paper-button__icon", "aria-hidden": "true", children: trailingIcon }) : null,
        loading ? /* @__PURE__ */ jsx("span", { className: "paper-visually-hidden", id: loadingStatusId, role: "status", children: loadingLabel }) : null
      ]
    }
  );
});

// src/components/forms/field.tsx
import { createFormHookContexts, useStore } from "@tanstack/react-form";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();
function useField() {
  const field = useFieldContext();
  const form = useFormContext();
  const submitted = useStore(form.store, (state) => state.submissionAttempts > 0);
  const errors = useStore(field.store, (state) => state.meta.errors);
  const showErrors = submitted || Boolean(field.state.meta.errorMap.onSubmit) || field.state.meta.isTouched && Boolean(field.options.validators?.onBlur);
  const first = showErrors ? errors[0] : void 0;
  const error = typeof first === "string" ? first : first?.message;
  return { field, error };
}
function FieldFrame({
  id,
  label,
  hint,
  error,
  required,
  children
}) {
  return /* @__PURE__ */ jsxs2("div", { className: `paper-field${error ? " paper-field--invalid" : ""}`, children: [
    /* @__PURE__ */ jsxs2("label", { className: "paper-field__label", htmlFor: id, id: `${id}-label`, children: [
      label,
      required ? /* @__PURE__ */ jsx2("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
    ] }),
    children,
    hint ? /* @__PURE__ */ jsx2("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
    error ? /* @__PURE__ */ jsx2("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
  ] });
}
function describedBy(id, hint, error, external) {
  return [hint ? `${id}-hint` : void 0, error ? `${id}-error` : void 0, external].filter(Boolean).join(" ") || void 0;
}

// src/components/forms/Input/Input.tsx
import { forwardRef as forwardRef2, useId as useId2 } from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Input = forwardRef2(function Input2({
  label,
  hint,
  id: providedId,
  required,
  readOnly,
  disabled,
  className,
  ...props
}, forwardedRef) {
  const { field, error } = useField();
  const generatedId = useId2();
  const id = providedId ?? `paper-input-${generatedId.replace(/:/gu, "")}`;
  const hintId = hint ? `${id}-hint` : void 0;
  const errorId = error ? `${id}-error` : void 0;
  const describedBy2 = [hintId, errorId, props["aria-describedby"]].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: [
        "paper-field",
        error ? "paper-field--invalid" : "",
        disabled ? "paper-field--disabled" : "",
        className ?? ""
      ].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsxs3("label", { className: "paper-field__label", htmlFor: id, children: [
          label,
          required ? /* @__PURE__ */ jsx3("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
        ] }),
        /* @__PURE__ */ jsx3(
          "input",
          {
            ...props,
            name: field.name,
            value: typeof field.state.value === "number" && Number.isNaN(field.state.value) ? "" : field.state.value ?? "",
            onChange: (event) => field.handleChange(props.type === "number" ? event.currentTarget.valueAsNumber : event.currentTarget.value),
            onBlur: field.handleBlur,
            id,
            required,
            readOnly,
            disabled,
            "aria-invalid": error ? true : void 0,
            "aria-describedby": describedBy2,
            className: "paper-input",
            ref: forwardedRef
          }
        ),
        hint ? /* @__PURE__ */ jsx3("p", { className: "paper-field__hint", id: hintId, children: hint }) : null,
        error ? /* @__PURE__ */ jsx3("p", { className: "paper-field__error", id: errorId, role: "alert", children: error || "Check this field." }) : null
      ]
    }
  );
});

// src/components/forms/ToggleSelect.tsx
import { useId as useId3 } from "react";

// src/icons/index.ts
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import {
  CheckIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon
} from "@heroicons/react/24/solid";
var iconSizeClassNames = {
  compact: "paper-icon-compact",
  default: "paper-icon-default",
  prominent: "paper-icon-prominent",
  emptyState: "paper-icon-empty-state"
};
function iconClassName(size = "default", className) {
  return [iconSizeClassNames[size], className].filter(Boolean).join(" ");
}

// src/components/forms/ToggleSelect.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function ToggleSelect({ label, options, hint, disabled }) {
  const id = `paper-toggle-${useId3().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const selected = field.state.value || [];
  const describedBy2 = [hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs4(
    "fieldset",
    {
      className: `paper-choice-field paper-toggle-select${error ? " paper-field--invalid" : ""}`,
      disabled,
      "aria-describedby": describedBy2,
      "aria-invalid": error ? true : void 0,
      children: [
        /* @__PURE__ */ jsx4("legend", { className: "paper-field__label", children: label }),
        /* @__PURE__ */ jsx4("div", { className: "paper-toggle-select__options", children: options.map((option) => {
          const pressed = selected.includes(option.value);
          return /* @__PURE__ */ jsxs4(
            "button",
            {
              type: "button",
              className: "paper-toggle-select__option",
              disabled: disabled || option.disabled,
              "aria-pressed": pressed,
              "aria-describedby": describedBy2,
              onBlur: field.handleBlur,
              onClick: () => field.handleChange(pressed ? [] : [option.value]),
              children: [
                /* @__PURE__ */ jsxs4("span", { className: "paper-toggle-select__text", children: [
                  /* @__PURE__ */ jsx4("span", { children: option.label }),
                  " ",
                  option.description ? /* @__PURE__ */ jsx4("span", { className: "paper-toggle-select__description", children: option.description }) : null
                ] }),
                /* @__PURE__ */ jsx4(CheckIcon, { className: "paper-icon-compact paper-toggle-select__check", "aria-hidden": "true" })
              ]
            },
            option.value
          );
        }) }),
        hint ? /* @__PURE__ */ jsx4("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
        error ? /* @__PURE__ */ jsx4("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
      ]
    }
  );
}

// src/components/forms/controls.tsx
import {
  useId as useId4
} from "react";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function TextArea({
  label,
  hint,
  required,
  id: providedId,
  ...props
}) {
  const id = providedId ?? `paper-textarea-${useId4().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsx5(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsx5(
    "textarea",
    {
      ...props,
      name: field.name,
      value: field.state.value ?? "",
      onChange: (event) => field.handleChange(event.currentTarget.value),
      onBlur: field.handleBlur,
      id,
      required,
      "aria-invalid": error ? true : void 0,
      "aria-describedby": describedBy(id, hint, error, props["aria-describedby"]),
      className: "paper-input paper-textarea"
    }
  ) });
}
function Select({
  label,
  hint,
  required,
  options,
  groups,
  placeholder,
  id: providedId,
  ...props
}) {
  const id = providedId ?? `paper-select-${useId4().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsx5(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs5("span", { className: "paper-select__control", children: [
    /* @__PURE__ */ jsxs5(
      "select",
      {
        ...props,
        name: field.name,
        value: field.state.value ?? "",
        onChange: (event) => field.handleChange(event.currentTarget.value),
        onBlur: field.handleBlur,
        id,
        required,
        "aria-invalid": error ? true : void 0,
        "aria-describedby": describedBy(id, hint, error, props["aria-describedby"]),
        className: "paper-input paper-select",
        children: [
          placeholder ? /* @__PURE__ */ jsx5("option", { value: "", children: placeholder }) : null,
          groups ? groups.map((group) => /* @__PURE__ */ jsx5("optgroup", { label: group.label, children: group.options.map((option) => /* @__PURE__ */ jsx5("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value)) }, group.label)) : options.map((option) => /* @__PURE__ */ jsx5("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value))
        ]
      }
    ),
    /* @__PURE__ */ jsx5(
      ChevronDownIcon,
      {
        className: iconClassName("compact", "paper-select__icon"),
        "aria-hidden": "true"
      }
    )
  ] }) });
}
function Checkbox({
  label,
  hint,
  required,
  id: providedId,
  ...props
}) {
  const id = providedId ?? `paper-checkbox-${useId4().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const selected = field.state.value;
  const option = typeof props.value === "string" ? props.value : void 0;
  return /* @__PURE__ */ jsxs5("fieldset", { className: `paper-choice-field${error ? " paper-field--invalid" : ""}`, children: [
    /* @__PURE__ */ jsxs5("label", { className: "paper-choice", htmlFor: id, children: [
      /* @__PURE__ */ jsx5(
        "input",
        {
          ...props,
          name: field.name,
          checked: option === void 0 ? selected === true : Array.isArray(selected) && selected.includes(option),
          onChange: (event) => {
            if (option === void 0) {
              field.handleChange(event.currentTarget.checked);
            } else {
              const values = Array.isArray(selected) ? selected : [];
              field.handleChange(event.currentTarget.checked ? [...values, option] : values.filter((value) => value !== option));
            }
          },
          onBlur: field.handleBlur,
          id,
          type: "checkbox",
          required,
          "aria-invalid": error ? true : void 0,
          "aria-describedby": describedBy(id, hint, error, props["aria-describedby"])
        }
      ),
      /* @__PURE__ */ jsx5("span", { children: label })
    ] }),
    hint ? /* @__PURE__ */ jsx5("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
    error ? /* @__PURE__ */ jsx5("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
  ] });
}

// src/components/forms/choice-controls.tsx
import { useId as useId5 } from "react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
function RadioSelect({
  label,
  hint,
  required,
  options,
  disabled,
  variant = "default"
}) {
  const id = `paper-radio-${useId5().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const segmented = variant === "segmented";
  return /* @__PURE__ */ jsxs6(
    "fieldset",
    {
      className: `paper-choice-field paper-radio-select paper-radio-select--${variant}${error ? " paper-field--invalid" : ""}`,
      "aria-describedby": describedBy(id, hint, error),
      "aria-invalid": error ? true : void 0,
      disabled,
      children: [
        /* @__PURE__ */ jsxs6("legend", { className: "paper-field__label", children: [
          label,
          required ? /* @__PURE__ */ jsx6("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
        ] }),
        /* @__PURE__ */ jsx6("div", { className: "paper-choice-list", children: options.map((option) => /* @__PURE__ */ jsxs6("label", { className: segmented ? "paper-radio-select__segment" : "paper-choice", children: [
          /* @__PURE__ */ jsx6(
            "input",
            {
              name: field.name,
              checked: field.state.value === option.value,
              onChange: () => field.handleChange(option.value),
              onBlur: field.handleBlur,
              type: "radio",
              value: option.value,
              disabled: option.disabled,
              required,
              "aria-invalid": error ? true : void 0
            }
          ),
          /* @__PURE__ */ jsx6("span", { className: segmented ? "paper-radio-select__label" : void 0, children: option.label }),
          segmented ? /* @__PURE__ */ jsx6(
            CheckIcon,
            {
              className: iconClassName("compact", "paper-radio-select__check"),
              "aria-hidden": "true"
            }
          ) : null
        ] }, option.value)) }),
        hint ? /* @__PURE__ */ jsx6("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
        error ? /* @__PURE__ */ jsx6("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
      ]
    }
  );
}
function RadioGroup({
  label,
  hint,
  required = true,
  options
}) {
  const id = `paper-radio-group-${useId5().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsxs6(
    "fieldset",
    {
      className: `paper-choice-field${error ? " paper-field--invalid" : ""}`,
      "aria-describedby": describedBy(id, hint, error),
      "aria-invalid": error ? true : void 0,
      children: [
        /* @__PURE__ */ jsxs6("legend", { className: "paper-field__label", children: [
          label,
          required ? /* @__PURE__ */ jsx6("span", { className: "paper-field__required", "aria-hidden": "true", children: "*" }) : null
        ] }),
        /* @__PURE__ */ jsx6("div", { className: "paper-radio-cards", children: options.map((option) => /* @__PURE__ */ jsxs6("label", { className: "paper-radio-card", children: [
          /* @__PURE__ */ jsx6(
            "input",
            {
              name: field.name,
              checked: field.state.value === option.value,
              onChange: () => field.handleChange(option.value),
              onBlur: field.handleBlur,
              type: "radio",
              value: option.value,
              required,
              disabled: option.disabled,
              "aria-invalid": error ? true : void 0
            }
          ),
          /* @__PURE__ */ jsxs6("span", { children: [
            /* @__PURE__ */ jsx6("strong", { children: option.label }),
            /* @__PURE__ */ jsx6("small", { children: option.description })
          ] })
        ] }, option.value)) }),
        hint ? /* @__PURE__ */ jsx6("p", { className: "paper-field__hint", id: `${id}-hint`, children: hint }) : null,
        error ? /* @__PURE__ */ jsx6("p", { className: "paper-field__error", id: `${id}-error`, role: "alert", children: error }) : null
      ]
    }
  );
}

// src/components/forms/AmountWithUnit.tsx
import { useId as useId6 } from "react";
import { jsx as jsx7, jsxs as jsxs7 } from "react/jsx-runtime";
function AmountWithUnit({ label, units, unitsLabel, unitField, hint, required, disabled, ...props }) {
  const id = `paper-amount-${useId6().replace(/:/gu, "")}`;
  const { field, error } = useField();
  return /* @__PURE__ */ jsx7(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs7("div", { className: "paper-compound-field", role: "group", "aria-labelledby": `${id}-label`, children: [
    /* @__PURE__ */ jsx7(
      "input",
      {
        ...props,
        name: field.name,
        value: typeof field.state.value === "number" && Number.isNaN(field.state.value) ? "" : field.state.value ?? "",
        onChange: (event) => field.handleChange(event.currentTarget.valueAsNumber),
        onBlur: field.handleBlur,
        required,
        disabled,
        id,
        type: "number",
        className: "paper-input",
        "aria-invalid": error ? true : void 0,
        "aria-describedby": describedBy(id, hint, error, props["aria-describedby"])
      }
    ),
    /* @__PURE__ */ jsx7(
      "select",
      {
        name: unitField.name,
        value: unitField.state.value,
        onChange: (event) => unitField.handleChange(event.currentTarget.value),
        onBlur: unitField.handleBlur,
        disabled,
        "aria-label": unitsLabel ?? `Unit for ${label.toLocaleLowerCase()}`,
        className: "paper-input paper-compound-field__unit",
        "aria-describedby": describedBy(id, hint, error, props["aria-describedby"]),
        children: units.map((unit) => /* @__PURE__ */ jsx7("option", { value: unit.value, disabled: unit.disabled, children: unit.label }, unit.value))
      }
    )
  ] }) });
}

// src/components/forms/autocomplete-controls.tsx
import { Combobox } from "@base-ui/react/combobox";
import { useId as useId7, useMemo, useRef, useState } from "react";
import { jsx as jsx8, jsxs as jsxs8 } from "react/jsx-runtime";
function useFilteredOptions(options, query, format, match, maxResults = 50) {
  return useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return options.filter((option) => !normalized || (match ? match(option, query) : format(option).toLocaleLowerCase().includes(normalized))).slice(0, maxResults);
  }, [format, match, maxResults, options, query]);
}
function ComboboxPopup({
  options,
  getId,
  format,
  portalContainer
}) {
  return /* @__PURE__ */ jsx8(Combobox.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx8(Combobox.Positioner, { className: "paper-combobox__positioner", sideOffset: 4, children: /* @__PURE__ */ jsxs8(Combobox.Popup, { className: "paper-combobox__popup paper-elevation-floating", children: [
    /* @__PURE__ */ jsx8(Combobox.Empty, { className: "paper-combobox__empty", children: "No matching options." }),
    /* @__PURE__ */ jsx8(Combobox.List, { children: options.map((option, index) => /* @__PURE__ */ jsxs8(
      Combobox.Item,
      {
        value: option,
        index,
        className: "paper-combobox__item",
        children: [
          /* @__PURE__ */ jsx8(Combobox.ItemIndicator, { className: "paper-combobox__indicator", "aria-hidden": "true", children: /* @__PURE__ */ jsx8(CheckIcon, { className: iconClassName("compact") }) }),
          /* @__PURE__ */ jsx8("span", { children: format(option) })
        ]
      },
      getId(option)
    )) })
  ] }) }) });
}
function AutocompleteInput({
  label,
  hint,
  required,
  options,
  format,
  getId,
  match,
  maxResults,
  disabled,
  placeholder
}) {
  const id = `paper-autocomplete-${useId7().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const [query, setQuery] = useState("");
  const [portalContainer, setPortalContainer] = useState(null);
  const filtered = useFilteredOptions(options, query, format, match, maxResults);
  return /* @__PURE__ */ jsx8(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs8(
    Combobox.Root,
    {
      items: options,
      filteredItems: filtered,
      value: field.state.value ?? null,
      onValueChange: (value, details2) => {
        if (details2.reason === "escape-key") details2.cancel();
        else field.handleChange(value);
      },
      onInputValueChange: (value, details2) => {
        if (details2.reason === "escape-key") details2.cancel();
        else setQuery(value);
      },
      itemToStringLabel: format,
      isItemEqualToValue: (left, right) => getId(left) === getId(right),
      disabled,
      children: [
        /* @__PURE__ */ jsxs8("div", { className: "paper-combobox__input-group", children: [
          /* @__PURE__ */ jsx8(
            Combobox.Input,
            {
              id,
              name: field.name,
              onBlur: field.handleBlur,
              "aria-required": required || void 0,
              placeholder,
              className: "paper-input",
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy(id, hint, error),
              ref: (node) => {
                if (node && node.ownerDocument.body !== portalContainer) setPortalContainer(node.ownerDocument.body);
              }
            }
          ),
          /* @__PURE__ */ jsx8(Combobox.Trigger, { className: "paper-combobox__trigger", "aria-label": `Show ${label.toLocaleLowerCase()} options`, children: /* @__PURE__ */ jsx8(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
        ] }),
        /* @__PURE__ */ jsx8(ComboboxPopup, { options: filtered, getId, format, portalContainer })
      ]
    }
  ) });
}
function AutocompleteMultiInput({
  label,
  hint,
  required,
  options,
  format,
  getId,
  match,
  maxResults,
  maxSelections,
  disabled,
  placeholder
}) {
  const id = `paper-multiautocomplete-${useId7().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const values = field.state.value ?? [];
  const [query, setQuery] = useState("");
  const [portalContainer, setPortalContainer] = useState(null);
  const filtered = useFilteredOptions(options, query, format, match, maxResults);
  const atLimit = maxSelections !== void 0 && values.length >= maxSelections;
  return /* @__PURE__ */ jsx8(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs8(
    Combobox.Root,
    {
      multiple: true,
      items: options,
      filteredItems: filtered,
      value: values,
      onValueChange: (value, details2) => {
        if (details2.reason === "escape-key") details2.cancel();
        else if (maxSelections === void 0 || value.length <= maxSelections || value.length < values.length) field.handleChange(value);
      },
      onInputValueChange: (value, details2) => {
        if (details2.reason === "escape-key") details2.cancel();
        else setQuery(value);
      },
      itemToStringLabel: format,
      isItemEqualToValue: (left, right) => getId(left) === getId(right),
      disabled,
      children: [
        /* @__PURE__ */ jsxs8(Combobox.Chips, { className: "paper-combobox__chips", children: [
          values.map((value) => /* @__PURE__ */ jsxs8(Combobox.Chip, { className: "paper-combobox__chip", children: [
            format(value),
            /* @__PURE__ */ jsx8(Combobox.ChipRemove, { "aria-label": `Remove ${format(value)}`, children: /* @__PURE__ */ jsx8(XMarkIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
          ] }, getId(value))),
          /* @__PURE__ */ jsx8(
            Combobox.Input,
            {
              id,
              name: field.name,
              onBlur: field.handleBlur,
              "aria-required": required || void 0,
              placeholder: atLimit ? "Maximum selections reached" : placeholder,
              className: "paper-combobox__chip-input",
              disabled: disabled || atLimit,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy(id, hint, error),
              ref: (node) => {
                if (node && node.ownerDocument.body !== portalContainer) setPortalContainer(node.ownerDocument.body);
              }
            }
          ),
          /* @__PURE__ */ jsx8(Combobox.Trigger, { disabled: disabled || atLimit, className: "paper-combobox__trigger", "aria-label": `Show ${label.toLocaleLowerCase()} options`, children: /* @__PURE__ */ jsx8(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
        ] }),
        /* @__PURE__ */ jsx8(ComboboxPopup, { options: filtered, getId, format, portalContainer })
      ]
    }
  ) });
}
function TagsInput({
  label,
  hint,
  required,
  options,
  match,
  maxResults = 50,
  maxSelections,
  disabled,
  placeholder = "Type to add a tag"
}) {
  const id = `paper-tags-${useId7().replace(/:/gu, "")}`;
  const { field, error } = useField();
  const values = field.state.value ?? [];
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);
  const chipsRef = useRef(null);
  const composing = useRef(false);
  const atLimit = maxSelections !== void 0 && values.length >= maxSelections;
  const tagKey = (value) => value.trim().toLocaleLowerCase();
  const candidate = query.trim();
  const knownTags = [...new Map(options.filter((value) => value.trim()).map((value) => [tagKey(value), value.trim()])).values()];
  const alreadySelected = values.some((value) => tagKey(value) === tagKey(candidate));
  const existingTag = knownTags.find((value) => tagKey(value) === tagKey(candidate));
  const canCreate = Boolean(candidate) && !alreadySelected && !existingTag;
  const suggestions = knownTags.filter(
    (value) => !values.some((selected) => tagKey(selected) === tagKey(value)) && (!candidate || (match ? match(value, query) : tagKey(value).includes(tagKey(candidate))))
  ).slice(0, maxResults);
  const items = canCreate ? [...suggestions, candidate] : suggestions;
  return /* @__PURE__ */ jsx8(FieldFrame, { id, label, hint, required, error, children: /* @__PURE__ */ jsxs8(
    Combobox.Root,
    {
      multiple: true,
      items,
      filteredItems: items,
      value: values,
      inputValue: query,
      open: open && !atLimit,
      onOpenChange: setOpen,
      onValueChange: (value, details2) => {
        if (details2.reason === "escape-key") details2.cancel();
        else if (maxSelections === void 0 || value.length <= maxSelections || value.length < values.length) {
          field.handleChange(value);
          if (value.length > values.length) {
            setQuery("");
            setOpen(false);
          }
        }
      },
      onInputValueChange: (value, details2) => {
        if (details2.reason === "escape-key") details2.cancel();
        else setQuery(value);
      },
      disabled,
      children: [
        /* @__PURE__ */ jsxs8(Combobox.Chips, { ref: chipsRef, className: "paper-combobox__chips paper-tags-input", children: [
          values.map((value) => /* @__PURE__ */ jsxs8(Combobox.Chip, { className: "paper-combobox__chip", children: [
            value,
            /* @__PURE__ */ jsx8(Combobox.ChipRemove, { "aria-label": `Remove ${value}`, children: /* @__PURE__ */ jsx8(XMarkIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
          ] }, value)),
          /* @__PURE__ */ jsx8(
            Combobox.Input,
            {
              id,
              name: field.name,
              onBlur: field.handleBlur,
              onCompositionStart: () => {
                composing.current = true;
              },
              onCompositionEnd: () => {
                composing.current = false;
              },
              onKeyDown: (event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                if (composing.current || event.nativeEvent.isComposing || event.keyCode === 229 || atLimit) {
                  event.preventBaseUIHandler();
                  return;
                }
                if (event.currentTarget.getAttribute("aria-activedescendant")) return;
                event.preventBaseUIHandler();
                if (candidate && !alreadySelected) {
                  field.handleChange([...values, existingTag ?? candidate]);
                  setQuery("");
                  setOpen(false);
                }
              },
              "aria-required": required || void 0,
              placeholder: atLimit ? "Maximum tags reached" : placeholder,
              className: "paper-combobox__chip-input",
              disabled,
              readOnly: atLimit,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy(id, hint, error),
              ref: (node) => {
                if (node && node.ownerDocument.body !== portalContainer) setPortalContainer(node.ownerDocument.body);
              }
            }
          ),
          /* @__PURE__ */ jsx8(Combobox.Trigger, { disabled: disabled || atLimit, className: "paper-combobox__trigger", "aria-label": `Show ${label.toLocaleLowerCase()} options`, children: /* @__PURE__ */ jsx8(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" }) })
        ] }),
        /* @__PURE__ */ jsx8(Combobox.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx8(Combobox.Positioner, { anchor: chipsRef, align: "start", className: "paper-combobox__positioner", sideOffset: 4, children: /* @__PURE__ */ jsxs8(Combobox.Popup, { className: "paper-combobox__popup paper-elevation-floating", children: [
          /* @__PURE__ */ jsx8(Combobox.Empty, { className: "paper-combobox__empty", children: alreadySelected ? "This tag is already added." : "Type to add a tag." }),
          /* @__PURE__ */ jsx8(Combobox.List, { children: items.map((value, index) => /* @__PURE__ */ jsx8(Combobox.Item, { value, index, className: "paper-combobox__item paper-tags-input__option", children: canCreate && value === candidate ? `Add \u201C${value}\u201D` : value }, value)) })
        ] }) }) })
      ]
    }
  ) });
}

// src/components/forms/form.tsx
import { createFormHook } from "@tanstack/react-form";
import { jsx as jsx9 } from "react/jsx-runtime";
function Form({ children, ...props }) {
  const form = useFormContext();
  return /* @__PURE__ */ jsx9("form", { ...props, noValidate: true, onSubmit: async (event) => {
    event.preventDefault();
    if (form.state.isSubmitting) return;
    const element = event.currentTarget;
    await form.handleSubmit();
    if (!form.state.isValid) {
      element.querySelector('input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"], [aria-invalid="true"] button:not(:disabled)')?.focus();
    }
  }, children });
}
var { useAppForm: usePaperForm, withForm: withPaperForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: { Input, Checkbox, Select, TextArea, RadioGroup, RadioSelect, ToggleSelect, AutocompleteInput, AutocompleteMultiInput, TagsInput, AmountWithUnit },
  formComponents: { Form }
});

// src/components/feedback/feedback.tsx
import { Toast } from "@base-ui/react/toast";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
function Flash({
  variant = "information",
  title,
  icon,
  action,
  visible = true,
  children,
  className,
  ...props
}) {
  if (!visible) return null;
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      ...props,
      className: `paper-flash paper-flash--${variant}${className ? ` ${className}` : ""}`,
      role: variant === "danger" ? "alert" : "status",
      children: [
        icon ? /* @__PURE__ */ jsx10("span", { className: "paper-flash__icon", "aria-hidden": "true", children: icon }) : null,
        /* @__PURE__ */ jsxs9("div", { className: "paper-flash__content", children: [
          title ? /* @__PURE__ */ jsx10("strong", { children: title }) : null,
          /* @__PURE__ */ jsx10("div", { children })
        ] }),
        action ? /* @__PURE__ */ jsx10("div", { className: "paper-flash__action", children: action }) : null
      ]
    }
  );
}
function Loading({
  label = "Loading",
  size = "default",
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs9("div", { ...props, className: `paper-loading paper-loading--${size}${className ? ` ${className}` : ""}`, role: "status", children: [
    /* @__PURE__ */ jsx10("span", { className: "paper-loading__spinner", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx10("span", { className: "paper-visually-hidden", children: label })
  ] });
}
function surfaceClassName({
  elevation = "flat",
  accent = false,
  className
} = {}) {
  return [
    "paper-surface-card",
    `paper-elevation-${elevation}`,
    accent ? "paper-accent-rail" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
}
function Surface({
  as: Element = "div",
  elevation = "flat",
  accent = false,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx10(Element, { ...props, className: surfaceClassName({ elevation, accent, className }) });
}
function ButtonGroup({ actions, label = "Actions", align = "start" }) {
  return /* @__PURE__ */ jsx10("div", { className: `paper-button-group paper-button-group--${align}`, role: "group", "aria-label": label, children: actions.filter((action) => action.visible !== false).map((action) => {
    const className = buttonClassName({ variant: action.variant });
    return action.href ? /* @__PURE__ */ jsx10(
      "a",
      {
        href: action.href,
        className,
        "aria-disabled": action.disabled || void 0,
        onClick: (event) => {
          if (action.disabled) event.preventDefault();
          else action.onSelect?.();
        },
        children: action.label
      },
      action.id
    ) : /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        className,
        disabled: action.disabled,
        onClick: action.onSelect,
        children: action.label
      },
      action.id
    );
  }) });
}
function PaperToastViewport() {
  const manager = Toast.useToastManager();
  return /* @__PURE__ */ jsx10(Toast.Viewport, { className: "paper-toast-viewport", children: manager.toasts.map((toast) => /* @__PURE__ */ jsxs9(Toast.Root, { toast, className: "paper-toast paper-elevation-floating", children: [
    /* @__PURE__ */ jsxs9(Toast.Content, { className: "paper-toast__content", children: [
      toast.title ? /* @__PURE__ */ jsx10(Toast.Title, { className: "paper-toast__title", children: toast.title }) : null,
      toast.description ? /* @__PURE__ */ jsx10(Toast.Description, { children: toast.description }) : null
    ] }),
    toast.actionProps ? /* @__PURE__ */ jsx10(Toast.Action, { className: buttonClassName({ variant: "outline" }), ...toast.actionProps }) : null,
    /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        className: "paper-toast__close",
        "aria-label": "Dismiss notification",
        onClick: () => manager.close(toast.id),
        children: /* @__PURE__ */ jsx10(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" })
      }
    )
  ] }, toast.id)) });
}
function ToastProvider({ children, timeout, limit }) {
  return /* @__PURE__ */ jsxs9(Toast.Provider, { timeout, limit, children: [
    children,
    /* @__PURE__ */ jsx10(PaperToastViewport, {})
  ] });
}
function useToast() {
  return Toast.useToastManager();
}

// src/components/forms/AppliedFilters/AppliedFilters.tsx
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
function AppliedFilters({
  filters,
  onClear,
  label = "Applied filters",
  clearLabel = "Clear all",
  className
}) {
  if (filters.length === 0) return null;
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: className ? `paper-applied-filters ${className}` : "paper-applied-filters",
      role: "group",
      "aria-label": label,
      children: [
        /* @__PURE__ */ jsx11("p", { className: "paper-applied-filters__list", children: filters.join(" \xB7 ") }),
        /* @__PURE__ */ jsx11(Button, { variant: "link", onClick: onClear, children: clearLabel })
      ]
    }
  );
}

// src/components/feedback/EmptyState/EmptyState.tsx
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
function EmptyState({
  title,
  headingLevel,
  description,
  actions,
  variant = "surface",
  headingRef,
  className,
  ...props
}) {
  const Heading = `h${headingLevel}`;
  const typeRole = headingLevel === 1 ? "paper-type-page" : headingLevel === 2 ? "paper-type-section" : "paper-type-component";
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      ...props,
      className: `paper-empty-state paper-empty-state--${variant}${className ? ` ${className}` : ""}`,
      children: [
        /* @__PURE__ */ jsx12(
          Heading,
          {
            ref: headingRef,
            tabIndex: headingRef ? -1 : void 0,
            className: `paper-empty-state__title ${typeRole}`,
            children: title
          }
        ),
        description ? /* @__PURE__ */ jsx12("p", { className: "paper-empty-state__description", children: description }) : null,
        actions ? /* @__PURE__ */ jsx12("div", { className: "paper-empty-state__actions", children: actions }) : null
      ]
    }
  );
}

// src/components/feedback/StatusTag/StatusTag.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
function StatusTag({ tone = "neutral", children, className }) {
  return /* @__PURE__ */ jsx13("span", { className: `paper-status-tag paper-status-tag--${tone}${className ? ` ${className}` : ""}`, children });
}

// src/components/page-structure/PageHeader/PageHeader.tsx
import { Fragment, jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
function nativeLink(props) {
  return /* @__PURE__ */ jsx14("a", { ...props });
}
function PageHeader({
  title,
  lead,
  status,
  actions,
  backLink,
  renderLink = nativeLink,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs12("header", { className: className ? `paper-page-header ${className}` : "paper-page-header", children: [
    backLink ? renderLink({
      href: backLink.href,
      className: "paper-page-header__back",
      children: /* @__PURE__ */ jsxs12(Fragment, { children: [
        /* @__PURE__ */ jsx14(ChevronLeftIcon, { className: iconClassName("compact"), "aria-hidden": "true" }),
        /* @__PURE__ */ jsx14("span", { children: backLink.label })
      ] })
    }) : null,
    /* @__PURE__ */ jsxs12("div", { className: "paper-page-header__main", children: [
      /* @__PURE__ */ jsxs12("div", { className: "paper-page-header__text", children: [
        /* @__PURE__ */ jsxs12("div", { className: "paper-page-header__heading", children: [
          /* @__PURE__ */ jsx14("h1", { className: "paper-page-header__title paper-type-page", children: title }),
          status ? /* @__PURE__ */ jsx14("div", { className: "paper-page-header__status", children: status }) : null
        ] }),
        lead ? /* @__PURE__ */ jsx14("p", { className: "paper-page-header__lead", children: lead }) : null,
        children
      ] }),
      actions ? /* @__PURE__ */ jsx14("div", { className: "paper-page-header__actions", children: actions }) : null
    ] })
  ] });
}

// src/components/page-structure/SectionHeading/SectionHeading.tsx
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
function SectionHeading({
  level,
  title,
  id,
  description,
  status,
  actions,
  className
}) {
  const Heading = `h${level}`;
  const typeRole = level === 2 ? "paper-type-section" : "paper-type-component";
  return /* @__PURE__ */ jsxs13("div", { className: className ? `paper-section-heading ${className}` : "paper-section-heading", children: [
    /* @__PURE__ */ jsxs13("div", { className: "paper-section-heading__text", children: [
      /* @__PURE__ */ jsxs13("div", { className: "paper-section-heading__heading", children: [
        /* @__PURE__ */ jsx15(Heading, { id, className: `paper-section-heading__title ${typeRole}`, children: title }),
        status ? /* @__PURE__ */ jsx15("div", { className: "paper-section-heading__status", children: status }) : null
      ] }),
      description ? /* @__PURE__ */ jsx15("p", { className: "paper-section-heading__description", children: description }) : null
    ] }),
    actions ? /* @__PURE__ */ jsx15("div", { className: "paper-section-heading__actions", children: actions }) : null
  ] });
}

// src/components/data-display/DescriptionList/DescriptionList.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
function DescriptionList({ items, layout = "stacked", className }) {
  return /* @__PURE__ */ jsx16("dl", { className: `paper-description-list paper-description-list--${layout}${className ? ` ${className}` : ""}`, children: items.map((item, index) => /* @__PURE__ */ jsxs14("div", { className: "paper-description-list__item", children: [
    /* @__PURE__ */ jsx16("dt", { className: "paper-description-list__label", children: item.label }),
    /* @__PURE__ */ jsx16("dd", { className: "paper-description-list__value", children: item.value })
  ] }, index)) });
}

// src/components/data-display/StatGrid/StatGrid.tsx
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
function StatGrid({ stats, className }) {
  return /* @__PURE__ */ jsx17("dl", { className: className ? `paper-stat-grid ${className}` : "paper-stat-grid", children: stats.map((stat, index) => /* @__PURE__ */ jsxs15("div", { className: "paper-stat-grid__item", children: [
    /* @__PURE__ */ jsx17("dt", { className: "paper-stat-grid__label", children: stat.label }),
    /* @__PURE__ */ jsxs15("dd", { className: "paper-stat-grid__value", children: [
      stat.value,
      stat.detail ? /* @__PURE__ */ jsx17("span", { className: "paper-stat-grid__detail", children: stat.detail }) : null
    ] })
  ] }, index)) });
}

// src/components/overlays/Modal/Modal.tsx
import { Dialog } from "@base-ui/react/dialog";
import {
  useState as useState2
} from "react";
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
function Modal({
  triggerLabel,
  trigger,
  triggerVariant = "default",
  title,
  description,
  children,
  closeLabel = "Close",
  action,
  footer,
  defaultOpen,
  open,
  onOpenChange,
  initialFocus
}) {
  const [portalContainer, setPortalContainer] = useState2(null);
  const captureTrigger = (node) => {
    if (node && node.ownerDocument.body !== portalContainer) {
      setPortalContainer(node.ownerDocument.body);
    }
  };
  return /* @__PURE__ */ jsxs16(
    Dialog.Root,
    {
      defaultOpen,
      open,
      onOpenChange: (nextOpen) => onOpenChange?.(nextOpen),
      children: [
        trigger ? /* @__PURE__ */ jsx18(Dialog.Trigger, { ref: captureTrigger, render: trigger }) : /* @__PURE__ */ jsx18(
          Dialog.Trigger,
          {
            ref: captureTrigger,
            className: buttonClassName({ variant: triggerVariant }),
            children: triggerLabel
          }
        ),
        /* @__PURE__ */ jsxs16(Dialog.Portal, { container: portalContainer, children: [
          /* @__PURE__ */ jsx18(Dialog.Backdrop, { className: "paper-modal__backdrop" }),
          /* @__PURE__ */ jsx18(Dialog.Viewport, { className: "paper-modal__viewport", children: /* @__PURE__ */ jsxs16(
            Dialog.Popup,
            {
              className: "paper-modal paper-surface-raised paper-elevation-showcase",
              initialFocus,
              children: [
                /* @__PURE__ */ jsxs16("header", { className: "paper-modal__header", children: [
                  /* @__PURE__ */ jsxs16("div", { children: [
                    /* @__PURE__ */ jsx18(Dialog.Title, { className: "paper-modal__title", children: title }),
                    description ? /* @__PURE__ */ jsx18(Dialog.Description, { className: "paper-modal__description", children: description }) : null
                  ] }),
                  /* @__PURE__ */ jsx18(
                    Dialog.Close,
                    {
                      className: "paper-modal__icon-close",
                      "aria-label": closeLabel,
                      children: /* @__PURE__ */ jsx18(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" })
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx18("div", { className: "paper-modal__content", children }),
                footer === void 0 ? /* @__PURE__ */ jsxs16("footer", { className: "paper-modal__footer", children: [
                  action ? /* @__PURE__ */ jsx18(
                    Dialog.Close,
                    {
                      className: buttonClassName({ variant: action.variant }),
                      disabled: action.disabled,
                      onClick: action.onAction,
                      children: action.label
                    }
                  ) : null,
                  /* @__PURE__ */ jsx18(Dialog.Close, { className: buttonClassName({ variant: "outline" }), children: closeLabel })
                ] }) : footer === null ? null : /* @__PURE__ */ jsx18("footer", { className: "paper-modal__footer", children: footer })
              ]
            }
          ) })
        ] })
      ]
    }
  );
}

// src/components/overlays/Drawer/Drawer.tsx
import { Dialog as Dialog2 } from "@base-ui/react/dialog";
import {
  useState as useState3
} from "react";
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
function Drawer({
  trigger,
  title,
  description,
  children,
  footer,
  placement = "end",
  closeLabel = "Close",
  defaultOpen,
  open,
  onOpenChange
}) {
  const [portalContainer, setPortalContainer] = useState3(null);
  return /* @__PURE__ */ jsxs17(
    Dialog2.Root,
    {
      defaultOpen,
      modal: true,
      open,
      onOpenChange: (nextOpen) => onOpenChange?.(nextOpen),
      children: [
        /* @__PURE__ */ jsx19(
          Dialog2.Trigger,
          {
            render: trigger,
            ref: (node) => {
              if (node && node.ownerDocument.body !== portalContainer) {
                setPortalContainer(node.ownerDocument.body);
              }
            }
          }
        ),
        /* @__PURE__ */ jsxs17(Dialog2.Portal, { container: portalContainer, children: [
          /* @__PURE__ */ jsx19(Dialog2.Backdrop, { className: "paper-drawer__backdrop" }),
          /* @__PURE__ */ jsx19(
            Dialog2.Viewport,
            {
              className: "paper-drawer__viewport",
              "data-placement": placement,
              children: /* @__PURE__ */ jsxs17(
                Dialog2.Popup,
                {
                  className: "paper-drawer paper-surface-raised paper-elevation-showcase",
                  "data-placement": placement,
                  children: [
                    /* @__PURE__ */ jsxs17("header", { className: "paper-drawer__header", children: [
                      /* @__PURE__ */ jsxs17("div", { className: "paper-drawer__heading", children: [
                        /* @__PURE__ */ jsx19(Dialog2.Title, { className: "paper-drawer__title", children: title }),
                        description ? /* @__PURE__ */ jsx19(Dialog2.Description, { className: "paper-drawer__description", children: description }) : null
                      ] }),
                      /* @__PURE__ */ jsx19(
                        Dialog2.Close,
                        {
                          className: buttonClassName({
                            variant: "ghost",
                            className: "paper-drawer__close"
                          }),
                          "aria-label": closeLabel,
                          children: /* @__PURE__ */ jsx19(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" })
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx19("div", { className: "paper-drawer__body", children }),
                    footer ? /* @__PURE__ */ jsx19("footer", { className: "paper-drawer__footer", children: footer }) : null
                  ]
                }
              )
            }
          )
        ] })
      ]
    }
  );
}

// src/components/overlays/ActionMenu/ActionMenu.tsx
import { Menu } from "@base-ui/react/menu";
import { useState as useState4 } from "react";
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
function ActionMenu({
  label,
  items,
  triggerVariant = "outline",
  defaultOpen,
  iconOnly = false,
  triggerIcon
}) {
  const [portalContainer, setPortalContainer] = useState4(null);
  return /* @__PURE__ */ jsxs18(Menu.Root, { defaultOpen, children: [
    /* @__PURE__ */ jsxs18(
      Menu.Trigger,
      {
        ref: (node) => {
          if (node && node.ownerDocument.body !== portalContainer) {
            setPortalContainer(node.ownerDocument.body);
          }
        },
        className: buttonClassName({ variant: triggerVariant, className: iconOnly ? "paper-action-menu__trigger--icon-only" : void 0 }),
        "aria-label": iconOnly ? label : void 0,
        title: iconOnly ? label : void 0,
        children: [
          !iconOnly && /* @__PURE__ */ jsx20("span", { children: label }),
          triggerIcon ? /* @__PURE__ */ jsx20("span", { className: "paper-action-menu__icon", "aria-hidden": "true", children: triggerIcon }) : iconOnly ? /* @__PURE__ */ jsx20(EllipsisHorizontalIcon, { className: iconClassName(), "aria-hidden": "true" }) : /* @__PURE__ */ jsx20(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" })
        ]
      }
    ),
    /* @__PURE__ */ jsx20(Menu.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx20(
      Menu.Positioner,
      {
        className: "paper-action-menu__positioner",
        sideOffset: 6,
        align: "end",
        children: /* @__PURE__ */ jsx20(Menu.Popup, { className: "paper-action-menu paper-elevation-floating", children: items.map((item) => /* @__PURE__ */ jsxs18(
          Menu.Item,
          {
            className: `paper-action-menu__item${item.destructive ? " paper-action-menu__item--destructive" : ""}`,
            disabled: item.disabled,
            label: item.label,
            onClick: item.onSelect,
            children: [
              item.icon ? /* @__PURE__ */ jsx20("span", { className: "paper-action-menu__icon", "aria-hidden": "true", children: item.icon }) : null,
              /* @__PURE__ */ jsx20("span", { children: item.label })
            ]
          },
          item.id
        )) })
      }
    ) })
  ] });
}

// src/components/data-display/Table.tsx
import {
  useId as useId8
} from "react";
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
import { createElement } from "react";
function classNames(...values) {
  return values.filter(Boolean).join(" ");
}
function Table({
  caption,
  captionVisibility = "visible",
  columns,
  rows,
  getRowKey,
  getRowProps,
  emptyMessage = "No data to display.",
  minWidth = `${Math.max(30, columns.length * 9)}rem`,
  tableClassName,
  className,
  style,
  ...regionProps
}) {
  if (columns.length === 0) {
    throw new Error("Table requires at least one column.");
  }
  const reactId = useId8();
  const captionId = `paper-table-caption-${reactId.replace(/:/gu, "")}`;
  const regionStyle = {
    ...style,
    "--paper-table-min-inline-size": minWidth
  };
  return /* @__PURE__ */ jsx21(
    "div",
    {
      ...regionProps,
      className: classNames("paper-table-region", "paper-focus-ring", className),
      style: regionStyle,
      role: "region",
      "aria-labelledby": captionId,
      tabIndex: 0,
      children: /* @__PURE__ */ jsxs19("table", { className: classNames("paper-table", tableClassName), children: [
        /* @__PURE__ */ jsx21(
          "caption",
          {
            id: captionId,
            className: classNames(
              "paper-table__caption",
              captionVisibility === "screen-reader" && "paper-table__caption--screen-reader"
            ),
            children: caption
          }
        ),
        /* @__PURE__ */ jsx21("thead", { children: /* @__PURE__ */ jsx21("tr", { children: columns.map((column) => /* @__PURE__ */ jsx21(
          "th",
          {
            scope: "col",
            className: classNames(
              "paper-table__header",
              `paper-table__cell--${column.align ?? "start"}`
            ),
            style: column.width ? { width: column.width } : void 0,
            children: column.header
          },
          column.id
        )) }) }),
        /* @__PURE__ */ jsx21("tbody", { children: rows.length === 0 ? /* @__PURE__ */ jsx21("tr", { children: /* @__PURE__ */ jsx21("td", { className: "paper-table__empty", colSpan: columns.length, children: emptyMessage }) }) : rows.map((row, rowIndex) => /* @__PURE__ */ createElement("tr", { ...getRowProps?.(row, rowIndex), key: getRowKey?.(row, rowIndex) ?? rowIndex }, columns.map((column) => {
          const Cell2 = column.rowHeader ? "th" : "td";
          return /* @__PURE__ */ jsx21(
            Cell2,
            {
              ...column.rowHeader ? { scope: "row" } : {},
              className: classNames(
                "paper-table__cell",
                column.rowHeader && "paper-table__row-header",
                `paper-table__cell--${column.align ?? "start"}`
              ),
              children: column.cell(row, rowIndex)
            },
            column.id
          );
        }))) })
      ] })
    }
  );
}

// src/components/data-display/HeatmapChart.tsx
import { DateTime, Interval } from "luxon";
import {
  useEffect,
  useRef as useRef2,
  useState as useState5
} from "react";
import { createPortal } from "react-dom";
import { Fragment as Fragment2, jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var CELL_WIDTH = 10;
var CELL_HEIGHT = 10;
var CELL_GAP = 3;
var OFFSET = { x: 30, y: 15 };
var WEEKDAYS = ["Mon", void 0, "Wed", void 0, "Fri", void 0, "Sun"];
function HeatmapChart({ id, data, year }) {
  const start = DateTime.fromObject({ year, month: 1, day: 1 });
  const end = DateTime.fromObject({ year, month: 12, day: 31 });
  const values = /* @__PURE__ */ new Map();
  const tooltips = /* @__PURE__ */ new Map();
  for (const datum of data) {
    values.set(datum.date, datum.value);
    if (datum.tooltip) tooltips.set(datum.date, datum.tooltip);
  }
  const dates = Interval.fromDateTimes(start, end.endOf("day")).splitBy({ day: 1 }).map((interval) => interval.start);
  const weekCount = start.weeksInWeekYear + 1;
  const columns = Array.from(
    { length: weekCount },
    () => Array.from({ length: 7 })
  );
  let columnIndex = 0;
  for (const date of dates) {
    if (date === null) continue;
    const isoDate = date.toISODate();
    if (isoDate === null) continue;
    columns[columnIndex][date.weekday - 1] = {
      date,
      value: values.get(isoDate) ?? 0,
      tooltip: tooltips.get(isoDate)
    };
    if (date.weekday === 7) columnIndex += 1;
  }
  const maxValue = Math.max(
    ...columns.flatMap((column) => column.map((cell) => cell?.value ?? 0))
  );
  let lastMonth = 0;
  const months = columns.map((column) => {
    const shouldRender = column.filter((cell) => cell !== void 0).some((cell) => cell.date.month > lastMonth);
    if (!shouldRender) return void 0;
    lastMonth += 1;
    return DateTime.fromObject({ month: lastMonth }).toFormat("LLL");
  });
  const tooltipLayerId = `paper-heatmap-tooltip-${id}`;
  const titleId = `paper-heatmap-title-${id}`;
  const width = weekCount * CELL_WIDTH + (weekCount - 1) * CELL_GAP + OFFSET.x + 10;
  const height = 7 * CELL_HEIGHT + 6 * CELL_GAP + OFFSET.y;
  return /* @__PURE__ */ jsxs20(
    "svg",
    {
      width,
      height,
      viewBox: `0 0 ${width} ${height}`,
      className: "paper-heatmap",
      role: "img",
      "aria-labelledby": titleId,
      "data-year": year,
      children: [
        /* @__PURE__ */ jsxs20("title", { id: titleId, children: [
          "Daily activity for ",
          year
        ] }),
        WEEKDAYS.map((label, row) => label ? /* @__PURE__ */ jsx22(
          "text",
          {
            className: "paper-heatmap__axis-label",
            textAnchor: "end",
            x: OFFSET.x - CELL_GAP * 2,
            y: OFFSET.y + CELL_HEIGHT * row + CELL_GAP * row,
            dominantBaseline: "hanging",
            children: label
          },
          label
        ) : null),
        months.map((label, column) => label ? /* @__PURE__ */ jsx22(
          "text",
          {
            className: "paper-heatmap__axis-label",
            textAnchor: "start",
            x: OFFSET.x + CELL_WIDTH * column + CELL_GAP * column,
            y: 0,
            dominantBaseline: "hanging",
            children: label
          },
          label
        ) : null),
        columns.map(
          (column, columnPosition) => column.map((cell, rowPosition) => /* @__PURE__ */ jsx22(
            Cell,
            {
              cell,
              tooltipLayerId,
              maxValue,
              column: columnPosition,
              row: rowPosition,
              parentWidth: width
            },
            `${columnPosition}-${rowPosition}`
          ))
        ),
        /* @__PURE__ */ jsx22("g", { id: tooltipLayerId, className: "paper-heatmap__tooltip-layer" })
      ]
    }
  );
}
function Cell({
  cell,
  tooltipLayerId,
  maxValue,
  row,
  column,
  parentWidth
}) {
  const [mounted, setMounted] = useState5(false);
  const [tooltipVisible, setTooltipVisible] = useState5(false);
  const x = OFFSET.x + CELL_WIDTH * column + CELL_GAP * column;
  const y = OFFSET.y + CELL_HEIGHT * row + CELL_GAP * row;
  const isoDate = cell?.date.toISODate() ?? void 0;
  const tooltip = cell && cell.value !== 0 ? cell.tooltip : void 0;
  const tooltipId = `${tooltipLayerId}-${column}-${row}`;
  const level = getCellDepth(maxValue, cell?.value);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  const rect = /* @__PURE__ */ jsx22(
    "rect",
    {
      width: CELL_WIDTH,
      height: CELL_HEIGHT,
      x,
      y,
      className: "paper-heatmap__cell",
      "data-level": level,
      "data-date": isoDate,
      "data-value": cell?.value,
      "aria-hidden": cell === void 0 ? true : void 0,
      "aria-label": cell ? cell.tooltip ?? `${isoDate}: ${cell.value}` : void 0,
      "aria-describedby": tooltip && tooltipVisible ? tooltipId : void 0,
      role: cell ? "graphics-symbol" : void 0,
      tabIndex: tooltip ? 0 : void 0,
      onMouseOver: tooltip ? () => setTooltipVisible(true) : void 0,
      onMouseOut: tooltip ? () => setTooltipVisible(false) : void 0,
      onFocus: tooltip ? () => setTooltipVisible(true) : void 0,
      onBlur: tooltip ? () => setTooltipVisible(false) : void 0,
      onTouchStart: tooltip ? () => setTooltipVisible(true) : void 0,
      onTouchMove: tooltip ? () => setTooltipVisible(true) : void 0,
      onTouchEnd: tooltip ? () => setTooltipVisible(false) : void 0,
      onTouchCancel: tooltip ? () => setTooltipVisible(false) : void 0
    }
  );
  if (!mounted || !tooltip) return rect;
  const tooltipLayer = document.getElementById(tooltipLayerId);
  return /* @__PURE__ */ jsxs20(Fragment2, { children: [
    rect,
    tooltipLayer ? createPortal(
      /* @__PURE__ */ jsx22(
        Tooltip,
        {
          id: tooltipId,
          row,
          column,
          visible: tooltipVisible,
          parentWidth,
          children: tooltip
        }
      ),
      tooltipLayer
    ) : null
  ] });
}
function Tooltip({
  id,
  row,
  column,
  children,
  visible,
  parentWidth
}) {
  const textRef = useRef2(null);
  const [rect, setRect] = useState5({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    arrowX: 0
  });
  useEffect(() => {
    if (!visible || textRef.current === null) return;
    const textRect = textRef.current.getBBox();
    const width = textRect.width + 12;
    const height = textRect.height + 12;
    const arrowX = OFFSET.x + CELL_WIDTH * column + CELL_GAP * column - width / 2;
    const y = OFFSET.y + CELL_HEIGHT * row + CELL_GAP * row - height - 2;
    let x = arrowX;
    if (x < 0) x = 20;
    if (x + width > parentWidth) x = parentWidth - width;
    x = Math.max(0, x);
    setRect({ x, y, width, height, arrowX });
  }, [children, column, parentWidth, row, visible]);
  return /* @__PURE__ */ jsxs20(
    "g",
    {
      id,
      role: "tooltip",
      className: `paper-heatmap__tooltip${visible ? "" : " hidden"}`,
      "aria-hidden": !visible,
      children: [
        /* @__PURE__ */ jsx22(
          "rect",
          {
            width: rect.width,
            height: rect.height,
            x: rect.x,
            y: rect.y,
            className: "paper-heatmap__tooltip-background"
          }
        ),
        /* @__PURE__ */ jsx22(
          "polygon",
          {
            points: pointsForRect(rect),
            className: "paper-heatmap__tooltip-background"
          }
        ),
        /* @__PURE__ */ jsx22(
          "text",
          {
            ref: textRef,
            x: rect.x + 6,
            y: rect.y + 8,
            dominantBaseline: "hanging",
            className: "paper-heatmap__tooltip-text",
            children
          }
        )
      ]
    }
  );
}
function pointsForRect({
  y,
  width,
  height,
  arrowX
}) {
  const size = 8;
  const middle = arrowX + width / 2 + CELL_WIDTH / 2;
  const left = middle - size / 2;
  const right = middle + size / 2;
  const top = y + height - 1;
  const bottom = top + 5;
  return `${left},${top} ${right},${top} ${middle},${bottom}`;
}
function getCellDepth(maxValue, value) {
  if (value === void 0) return "empty";
  if (value === 0) return "0";
  const ratio = maxValue === 0 ? 0 : value / maxValue;
  if (ratio < 0.25) return "1";
  if (ratio < 0.5) return "2";
  if (ratio < 0.75) return "3";
  return "4";
}

// src/components/navigation/Navbar.tsx
import { Menu as Menu2 } from "@base-ui/react/menu";
import {
  useId as useId9,
  useRef as useRef3,
  useState as useState6,
  useSyncExternalStore
} from "react";

// src/components/navigation/navigation.tsx
import {
  cloneElement
} from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
function defaultRenderLink(props) {
  return /* @__PURE__ */ jsx23("a", { ...props });
}
function renderNavigationLink(renderLink, props, key) {
  const element = (renderLink ?? defaultRenderLink)(props);
  return key === void 0 ? element : cloneElement(element, { key });
}
function disabledLinkClick(event) {
  event.preventDefault();
}
function linkIsCurrent(item, currentPath) {
  return item.current ?? (currentPath !== void 0 && item.href === currentPath);
}

// src/components/navigation/Navbar.tsx
import { Fragment as Fragment3, jsx as jsx24, jsxs as jsxs21 } from "react/jsx-runtime";
function noSubscription() {
  return () => {
  };
}
function NavbarDropdownMenu({
  item,
  currentPath,
  renderLink
}) {
  const [portalContainer, setPortalContainer] = useState6(null);
  return /* @__PURE__ */ jsxs21(Menu2.Root, { children: [
    /* @__PURE__ */ jsxs21(
      Menu2.Trigger,
      {
        ref: (node) => {
          if (node && node.ownerDocument.body !== portalContainer) {
            setPortalContainer(node.ownerDocument.body);
          }
        },
        className: "paper-navbar__dropdown-trigger",
        children: [
          /* @__PURE__ */ jsx24("span", { children: item.label }),
          /* @__PURE__ */ jsx24(ChevronDownIcon, { className: iconClassName("compact"), "aria-hidden": "true" })
        ]
      }
    ),
    /* @__PURE__ */ jsx24(Menu2.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx24(Menu2.Positioner, { className: "paper-navbar__positioner", sideOffset: 6, align: "end", children: /* @__PURE__ */ jsx24(Menu2.Popup, { className: "paper-navbar__dropdown paper-elevation-floating", children: item.links.map((link) => {
      const current = linkIsCurrent(link, currentPath);
      if (link.disabled) {
        return /* @__PURE__ */ jsxs21(
          Menu2.Item,
          {
            className: "paper-navbar__dropdown-item",
            disabled: true,
            label: link.label,
            children: [
              link.icon ? /* @__PURE__ */ jsx24("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
              /* @__PURE__ */ jsx24("span", { children: link.label })
            ]
          },
          link.id
        );
      }
      return /* @__PURE__ */ jsx24(
        Menu2.LinkItem,
        {
          className: "paper-navbar__dropdown-item",
          label: link.label,
          closeOnClick: true,
          "aria-current": current ? "page" : void 0,
          onClick: link.onSelect,
          render: renderNavigationLink(renderLink, {
            href: link.href,
            children: /* @__PURE__ */ jsxs21(Fragment3, { children: [
              link.icon ? /* @__PURE__ */ jsx24("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
              /* @__PURE__ */ jsx24("span", { children: link.label })
            ] })
          })
        },
        link.id
      );
    }) }) }) })
  ] });
}
function Navbar({
  navigation,
  brand,
  brandHref,
  actions,
  mobileFooter,
  mobileNavigation = true,
  currentPath,
  renderLink,
  label = "Main navigation",
  menuLabel = "Menu",
  isLoading = false
}) {
  const menuId = `paper-navbar-${useId9().replace(/:/gu, "")}`;
  const [mobileOpen, setMobileOpen] = useState6(false);
  const hydrated = useSyncExternalStore(noSubscription, () => true, () => false);
  const mobileTriggerRef = useRef3(null);
  const closeMobile = () => setMobileOpen(false);
  const onMobileClick = (event) => {
    const link = event.target.closest("a[href]");
    if (link && event.currentTarget.contains(link) && link.getAttribute("aria-disabled") !== "true") closeMobile();
  };
  const onMobileKeyDown = (event) => {
    if (event.key !== "Escape") return;
    setMobileOpen(false);
    mobileTriggerRef.current?.focus();
  };
  return /* @__PURE__ */ jsxs21("nav", { className: "paper-navbar", "aria-label": label, onKeyDown: mobileNavigation && mobileOpen ? onMobileKeyDown : void 0, onClick: mobileNavigation && mobileOpen ? onMobileClick : void 0, children: [
    /* @__PURE__ */ jsxs21("div", { className: "paper-navbar__inner", children: [
      /* @__PURE__ */ jsx24("div", { className: "paper-navbar__brand", children: renderNavigationLink(renderLink, { href: brandHref, children: brand }) }),
      navigation.length > 0 ? /* @__PURE__ */ jsx24("div", { className: "paper-navbar__desktop-links", children: navigation.map((item) => {
        if (item.type === "dropdown") {
          return /* @__PURE__ */ jsx24(NavbarDropdownMenu, { item, currentPath, renderLink }, item.id);
        }
        const current = linkIsCurrent(item, currentPath);
        return renderNavigationLink(renderLink, {
          href: item.href,
          className: "paper-navbar__link",
          "aria-current": current ? "page" : void 0,
          "aria-disabled": item.disabled || void 0,
          tabIndex: item.disabled ? -1 : void 0,
          onClick: item.disabled ? disabledLinkClick : () => item.onSelect?.(),
          children: item.label
        }, item.id);
      }) }) : null,
      actions ? /* @__PURE__ */ jsx24("div", { className: "paper-navbar__actions", children: actions }) : null,
      mobileNavigation ? /* @__PURE__ */ jsx24(
        "button",
        {
          ref: mobileTriggerRef,
          type: "button",
          className: "paper-navbar__mobile-trigger",
          "aria-controls": menuId,
          "aria-expanded": mobileOpen,
          "aria-label": mobileOpen ? `Close ${menuLabel.toLocaleLowerCase()}` : `Open ${menuLabel.toLocaleLowerCase()}`,
          disabled: !hydrated,
          onClick: () => setMobileOpen((open) => !open),
          children: mobileOpen ? /* @__PURE__ */ jsx24(XMarkIcon, { className: iconClassName("prominent"), "aria-hidden": "true" }) : /* @__PURE__ */ jsx24(Bars3Icon, { className: iconClassName("prominent"), "aria-hidden": "true" })
        }
      ) : null
    ] }),
    mobileNavigation ? /* @__PURE__ */ jsxs21(
      "div",
      {
        id: menuId,
        className: "paper-navbar__mobile-panel",
        "data-open": mobileOpen ? "true" : "false",
        hidden: !mobileOpen,
        children: [
          navigation.map(
            (item) => item.type === "link" ? /* @__PURE__ */ jsx24("div", { children: renderNavigationLink(renderLink, {
              href: item.href,
              className: "paper-navbar__mobile-link",
              "aria-current": linkIsCurrent(item, currentPath) ? "page" : void 0,
              "aria-disabled": item.disabled || void 0,
              tabIndex: item.disabled ? -1 : void 0,
              onClick: item.disabled ? disabledLinkClick : () => {
                item.onSelect?.();
                closeMobile();
              },
              children: item.label
            }) }, item.id) : /* @__PURE__ */ jsxs21("section", { className: "paper-navbar__mobile-group", "aria-labelledby": `${menuId}-${item.id}`, children: [
              /* @__PURE__ */ jsx24("h2", { id: `${menuId}-${item.id}`, children: item.label }),
              item.links.map(
                (link) => renderNavigationLink(renderLink, {
                  href: link.href,
                  className: "paper-navbar__mobile-link",
                  "aria-current": linkIsCurrent(link, currentPath) ? "page" : void 0,
                  "aria-disabled": link.disabled || void 0,
                  tabIndex: link.disabled ? -1 : void 0,
                  onClick: link.disabled ? disabledLinkClick : () => {
                    link.onSelect?.();
                    closeMobile();
                  },
                  children: /* @__PURE__ */ jsxs21(Fragment3, { children: [
                    link.icon ? /* @__PURE__ */ jsx24("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
                    /* @__PURE__ */ jsx24("span", { children: link.label })
                  ] })
                }, link.id)
              )
            ] }, item.id)
          ),
          mobileFooter?.(closeMobile)
        ]
      }
    ) : null,
    isLoading ? /* @__PURE__ */ jsx24("div", { className: "paper-navbar__loading", role: "status", children: /* @__PURE__ */ jsx24("span", { className: "paper-sr-only", children: "Loading navigation" }) }) : null
  ] });
}

// src/components/navigation/Sidebar.tsx
import { useId as useId10 } from "react";
import { Fragment as Fragment4, jsx as jsx25, jsxs as jsxs22 } from "react/jsx-runtime";
function sidebarSectionHeadingId(sidebarId, sectionId) {
  const suffix = sectionId.replace(/[^a-zA-Z0-9_-]+/gu, "-").replace(/^-+|-+$/gu, "") || "section";
  return `${sidebarId}-${suffix}`;
}
function Sidebar({
  sections: sections3,
  currentPath,
  renderLink,
  label = "Section navigation"
}) {
  const sidebarId = `paper-sidebar-${useId10().replace(/:/gu, "")}`;
  return /* @__PURE__ */ jsx25("nav", { className: "paper-sidebar", "aria-label": label, children: sections3.map((section2) => /* @__PURE__ */ jsxs22("section", { className: "paper-sidebar__section", "aria-labelledby": sidebarSectionHeadingId(sidebarId, section2.id), children: [
    /* @__PURE__ */ jsx25("h2", { id: sidebarSectionHeadingId(sidebarId, section2.id), className: "paper-sidebar__title", children: section2.title }),
    /* @__PURE__ */ jsx25("ul", { className: "paper-sidebar__list", children: section2.links.map((link) => {
      const current = linkIsCurrent(link, currentPath);
      return /* @__PURE__ */ jsx25("li", { children: renderNavigationLink(renderLink, {
        href: link.href,
        className: "paper-sidebar__link",
        "aria-current": current ? "page" : void 0,
        "aria-disabled": link.disabled || void 0,
        tabIndex: link.disabled ? -1 : void 0,
        onClick: link.disabled ? disabledLinkClick : () => link.onSelect?.(),
        children: /* @__PURE__ */ jsxs22(Fragment4, { children: [
          link.icon ? /* @__PURE__ */ jsx25("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
          /* @__PURE__ */ jsx25("span", { children: link.label })
        ] })
      }) }, link.id);
    }) })
  ] }, section2.id)) });
}

// src/components/navigation/Breadcrumb.tsx
import { Fragment as Fragment5, jsx as jsx26, jsxs as jsxs23 } from "react/jsx-runtime";
function Breadcrumb({ items, renderLink, label = "Breadcrumb" }) {
  return /* @__PURE__ */ jsx26("nav", { className: "paper-breadcrumb", "aria-label": label, children: /* @__PURE__ */ jsx26("ol", { className: "paper-breadcrumb__list", children: items.map((item, index) => {
    const current = index === items.length - 1;
    return /* @__PURE__ */ jsxs23("li", { className: "paper-breadcrumb__item", children: [
      current || !item.href ? /* @__PURE__ */ jsxs23("span", { className: "paper-breadcrumb__current", "aria-current": current ? "page" : void 0, children: [
        item.icon ? /* @__PURE__ */ jsx26("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: item.icon }) : null,
        /* @__PURE__ */ jsx26("span", { children: item.label })
      ] }) : renderNavigationLink(renderLink, {
        href: item.href,
        className: "paper-breadcrumb__link",
        children: /* @__PURE__ */ jsxs23(Fragment5, { children: [
          item.icon ? /* @__PURE__ */ jsx26("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: item.icon }) : null,
          /* @__PURE__ */ jsx26("span", { children: item.label })
        ] })
      }),
      !current ? /* @__PURE__ */ jsx26(ChevronRightIcon, { className: "paper-breadcrumb__separator paper-icon-compact", "aria-hidden": "true" }) : null
    ] }, item.id);
  }) }) });
}

// src/components/navigation/Tabbar.tsx
import { useEffect as useEffect2, useRef as useRef4 } from "react";
import { Fragment as Fragment6, jsx as jsx27, jsxs as jsxs24 } from "react/jsx-runtime";
function TabbarList({
  links,
  currentPath,
  renderLink,
  orientation
}) {
  const listRef = useRef4(null);
  const currentId = links.find((link) => linkIsCurrent(link, currentPath))?.id;
  useEffect2(() => {
    const list = listRef.current;
    if (!list || orientation !== "horizontal" || !currentId) return;
    const revealCurrent = () => {
      const current = list.querySelector('[aria-current="page"]');
      if (!current) return;
      const viewport = list.getBoundingClientRect();
      const destination = current.getBoundingClientRect();
      if (destination.left < viewport.left) list.scrollLeft += destination.left - viewport.left;
      else if (destination.right > viewport.right) list.scrollLeft += destination.right - viewport.right;
    };
    revealCurrent();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(revealCurrent);
    observer.observe(list);
    return () => observer.disconnect();
  }, [currentId, orientation]);
  return /* @__PURE__ */ jsx27("ul", { ref: listRef, className: `paper-tabbar__list paper-tabbar__list--${orientation}`, children: links.map((link) => {
    const current = linkIsCurrent(link, currentPath);
    return /* @__PURE__ */ jsx27("li", { children: renderNavigationLink(renderLink, {
      href: link.href,
      className: "paper-tabbar__link",
      "aria-current": current ? "page" : void 0,
      "aria-disabled": link.disabled || void 0,
      tabIndex: link.disabled ? -1 : void 0,
      onClick: link.disabled ? disabledLinkClick : () => link.onSelect?.(),
      children: /* @__PURE__ */ jsxs24(Fragment6, { children: [
        link.icon ? /* @__PURE__ */ jsx27("span", { className: "paper-navigation__icon", "aria-hidden": "true", children: link.icon }) : null,
        /* @__PURE__ */ jsx27("span", { children: link.label })
      ] })
    }) }, link.id);
  }) });
}
function Tabbar({ label = "Page sections", ...props }) {
  return /* @__PURE__ */ jsx27("nav", { className: "paper-tabbar", "aria-label": label, children: /* @__PURE__ */ jsx27(TabbarList, { ...props, label, orientation: "horizontal" }) });
}
function VerticalTabbar({ label = "Page sections", ...props }) {
  return /* @__PURE__ */ jsx27("nav", { className: "paper-tabbar paper-tabbar--vertical", "aria-label": label, children: /* @__PURE__ */ jsx27(TabbarList, { ...props, label, orientation: "vertical" }) });
}

// src/components/navigation/Pagination.tsx
import { Fragment as Fragment7, jsx as jsx28, jsxs as jsxs25 } from "react/jsx-runtime";
function paginationTokens(totalPages, currentPage, siblingCount) {
  const visible = /* @__PURE__ */ new Set([1, totalPages]);
  for (let page = currentPage - siblingCount; page <= currentPage + siblingCount; page += 1) {
    if (page >= 1 && page <= totalPages) visible.add(page);
  }
  const pages = [...visible].sort((left, right) => left - right);
  const tokens = [];
  pages.forEach((page, index) => {
    const previous = pages[index - 1];
    if (previous !== void 0 && page - previous > 1) {
      tokens.push(previous === 1 ? "start-gap" : "end-gap");
    }
    tokens.push(page);
  });
  return tokens;
}
function Pagination({
  totalPages,
  currentPage,
  getHref,
  onPageChange,
  siblingCount = 2,
  label = "Pagination",
  renderLink
}) {
  if (!Number.isInteger(totalPages) || totalPages < 1) {
    throw new RangeError("Pagination totalPages must be a positive integer.");
  }
  if (!Number.isInteger(currentPage) || currentPage < 1 || currentPage > totalPages) {
    throw new RangeError("Pagination currentPage must be within totalPages.");
  }
  if (!Number.isInteger(siblingCount) || siblingCount < 0) {
    throw new RangeError("Pagination siblingCount must be a non-negative integer.");
  }
  if (!getHref && !onPageChange) {
    throw new Error("Pagination requires getHref or onPageChange.");
  }
  const activate = (page) => (event) => {
    if (!onPageChange || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onPageChange(page);
  };
  const tokens = paginationTokens(totalPages, currentPage, siblingCount);
  const control = (page, content, controlLabel, disabled) => {
    if (disabled) {
      return /* @__PURE__ */ jsx28("span", { className: "paper-pagination__control", "aria-disabled": "true", children: content });
    }
    if (getHref) {
      return renderNavigationLink(renderLink, {
        href: getHref(page),
        className: "paper-pagination__control",
        "aria-label": controlLabel,
        onClick: activate(page),
        children: content
      });
    }
    return /* @__PURE__ */ jsx28("button", { type: "button", className: "paper-pagination__control", "aria-label": controlLabel, onClick: () => onPageChange?.(page), children: content });
  };
  return /* @__PURE__ */ jsx28("nav", { className: "paper-pagination", "aria-label": label, children: /* @__PURE__ */ jsxs25("ul", { className: "paper-pagination__list", children: [
    /* @__PURE__ */ jsx28("li", { children: control(currentPage - 1, /* @__PURE__ */ jsxs25(Fragment7, { children: [
      /* @__PURE__ */ jsx28(ChevronLeftIcon, { className: iconClassName("compact"), "aria-hidden": "true" }),
      /* @__PURE__ */ jsx28("span", { children: "Previous" })
    ] }), "Previous page", currentPage === 1) }),
    tokens.map(
      (token) => typeof token === "string" ? /* @__PURE__ */ jsx28("li", { className: "paper-pagination__gap", "aria-hidden": "true", children: /* @__PURE__ */ jsx28(EllipsisHorizontalIcon, { className: iconClassName("default") }) }, token) : /* @__PURE__ */ jsx28("li", { children: token === currentPage ? /* @__PURE__ */ jsx28("span", { className: "paper-pagination__page", "aria-current": "page", "aria-label": `Page ${token}`, children: token }) : getHref ? renderNavigationLink(renderLink, {
        href: getHref(token),
        className: "paper-pagination__page",
        "aria-label": `Page ${token}`,
        onClick: activate(token),
        children: token
      }) : /* @__PURE__ */ jsx28("button", { type: "button", className: "paper-pagination__page", "aria-label": `Page ${token}`, onClick: () => onPageChange?.(token), children: token }) }, token)
    ),
    /* @__PURE__ */ jsx28("li", { children: control(currentPage + 1, /* @__PURE__ */ jsxs25(Fragment7, { children: [
      /* @__PURE__ */ jsx28("span", { children: "Next" }),
      /* @__PURE__ */ jsx28(ChevronRightIcon, { className: iconClassName("compact"), "aria-hidden": "true" })
    ] }), "Next page", currentPage === totalPages) })
  ] }) });
}

// src/components/navigation/Tabs/Tabs.tsx
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import {
  forwardRef as forwardRef3
} from "react";
import { jsx as jsx29 } from "react/jsx-runtime";
function classes(base, className) {
  return [base, className].filter(Boolean).join(" ");
}
function moveTabFocus(event, loopFocus) {
  const tabs = Array.from(
    event.currentTarget.querySelectorAll(
      '[role="tab"]:not([aria-disabled="true"])'
    )
  );
  const currentIndex = tabs.indexOf(event.target);
  if (currentIndex < 0 || tabs.length === 0) return;
  let nextIndex;
  const vertical = event.currentTarget.dataset.orientation === "vertical";
  if (event.key === "Home") nextIndex = 0;
  if (event.key === "End") nextIndex = tabs.length - 1;
  if (event.key === (vertical ? "ArrowDown" : "ArrowRight")) {
    nextIndex = currentIndex + 1;
  }
  if (event.key === (vertical ? "ArrowUp" : "ArrowLeft")) {
    nextIndex = currentIndex - 1;
  }
  if (nextIndex === void 0) return;
  if (loopFocus) nextIndex = (nextIndex + tabs.length) % tabs.length;
  else nextIndex = Math.max(0, Math.min(nextIndex, tabs.length - 1));
  event.preventDefault();
  event.stopPropagation();
  tabs[nextIndex]?.focus();
}
var TabsRoot = forwardRef3(function TabsRoot2({ className, onValueChange, ...props }, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.Root,
    {
      ref,
      className: classes("paper-tabs", className),
      onValueChange: (nextValue) => {
        if (typeof nextValue === "string") onValueChange?.(nextValue);
      },
      ...props
    }
  );
});
var TabsList = forwardRef3(function TabsList2({
  activateOnFocus = true,
  className,
  loopFocus = true,
  onKeyDownCapture,
  ...props
}, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.List,
    {
      ref,
      activateOnFocus,
      loopFocus,
      className: classes("paper-tabs__list", className),
      onKeyDownCapture: (event) => {
        onKeyDownCapture?.(event);
        if (!event.defaultPrevented) moveTabFocus(event, loopFocus);
      },
      ...props
    }
  );
});
var TabsTab = forwardRef3(function TabsTab2({ className, type = "button", ...props }, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.Tab,
    {
      ref,
      type,
      className: classes("paper-tabs__tab", className),
      ...props
    }
  );
});
var TabsPanel = forwardRef3(function TabsPanel2({ className, keepMounted = true, value, ...props }, ref) {
  return /* @__PURE__ */ jsx29(
    BaseTabs.Panel,
    {
      ref,
      className: classes("paper-tabs__panel", className),
      "data-value": String(value),
      keepMounted,
      value,
      ...props
    }
  );
});
var Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel
};

// src/catalog/examples/action-menu.recommended.tsx
import { useState as useState7 } from "react";
import { jsx as jsx30, jsxs as jsxs26 } from "react/jsx-runtime";
function ActionMenuFixture() {
  const [result, setResult] = useState7("Choose a log action.");
  const items = [
    {
      id: "edit",
      label: "Edit log",
      onSelect: () => setResult("Edit log selected")
    },
    {
      id: "duplicate",
      label: "Duplicate log",
      disabled: true,
      onSelect: () => setResult("Duplicate log selected")
    },
    {
      id: "delete",
      label: "Delete log",
      destructive: true,
      onSelect: () => setResult(
        "Delete log selected \u2014 request confirmation before deleting data."
      )
    }
  ];
  return /* @__PURE__ */ jsxs26("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx30("div", { className: "paper-cluster", children: /* @__PURE__ */ jsx30(ActionMenu, { label: "Log actions", items }) }),
    /* @__PURE__ */ jsx30("p", { children: "Use a labelled trigger when the menu needs to explain its scope. In a compact row, an ellipsis can use the row title as context." }),
    /* @__PURE__ */ jsxs26("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx30("span", { children: "August Japanese reading log" }),
      /* @__PURE__ */ jsx30(
        ActionMenu,
        {
          label: "Actions for August Japanese reading log",
          items,
          iconOnly: true,
          triggerVariant: "ghost"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs26("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx30("span", { children: "Account menu" }),
      /* @__PURE__ */ jsx30(
        ActionMenu,
        {
          label: "Anton account menu",
          iconOnly: true,
          triggerIcon: /* @__PURE__ */ jsx30(UserCircleIcon, {}),
          triggerVariant: "ghost",
          items: [{ id: "profile", label: "My profile", onSelect: () => setResult("My profile selected") }]
        }
      )
    ] }),
    /* @__PURE__ */ jsx30("p", { role: "status", children: result }),
    /* @__PURE__ */ jsx30("p", { children: "Duplication is unavailable for this archived example." })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/action-menu.recommended.tsx
var action_menu_recommended_default = `import { ActionMenu } from 'paper-ui'
import { UserCircleIcon } from 'paper-ui/icons'
import { useState } from 'react'

export default function ActionMenuFixture() {
  const [result, setResult] = useState('Choose a log action.')
  const items = [
    {
      id: 'edit',
      label: 'Edit log',
      onSelect: () => setResult('Edit log selected'),
    },
    {
      id: 'duplicate',
      label: 'Duplicate log',
      disabled: true,
      onSelect: () => setResult('Duplicate log selected'),
    },
    {
      id: 'delete',
      label: 'Delete log',
      destructive: true,
      onSelect: () =>
        setResult(
          'Delete log selected \u2014 request confirmation before deleting data.',
        ),
    },
  ]
  return (
    <div className="paper-stack">
      <div className="paper-cluster">
        <ActionMenu label="Log actions" items={items} />
      </div>
      <p>
        Use a labelled trigger when the menu needs to explain its scope. In a
        compact row, an ellipsis can use the row title as context.
      </p>
      <div className="paper-cluster">
        <span>August Japanese reading log</span>
        <ActionMenu
          label="Actions for August Japanese reading log"
          items={items}
          iconOnly
          triggerVariant="ghost"
        />
      </div>
      <div className="paper-cluster">
        <span>Account menu</span>
        <ActionMenu label="Anton account menu" iconOnly triggerIcon={<UserCircleIcon />} triggerVariant="ghost"
          items={[{ id: 'profile', label: 'My profile', onSelect: () => setResult('My profile selected') }]} />
      </div>
      <p role="status">{result}</p>
      <p>Duplication is unavailable for this archived example.</p>
    </div>
  )
}
`;

// src/catalog/examples/modal.composable-search.tsx
import { useStore as useStore2 } from "@tanstack/react-form";
import { useRef as useRef5, useState as useState8 } from "react";
import { jsx as jsx31, jsxs as jsxs27 } from "react/jsx-runtime";
function ComposableModalFixture() {
  const [open, setOpen] = useState8(false);
  const searchRef = useRef5(null);
  const methods = usePaperForm({ defaultValues: { query: "" } });
  const query = useStore2(methods.store, (state) => state.values["query"]);
  const matches = ["Button", "Input", "Modal"].filter(
    (name) => name.toLowerCase().includes(query.toLowerCase())
  );
  return /* @__PURE__ */ jsx31(
    Modal,
    {
      trigger: /* @__PURE__ */ jsx31(Button, { variant: "ghost", children: "Search examples" }),
      title: "Search examples",
      description: "Try Button, Input, or Modal.",
      open,
      onOpenChange: setOpen,
      initialFocus: searchRef,
      footer: null,
      children: /* @__PURE__ */ jsxs27(methods.AppForm, { children: [
        /* @__PURE__ */ jsx31(methods.AppField, { name: "query", children: (field) => /* @__PURE__ */ jsx31(field.Input, { label: "Component name", type: "search", ref: searchRef }) }),
        /* @__PURE__ */ jsx31("p", { role: "status", children: matches.length ? matches.join(", ") : "No matching components." })
      ] })
    }
  );
}

// example-source:/app/packages/paper-ui/src/catalog/examples/modal.composable-search.tsx
var modal_composable_search_default = `import { useStore } from '@tanstack/react-form'
import { Button, Modal, usePaperForm } from 'paper-ui'
import { useRef, useState } from 'react'

export default function ComposableModalFixture() {
  const [open, setOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const methods = usePaperForm({ defaultValues: { query: '' } })
  const query = useStore(methods.store, state => state.values['query'])
  const matches = ['Button', 'Input', 'Modal'].filter(name =>
    name.toLowerCase().includes(query.toLowerCase()),
  )
  return (
    <Modal
      trigger={<Button variant="ghost">Search examples</Button>}
      title="Search examples"
      description="Try Button, Input, or Modal."
      open={open}
      onOpenChange={setOpen}
      initialFocus={searchRef}
      footer={null}
    >
      <methods.AppForm>
        <methods.AppField name="query">
          {field => (
            <field.Input label="Component name" type="search" ref={searchRef} />
          )}
        </methods.AppField>
        <p role="status">
          {matches.length ? matches.join(', ') : 'No matching components.'}
        </p>
      </methods.AppForm>
    </Modal>
  )
}
`;

// src/catalog/examples/modal.recommended.tsx
import { useState as useState9 } from "react";
import { Fragment as Fragment8, jsx as jsx32, jsxs as jsxs28 } from "react/jsx-runtime";
function DeleteLogFixture() {
  const [deleted, setDeleted] = useState9(false);
  return /* @__PURE__ */ jsxs28(Fragment8, { children: [
    /* @__PURE__ */ jsx32(
      Modal,
      {
        triggerLabel: "Review deletion",
        triggerVariant: "destructive",
        title: "Delete this reading log?",
        description: "This demo only changes its local example state.",
        closeLabel: "Keep log",
        action: {
          label: "Delete log",
          variant: "destructive",
          onAction: () => setDeleted(true)
        },
        children: /* @__PURE__ */ jsx32("p", { children: "August Japanese reading \xB7 1,240 pages" })
      }
    ),
    /* @__PURE__ */ jsx32("p", { role: "status", children: deleted ? "Example log deleted" : "Example log is available" })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/modal.recommended.tsx
var modal_recommended_default = `import { Modal } from 'paper-ui'
import { useState } from 'react'

export default function DeleteLogFixture() {
  const [deleted, setDeleted] = useState(false)
  return (
    <>
      <Modal
        triggerLabel="Review deletion"
        triggerVariant="destructive"
        title="Delete this reading log?"
        description="This demo only changes its local example state."
        closeLabel="Keep log"
        action={{
          label: 'Delete log',
          variant: 'destructive',
          onAction: () => setDeleted(true),
        }}
      >
        <p>August Japanese reading \xB7 1,240 pages</p>
      </Modal>
      <p role="status">
        {deleted ? 'Example log deleted' : 'Example log is available'}
      </p>
    </>
  )
}
`;

// src/catalog/examples/button.classes.tsx
import { jsx as jsx33, jsxs as jsxs29 } from "react/jsx-runtime";
function Example() {
  return /* @__PURE__ */ jsxs29("div", { className: "paper-cluster", children: [
    /* @__PURE__ */ jsx33("button", { type: "button", className: "paper-button paper-button--default", children: "Save log" }),
    /* @__PURE__ */ jsx33("a", { href: "#logs", className: "paper-button paper-button--outline", children: "View logs" }),
    /* @__PURE__ */ jsxs29(
      "button",
      {
        type: "button",
        disabled: true,
        "aria-busy": "true",
        className: "paper-button paper-button--default paper-button--loading",
        children: [
          /* @__PURE__ */ jsx33("span", { className: "paper-button__spinner", "aria-hidden": "true" }),
          /* @__PURE__ */ jsx33("span", { className: "paper-button__label", children: "Saving log" })
        ]
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/button.classes.tsx
var button_classes_default = 'export default function Example() {\n  return (\n    <div className="paper-cluster">\n      <button type="button" className="paper-button paper-button--default">\n        Save log\n      </button>\n      <a href="#logs" className="paper-button paper-button--outline">\n        View logs\n      </a>\n      <button\n        type="button"\n        disabled\n        aria-busy="true"\n        className="paper-button paper-button--default paper-button--loading"\n      >\n        <span className="paper-button__spinner" aria-hidden="true" />\n        <span className="paper-button__label">Saving log</span>\n      </button>\n    </div>\n  )\n}\n';

// src/catalog/examples/button.icons-disabled.tsx
import { jsx as jsx34, jsxs as jsxs30 } from "react/jsx-runtime";
function Example2() {
  return /* @__PURE__ */ jsxs30("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsxs30("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx34(Button, { leadingIcon: /* @__PURE__ */ jsx34(PlusIcon, { className: iconClassName() }), children: "Add reading" }),
      /* @__PURE__ */ jsx34(
        Button,
        {
          variant: "ghost",
          "aria-label": "Close entry",
          style: { minWidth: "2.75rem", minHeight: "2.75rem" },
          children: /* @__PURE__ */ jsx34(XMarkIcon, { className: iconClassName(), "aria-hidden": "true" })
        }
      ),
      /* @__PURE__ */ jsx34(Button, { disabled: true, "aria-describedby": "archive-reason", children: "Submit to contest" })
    ] }),
    /* @__PURE__ */ jsx34("p", { id: "archive-reason", children: "This contest is archived and no longer accepts entries." })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/button.icons-disabled.tsx
var button_icons_disabled_default = `import { Button } from 'paper-ui'
import { PlusIcon, XMarkIcon, iconClassName } from 'paper-ui/icons'

export default function Example() {
  return (
    <div className="paper-stack">
      <div className="paper-cluster">
        <Button leadingIcon={<PlusIcon className={iconClassName()} />}>
          Add reading
        </Button>
        <Button
          variant="ghost"
          aria-label="Close entry"
          style={{ minWidth: '2.75rem', minHeight: '2.75rem' }}
        >
          <XMarkIcon className={iconClassName()} aria-hidden="true" />
        </Button>
        <Button disabled aria-describedby="archive-reason">
          Submit to contest
        </Button>
      </div>
      <p id="archive-reason">
        This contest is archived and no longer accepts entries.
      </p>
    </div>
  )
}
`;

// src/catalog/examples/button.loading.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
function Example3() {
  return /* @__PURE__ */ jsx35(Button, { loading: true, loadingLabel: "Saving reading log", children: "Save log" });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/button.loading.tsx
var button_loading_default = `import { Button } from 'paper-ui'

export default function Example() {
  return (
    <Button loading loadingLabel="Saving reading log">
      Save log
    </Button>
  )
}
`;

// src/catalog/examples/button.narrow.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
function Example4() {
  return /* @__PURE__ */ jsx36(Button, { fullWidth: true, children: "Add this finished book to the August reading log" });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/button.narrow.tsx
var button_narrow_default = "import { Button } from 'paper-ui'\n\nexport default function Example() {\n  return (\n    <Button fullWidth>Add this finished book to the August reading log</Button>\n  )\n}\n";

// src/catalog/examples/button.variants.tsx
import { jsx as jsx37, jsxs as jsxs31 } from "react/jsx-runtime";
function Example5() {
  return /* @__PURE__ */ jsxs31("div", { className: "paper-cluster", children: [
    /* @__PURE__ */ jsx37(Button, { children: "Save log" }),
    /* @__PURE__ */ jsx37(Button, { variant: "outline", children: "Cancel" }),
    /* @__PURE__ */ jsx37(Button, { variant: "ghost", children: "More options" }),
    /* @__PURE__ */ jsx37(Button, { variant: "link", children: "Clear filters" }),
    /* @__PURE__ */ jsx37(Button, { variant: "destructive", children: "Delete log" }),
    /* @__PURE__ */ jsx37("a", { className: buttonClassName({ variant: "outline" }), href: "#logs", children: "View logs" })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/button.variants.tsx
var button_variants_default = `import { Button, buttonClassName } from 'paper-ui'

export default function Example() {
  return (
    <div className="paper-cluster">
      <Button>Save log</Button>
      <Button variant="outline">Cancel</Button>
      <Button variant="ghost">More options</Button>
      <Button variant="link">Clear filters</Button>
      <Button variant="destructive">Delete log</Button>
      <a className={buttonClassName({ variant: 'outline' })} href="#logs">
        View logs
      </a>
    </div>
  )
}
`;

// src/catalog/button.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var buttonFixtures = [
  defineCatalogFixture({
    "id": "button.variants",
    "name": "Button variants",
    "description": "The complete action hierarchy beside a semantic anchor.",
    "tags": [
      "button",
      "variants",
      "anchor"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: button_variants_default,
    render: () => /* @__PURE__ */ jsx38(Example5, {})
  }),
  defineCatalogFixture({
    "id": "button.loading",
    "name": "Loading action",
    "description": "A busy action keeps its visible and accessible name stable.",
    "tags": [
      "button",
      "loading",
      "disabled"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: button_loading_default,
    render: () => /* @__PURE__ */ jsx38(Example3, {})
  }),
  defineCatalogFixture({
    "id": "button.narrow",
    "name": "Long, full-width action",
    "description": "Long localized content at a narrow viewport.",
    "tags": [
      "button",
      "full width",
      "long content",
      "narrow"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: button_narrow_default,
    render: () => /* @__PURE__ */ jsx38(Example4, {})
  }),
  defineCatalogFixture({
    "id": "button.icons-disabled",
    "name": "Icons and unavailable actions",
    "description": "A labelled action with a decorative icon, an explicitly named icon-only control, and an unavailable action with its reason.",
    "tags": [
      "button",
      "icons",
      "disabled"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: button_icons_disabled_default,
    render: () => /* @__PURE__ */ jsx38(Example2, {})
  }),
  defineCatalogFixture({
    "id": "button.classes",
    "name": "Native elements with public classes",
    "description": "Use the public CSS recipe directly when a React wrapper is unnecessary. Native roles, disabled state and busy semantics stay explicit.",
    "tags": [
      "button",
      "classes",
      "native"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: button_classes_default,
    render: () => /* @__PURE__ */ jsx38(Example, {})
  })
];
var buttonDocument = defineCatalogDocument({
  "id": "component.button",
  "route": "/components/actions/button",
  "name": "Button",
  "category": "actions",
  "summary": "Triggers an immediate action with explicit hierarchy and safe defaults.",
  "keywords": [
    "button",
    "action",
    "loading",
    "anchor",
    "submit"
  ],
  "sourcePath": "src/components/actions/Button/Button.tsx",
  "fixtureIds": [
    "button.variants",
    "button.loading",
    "button.narrow",
    "button.icons-disabled",
    "button.classes"
  ],
  "behaviorTestIds": [
    "button.semantics",
    "button.recipe-parity",
    "button.loading"
  ],
  "guidance": {
    "whenToUse": [
      "Trigger an immediate operation such as saving or deleting."
    ],
    "whenNotToUse": [
      "Use an anchor for navigation to another resource."
    ],
    "content": [
      "Start with a specific verb and keep labels stable while loading."
    ],
    "commonMistakes": [
      "Do not use the link variant to turn navigation into a button."
    ]
  },
  "accessibility": {
    "requirements": [
      "Provide a stable accessible name and visible focus."
    ],
    "keyboard": [
      "Enter and Space activate a button; anchors retain native Enter behavior."
    ],
    "knownConstraints": [
      "Icon-only actions need an explicit aria-label."
    ]
  },
  "api": {
    "react": [
      "Button"
    ],
    "props": [
      {
        "name": "variant",
        "type": '"default" | "outline" | "ghost" | "link" | "destructive"',
        "description": "Choose emphasis by the action\u2019s role. Link styling still renders a button.",
        "defaultValue": "default"
      },
      {
        "name": "type",
        "type": '"button" | "submit" | "reset"',
        "description": "Use submit explicitly for form submission; call methods.reset() for RHF resets.",
        "defaultValue": "button"
      },
      {
        "name": "loading",
        "type": "boolean",
        "description": "Disables repeat activation and announces progress without replacing the label.",
        "defaultValue": "false"
      },
      {
        "name": "loadingLabel",
        "type": "string",
        "description": "Accessible progress description; name the pending operation.",
        "defaultValue": "Working"
      },
      {
        "name": "disabled",
        "type": "boolean",
        "description": "Makes an unavailable action inert. Keep its reason visible nearby.",
        "defaultValue": "false"
      },
      {
        "name": "fullWidth",
        "type": "boolean",
        "description": "Fills the available inline width; long labels may wrap.",
        "defaultValue": "false"
      },
      {
        "name": "leadingIcon / trailingIcon",
        "type": "ReactNode",
        "description": "Decorative icons; provide aria-label on icon-only actions."
      },
      {
        "name": "children",
        "type": "ReactNode",
        "description": "A concise visible action label."
      },
      {
        "name": "onClick",
        "type": "MouseEventHandler<HTMLButtonElement>",
        "description": "Application-owned action handler. Native button attributes and refs are forwarded."
      }
    ],
    "cssClasses": [
      "paper-button",
      "buttonClassName()"
    ],
    "publicTypes": [
      "ButtonProps",
      "ButtonVariant",
      "ButtonRecipeOptions"
    ],
    "defaults": [
      "variant=default",
      "type=button",
      "loading=false"
    ],
    "invalidCombinations": [
      "Do not put href on Button; style a real anchor with buttonClassName()."
    ]
  },
  "migration": {
    "legacy": [
      "ui Button",
      "btn primary",
      "btn secondary",
      "btn danger"
    ],
    "notes": [
      "Map intent: primary to default, secondary to outline, danger to destructive."
    ]
  },
  "sections": {
    "required": {
      "overview": {
        "heading": "Overview",
        "content": [
          "Button expresses action hierarchy without changing native semantics."
        ]
      },
      "whenToUse": {
        "heading": "When to use",
        "content": [
          "Use Button for an immediate user-initiated operation such as saving a log, applying a filter, or deleting an entry. Use one default button for the primary action in a local task; give supporting actions less emphasis."
        ]
      },
      "whenNotToUse": {
        "heading": "When not to use",
        "content": [
          "Use an anchor when activation navigates to a URL, including links styled with buttonClassName(). Do not use a disabled button as an explanation; keep the reason visible near the unavailable action."
        ]
      },
      "choosingBetween": {
        "heading": "Choose between",
        "content": [
          "Default is the emphasized action, outline is a neutral alternative, ghost is a low-emphasis toolbar action, and link is an action embedded in prose. Destructive communicates irreversible intent; it does not replace a confirmation when the consequence is difficult to undo."
        ]
      },
      "anatomy": {
        "heading": "Anatomy",
        "content": [
          "A control contains an optional icon, a stable text label, and a lower interactive edge."
        ]
      },
      "recommendedExample": {
        "heading": "Recommended example",
        "content": [
          "Compare action emphasis, then select Loading, Icons and unavailable actions, or Native elements with public classes. The native example uses the same recipe without requiring the Button wrapper; supply native type, disabled and aria-busy attributes yourself."
        ]
      },
      "variants": {
        "heading": "Variants",
        "content": [
          "Use default for the main action in a task, outline for a visible alternative, ghost for compact secondary actions, link for button behavior that belongs inline with text, and destructive for a destructive operation. buttonClassName() gives a real anchor the same visual hierarchy without changing its navigation semantics."
        ]
      },
      "statesAndAdaptation": {
        "heading": "States and adaptation",
        "content": [
          "loading prevents repeat activation, sets aria-busy, keeps the original accessible name, and announces loadingLabel as a description. fullWidth is intended for constrained layouts; density changes sizing without changing hierarchy."
        ]
      },
      "behavior": {
        "heading": "Behavior",
        "content": [
          "Buttons activate on Enter or Space and default to type=button, preventing accidental form submission. Explicitly set type=submit inside a TanStack Form and submit through methods.handleSubmit(); use methods.reset() only for a deliberate reset workflow."
        ]
      },
      "contentGuidance": {
        "heading": "Content guidance",
        "content": [
          "Start with a specific verb and name the object when context is not obvious: Save log, Delete entry, or Clear filters. Keep the visible label stable while loading and avoid vague labels such as OK or Submit."
        ]
      },
      "accessibility": {
        "heading": "Accessibility",
        "content": [
          "Keep an accessible name, do not convey danger through color alone, and preserve native anchor roles for navigation."
        ]
      },
      "implementation": {
        "heading": "Implementation",
        "content": [
          "Button and buttonClassName() call the same recipe; load paper-ui/styles.css once at the application root."
        ]
      },
      "apiReference": {
        "heading": "API reference",
        "content": [
          "ButtonProps adds variant, loading, loadingLabel, icons, and fullWidth to native button attributes."
        ]
      },
      "relatedPatterns": {
        "heading": "Related patterns",
        "content": [
          "Use Modal for focused confirmation and ActionMenu when several contextual actions compete."
        ]
      },
      "migration": {
        "heading": "Migration",
        "content": [
          "Review old primary, secondary, and danger classes by semantic job instead of renaming mechanically."
        ]
      },
      "lifecycle": {
        "heading": "Lifecycle",
        "content": [
          "Stable in Paper 0.1.0; behavior and variant vocabulary are migration-ready."
        ]
      }
    },
    "pageSections": [
      "usage",
      "examples",
      "variantsAndStates",
      "behavior",
      "contentGuidance",
      "accessibility"
    ]
  },
  "kind": "component",
  "aliases": [],
  "lifecycle": "Stable",
  "reviewDate": "2026-09-05",
  "packageVersion": "0.1.0",
  "dependencies": {
    "documents": [],
    "packages": [
      "paper-ui"
    ]
  },
  "changelog": [
    {
      "date": "2026-09-05",
      "note": "Published the Stable Phase 2 contract."
    }
  ]
});

// src/catalog/examples/input.error.tsx
import { useEffect as useEffect3 } from "react";
import { jsx as jsx39 } from "react/jsx-runtime";
function InputErrorFixture() {
  const methods = usePaperForm({ defaultValues: { pages: "" } });
  useEffect3(() => {
    methods.setFieldMeta("pages", (meta) => ({
      ...meta,
      isTouched: true,
      errorMap: { onSubmit: "Enter the number of pages read." }
    }));
  }, [methods]);
  return /* @__PURE__ */ jsx39(methods.AppForm, { children: /* @__PURE__ */ jsx39(
    methods.AppField,
    {
      name: "pages",
      validators: {
        onChange: ({ value }) => {
          if (!value) return "This field is required.";
        }
      },
      children: (field) => /* @__PURE__ */ jsx39(
        field.Input,
        {
          label: "Pages read",
          hint: "Use whole pages.",
          inputMode: "numeric",
          required: true
        }
      )
    }
  ) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/input.error.tsx
var input_error_default = `import { usePaperForm } from 'paper-ui'
import { useEffect } from 'react'

export default function InputErrorFixture() {
  const methods = usePaperForm({ defaultValues: { pages: '' } })
  useEffect(() => {
    methods.setFieldMeta('pages', meta => ({
      ...meta,
      isTouched: true,
      errorMap: { onSubmit: 'Enter the number of pages read.' },
    }))
  }, [methods])
  return (
    <methods.AppForm>
      <methods.AppField
        name="pages"
        validators={{
          onChange: ({ value }) => {
            if (!value) return 'This field is required.'
          },
        }}
      >
        {field => (
          <field.Input
            label="Pages read"
            hint="Use whole pages."
            inputMode="numeric"
            required
          />
        )}
      </methods.AppField>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/input.recommended.tsx
import { useState as useState10 } from "react";
import { jsx as jsx40, jsxs as jsxs32 } from "react/jsx-runtime";
function InputRecommendedFixture() {
  const [saved, setSaved] = useState10("");
  const methods = usePaperForm({
    defaultValues: {
      title: "August reading log",
      date: "2026-08-31",
      pages: 24
    },
    onSubmit: ({ value: { title, date, pages } }) => setSaved(`Saved: ${title} \xB7 ${pages} pages \xB7 ${date}`)
  });
  return /* @__PURE__ */ jsx40(methods.AppForm, { children: /* @__PURE__ */ jsxs32(methods.Form, { className: "paper-stack w-full max-w-2xl", children: [
    /* @__PURE__ */ jsx40(
      methods.AppField,
      {
        name: "title",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Enter a log title.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx40(field.Input, { label: "Log title", required: true })
      }
    ),
    /* @__PURE__ */ jsxs32("div", { className: "grid gap-4 sm:grid-cols-2", children: [
      /* @__PURE__ */ jsx40(
        methods.AppField,
        {
          name: "pages",
          validators: {
            onChange: ({ value }) => {
              if (!Number.isFinite(value)) return "Enter the number of pages.";
              if (value < 1) return "Enter at least one page.";
            }
          },
          children: (field) => /* @__PURE__ */ jsx40(field.Input, { label: "Pages read", type: "number", min: 1, required: true })
        }
      ),
      /* @__PURE__ */ jsx40(
        methods.AppField,
        {
          name: "date",
          validators: {
            onChange: ({ value }) => {
              if (!value) return "Choose a date.";
            }
          },
          children: (field) => /* @__PURE__ */ jsx40(
            field.Input,
            {
              label: "Date",
              type: "date",
              hint: "The date you read, in UTC. Fields stay aligned when only one has a hint.",
              required: true
            }
          )
        }
      )
    ] }),
    /* @__PURE__ */ jsx40("div", { className: "paper-cluster", children: /* @__PURE__ */ jsx40(Button, { type: "submit", children: "Save log" }) }),
    saved && /* @__PURE__ */ jsx40("p", { role: "status", children: saved })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/input.recommended.tsx
var input_recommended_default = `import { Button, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function InputRecommendedFixture() {
  const [saved, setSaved] = useState('')
  const methods = usePaperForm({
    defaultValues: {
      title: 'August reading log',
      date: '2026-08-31',
      pages: 24,
    },
    onSubmit: ({ value: { title, date, pages } }) =>
      setSaved(\`Saved: \${title} \xB7 \${pages} pages \xB7 \${date}\`),
  })
  return (
    <methods.AppForm>
      <methods.Form className="paper-stack w-full max-w-2xl">
        <methods.AppField
          name="title"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Enter a log title.'
            },
          }}
        >
          {field => <field.Input label="Log title" required />}
        </methods.AppField>
        <div className="grid gap-4 sm:grid-cols-2">
          <methods.AppField
            name="pages"
            validators={{
              onChange: ({ value }) => {
                if (!Number.isFinite(value)) return 'Enter the number of pages.'
                if (value < 1) return 'Enter at least one page.'
              },
            }}
          >
            {field => (
              <field.Input label="Pages read" type="number" min={1} required />
            )}
          </methods.AppField>
          <methods.AppField
            name="date"
            validators={{
              onChange: ({ value }) => {
                if (!value) return 'Choose a date.'
              },
            }}
          >
            {field => (
              <field.Input
                label="Date"
                type="date"
                hint="The date you read, in UTC. Fields stay aligned when only one has a hint."
                required
              />
            )}
          </methods.AppField>
        </div>
        <div className="paper-cluster">
          <Button type="submit">Save log</Button>
        </div>
        {saved && <p role="status">{saved}</p>}
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/input.states.tsx
import { jsx as jsx41, jsxs as jsxs33 } from "react/jsx-runtime";
function InputStatesFixture() {
  const methods = usePaperForm({
    defaultValues: { readonly: "Japanese", disabled: "Archived" }
  });
  return /* @__PURE__ */ jsx41(methods.AppForm, { children: /* @__PURE__ */ jsxs33("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx41(methods.AppField, { name: "readonly", children: (field) => /* @__PURE__ */ jsx41(field.Input, { label: "Language", readOnly: true }) }),
    /* @__PURE__ */ jsx41(methods.AppField, { name: "disabled", children: (field) => /* @__PURE__ */ jsx41(field.Input, { label: "Status", disabled: true }) })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/input.states.tsx
var input_states_default = `import { usePaperForm } from 'paper-ui'

export default function InputStatesFixture() {
  const methods = usePaperForm({
    defaultValues: { readonly: 'Japanese', disabled: 'Archived' },
  })
  return (
    <methods.AppForm>
      <div className="paper-stack">
        <methods.AppField name="readonly">
          {field => <field.Input label="Language" readOnly />}
        </methods.AppField>
        <methods.AppField name="disabled">
          {field => <field.Input label="Status" disabled />}
        </methods.AppField>
      </div>
    </methods.AppForm>
  )
}
`;

// src/catalog/input.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
var inputFixtures = [
  defineCatalogFixture({
    "id": "input.recommended",
    "name": "Reading-log title",
    "description": "Label, hint, required rule, value, and submission in one field.",
    "tags": [
      "input",
      "form",
      "required",
      "hint"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: input_recommended_default,
    render: () => /* @__PURE__ */ jsx42(InputRecommendedFixture, {})
  }),
  defineCatalogFixture({
    "id": "input.states",
    "name": "Read-only and disabled",
    "description": "Two non-editable states with distinct native semantics.",
    "tags": [
      "input",
      "readonly",
      "disabled"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: input_states_default,
    render: () => /* @__PURE__ */ jsx42(InputStatesFixture, {})
  }),
  defineCatalogFixture({
    "id": "input.error",
    "name": "Validation error",
    "description": "Hint and error remain associated with the invalid input.",
    "tags": [
      "input",
      "error",
      "accessibility"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: input_error_default,
    render: () => /* @__PURE__ */ jsx42(InputErrorFixture, {})
  })
];
var inputDocument = defineCatalogDocument({
  "id": "component.input",
  "route": "/components/forms/input",
  "name": "Input",
  "category": "forms",
  "summary": "Collects one line of text with complete field anatomy and form state.",
  "keywords": [
    "input",
    "field",
    "form",
    "error",
    "hint"
  ],
  "sourcePath": "src/components/forms/Input/Input.tsx",
  "fixtureIds": [
    "input.recommended",
    "input.states",
    "input.error"
  ],
  "behaviorTestIds": [
    "input.associations",
    "input.validation",
    "input.native-states"
  ],
  "guidance": {
    "whenToUse": [
      "Collect a short, free-form value in a TanStack Form."
    ],
    "whenNotToUse": [
      "Use a choice control when the set of valid values is known."
    ],
    "content": [
      "Use a visible noun label and a hint only when it adds constraints or context.",
      "Fields fill their parent column. Set a readable width on the form, such as w-full max-w-2xl, then group related fields with grid gap-4 sm:grid-cols-2. A full-width field and a two-field row share the same outside edges."
    ],
    "commonMistakes": [
      "Do not use placeholder text as the only label."
    ]
  },
  "accessibility": {
    "requirements": [
      "Associate label, hint, and error IDs with the native input."
    ],
    "keyboard": [
      "The native input follows platform text-editing behavior."
    ],
    "knownConstraints": [
      "Input requires a @tanstack/react-form form.AppForm."
    ]
  },
  "api": {
    "react": [
      "Input"
    ],
    "props": [
      {
        "name": "name (AppField)",
        "type": "string",
        "description": "TanStack Form field path.",
        "required": true
      },
      {
        "name": "label",
        "type": "string",
        "description": "Persistent visible label.",
        "required": true
      },
      {
        "name": "hint",
        "type": "string",
        "description": "Format, constraint or consequence; associated with the input."
      },
      {
        "name": "validators (AppField)",
        "type": "FieldValidators",
        "description": "Validation messages and constraints supplied to TanStack Form."
      },
      {
        "name": "required",
        "type": "boolean",
        "description": "Sets the native required attribute and label marker; add the corresponding required validator.",
        "defaultValue": "false"
      },
      {
        "name": "type / inputMode",
        "type": "native input attributes",
        "description": "Use type for input semantics and inputMode for the platform keyboard.",
        "defaultValue": "text"
      },
      {
        "name": "readOnly",
        "type": "boolean",
        "description": "Allows focus and copying while preventing edits.",
        "defaultValue": "false"
      },
      {
        "name": "disabled",
        "type": "boolean",
        "description": "Makes the field unavailable.",
        "defaultValue": "false"
      },
      {
        "name": "id / ref",
        "type": "string / Ref<HTMLInputElement>",
        "description": "Optional explicit ID or imperative focus target."
      }
    ],
    "cssClasses": [
      "paper-field",
      "paper-input"
    ],
    "publicTypes": [
      "InputProps"
    ],
    "defaults": [
      "Native text input behavior",
      "Errors come from @tanstack/react-form state"
    ],
    "invalidCombinations": [
      "Do not render Input outside form.AppForm."
    ]
  },
  "migration": {
    "legacy": [
      "ui/components/Form Input"
    ],
    "notes": [
      "Keep names and validation rules in @tanstack/react-form; replace implicit global field styling."
    ]
  },
  "sections": {
    "required": {
      "overview": {
        "heading": "Overview",
        "content": [
          "Input owns the complete visible and semantic field relationship."
        ]
      },
      "whenToUse": {
        "heading": "When to use",
        "content": [
          "Use Input for a short free-form value that fits on one line, such as a reading-log title, page count, email address, or password. Set the appropriate native type or inputMode so platform keyboards and validation can help."
        ]
      },
      "whenNotToUse": {
        "heading": "When not to use",
        "content": [
          "Use TextArea for prose and Select, radio controls, or Autocomplete when valid choices are known. Do not use placeholder text as the label: it disappears after entry and does not provide a persistent field name."
        ]
      },
      "choosingBetween": {
        "heading": "Choose between",
        "content": [
          "A read-only value remains focusable and selectable. A disabled control prevents editing but retains its TanStack Form value. Use readOnly when users may need to inspect or copy the value; the submission handler decides which values belong in the operation."
        ]
      },
      "anatomy": {
        "heading": "Anatomy",
        "content": [
          "The field contains a persistent label, native input, optional hint, and validation message. Hints sit below controls so fields with and without hints align in a row."
        ]
      },
      "recommendedExample": {
        "heading": "Recommended example",
        "content": [
          "Edit the full-width log title and the paired Pages read and Date fields, then save to see their submitted values. The row shares the title field\u2019s outside edges, and both controls align even though only Date has a hint. Clear the title and save again to test error and focus behavior."
        ]
      },
      "variants": {
        "heading": "Variants",
        "content": [
          "Text, email, password, numeric-input-mode, and other native types share the field anatomy."
        ]
      },
      "statesAndAdaptation": {
        "heading": "States and adaptation",
        "content": [
          "required adds the native required state and a visible marker. An invalid field sets aria-invalid and connects its alert message alongside any hint. Read-only remains operable for selection; disabled is visually muted and cannot be edited, but its form value remains available on submission."
        ]
      },
      "behavior": {
        "heading": "Behavior",
        "content": [
          "Value, blur and validation state come from the enclosing form.AppField."
        ]
      },
      "contentGuidance": {
        "heading": "Content guidance",
        "content": [
          "Use a persistent noun phrase for the label. Add a hint only for a format, constraint, or consequence the label cannot carry. An error should explain how to fix the value\u2014Enter the number of pages read is more useful than Invalid input.",
          "Fields fill their parent column. Set a readable width on the form, such as w-full max-w-2xl, then group related fields with grid gap-4 sm:grid-cols-2. A full-width field and a two-field row share the same outside edges."
        ]
      },
      "accessibility": {
        "heading": "Accessibility",
        "content": [
          "htmlFor, aria-describedby, aria-invalid, and role=alert connect the field anatomy without relying on color."
        ]
      },
      "implementation": {
        "heading": "Implementation",
        "content": [
          "Create form with usePaperForm(), wrap form.Form in form.AppForm, and bind each named form.AppField to field.Input."
        ]
      },
      "apiReference": {
        "heading": "API reference",
        "content": [
          "InputProps requires label and accepts hint and native input attributes. Put name and validators on form.AppField."
        ]
      },
      "relatedPatterns": {
        "heading": "Related patterns",
        "content": [
          "Button submits the surrounding form; future choice controls reuse the same field anatomy."
        ]
      },
      "migration": {
        "heading": "Migration",
        "content": [
          "Move validation into AppField validators and remove application-owned label/error selectors."
        ]
      },
      "lifecycle": {
        "heading": "Lifecycle",
        "content": [
          "Stable in Paper 0.1.0 with deterministic associations and TanStack Form ownership."
        ]
      }
    },
    "pageSections": [
      "usage",
      "examples",
      "variantsAndStates",
      "behavior",
      "contentGuidance",
      "accessibility"
    ]
  },
  "kind": "component",
  "aliases": [],
  "lifecycle": "Stable",
  "reviewDate": "2026-09-05",
  "packageVersion": "0.1.0",
  "dependencies": {
    "documents": [],
    "packages": [
      "paper-ui"
    ]
  },
  "changelog": [
    {
      "date": "2026-09-05",
      "note": "Published the Stable Phase 2 contract."
    }
  ]
});

// src/catalog/phase-two.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
var REVIEW_DATE = "2026-09-05";
var PACKAGE_VERSION = "0.1.0";
function section(heading, ...content) {
  return { heading, content };
}
function componentSections(name, details2) {
  void name;
  return { required: details2, pageSections: COMPONENT_PAGE_SECTION_KEYS };
}
function componentDocument(options) {
  return defineCatalogDocument({
    ...options,
    kind: "component",
    aliases: [],
    lifecycle: "Stable",
    reviewDate: REVIEW_DATE,
    packageVersion: PACKAGE_VERSION,
    dependencies: { documents: [], packages: ["paper-ui"] },
    changelog: [
      { date: REVIEW_DATE, note: "Published the Stable Phase 2 contract." }
    ]
  });
}
var phaseTwoFixtures = [
  ...buttonFixtures,
  ...inputFixtures,
  defineCatalogFixture({
    "id": "modal.recommended",
    "name": "Confirm log deletion",
    "description": "Confirm a local demo deletion, or keep the log with either dismissal path.",
    "tags": [
      "modal",
      "focus",
      "destructive"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: modal_recommended_default,
    render: () => /* @__PURE__ */ jsx43(DeleteLogFixture, {})
  }),
  defineCatalogFixture({
    "id": "modal.composable-search",
    "name": "Composable catalogue search",
    "description": "A controlled footerless dialog focuses a real form field and filters local examples.",
    "tags": [
      "modal",
      "controlled",
      "trigger",
      "initial focus",
      "footerless"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: modal_composable_search_default,
    render: () => /* @__PURE__ */ jsx43(ComposableModalFixture, {})
  }),
  defineCatalogFixture({
    "id": "action-menu.recommended",
    "name": "Reading-log actions",
    "description": "Common, unavailable, and destructive actions in one menu.",
    "tags": [
      "action menu",
      "keyboard",
      "disabled",
      "destructive"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true,
    code: action_menu_recommended_default,
    render: () => /* @__PURE__ */ jsx43(ActionMenuFixture, {})
  })
];
var phaseTwoDocuments = [
  buttonDocument,
  inputDocument,
  componentDocument({
    id: "component.modal",
    route: "/components/overlays/modal",
    name: "Modal",
    category: "overlays",
    summary: "Temporarily focuses attention while managing focus, dismissal, and return.",
    keywords: ["modal", "dialog", "overlay", "focus", "confirmation"],
    sourcePath: "src/components/overlays/Modal/Modal.tsx",
    fixtureIds: ["modal.recommended", "modal.composable-search"],
    behaviorTestIds: ["modal.keyboard", "modal.focus-containment", "modal.focus-return"],
    guidance: {
      whenToUse: ["Require a focused decision or a short task without losing page context."],
      whenNotToUse: ["Use a page when the task is long, linkable, or needs deep navigation."],
      content: ["Name the decision in the title and make the consequence concrete."],
      commonMistakes: ["Do not nest modals or hide the only escape action."]
    },
    accessibility: {
      requirements: ["Expose a labelled dialog, trap focus while open, and return focus on close."],
      keyboard: ["Escape closes; Tab remains within the dialog."],
      knownConstraints: ["Long workflows should move to a dedicated page."]
    },
    api: {
      react: ["Modal"],
      props: [
        {
          "name": "title",
          "type": "string",
          "description": "Visible title and accessible dialog name.",
          "required": true
        },
        {
          "name": "children",
          "type": "ReactNode",
          "description": "A short bounded task or confirmation content.",
          "required": true
        },
        {
          "name": "description",
          "type": "string",
          "description": "Explain the consequence or task; associated with the dialog."
        },
        {
          "name": "triggerLabel",
          "type": "string",
          "description": "Name for the standard trigger; required unless supplying trigger."
        },
        {
          "name": "trigger",
          "type": "ReactElement",
          "description": "Application-owned trigger receiving dialog behavior and ARIA state."
        },
        {
          "name": "triggerVariant",
          "type": "ButtonVariant",
          "description": "Appearance of the standard trigger.",
          "defaultValue": "default"
        },
        {
          "name": "open / onOpenChange",
          "type": "boolean / (open: boolean) => void",
          "description": "Provide both for controlled state; close after successful async work."
        },
        {
          "name": "defaultOpen",
          "type": "boolean",
          "description": "Initial open state for an uncontrolled dialog.",
          "defaultValue": "false"
        },
        {
          "name": "initialFocus",
          "type": "boolean | RefObject<HTMLElement> | callback",
          "description": "Choose the initial focus target; use a field ref for editing tasks."
        },
        {
          "name": "closeLabel",
          "type": "string",
          "description": "Label for header and standard footer dismissal.",
          "defaultValue": "Close"
        },
        {
          "name": "action",
          "type": "{ label, variant?, disabled?, onAction }",
          "description": "Optional synchronous action in the standard footer. Activation also closes the dialog."
        },
        {
          "name": "footer",
          "type": "ReactNode",
          "description": "Replace the standard footer, or pass null to omit it. Use for async actions.",
          "defaultValue": "standard action/close footer"
        }
      ],
      cssClasses: [],
      publicTypes: ["ModalProps"],
      defaults: ["modal=true", "triggerVariant=default", "closeLabel=Close"],
      invalidCombinations: ["Do not nest Modal instances."]
    },
    migration: {
      legacy: ["ui Modal", "Headless UI Dialog"],
      notes: ["Paper owns the Base UI wrapper; applications do not import Base UI directly."]
    },
    sections: componentSections("Modal", {
      overview: section("Overview", "Modal creates a focused layer above the current task and restores context after dismissal."),
      whenToUse: section("When to use", "Use it for concise confirmations and bounded editing tasks."),
      whenNotToUse: section("When not to use", "Avoid it for passive notices, multi-step flows, or content that deserves a URL."),
      choosingBetween: section("Choose between", "Use Flash for non-blocking feedback, ActionMenu for contextual choices, and a page for sustained work."),
      anatomy: section("Anatomy", "Backdrop, viewport, titled popup, optional description, content, close affordance, and footer."),
      recommendedExample: section("Recommended example", "Review deletion and choose Delete log to see a local confirmation. Keep log, Escape, and the header close leave the value unchanged. Search examples demonstrates controlled state, initial focus, and a footerless search field."),
      variants: section("Variants", "Use the standard triggerLabel and action contract for common confirmations. Pass an application-owned trigger and footer when the task needs a composed entry point or action layout; footer=null intentionally omits the footer."),
      statesAndAdaptation: section("States and adaptation", "Use open with onOpenChange when application state or another control owns visibility. The viewport scrolls long content and remains bounded at phone, tablet, and desktop widths."),
      behavior: section("Behavior", "Base UI manages opening, focus containment, Escape/outside dismissal, and focus return. initialFocus targets an application-owned field. The standard action closes immediately; for an asynchronous save, use controlled open state and a custom footer, keep errors inside the dialog, and close only after success."),
      contentGuidance: section("Content guidance", "Use a question for confirmation titles and put consequences in the description."),
      accessibility: section("Accessibility", "Title and description label the dialog; two close paths ensure touch-screen-reader escape."),
      implementation: section("Implementation", "Import Modal from paper-ui; Base UI remains a private interaction dependency."),
      apiReference: section("API reference", "ModalProps supports controlled or uncontrolled open state, a standard or application-owned trigger, initialFocus, footer replacement or omission, title, description, content, close, and optional primary action contracts."),
      relatedPatterns: section("Related patterns", "Pair with destructive Button intent only when the action is genuinely destructive."),
      migration: section("Migration", "Replace legacy Dialog imports with Modal and remove application-owned portal/focus logic."),
      lifecycle: section("Lifecycle", "Stable in Paper 0.1.0 after keyboard, containment, dismissal, and return tests.")
    })
  }),
  componentDocument({
    id: "component.action-menu",
    route: "/components/actions/action-menu",
    name: "ActionMenu",
    category: "actions",
    summary: "Presents a compact keyboard-operable set of contextual actions.",
    keywords: ["menu", "actions", "keyboard", "disabled", "destructive"],
    sourcePath: "src/components/overlays/ActionMenu/ActionMenu.tsx",
    fixtureIds: ["action-menu.recommended"],
    behaviorTestIds: ["action-menu.keyboard", "action-menu.disabled", "action-menu.selection"],
    guidance: {
      whenToUse: ["Group three or more contextual actions for one object."],
      whenNotToUse: ["Keep a single important action visible as Button."],
      content: ["Use parallel verb phrases ordered by frequency, with destructive actions last."],
      commonMistakes: ["Do not use a menu to hide primary page navigation."]
    },
    accessibility: {
      requirements: ["Expose a named trigger, menu semantics, roving focus, and disabled state."],
      keyboard: ["Enter, Space, or ArrowDown opens; arrows move; Escape closes and returns focus."],
      knownConstraints: ["Menu items are actions; router-neutral link integration is a separate contract."]
    },
    api: {
      react: ["ActionMenu"],
      props: [
        { "name": "iconOnly", "type": "boolean", "defaultValue": "false", "description": "Show a named icon instead of text and chevron, with a 44px target in both densities. Defaults to an ellipsis; use triggerIcon for a recognizable account or context icon. Give label the full action context." },
        { "name": "triggerIcon", "type": "ReactNode", "description": "Optional decorative replacement for the default trigger icon. Paper supplies its size and hides it from assistive technology; label supplies the accessible name." },
        {
          "name": "label",
          "type": "string",
          "description": "Visible trigger name identifying which object the actions affect.",
          "required": true
        },
        {
          "name": "items",
          "type": "readonly ActionMenuItem[]",
          "description": "Stable id, visible label and onSelect callback for each action.",
          "required": true
        },
        {
          "name": "items[].disabled",
          "type": "boolean",
          "description": "Keeps an action visible but unavailable; explain why outside the menu.",
          "defaultValue": "false"
        },
        {
          "name": "items[].destructive",
          "type": "boolean",
          "description": "Highlights destructive intent; does not add confirmation.",
          "defaultValue": "false"
        },
        {
          "name": "items[].icon",
          "type": "ReactNode",
          "description": "Optional decorative icon beside the label."
        },
        {
          "name": "triggerVariant",
          "type": "ButtonVariant",
          "description": "Choose an appropriate trigger hierarchy.",
          "defaultValue": "outline"
        },
        {
          "name": "defaultOpen",
          "type": "boolean",
          "description": "Initial open state; normally leave closed.",
          "defaultValue": "false"
        }
      ],
      cssClasses: [],
      publicTypes: ["ActionMenuProps", "ActionMenuItem"],
      defaults: ["triggerVariant=outline", "loopFocus=true"],
      invalidCombinations: ["Do not treat disabled items as explanatory text."]
    },
    migration: {
      legacy: ["ui ActionMenu", "Headless UI Menu"],
      notes: ["Map each item to a stable id and explicit selection callback."]
    },
    sections: componentSections("ActionMenu", {
      overview: section("Overview", "ActionMenu keeps secondary contextual operations available without crowding the page."),
      whenToUse: section("When to use", "Use it for several actions that operate on the same reading log or table row."),
      whenNotToUse: section("When not to use", "Do not hide the main task or global navigation in a contextual action menu."),
      choosingBetween: section("Choose between", "Use Button for one visible action, Modal for a focused decision, and ActionMenu for a compact action set."),
      anatomy: section("Anatomy", "A named trigger anchors a floating menu containing text-labelled action items."),
      recommendedExample: section("Recommended example", "Open Log actions with the keyboard or pointer, select Edit log, and read the outcome below. Duplication stays disabled with its reason visible. Delete demonstrates intent only; real data deletion should request confirmation."),
      variants: section("Variants", "The trigger may use an appropriate Button variant; destructive styling belongs to the item, not the whole menu."),
      statesAndAdaptation: section("States and adaptation", "Highlighted, disabled, destructive, open, and closed states retain non-color cues and density sizing."),
      behavior: section("Behavior", "Base UI owns trigger activation, placement, roving focus, typeahead, dismissal, selection, and focus return."),
      contentGuidance: section("Content guidance", "Use short parallel verb phrases, order by likely use, and place destructive actions last."),
      accessibility: section("Accessibility", "The trigger names the action set; disabled items remain identifiable but cannot be selected."),
      implementation: section("Implementation", "Pass immutable item metadata and callbacks to ActionMenu; do not expose Base UI primitives to applications."),
      apiReference: section("API reference", "ActionMenuItem requires id, label, and onSelect; icon, disabled, and destructive are optional."),
      relatedPatterns: section("Related patterns", "Use Modal after selection only when the chosen action needs focused confirmation."),
      migration: section("Migration", "Replace legacy render-prop menus and implicit class ordering with explicit item intent."),
      lifecycle: section("Lifecycle", "Stable in Paper 0.1.0 with keyboard, disabled-item, selection, and real-browser pointer evidence.")
    })
  })
];

// src/catalog/examples/button-group.entry-actions.tsx
import { useState as useState11 } from "react";
import { jsx as jsx44, jsxs as jsxs34 } from "react/jsx-runtime";
function EntryActionsFixture() {
  const [message, setMessage] = useState11("Choose an action to see its result.");
  return /* @__PURE__ */ jsxs34("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx44(
      ButtonGroup,
      {
        label: "Entry actions",
        actions: [
          {
            id: "view",
            label: "View log",
            href: "#reading-summary",
            variant: "outline"
          },
          {
            id: "edit",
            label: "Edit log",
            onSelect: () => setMessage("Editing the local preview entry.")
          },
          {
            id: "delete",
            label: "Delete log",
            variant: "destructive",
            disabled: true
          }
        ]
      }
    ),
    /* @__PURE__ */ jsx44("p", { role: "status", children: message }),
    /* @__PURE__ */ jsx44("p", { id: "reading-summary", children: "Reading log: 48 pages of Japanese fiction. Deletion is unavailable for this submitted entry." })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/button-group.entry-actions.tsx
var button_group_entry_actions_default = `import { ButtonGroup } from 'paper-ui'
import { useState } from 'react'

export default function EntryActionsFixture() {
  const [message, setMessage] = useState('Choose an action to see its result.')
  return (
    <div className="paper-stack">
      <ButtonGroup
        label="Entry actions"
        actions={[
          {
            id: 'view',
            label: 'View log',
            href: '#reading-summary',
            variant: 'outline',
          },
          {
            id: 'edit',
            label: 'Edit log',
            onSelect: () => setMessage('Editing the local preview entry.'),
          },
          {
            id: 'delete',
            label: 'Delete log',
            variant: 'destructive',
            disabled: true,
          },
        ]}
      />
      <p role="status">{message}</p>
      <p id="reading-summary">
        Reading log: 48 pages of Japanese fiction. Deletion is unavailable for
        this submitted entry.
      </p>
    </div>
  )
}
`;

// src/catalog/examples/surface.summary.tsx
import { jsx as jsx45, jsxs as jsxs35 } from "react/jsx-runtime";
function Example6() {
  return /* @__PURE__ */ jsxs35("div", { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    ["flat", "floating", "showcase"].map((elevation) => /* @__PURE__ */ jsxs35(
      Surface,
      {
        as: "article",
        elevation,
        className: "paper-stack",
        children: [
          /* @__PURE__ */ jsxs35("h3", { style: { margin: 0 }, children: [
            elevation === "flat" ? "Flat" : elevation === "floating" ? "Floating" : "Showcase",
            " ",
            "summary"
          ] }),
          /* @__PURE__ */ jsx45("p", { style: { margin: 0 }, children: "August Japanese: 1,240 pages across 18 entries." })
        ]
      },
      elevation
    )),
    /* @__PURE__ */ jsxs35(Surface, { as: "article", accent: true, className: "paper-stack", children: [
      /* @__PURE__ */ jsx45("h3", { style: { margin: 0 }, children: "Flat with accent rail" }),
      /* @__PURE__ */ jsx45("p", { style: { margin: 0 }, children: "The same reading summary with an independent accent." })
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/surface.summary.tsx
var surface_summary_default = `import { Surface } from 'paper-ui'

export default function Example() {
  return (
    <div className="paper-stack" style={{ maxWidth: '32rem' }}>
      {(['flat', 'floating', 'showcase'] as const).map(elevation => (
        <Surface
          key={elevation}
          as="article"
          elevation={elevation}
          className="paper-stack"
        >
          <h3 style={{ margin: 0 }}>
            {elevation === 'flat'
              ? 'Flat'
              : elevation === 'floating'
              ? 'Floating'
              : 'Showcase'}{' '}
            summary
          </h3>
          <p style={{ margin: 0 }}>
            August Japanese: 1,240 pages across 18 entries.
          </p>
        </Surface>
      ))}
      <Surface as="article" accent className="paper-stack">
        <h3 style={{ margin: 0 }}>Flat with accent rail</h3>
        <p style={{ margin: 0 }}>
          The same reading summary with an independent accent.
        </p>
      </Surface>
    </div>
  )
}
`;

// src/catalog/button-group-and-surface.tsx
import { jsx as jsx46 } from "react/jsx-runtime";
var buttonGroupAndSurfaceDetails = {
  "component.button-group": {
    example: "View log follows an in-preview anchor, Edit log updates the status, and Delete log is disabled with a visible reason. These actions stay independently focusable.",
    anatomy: "A labelled role=group wraps real links and buttons. The wrapper has no toolbar keyboard behavior.",
    variants: "align=start or end positions the group. Individual actions use default, outline, ghost, link, or destructive; keep one primary action per task.",
    states: "Actions wrap when width is limited. visible=false removes an action. Disabled buttons use native disabled; disabled links retain link semantics but block activation with aria-disabled.",
    content: "Name the operation, such as Edit log. The group label describes the shared subject; explain why disabled destructive operations are unavailable.",
    implementation: "Import ButtonGroup from paper-ui. Provide href for navigation and onSelect for an operation; when both exist, onSelect also runs before navigation. The example defines the complete actions list and updates local status.",
    props: [{ name: "actions", type: "readonly ButtonGroupAction[]", required: true, description: "Each action requires id and label. Optional href, onSelect, variant, disabled, and visible determine native link/button behavior." }, { name: "label", type: "string", defaultValue: '"Actions"', description: "Accessible group name." }, { name: "align", type: '"start" | "end"', defaultValue: '"start"', description: "Horizontal alignment; actions wrap on small screens." }]
  },
  "component.surface": {
    example: "Compare the same reading summary at flat, floating, and showcase elevation, then compare a flat surface with an accent rail. Content and spacing stay constant so each treatment is visible.",
    anatomy: "A semantic container provides a background, rule, padding, optional shadow, and optional inline-start accent rail. Content headings and internal spacing remain explicit.",
    variants: "Flat is the default content group. Floating adds the lighter offset shadow. Showcase uses the stronger shadow for an intentional demonstration. accent adds a rail independently of elevation; it does not imply success or selection.",
    states: "Surface has no selected, disabled, hover, or interactive state. Content wraps with the available width. Theme changes surface/rule/shadow tokens; density inherits from the Paper root.",
    content: "Give articles or named sections a heading that describes their content. Avoid repeated nested cards: use spacing and rules within a surface before adding another container.",
    implementation: "Import Surface for div, article, or section containers. For another existing semantic element, use surfaceClassName with elevation, accent, and className. Load paper-ui/styles.css once; the recipe adds no click or keyboard behavior.",
    props: [{ name: "as", type: '"div" | "article" | "section"', defaultValue: '"div"', description: "HTML element. Use article for self-contained content; label a section when it should be a named region." }, { name: "elevation", type: '"flat" | "floating" | "showcase"', defaultValue: '"flat"', description: "Rule-only, light offset shadow, or stronger offset shadow." }, { name: "accent", type: "boolean", defaultValue: "false", description: "Adds the shared inline-start accent rail inside the surface boundary." }, { name: "className", type: "string", description: "Additional composition classes; paper-stack can space the children." }, { name: "children", type: "ReactNode", description: "Caller-owned content and heading hierarchy." }]
  }
};
var buttonGroupAndSurfaceCommonMistakes = {
  "component.button-group": "Do not add toolbar arrow-key expectations: each link or button is reached independently with Tab.",
  "component.surface": "Do not treat an accent rail or shadow as an interactive affordance. Surface has no click or selection behavior."
};
var buttonGroupFixtures = [
  defineCatalogFixture({ ...{
    "id": "button-group.entry-actions",
    "name": "Entry actions",
    "description": "Run the actions and inspect their visible result.",
    "tags": [
      "button",
      "group",
      "entry",
      "actions"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: button_group_entry_actions_default, render: () => /* @__PURE__ */ jsx46(EntryActionsFixture, {}) })
];
var buttonGroupSpec = { id: "component.button-group", route: "/components/actions/button-group", name: "ButtonGroup", category: "actions", summary: "Keeps related visible actions together without changing link or button semantics.", fixtureId: "button-group.entry-actions", sourcePath: "src/components/feedback/feedback.tsx", react: ["ButtonGroup"], types: ["ButtonGroupProps", "ButtonGroupAction"], when: "Use for a small, stable set of related actions that should remain visible.", avoid: "Use ActionMenu when the set is contextual or too large for the available width.", choose: "ButtonGroup prioritizes visibility; ActionMenu prioritizes compact contextual access.", behavior: "Each entry remains a real anchor or button and the wrapper contributes only a labelled group.", accessibility: "Preserve native roles, express disabled links with aria-disabled plus blocked activation, and label the group.", migration: "Replace responsive Next Link coupling with router-neutral href or onSelect actions." };
var surfaceFixtures = [
  defineCatalogFixture({ ...{
    "id": "surface.summary",
    "name": "Elevation and accent comparison",
    "description": "Compare the supported presentations with consistent reading-log content.",
    "tags": [
      "surface",
      "summary"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: surface_summary_default, render: () => /* @__PURE__ */ jsx46(Example6, {}) })
];
var surfaceSpec = { id: "component.surface", route: "/components/data-display/surface", name: "Surface", category: "data-display", summary: "Composes flat, floating, or showcase content with optional Paper accent rail.", fixtureId: "surface.summary", sourcePath: "src/components/feedback/feedback.tsx", react: ["Surface", "surfaceClassName"], types: ["SurfaceProps"], css: ["paper-surface-card", "paper-elevation-*"], when: "Use to group related content when a semantic article, section, or neutral container also needs Paper surface treatment.", avoid: "Do not wrap every block in a card or use elevation as decoration without hierarchy.", choose: "Flat separates with rules; floating marks transient or interactive content; showcase is reserved for strong demonstrations.", behavior: "Surface adds no interaction and preserves the semantics selected with the as prop.", accessibility: "Choose article or section only when the content merits that landmark; elevation cannot replace headings.", migration: "Replace copied card and shadow classes with the shared recipe or Surface." };

// src/catalog/examples/flash.statuses.tsx
import { jsx as jsx47, jsxs as jsxs36 } from "react/jsx-runtime";
function Example7() {
  return /* @__PURE__ */ jsxs36("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx47(Flash, { title: "Contest note", children: "Only finished reading counts." }),
    /* @__PURE__ */ jsx47(Flash, { variant: "success", title: "Entry saved", children: "48 pages added." }),
    /* @__PURE__ */ jsx47(
      Flash,
      {
        variant: "warning",
        title: "Check the date",
        action: /* @__PURE__ */ jsx47("a", { href: "#contest-window", children: "Review dates" }),
        children: "This entry is outside the contest window."
      }
    ),
    /* @__PURE__ */ jsx47(Flash, { variant: "danger", title: "Could not save", children: "Review the highlighted fields before trying again." }),
    /* @__PURE__ */ jsx47("p", { id: "contest-window", children: "Contest window: August 1\u201331. Choose a reading date inside this window." })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/flash.statuses.tsx
var flash_statuses_default = `import { Flash } from 'paper-ui'

export default function Example() {
  return (
    <div className="paper-stack">
      <Flash title="Contest note">Only finished reading counts.</Flash>
      <Flash variant="success" title="Entry saved">
        48 pages added.
      </Flash>
      <Flash
        variant="warning"
        title="Check the date"
        action={<a href="#contest-window">Review dates</a>}
      >
        This entry is outside the contest window.
      </Flash>
      <Flash variant="danger" title="Could not save">
        Review the highlighted fields before trying again.
      </Flash>
      <p id="contest-window">
        Contest window: August 1\u201331. Choose a reading date inside this window.
      </p>
    </div>
  )
}
`;

// src/catalog/examples/loading.entries.tsx
import { jsx as jsx48, jsxs as jsxs37 } from "react/jsx-runtime";
function Example8() {
  return /* @__PURE__ */ jsxs37("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx48("p", { children: "Loading reading entries. Your saved entries will appear here." }),
    /* @__PURE__ */ jsxs37("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsxs37("span", { className: "paper-stack", children: [
        /* @__PURE__ */ jsx48(
          Loading,
          {
            label: "Loading reading entries, small example",
            size: "small"
          }
        ),
        /* @__PURE__ */ jsx48("span", { children: "Small" })
      ] }),
      /* @__PURE__ */ jsxs37("span", { className: "paper-stack", children: [
        /* @__PURE__ */ jsx48(Loading, { label: "Loading reading entries, default example" }),
        /* @__PURE__ */ jsx48("span", { children: "Default" })
      ] }),
      /* @__PURE__ */ jsxs37("span", { className: "paper-stack", children: [
        /* @__PURE__ */ jsx48(
          Loading,
          {
            label: "Loading reading entries, large example",
            size: "large"
          }
        ),
        /* @__PURE__ */ jsx48("span", { children: "Large" })
      ] })
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/loading.entries.tsx
var loading_entries_default = `import { Loading } from 'paper-ui'

export default function Example() {
  return (
    <div className="paper-stack">
      <p>Loading reading entries. Your saved entries will appear here.</p>
      <div className="paper-cluster">
        <span className="paper-stack">
          <Loading
            label="Loading reading entries, small example"
            size="small"
          />
          <span>Small</span>
        </span>
        <span className="paper-stack">
          <Loading label="Loading reading entries, default example" />
          <span>Default</span>
        </span>
        <span className="paper-stack">
          <Loading
            label="Loading reading entries, large example"
            size="large"
          />
          <span>Large</span>
        </span>
      </div>
    </div>
  )
}
`;

// src/catalog/examples/toast.entry-saved.tsx
import { jsx as jsx49, jsxs as jsxs38 } from "react/jsx-runtime";
function ToastFixture() {
  const toast = useToast();
  return /* @__PURE__ */ jsxs38("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx49("p", { children: "Notifications remain visible in this preview until dismissed. Applications normally use a finite timeout." }),
    /* @__PURE__ */ jsxs38("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx49(
        Button,
        {
          onClick: () => toast.add({
            title: "Entry saved",
            description: "48 pages added to August Japanese."
          }),
          children: "Show notification"
        }
      ),
      /* @__PURE__ */ jsx49(
        Button,
        {
          variant: "outline",
          onClick: () => toast.add({
            title: "Could not sync entry",
            description: "Your local entry is safe. Retry from the reading log.",
            priority: "high"
          }),
          children: "Show failure"
        }
      ),
      /* @__PURE__ */ jsx49(Button, { variant: "ghost", onClick: () => toast.close(), children: "Dismiss all" })
    ] })
  ] });
}
function Example9() {
  return /* @__PURE__ */ jsx49(ToastProvider, { timeout: 0, children: /* @__PURE__ */ jsx49(ToastFixture, {}) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/toast.entry-saved.tsx
var toast_entry_saved_default = `import { Button, ToastProvider, useToast } from 'paper-ui'

function ToastFixture() {
  const toast = useToast()
  return (
    <div className="paper-stack">
      <p>
        Notifications remain visible in this preview until dismissed.
        Applications normally use a finite timeout.
      </p>
      <div className="paper-cluster">
        <Button
          onClick={() =>
            toast.add({
              title: 'Entry saved',
              description: '48 pages added to August Japanese.',
            })
          }
        >
          Show notification
        </Button>
        <Button
          variant="outline"
          onClick={() =>
            toast.add({
              title: 'Could not sync entry',
              description:
                'Your local entry is safe. Retry from the reading log.',
              priority: 'high',
            })
          }
        >
          Show failure
        </Button>
        <Button variant="ghost" onClick={() => toast.close()}>
          Dismiss all
        </Button>
      </div>
    </div>
  )
}

export default function Example() {
  return (
    <ToastProvider timeout={0}>
      <ToastFixture />
    </ToastProvider>
  )
}
`;

// src/catalog/feedback-components.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
var feedbackDetails = {
  "component.flash": {
    example: "Compare all four statuses with identical anatomy. The warning includes a real in-preview recovery link; title and text explain the status without relying on the rail color.",
    anatomy: "A colored square rail, optional decorative icon, title/body, and optional action form one persistent message.",
    variants: "Information, success, warning, and danger encode distinct outcomes.",
    states: "visible=false unmounts the message; caller-owned actions keep native semantics.",
    content: "Name the issue and its recovery.",
    implementation: "Import Flash from paper-ui. Pass body content as children and a real Paper button or link as action. Visibility is caller-controlled; Flash does not add its own dismiss state.",
    props: [{ name: "variant", type: '"information" | "success" | "warning" | "danger"', defaultValue: '"information"', description: "Danger uses role=alert; the other variants use role=status." }, { name: "title", type: "string", description: "Short outcome or problem statement above the body." }, { name: "children", type: "ReactNode", description: "Consequence and recovery copy." }, { name: "action", type: "ReactNode", description: "Caller-owned link or button; no action behavior is inferred." }, { name: "icon", type: "ReactNode", description: "Optional decorative icon, hidden from assistive technology." }, { name: "visible", type: "boolean", defaultValue: "true", description: "False returns null and removes the message from the document." }]
  },
  "component.loading": {
    example: "Compare all three spinner sizes with a visible description of the pending reading operation. The accessible label remains present even with reduced motion enabled.",
    anatomy: "A decorative spinner sits inside a role=status wrapper with a visually hidden label. Visible loading copy must be supplied by the application.",
    variants: "small fits inline operations; default fits a section; large gives a pending page or region stronger emphasis. Size does not indicate percentage progress.",
    states: "Indeterminate only. Reduced motion stops the animation while retaining the shape and label. Unmount Loading when content or an error replaces the pending state.",
    content: "Name what is loading: Loading reading entries. For long waits, also show visible explanatory text so sighted users know which operation is pending.",
    implementation: "Import Loading from paper-ui. Render conditionally while waiting; mark the region being updated aria-busy when appropriate. Keep one useful status announcement per operation.",
    props: [{ name: "label", type: "string", defaultValue: '"Loading"', description: "Visually hidden accessible status text; supply visible copy alongside the spinner when needed." }, { name: "size", type: '"small" | "default" | "large"', defaultValue: '"default"', description: "Visual spinner size, independent of the loading state." }]
  },
  "component.toast": {
    example: "Trigger a confirmation or failure, add several notifications to inspect the queue, then dismiss one or all. This teaching preview uses timeout=0; choose a finite timeout in application setup unless persistence is intentional.",
    anatomy: "One provider owns the queue and viewport; each notification contains a title, optional description/action, and named dismiss button.",
    variants: "low priority announces politely; high interrupts and belongs to urgent failures. The type field is metadata and does not currently select a Paper color variant.",
    states: "ToastProvider defaults follow Base UI: a five-second timeout and three visible toasts. timeout=0 disables expiry. Hover/focus pauses expiry; closing removes a notification through the manager. Keep durable recovery beside the affected content.",
    content: "Use a completed outcome for confirmation, such as Entry saved. An error should explain what remains safe and where to retry. Do not put the only copy of a critical recovery action in a timed message.",
    implementation: "Mount ToastProvider once above components calling useToast. The hook must run inside that provider, not in the same component that creates it. Use add for a new message, update for an existing id, close for dismissal, and promise for loading/success/error transitions. ToastContainer is a migration alias for the provider.",
    props: [{ name: "ToastProvider.timeout", type: "number", defaultValue: "5000", description: "Milliseconds before dismissal; 0 disables automatic dismissal." }, { name: "ToastProvider.limit", type: "number", defaultValue: "3", description: "Maximum notifications visible at once." }, { name: "add(options)", type: "(ToastOptions) => string", description: "Returns an id. Options include title, description, priority, timeout, actionProps, onClose, and onRemove." }, { name: "close(id?)", type: "(string?) => void", description: "Dismiss one notification, or all when the id is omitted." }, { name: "update(id, options)", type: "(string, ToastUpdateOptions) => void", description: "Change an existing notification without adding another." }, { name: "promise(promise, options)", type: "<Value>(Promise<Value>, ToastPromiseOptions<Value>) => Promise<Value>", description: "Supply loading, success, and error messages or callbacks for one asynchronous operation." }]
  }
};
var feedbackCommonMistakes = {
  "component.flash": "Do not use a danger rail for neutral context; danger also interrupts assistive technology with an alert.",
  "component.loading": "Do not assume label is visible: it is visually hidden. Supply visible waiting copy when users need more than a spinner.",
  "component.toast": "Do not call useToast above its provider or duplicate providers for every action. Mount one provider around the application area."
};
var flashFixtures = [
  defineCatalogFixture({ ...{
    "id": "flash.statuses",
    "name": "Feedback statuses",
    "description": "Compare the supported presentations with consistent reading-log content.",
    "tags": [
      "flash",
      "statuses"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: flash_statuses_default, render: () => /* @__PURE__ */ jsx50(Example7, {}) })
];
var flashSpec = { id: "component.flash", route: "/components/feedback/flash", name: "Flash", category: "feedback", summary: "Places persistent contextual feedback beside the content it describes.", fixtureId: "flash.statuses", sourcePath: "src/components/feedback/feedback.tsx", react: ["Flash"], types: ["FlashProps", "FlashVariant"], when: "Use for page or section feedback that should remain until the context changes, such as a saved result, a contest warning, or a failed page-level operation.", avoid: "Use Toast for brief asynchronous confirmation and an inline field error when the user must repair one value. Do not put required recovery only in a Flash that may disappear with its surrounding content.", choose: "Flash stays beside the content it explains. Toast is transient and global; field errors identify a specific control and its recovery.", variants: "Information covers neutral context, success confirms a completed outcome, and warning calls for review before proceeding. Danger is reserved for an urgent failure and changes the live-region role from status to alert.", states: "Setting visible=false removes the message from the document. Actions are caller-owned links or buttons, so their label, disabled state, and navigation behavior must remain explicit.", behavior: "Danger feedback is announced as an alert; information, success, and warning use a polite status announcement. Changing visible to false unmounts the message instead of merely hiding it.", content: "Write the title as the outcome or issue, then use the body for consequence and recovery. Keep any action label specific\u2014Review fields or Try again instead of OK.", accessibility: "Pair the colored edge with a title and message text, because color does not name the status. Provide actions as real links or buttons and reserve danger for messages that warrant an interrupting alert.", migration: "Map error to danger, remove Next Link ownership, and pass any action as router-owned React content.", pageSections: ["usage", "examples", "variantsAndStates", "behavior", "contentGuidance", "accessibility"] };
var loadingFixtures = [
  defineCatalogFixture({ ...{
    "id": "loading.entries",
    "name": "Loading sizes and visible context",
    "description": "Compare the supported presentations with consistent reading-log content.",
    "tags": [
      "loading",
      "entries"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: loading_entries_default, render: () => /* @__PURE__ */ jsx50(Example8, {}) })
];
var loadingSpec = { id: "component.loading", route: "/components/feedback/loading", name: "Loading", category: "feedback", summary: "Communicates indeterminate progress with a named, reduced-motion-safe status.", fixtureId: "loading.entries", sourcePath: "src/components/feedback/feedback.tsx", react: ["Loading"], types: ["LoadingProps"], when: "Use while a bounded region is waiting and no meaningful progress value exists.", avoid: "Use native progress for measurable work and retain existing content when optimistic updates are safer.", choose: "Loading indicates indeterminate work; Button loading prevents repeat action while keeping its name.", behavior: "The status label is available to assistive technology and animation stops under reduced motion.", accessibility: "Supply a label that names what is loading and do not rely on spinner motion alone.", migration: "Replace legacy Loading and remove application-owned spinner SVGs." };
var toastFixtures = [
  defineCatalogFixture({ ...{
    "id": "toast.entry-saved",
    "name": "Queued notifications",
    "description": "Run the actions and inspect their visible result.",
    "tags": [
      "toast",
      "entry",
      "saved"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: toast_entry_saved_default, render: () => /* @__PURE__ */ jsx50(Example9, {}) })
];
var toastSpec = { id: "component.toast", route: "/components/feedback/toast", name: "Toast", category: "feedback", summary: "Queues brief asynchronous notifications with polite/urgent priority and explicit dismissal.", fixtureId: "toast.entry-saved", sourcePath: "src/components/feedback/feedback.tsx", react: ["ToastProvider", "useToast", "ToastContainer"], types: ["ToastProviderProps"], when: "Use for brief confirmation or failure that is not tied to one visible field or section.", avoid: "Use Flash for persistent contextual feedback and never put required recovery only in an expiring toast.", choose: "Toast is transient global feedback; Flash remains in context until it is no longer relevant.", behavior: "Base UI owns queueing, live announcements, timeout pause, swipe, and stacking behind the Paper provider.", accessibility: "Choose low or high priority intentionally, keep a visible dismiss action, and repeat critical recovery in context.", migration: "Replace react-toastify setup with ToastProvider and useToast; ToastContainer remains a migration alias." };

// src/catalog/examples/autocomplete.language.tsx
import { useStore as useStore3 } from "@tanstack/react-form";
import { useState as useState12 } from "react";
import { jsx as jsx51, jsxs as jsxs39 } from "react/jsx-runtime";
var LANGUAGES = [
  { id: "ja", label: "Japanese" },
  { id: "zh", label: "Chinese" },
  { id: "ko", label: "Korean" }
];
function Example10() {
  const [savedValues, setSavedValues] = useState12(null);
  const methods = usePaperForm({
    defaultValues: { autocomplete: { id: "ja", label: "Japanese" } },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore3(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx51(methods.AppForm, { children: /* @__PURE__ */ jsxs39(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx51(
      methods.AppField,
      {
        name: "autocomplete",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx51(
          field.AutocompleteInput,
          {
            label: "Language",
            required: true,
            options: LANGUAGES,
            format: (option) => option.label,
            getId: (option) => option.id,
            placeholder: "Search languages"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs39("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx51(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx51(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx51(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx51(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs39("details", { children: [
      /* @__PURE__ */ jsx51("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx51("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/autocomplete.language.empty.tsx
import { useStore as useStore4 } from "@tanstack/react-form";
import { useState as useState13 } from "react";
import { jsx as jsx52, jsxs as jsxs40 } from "react/jsx-runtime";
var LANGUAGES2 = [
  { id: "ja", label: "Japanese" },
  { id: "zh", label: "Chinese" },
  { id: "ko", label: "Korean" }
];
function Example11() {
  const [savedValues, setSavedValues] = useState13(null);
  const methods = usePaperForm({
    defaultValues: { autocomplete: null },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore4(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx52(methods.AppForm, { children: /* @__PURE__ */ jsxs40(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx52(
      methods.AppField,
      {
        name: "autocomplete",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx52(
          field.AutocompleteInput,
          {
            label: "Language",
            required: true,
            options: LANGUAGES2,
            format: (option) => option.label,
            getId: (option) => option.id,
            placeholder: "Search languages"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs40("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx52(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx52(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx52(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx52(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs40("details", { children: [
      /* @__PURE__ */ jsx52("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx52("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/autocomplete.language.empty.tsx
var autocomplete_language_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

const LANGUAGES = [
  { id: 'ja', label: 'Japanese' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ko', label: 'Korean' },
]

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { autocomplete: null as (typeof LANGUAGES)[number] | null },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="autocomplete"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.AutocompleteInput
              label="Language"
              required
              options={LANGUAGES}
              format={option => option.label}
              getId={option => option.id}
              placeholder="Search languages"
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/autocomplete.language.tsx
var autocomplete_language_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

const LANGUAGES = [
  { id: 'ja', label: 'Japanese' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ko', label: 'Korean' },
]

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { autocomplete: { id: 'ja', label: 'Japanese' } },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="autocomplete"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.AutocompleteInput
              label="Language"
              required
              options={LANGUAGES}
              format={option => option.label}
              getId={option => option.id}
              placeholder="Search languages"
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/multi-autocomplete.languages.tsx
import { useStore as useStore5 } from "@tanstack/react-form";
import { useState as useState14 } from "react";
import { jsx as jsx53, jsxs as jsxs41 } from "react/jsx-runtime";
var LANGUAGES3 = [
  { id: "ja", label: "Japanese" },
  { id: "zh", label: "Chinese" },
  { id: "ko", label: "Korean" }
];
function Example12() {
  const [savedValues, setSavedValues] = useState14(null);
  const methods = usePaperForm({
    defaultValues: { multiple: [{ id: "ja", label: "Japanese" }] },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore5(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx53(methods.AppForm, { children: /* @__PURE__ */ jsxs41(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx53(
      methods.AppField,
      {
        name: "multiple",
        validators: {
          onChange: ({ value }) => {
            if (!value.length) return "Choose at least one option.";
            if (value.length !== 2)
              return "Choose two languages for this bilingual entry.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx53(
          field.AutocompleteMultiInput,
          {
            label: "Languages",
            hint: "Choose two languages for this bilingual entry.",
            required: true,
            maxSelections: 2,
            options: LANGUAGES3,
            format: (option) => option.label,
            getId: (option) => option.id,
            placeholder: "Add language"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs41("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx53(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx53(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx53(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx53(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs41("details", { children: [
      /* @__PURE__ */ jsx53("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx53("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/multi-autocomplete.languages.empty.tsx
import { useStore as useStore6 } from "@tanstack/react-form";
import { useState as useState15 } from "react";
import { jsx as jsx54, jsxs as jsxs42 } from "react/jsx-runtime";
var LANGUAGES4 = [
  { id: "ja", label: "Japanese" },
  { id: "zh", label: "Chinese" },
  { id: "ko", label: "Korean" }
];
function Example13() {
  const [savedValues, setSavedValues] = useState15(null);
  const methods = usePaperForm({
    defaultValues: { multiple: [] },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore6(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx54(methods.AppForm, { children: /* @__PURE__ */ jsxs42(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx54(
      methods.AppField,
      {
        name: "multiple",
        validators: {
          onChange: ({ value }) => {
            if (!value.length) return "Choose at least one option.";
            if (value.length !== 2)
              return "Choose two languages for this bilingual entry.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx54(
          field.AutocompleteMultiInput,
          {
            label: "Languages",
            hint: "Choose two languages for this bilingual entry.",
            required: true,
            maxSelections: 2,
            options: LANGUAGES4,
            format: (option) => option.label,
            getId: (option) => option.id,
            placeholder: "Add language"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs42("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx54(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx54(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx54(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx54(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs42("details", { children: [
      /* @__PURE__ */ jsx54("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx54("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/multi-autocomplete.languages.empty.tsx
var multi_autocomplete_languages_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

const LANGUAGES = [
  { id: 'ja', label: 'Japanese' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ko', label: 'Korean' },
]

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { multiple: [] as (typeof LANGUAGES)[number][] },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="multiple"
          validators={{
            onChange: ({ value }) => {
              if (!value.length) return 'Choose at least one option.'
              if (value.length !== 2)
                return 'Choose two languages for this bilingual entry.'
            },
          }}
        >
          {field => (
            <field.AutocompleteMultiInput
              label="Languages"
              hint="Choose two languages for this bilingual entry."
              required
              maxSelections={2}
              options={LANGUAGES}
              format={option => option.label}
              getId={option => option.id}
              placeholder="Add language"
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/multi-autocomplete.languages.tsx
var multi_autocomplete_languages_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

const LANGUAGES = [
  { id: 'ja', label: 'Japanese' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ko', label: 'Korean' },
]

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { multiple: [{ id: 'ja', label: 'Japanese' }] },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="multiple"
          validators={{
            onChange: ({ value }) => {
              if (!value.length) return 'Choose at least one option.'
              if (value.length !== 2)
                return 'Choose two languages for this bilingual entry.'
            },
          }}
        >
          {field => (
            <field.AutocompleteMultiInput
              label="Languages"
              hint="Choose two languages for this bilingual entry."
              required
              maxSelections={2}
              options={LANGUAGES}
              format={option => option.label}
              getId={option => option.id}
              placeholder="Add language"
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/tags.entry.tsx
import { useStore as useStore7 } from "@tanstack/react-form";
import { useState as useState16 } from "react";
import { jsx as jsx55, jsxs as jsxs43 } from "react/jsx-runtime";
function Example14() {
  const [savedValues, setSavedValues] = useState16(null);
  const methods = usePaperForm({
    defaultValues: { tags: ["fiction"] },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore7(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx55(methods.AppForm, { children: /* @__PURE__ */ jsxs43(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx55(
      methods.AppField,
      {
        name: "tags",
        validators: {
          onChange: ({ value }) => {
            if (!value.length) return "Add at least one tag.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx55(
          field.TagsInput,
          {
            label: "Tags",
            hint: "Choose a suggestion or type a new tag and press Enter. Add up to four tags.",
            required: true,
            options: ["fiction", "history", "manga", "nonfiction"],
            maxSelections: 4,
            placeholder: "Add tag"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs43("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx55(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx55(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx55(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx55(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs43("details", { children: [
      /* @__PURE__ */ jsx55("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx55("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/tags.entry.empty.tsx
import { useStore as useStore8 } from "@tanstack/react-form";
import { useState as useState17 } from "react";
import { jsx as jsx56, jsxs as jsxs44 } from "react/jsx-runtime";
function Example15() {
  const [savedValues, setSavedValues] = useState17(null);
  const methods = usePaperForm({
    defaultValues: { tags: [] },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore8(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx56(methods.AppForm, { children: /* @__PURE__ */ jsxs44(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx56(
      methods.AppField,
      {
        name: "tags",
        validators: {
          onChange: ({ value }) => {
            if (!value.length) return "Add at least one tag.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx56(
          field.TagsInput,
          {
            label: "Tags",
            hint: "Choose a suggestion or type a new tag and press Enter. Add up to four tags.",
            required: true,
            options: ["fiction", "history", "manga", "nonfiction"],
            maxSelections: 4,
            placeholder: "Add tag"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs44("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx56(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx56(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx56(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx56(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs44("details", { children: [
      /* @__PURE__ */ jsx56("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx56("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/tags.entry.empty.tsx
var tags_entry_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { tags: [] as string[] },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="tags"
          validators={{
            onChange: ({ value }) => {
              if (!value.length) return 'Add at least one tag.'
            },
          }}
        >
          {field => (
            <field.TagsInput
              label="Tags"
              hint="Choose a suggestion or type a new tag and press Enter. Add up to four tags."
              required
              options={['fiction', 'history', 'manga', 'nonfiction']}
              maxSelections={4}
              placeholder="Add tag"
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/tags.entry.tsx
var tags_entry_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { tags: ['fiction'] },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="tags"
          validators={{
            onChange: ({ value }) => {
              if (!value.length) return 'Add at least one tag.'
            },
          }}
        >
          {field => (
            <field.TagsInput
              label="Tags"
              hint="Choose a suggestion or type a new tag and press Enter. Add up to four tags."
              required
              options={['fiction', 'history', 'manga', 'nonfiction']}
              maxSelections={4}
              placeholder="Add tag"
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/forms-feedback-spec.ts
var fieldProps = [
  { name: "name", type: "string", required: true, description: "Pass name to form.AppField, not the bound control. Initialize it through usePaperForm defaultValues." },
  { name: "label", type: "string", required: true, description: "Persistent visible label; do not replace it with a placeholder." },
  { name: "hint", type: "string", description: "Help shown below the control and connected through aria-describedby. Optional hints do not shift adjacent controls out of alignment." },
  { name: "required", type: "boolean", defaultValue: "false (RadioGroup: true)", description: "Marks the native control as required; add the required rule to the AppField or form validators." }
];
var validationProp = { name: "validators (AppField)", type: "FieldValidators", description: "TanStack validation and recovery messages belong on form.AppField or the form. form.Form submits through handleSubmit and focuses errors." };
var optionsProp = { name: "options", type: "readonly Option<string>[]", required: true, description: "Objects with value, label, and optional disabled. Persist value; show label to the user." };
var disabledProp = { name: "disabled", type: "boolean", defaultValue: "false", description: "Prevents editing. Explain why the value is unavailable in nearby text." };
var formSetup = "Import usePaperForm from paper-ui. Initialize typed defaultValues, validators and onSubmit. Wrap form.Form in form.AppForm; bind each named form.AppField to field.Input or another bound control. required and numeric bounds need matching validators. Use reset(values, { keepDefaultValues: true }) to load another draft. The screen owns persistence and server errors.";

// src/catalog/form-autocomplete.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var autocompleteProps = [
  ...fieldProps,
  validationProp,
  { name: "options", type: "readonly Value[]", required: true, description: "Known objects supplied by the application. Fetch remote suggestions outside this component." },
  { name: "format", type: "(option: Value) => string", required: true, description: "Visible option label, also used for case-insensitive substring matching." },
  { name: "getId", type: "(option: Value) => string", required: true, description: "Stable unique identifier used to compare refreshed option objects with selected values." },
  { name: "match", type: "(option: Value, query: string) => boolean", description: "Optional custom matching, for example a label or language-code search." },
  { name: "maxResults", type: "number", defaultValue: "50", description: "Maximum filtered results rendered in the popup; this is not a selection limit." },
  { name: "placeholder", type: "string", description: "A short search prompt shown when the query is empty." },
  disabledProp
];
var selectionLimit = { name: "maxSelections", type: "number", description: "Maximum selected values. At the limit, remove a chip before adding another value." };
var autocompleteDetails = {
  "component.autocomplete": {
    example: "Search Japanese, Chinese, or Korean, select a result, then save. The empty example requires a known option: typed text alone is not a submitted selection.",
    anatomy: "A labelled combobox has an input, open-list trigger, active option, and empty-result message. The popup remains in the input's document.",
    variants: "One object selection, initialized with an object or null. This small dataset makes keyboard behavior easy to inspect; use a native Select for three actual production choices.",
    states: "Search an unmatched word to see No matching options. AppField validators check the selected object and supply recovery messages. Configure onBlur validation when needed. disabled prevents both input and trigger interaction.",
    content: "Labels should explain the selected value; placeholders explain searching. Stable IDs must be unique even when visible labels repeat.",
    implementation: formSetup,
    props: autocompleteProps
  },
  "component.multi-autocomplete": {
    example: "Choose two languages for a bilingual entry, then save. The consumer-supplied AppField validators requires two; maxSelections prevents a third. Remove a named chip to see the recovery message on submission.",
    anatomy: "Selected-object chips and a combobox input share a border; each chip exposes a named removal button.",
    variants: "Initialize an array of objects, including [] for empty. maxSelections limits the chosen values; maxResults separately limits visible search results.",
    states: "At the selection limit the input and trigger stop accepting additions while removal stays available. Removing one chip re-enables search. Escape dismisses the popup without clearing selected chips. The component does not create unknown objects or fetch remote data.",
    content: "State a selection limit in the hint before the user reaches it. Use AppField validators for eligibility or a minimum selection count; validation messages stay associated with the field. Use labels that still make sense in Remove Japanese removal actions.",
    implementation: formSetup,
    props: [...autocompleteProps, selectionLimit]
  },
  "component.tags-input": {
    example: "Choose a reading tag, type book club and press Enter to create it, then remove a chip. The input keeps focus so another tag can be added immediately.",
    anatomy: "A text input and removable chips share one field. The popup offers unselected suggestions and an Add action for a new tag.",
    variants: "Accepts string[] instead of objects, so format and getId are not needed. Use MultiAutocomplete when tags have separate identifiers and labels.",
    states: "Selected tags are absent from suggestions. New tags are trimmed and duplicates are ignored regardless of case. required means at least one tag. At the limit the input keeps focus but accepts no additions; removal stays available.",
    content: "Offer common tags as suggestions without making them a fixed vocabulary. State any selection limit in the hint. An empty options array still allows free-text tags.",
    implementation: formSetup,
    props: [...autocompleteProps.filter((prop) => !["getId", "format", "options"].includes(prop.name)), { name: "options", type: "readonly string[]", required: true, description: "Suggested tags. Users can also create their own strings; selected tags are hidden from this list." }, selectionLimit]
  }
};
var autocompleteCommonMistakes = {
  "component.autocomplete": "Do not persist the typed query as the selected option. Submission stores the chosen object, not arbitrary search text.",
  "component.multi-autocomplete": "Do not use maxResults to limit selection; maxSelections limits the chosen array and should be explained in the hint.",
  "component.tags-input": "Do not intercept Enter on the parent form to save while the tags field is focused. TagsInput uses Enter to add a tag or select the highlighted suggestion."
};
var autocompleteFixtures = [
  defineCatalogFixture({ ...{
    "id": "autocomplete.language",
    "name": "Language search",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "autocomplete",
      "language"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: autocomplete_language_default, render: () => /* @__PURE__ */ jsx57(Example10, {}) }),
  defineCatalogFixture({ ...{
    "id": "autocomplete.language.empty",
    "name": "Language search \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "autocomplete",
      "language",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: autocomplete_language_empty_default, render: () => /* @__PURE__ */ jsx57(Example11, {}) })
];
var autocompleteSpec = { id: "component.autocomplete", route: "/components/forms/autocomplete", name: "Autocomplete", category: "forms", summary: "Filters a long option collection while Base UI manages active-descendant selection.", fixtureId: "autocomplete.language", sourcePath: "src/components/forms/autocomplete-controls.tsx", react: ["AutocompleteInput"], types: ["AutocompleteInputProps"], when: "Use for a long known collection where searching is materially faster than scanning.", avoid: "Use native Select for short sets and Input when free-form values are valid.", choose: "Autocomplete selects one known object; Input accepts text and Select exposes a short known list.", behavior: "Typing filters, Arrow keys highlight, Enter selects, and Escape dismisses the popup and preserves the selected value, including when the popup is already closed.", accessibility: "Keep the combobox label, expanded state, active option, empty result, and error relationships programmatic.", migration: "Replace Headless UI Combobox and pass stable format/getId functions to Paper." };
var multiAutocompleteFixtures = [
  defineCatalogFixture({ ...{
    "id": "multi-autocomplete.languages",
    "name": "Multiple languages",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "multi",
      "autocomplete",
      "languages"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: multi_autocomplete_languages_default, render: () => /* @__PURE__ */ jsx57(Example12, {}) }),
  defineCatalogFixture({ ...{
    "id": "multi-autocomplete.languages.empty",
    "name": "Multiple languages \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "multi",
      "autocomplete",
      "languages",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: multi_autocomplete_languages_empty_default, render: () => /* @__PURE__ */ jsx57(Example13, {}) })
];
var multiAutocompleteSpec = { id: "component.multi-autocomplete", route: "/components/forms/multi-autocomplete", name: "MultiAutocomplete", category: "forms", summary: "Selects several known objects from a searchable collection and represents them as removable chips.", fixtureId: "multi-autocomplete.languages", sourcePath: "src/components/forms/autocomplete-controls.tsx", react: ["AutocompleteMultiInput"], types: ["AutocompleteMultiInputProps"], when: "Use when several values may be selected from one long known collection.", avoid: "Use Checkbox lists for a short stable set and TagsInput for simple string labels.", choose: "MultiAutocomplete preserves object identity; TagsInput is optimized for string tags.", behavior: "Base UI manages filtering and selection; named remove buttons update the TanStack Form array.", accessibility: "Each chip has a named removal action and the input exposes combobox state and results.", migration: "Replace Headless UI multi-combobox and supply stable object identifiers." };
var tagsInputFixtures = [
  defineCatalogFixture({ ...{
    "id": "tags.entry",
    "name": "Suggested and custom entry tags",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "tags",
      "entry"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: tags_entry_default, render: () => /* @__PURE__ */ jsx57(Example14, {}) }),
  defineCatalogFixture({ ...{
    "id": "tags.entry.empty",
    "name": "Entry tags \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "tags",
      "entry",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: tags_entry_empty_default, render: () => /* @__PURE__ */ jsx57(Example15, {}) })
];
var tagsInputSpec = { id: "component.tags-input", route: "/components/forms/tags-input", name: "TagsInput", category: "forms", summary: "Adds suggested or custom text tags as removable chips.", fixtureId: "tags.entry", sourcePath: "src/components/forms/autocomplete-controls.tsx", react: ["TagsInput"], types: ["TagsInputProps"], when: "Use for short user-defined labels such as reading-entry categories.", avoid: "Use MultiAutocomplete for a fixed vocabulary or objects with separate identifiers and labels. TagsInput does not fetch or persist suggestions.", choose: "TagsInput accepts free text and suggested strings. MultiAutocomplete limits selection to known values.", behavior: "Enter adds the typed tag or chooses the highlighted suggestion without submitting the form. The Add suggestion also supports pointer selection. Adding clears the query and keeps input focus; IME composition never commits a tag. Escape dismisses without clearing tags.", accessibility: "Suggestions expose combobox semantics; selected tags have named removal actions. The input retains focus after adding or removing, including when the selection limit is reached.", migration: "Pass existing tags as suggestions; custom tags are included in the TanStack Form string array and saved by the application." };

// src/catalog/examples/radio-group.format.tsx
import { useStore as useStore9 } from "@tanstack/react-form";
import { useState as useState18 } from "react";
import { jsx as jsx58, jsxs as jsxs45 } from "react/jsx-runtime";
function Example16() {
  const [savedValues, setSavedValues] = useState18(null);
  const methods = usePaperForm({
    defaultValues: { format: "book" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore9(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx58(methods.AppForm, { children: /* @__PURE__ */ jsxs45(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx58(
      methods.AppField,
      {
        name: "format",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx58(
          field.RadioGroup,
          {
            label: "Reading format",
            options: [
              {
                value: "book",
                label: "Book",
                description: "Printed or digital text"
              },
              {
                value: "audio",
                label: "Audio",
                description: "Narrated content"
              },
              {
                value: "other",
                label: "Other",
                description: "Another eligible format",
                disabled: true
              }
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs45("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx58(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx58(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx58(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx58(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs45("details", { children: [
      /* @__PURE__ */ jsx58("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx58("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/radio-group.format.empty.tsx
import { useStore as useStore10 } from "@tanstack/react-form";
import { useState as useState19 } from "react";
import { jsx as jsx59, jsxs as jsxs46 } from "react/jsx-runtime";
function Example17() {
  const [savedValues, setSavedValues] = useState19(null);
  const methods = usePaperForm({
    defaultValues: { format: "" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore10(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx59(methods.AppForm, { children: /* @__PURE__ */ jsxs46(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx59(
      methods.AppField,
      {
        name: "format",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx59(
          field.RadioGroup,
          {
            label: "Reading format",
            options: [
              {
                value: "book",
                label: "Book",
                description: "Printed or digital text"
              },
              {
                value: "audio",
                label: "Audio",
                description: "Narrated content"
              },
              {
                value: "other",
                label: "Other",
                description: "Another eligible format",
                disabled: true
              }
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs46("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx59(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx59(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx59(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx59(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs46("details", { children: [
      /* @__PURE__ */ jsx59("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx59("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/radio-group.format.empty.tsx
var radio_group_format_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { format: '' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="format"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.RadioGroup
              label="Reading format"
              options={[
                {
                  value: 'book',
                  label: 'Book',
                  description: 'Printed or digital text',
                },
                {
                  value: 'audio',
                  label: 'Audio',
                  description: 'Narrated content',
                },
                {
                  value: 'other',
                  label: 'Other',
                  description: 'Another eligible format',
                  disabled: true,
                },
              ]}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/radio-group.format.tsx
var radio_group_format_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { format: 'book' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="format"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.RadioGroup
              label="Reading format"
              options={[
                {
                  value: 'book',
                  label: 'Book',
                  description: 'Printed or digital text',
                },
                {
                  value: 'audio',
                  label: 'Audio',
                  description: 'Narrated content',
                },
                {
                  value: 'other',
                  label: 'Other',
                  description: 'Another eligible format',
                  disabled: true,
                },
              ]}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/radio-select.native.tsx
import { jsx as jsx60 } from "react/jsx-runtime";
function DefaultRadioFixture() {
  const methods = usePaperForm({ defaultValues: { viewport: "tablet" } });
  return /* @__PURE__ */ jsx60(methods.AppForm, { children: /* @__PURE__ */ jsx60(methods.AppField, { name: "viewport", children: (field) => /* @__PURE__ */ jsx60(
    field.RadioSelect,
    {
      label: "Preview size",
      options: [
        { value: "phone", label: "Phone" },
        { value: "tablet", label: "Tablet" },
        { value: "desktop", label: "Desktop", disabled: true }
      ],
      hint: "Desktop preview is unavailable in this example."
    }
  ) }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/radio-select.native.tsx
var radio_select_native_default = `import { usePaperForm } from 'paper-ui'

export default function DefaultRadioFixture() {
  const methods = usePaperForm({ defaultValues: { viewport: 'tablet' } })
  return (
    <methods.AppForm>
      <methods.AppField name="viewport">
        {field => (
          <field.RadioSelect
            label="Preview size"
            options={[
              { value: 'phone', label: 'Phone' },
              { value: 'tablet', label: 'Tablet' },
              { value: 'desktop', label: 'Desktop', disabled: true },
            ]}
            hint="Desktop preview is unavailable in this example."
          />
        )}
      </methods.AppField>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/radio-select.viewport.tsx
import { useStore as useStore11 } from "@tanstack/react-form";
import { useState as useState20 } from "react";
import { jsx as jsx61, jsxs as jsxs47 } from "react/jsx-runtime";
function Example18() {
  const [savedValues, setSavedValues] = useState20(null);
  const methods = usePaperForm({
    defaultValues: { viewport: "desktop" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore11(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx61(methods.AppForm, { children: /* @__PURE__ */ jsxs47(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx61(
      methods.AppField,
      {
        name: "viewport",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx61(
          field.RadioSelect,
          {
            label: "Preview size",
            hint: "Choose one viewport for the isolated preview.",
            variant: "segmented",
            required: true,
            options: [
              { value: "phone", label: "Phone" },
              { value: "tablet", label: "Tablet" },
              { value: "desktop", label: "Desktop" }
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs47("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx61(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx61(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx61(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx61(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs47("details", { children: [
      /* @__PURE__ */ jsx61("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx61("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/radio-select.viewport.empty.tsx
import { useStore as useStore12 } from "@tanstack/react-form";
import { useState as useState21 } from "react";
import { jsx as jsx62, jsxs as jsxs48 } from "react/jsx-runtime";
function Example19() {
  const [savedValues, setSavedValues] = useState21(null);
  const methods = usePaperForm({
    defaultValues: { viewport: "" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore12(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx62(methods.AppForm, { children: /* @__PURE__ */ jsxs48(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx62(
      methods.AppField,
      {
        name: "viewport",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx62(
          field.RadioSelect,
          {
            label: "Preview size",
            hint: "Choose one viewport for the isolated preview.",
            variant: "segmented",
            required: true,
            options: [
              { value: "phone", label: "Phone" },
              { value: "tablet", label: "Tablet" },
              { value: "desktop", label: "Desktop" }
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs48("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx62(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx62(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx62(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx62(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs48("details", { children: [
      /* @__PURE__ */ jsx62("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx62("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/radio-select.viewport.empty.tsx
var radio_select_viewport_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { viewport: '' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="viewport"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.RadioSelect
              label="Preview size"
              hint="Choose one viewport for the isolated preview."
              variant="segmented"
              required
              options={[
                { value: 'phone', label: 'Phone' },
                { value: 'tablet', label: 'Tablet' },
                { value: 'desktop', label: 'Desktop' },
              ]}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/radio-select.viewport.tsx
var radio_select_viewport_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { viewport: 'desktop' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="viewport"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.RadioSelect
              label="Preview size"
              hint="Choose one viewport for the isolated preview."
              variant="segmented"
              required
              options={[
                { value: 'phone', label: 'Phone' },
                { value: 'tablet', label: 'Tablet' },
                { value: 'desktop', label: 'Desktop' },
              ]}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/toggle-select.modifiers.tsx
import { useState as useState22 } from "react";
import { jsx as jsx63, jsxs as jsxs49 } from "react/jsx-runtime";
function Example20() {
  const methods = usePaperForm({
    defaultValues: { modifiers: ["Manga"] },
    onSubmit: ({ value: { modifiers } }) => {
      setSaved(
        modifiers.length ? `Saved: ${modifiers[0]}.` : "Saved without a modifier."
      );
    }
  });
  const [saved, setSaved] = useState22(null);
  return /* @__PURE__ */ jsx63(methods.AppForm, { children: /* @__PURE__ */ jsxs49(methods.Form, { className: "paper-stack", children: [
    /* @__PURE__ */ jsx63(methods.AppField, { name: "modifiers", children: (field) => /* @__PURE__ */ jsx63(
      field.ToggleSelect,
      {
        label: "Score modifiers",
        hint: "Choose one, or leave all off.",
        options: [
          { value: "Manga", label: "Manga", description: "\xD70.2" },
          { value: "Comic", label: "Comic", description: "\xD70.2" },
          {
            value: "Two column",
            label: "Two column",
            description: "\xD71.6"
          }
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs49("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx63(Button, { type: "submit", children: "Save selection" }),
      /* @__PURE__ */ jsx63(
        Button,
        {
          variant: "ghost",
          onClick: () => {
            methods.reset();
            setSaved(null);
          },
          children: "Reset"
        }
      )
    ] }),
    saved ? /* @__PURE__ */ jsx63("p", { role: "status", children: saved }) : null
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/toggle-select.modifiers.tsx
var toggle_select_modifiers_default = `import { Button, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const methods = usePaperForm({
    defaultValues: { modifiers: ['Manga'] },
    onSubmit: ({ value: { modifiers } }) => {
      setSaved(
        modifiers.length
          ? \`Saved: \${modifiers[0]}.\`
          : 'Saved without a modifier.',
      )
    },
  })
  const [saved, setSaved] = useState<string | null>(null)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack">
        <methods.AppField name="modifiers">
          {field => (
            <field.ToggleSelect
              label="Score modifiers"
              hint="Choose one, or leave all off."
              options={[
                { value: 'Manga', label: 'Manga', description: '\xD70.2' },
                { value: 'Comic', label: 'Comic', description: '\xD70.2' },
                {
                  value: 'Two column',
                  label: 'Two column',
                  description: '\xD71.6',
                },
              ]}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save selection</Button>
          <Button
            variant="ghost"
            onClick={() => {
              methods.reset()
              setSaved(null)
            }}
          >
            Reset
          </Button>
        </div>
        {saved ? <p role="status">{saved}</p> : null}
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/toggle-select.modifiers.empty.tsx
import { useState as useState23 } from "react";
import { jsx as jsx64, jsxs as jsxs50 } from "react/jsx-runtime";
function Example21() {
  const methods = usePaperForm({
    defaultValues: { modifiers: [] },
    onSubmit: ({ value: { modifiers } }) => {
      setSaved(
        modifiers.length ? "Saved with passive listening." : "Saved without a modifier."
      );
    }
  });
  const [saved, setSaved] = useState23(null);
  return /* @__PURE__ */ jsx64(methods.AppForm, { children: /* @__PURE__ */ jsxs50(methods.Form, { className: "paper-stack", children: [
    /* @__PURE__ */ jsx64(methods.AppField, { name: "modifiers", children: (field) => /* @__PURE__ */ jsx64(
      field.ToggleSelect,
      {
        label: "Score modifiers",
        options: [
          {
            value: "Passive listening",
            label: "Passive listening",
            description: "\xD70.5"
          }
        ]
      }
    ) }),
    /* @__PURE__ */ jsx64(Button, { type: "submit", children: "Save selection" }),
    saved ? /* @__PURE__ */ jsx64("p", { role: "status", children: saved }) : null
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/toggle-select.modifiers.empty.tsx
var toggle_select_modifiers_empty_default = `import { Button, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const methods = usePaperForm({
    defaultValues: { modifiers: [] as string[] },
    onSubmit: ({ value: { modifiers } }) => {
      setSaved(
        modifiers.length
          ? 'Saved with passive listening.'
          : 'Saved without a modifier.',
      )
    },
  })
  const [saved, setSaved] = useState<string | null>(null)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack">
        <methods.AppField name="modifiers">
          {field => (
            <field.ToggleSelect
              label="Score modifiers"
              options={[
                {
                  value: 'Passive listening',
                  label: 'Passive listening',
                  description: '\xD70.5',
                },
              ]}
            />
          )}
        </methods.AppField>
        <Button type="submit">Save selection</Button>
        {saved ? <p role="status">{saved}</p> : null}
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/form-choice-groups.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var choiceGroupDetails = {
  "component.radio-select": {
    example: "The segmented example selects a preview size. Compare it with the native-radio example, then submit an empty group to see how required validation is attached to the decision.",
    anatomy: "A fieldset and legend contain same-name native radios; segmented changes the labels' presentation without changing selection semantics.",
    variants: "Default uses visible native radio indicators; segmented joins concise peer choices.",
    states: "One option can be selected. Use defaultValues to initialize a required decision; disabled may apply to the whole group or an option.",
    content: "Use short peer labels.",
    implementation: formSetup,
    props: [...fieldProps, validationProp, optionsProp, { name: "variant", type: '"default" | "segmented"', defaultValue: '"default"', description: "Default radios for ordinary forms; segmented for two to four short peer choices." }, disabledProp]
  },
  "component.toggle-select": {
    example: "Select a different reading modifier, or press the selected one again to clear it. Save selection shows the stored choice. The listening example starts with its one optional modifier off.",
    anatomy: "One labelled group joins equal-width choices inside a shared border. A short description sits below each label, and a check has reserved space so selection does not move the text.",
    variants: "One connected presentation for one to four concise options. Wide controls use equal-width segments with descriptions beneath the labels. Narrow controls stack connected rows with inline descriptions; a single option spans the control.",
    states: "Initialize the field with [] for no selection or [value] for one selected option. Disabled options cannot be pressed. reset restores the initial selection; validation errors describe the group and focus its first enabled choice.",
    content: "Keep labels parallel and short. Use description for a rate or another small fact that changes the decision. Explain that all options can be off when that is not obvious.",
    implementation: formSetup,
    props: [
      ...fieldProps.filter((prop) => prop.name !== "required"),
      { ...optionsProp, description: "Each option has value and label, with optional description and disabled. The form stores [] or [value]; choosing another option replaces the previous choice." },
      validationProp,
      disabledProp
    ]
  },
  "component.radio-group": {
    example: "Choose Book or Audio and inspect the saved string value. Other is visibly unavailable. The empty example shows the required error for the whole decision.",
    anatomy: "A fieldset/legend contains native radios presented as cards, each with a label and supporting description.",
    variants: "One card presentation. Use RadioSelect for choices that do not need descriptions; adding cards to terse choices adds unnecessary visual weight.",
    states: "Required defaults to true. A disabled option remains visible for explanation but cannot be chosen. Cards stack on narrow screens; long descriptions wrap within their label.",
    content: "Use the label for the format name and the description for the difference that affects the decision. Do not repeat the label in the description.",
    implementation: formSetup,
    props: [...fieldProps, validationProp, { ...optionsProp, type: "readonly RadioGroupOption<string>[]", description: "Each option requires value, label, and description; disabled is optional." }]
  }
};
var choiceGroupCommonMistakes = {
  "component.radio-select": "Do not use segmented radios for commands. A radio changes one stored value; a button runs an operation.",
  "component.toggle-select": "Do not use this for several simultaneous choices or a required decision that cannot be cleared. Use checkboxes or RadioSelect for those cases.",
  "component.radio-group": "Do not hide the distinction between options in a tooltip. Keep decision-making descriptions visible beside each radio."
};
var toggleSelectFixtures = [
  defineCatalogFixture({
    id: "toggle-select.modifiers",
    name: "Optional reading modifier",
    description: "Choose one modifier, clear it, save the selection, and restore the initial value.",
    tags: ["toggle", "selection", "modifiers"],
    themes: ["light", "dark"],
    densities: ["comfortable", "compact"],
    viewports: [
      { id: "phone", label: "Phone", width: 320, height: 720 },
      { id: "desktop", label: "Desktop", width: 1280, height: 800 }
    ],
    deterministic: true,
    code: toggle_select_modifiers_default,
    render: () => /* @__PURE__ */ jsx65(Example20, {})
  }),
  defineCatalogFixture({
    id: "toggle-select.modifiers.empty",
    name: "Single optional modifier",
    description: "An empty selection is valid. Turn passive listening on or off before saving.",
    tags: ["toggle", "selection", "modifiers", "empty"],
    themes: ["light", "dark"],
    densities: ["comfortable", "compact"],
    viewports: [
      { id: "phone", label: "Phone", width: 320, height: 720 },
      { id: "desktop", label: "Desktop", width: 1280, height: 800 }
    ],
    deterministic: true,
    code: toggle_select_modifiers_empty_default,
    render: () => /* @__PURE__ */ jsx65(Example21, {})
  })
];
var toggleSelectSpec = {
  id: "component.toggle-select",
  route: "/components/forms/toggle-select",
  name: "ToggleSelect",
  category: "forms",
  summary: "Choose one optional value, with a second press to clear it.",
  fixtureId: "toggle-select.modifiers",
  sourcePath: "src/components/forms/ToggleSelect.tsx",
  react: ["ToggleSelect"],
  types: ["ToggleSelectProps", "Option"],
  css: ["paper-toggle-select", "paper-toggle-select__options", "paper-toggle-select__option", "paper-toggle-select__text", "paper-toggle-select__description", "paper-toggle-select__check"],
  when: "Use for a small set of optional, mutually exclusive choices such as score modifiers.",
  avoid: "Use RadioSelect when one choice must always remain selected, or Checkbox for independent choices that can be combined.",
  choose: "ToggleSelect lets users return to no selection by pressing the active option again.",
  behavior: "Tab moves between enabled choices. Enter, Space, or a click toggles the focused choice without submitting the form. Selecting another choice clears the previous one; focus stays on the pressed option.",
  accessibility: "The visible legend names the group. Each button exposes its pressed state, and a check reinforces selection without relying on color. Disabled choices are skipped by Tab.",
  migration: "Replace separate form buttons and selection state with ToggleSelect inside form.AppForm. Initialize the named field with [] or [value]."
};
var radioSelectFixtures = [
  defineCatalogFixture({ ...{
    "id": "radio-select.viewport",
    "name": "Preview size",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "radio",
      "select",
      "viewport"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: radio_select_viewport_default, render: () => /* @__PURE__ */ jsx65(Example18, {}) }),
  defineCatalogFixture({ ...{
    "id": "radio-select.viewport.empty",
    "name": "Preview size \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "radio",
      "select",
      "viewport",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: radio_select_viewport_empty_default, render: () => /* @__PURE__ */ jsx65(Example19, {}) })
];
var radioSelectNativeFixtures = [
  defineCatalogFixture({ ...{
    "id": "radio-select.native",
    "name": "Native radio presentation",
    "description": "Compare default radios with the segmented example, including a disabled option.",
    "tags": [
      "radio",
      "select",
      "native"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: radio_select_native_default, render: () => /* @__PURE__ */ jsx65(DefaultRadioFixture, {}) })
];
var radioSelectSpec = { id: "component.radio-select", route: "/components/forms/radio-select", name: "RadioSelect", category: "forms", summary: "Shows concise mutually exclusive options as native radios, with an optional connected segmented presentation.", fixtureId: "radio-select.viewport", sourcePath: "src/components/forms/choice-controls.tsx", react: ["RadioSelect"], types: ["RadioSelectProps", "RadioSelectVariant", "Option"], when: "Use for two to four short choices that benefit from remaining visible; use segmented for compact peer choices such as preview sizes.", avoid: "Use Select for longer sets, RadioGroup when each choice needs description copy, and Button when activation does not select a form value.", choose: "Default exposes familiar radio controls; segmented visually connects short peers without changing their native semantics.", variants: "Default presents inline native radios. Segmented presents the same inputs as a connected quiet-surface group with an accent edge and check for the selected value.", states: "Both variants cover selected, unselected, focus-visible, disabled, and invalid states in light, dark, comfortable, compact, narrow, and forced-color settings.", behavior: "Arrow keys move within the native same-name radio set and Space selects. Segmented remains a form control and never exposes button or aria-pressed semantics.", content: "Use two to four parallel, concise nouns such as Phone, Tablet, and Desktop; require and initialize one value when the decision cannot be empty.", accessibility: "A fieldset and legend name the group while each label names its native radio. The segmented check and accent edge reinforce selection without replacing checked state.", migration: 'Preserve submitted option values and replace button-like viewport selectors with variant="segmented" rather than recreating toggle-button semantics.', pageSections: ["usage", "examples", "variantsAndStates", "behavior", "contentGuidance", "accessibility"] };
var radioGroupFixtures = [
  defineCatalogFixture({ ...{
    "id": "radio-group.format",
    "name": "Reading format",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "radio",
      "group",
      "format"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: radio_group_format_default, render: () => /* @__PURE__ */ jsx65(Example16, {}) }),
  defineCatalogFixture({ ...{
    "id": "radio-group.format.empty",
    "name": "Reading format \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "radio",
      "group",
      "format",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: radio_group_format_empty_default, render: () => /* @__PURE__ */ jsx65(Example17, {}) })
];
var radioGroupSpec = { id: "component.radio-group", route: "/components/forms/radio-group", name: "RadioGroup", category: "forms", summary: "Presents mutually exclusive choices that each require supporting description.", fixtureId: "radio-group.format", sourcePath: "src/components/forms/choice-controls.tsx", react: ["RadioGroup"], types: ["RadioGroupProps", "RadioGroupOption"], when: "Use when each mutually exclusive choice needs a label and explanatory description.", avoid: "Use RadioSelect for terse choices and Checkbox for independent choices.", choose: "Choose the smallest radio presentation that gives users enough context to decide.", behavior: "Native radio focus and arrow-key selection work without a custom composite widget.", accessibility: "Use a legend for the decision, preserve disabled semantics, and never rely on the selected border alone.", migration: "Remove Headless UI RadioGroup imports; Paper uses native radios and TanStack Form." };

// src/catalog/examples/amount.progress.tsx
import { useStore as useStore13 } from "@tanstack/react-form";
import { useState as useState24 } from "react";
import { jsx as jsx66, jsxs as jsxs51 } from "react/jsx-runtime";
function Example22() {
  const [savedValues, setSavedValues] = useState24(null);
  const methods = usePaperForm({
    defaultValues: { progressValue: 48, progressUnit: "pages" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore13(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx66(methods.AppForm, { children: /* @__PURE__ */ jsxs51(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx66(methods.AppField, { name: "progressUnit", children: (unitField) => /* @__PURE__ */ jsx66(
      methods.AppField,
      {
        name: "progressValue",
        validators: {
          onChange: ({ value }) => {
            if (!Number.isFinite(value)) return "Enter an amount.";
            if (value < 1) return "Enter at least 1.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx66(
          field.AmountWithUnit,
          {
            label: "Progress",
            hint: "Enter a positive amount; choose pages or minutes.",
            min: 1,
            required: true,
            units: [
              { value: "pages", label: "pages" },
              { value: "minutes", label: "minutes" }
            ],
            unitField
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsxs51("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx66(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx66(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx66(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx66(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs51("details", { children: [
      /* @__PURE__ */ jsx66("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx66("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/amount.progress.empty.tsx
import { useStore as useStore14 } from "@tanstack/react-form";
import { useState as useState25 } from "react";
import { jsx as jsx67, jsxs as jsxs52 } from "react/jsx-runtime";
function Example23() {
  const [savedValues, setSavedValues] = useState25(null);
  const methods = usePaperForm({
    defaultValues: { progressValue: NaN, progressUnit: "pages" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore14(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx67(methods.AppForm, { children: /* @__PURE__ */ jsxs52(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx67(methods.AppField, { name: "progressUnit", children: (unitField) => /* @__PURE__ */ jsx67(
      methods.AppField,
      {
        name: "progressValue",
        validators: {
          onChange: ({ value }) => {
            if (!Number.isFinite(value)) return "Enter an amount.";
            if (value < 1) return "Enter at least 1.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx67(
          field.AmountWithUnit,
          {
            label: "Progress",
            hint: "Enter a positive amount; choose pages or minutes.",
            min: 1,
            required: true,
            units: [
              { value: "pages", label: "pages" },
              { value: "minutes", label: "minutes" }
            ],
            unitField
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsxs52("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx67(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx67(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx67(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx67(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs52("details", { children: [
      /* @__PURE__ */ jsx67("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx67("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/amount.progress.empty.tsx
var amount_progress_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { progressValue: NaN, progressUnit: 'pages' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField name="progressUnit">
          {unitField => (
            <methods.AppField
              name="progressValue"
              validators={{
                onChange: ({ value }) => {
                  if (!Number.isFinite(value)) return 'Enter an amount.'
                  if (value < 1) return 'Enter at least 1.'
                },
              }}
            >
              {field => (
                <field.AmountWithUnit
                  label="Progress"
                  hint="Enter a positive amount; choose pages or minutes."
                  min={1}
                  required
                  units={[
                    { value: 'pages', label: 'pages' },
                    { value: 'minutes', label: 'minutes' },
                  ]}
                  unitField={unitField}
                />
              )}
            </methods.AppField>
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/amount.progress.tsx
var amount_progress_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { progressValue: 48, progressUnit: 'pages' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField name="progressUnit">
          {unitField => (
            <methods.AppField
              name="progressValue"
              validators={{
                onChange: ({ value }) => {
                  if (!Number.isFinite(value)) return 'Enter an amount.'
                  if (value < 1) return 'Enter at least 1.'
                },
              }}
            >
              {field => (
                <field.AmountWithUnit
                  label="Progress"
                  hint="Enter a positive amount; choose pages or minutes."
                  min={1}
                  required
                  units={[
                    { value: 'pages', label: 'pages' },
                    { value: 'minutes', label: 'minutes' },
                  ]}
                  unitField={unitField}
                />
              )}
            </methods.AppField>
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/checkbox.public-entry.tsx
import { useStore as useStore15 } from "@tanstack/react-form";
import { useState as useState26 } from "react";
import { jsx as jsx68, jsxs as jsxs53 } from "react/jsx-runtime";
function Example24() {
  const [savedValues, setSavedValues] = useState26(null);
  const methods = usePaperForm({
    defaultValues: { public: true },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore15(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx68(methods.AppForm, { children: /* @__PURE__ */ jsxs53(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx68(methods.AppField, { name: "public", children: (field) => /* @__PURE__ */ jsx68(
      field.Checkbox,
      {
        label: "Show this entry on my profile",
        hint: "Contest moderators can always review submitted entries."
      }
    ) }),
    /* @__PURE__ */ jsxs53("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx68(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx68(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx68(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx68(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs53("details", { children: [
      /* @__PURE__ */ jsx68("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx68("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/checkbox.public-entry.empty.tsx
import { useStore as useStore16 } from "@tanstack/react-form";
import { useState as useState27 } from "react";
import { jsx as jsx69, jsxs as jsxs54 } from "react/jsx-runtime";
function Example25() {
  const [savedValues, setSavedValues] = useState27(null);
  const methods = usePaperForm({
    defaultValues: { public: false },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore16(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx69(methods.AppForm, { children: /* @__PURE__ */ jsxs54(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx69(methods.AppField, { name: "public", children: (field) => /* @__PURE__ */ jsx69(
      field.Checkbox,
      {
        label: "Show this entry on my profile",
        hint: "Contest moderators can always review submitted entries."
      }
    ) }),
    /* @__PURE__ */ jsxs54("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx69(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx69(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx69(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx69(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs54("details", { children: [
      /* @__PURE__ */ jsx69("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx69("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/checkbox.public-entry.empty.tsx
var checkbox_public_entry_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { public: false },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField name="public">
          {field => (
            <field.Checkbox
              label="Show this entry on my profile"
              hint="Contest moderators can always review submitted entries."
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/checkbox.public-entry.tsx
var checkbox_public_entry_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { public: true },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField name="public">
          {field => (
            <field.Checkbox
              label="Show this entry on my profile"
              hint="Contest moderators can always review submitted entries."
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/select.language.tsx
import { useStore as useStore17 } from "@tanstack/react-form";
import { useState as useState28 } from "react";
import { jsx as jsx70, jsxs as jsxs55 } from "react/jsx-runtime";
var LANGUAGES5 = [
  { id: "ja", label: "Japanese" },
  { id: "zh", label: "Chinese" },
  { id: "ko", label: "Korean" }
];
function Example26() {
  const [savedValues, setSavedValues] = useState28(null);
  const methods = usePaperForm({
    defaultValues: { language: "ja" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore17(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx70(methods.AppForm, { children: /* @__PURE__ */ jsxs55(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx70(
      methods.AppField,
      {
        name: "language",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx70(
          field.Select,
          {
            label: "Language",
            hint: "Choose the language you read.",
            required: true,
            placeholder: "Choose a language",
            options: LANGUAGES5.map(({ id, label }) => ({ value: id, label }))
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs55("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx70(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx70(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx70(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx70(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs55("details", { children: [
      /* @__PURE__ */ jsx70("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx70("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/select.language.empty.tsx
import { useStore as useStore18 } from "@tanstack/react-form";
import { useState as useState29 } from "react";
import { jsx as jsx71, jsxs as jsxs56 } from "react/jsx-runtime";
var LANGUAGES6 = [
  { id: "ja", label: "Japanese" },
  { id: "zh", label: "Chinese" },
  { id: "ko", label: "Korean" }
];
function Example27() {
  const [savedValues, setSavedValues] = useState29(null);
  const methods = usePaperForm({
    defaultValues: { language: "" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore18(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx71(methods.AppForm, { children: /* @__PURE__ */ jsxs56(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx71(
      methods.AppField,
      {
        name: "language",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "Choose an option.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx71(
          field.Select,
          {
            label: "Language",
            hint: "Choose the language you read.",
            required: true,
            placeholder: "Choose a language",
            options: LANGUAGES6.map(({ id, label }) => ({ value: id, label }))
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs56("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx71(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx71(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx71(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx71(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs56("details", { children: [
      /* @__PURE__ */ jsx71("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx71("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/select.language.empty.tsx
var select_language_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

const LANGUAGES = [
  { id: 'ja', label: 'Japanese' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ko', label: 'Korean' },
]

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { language: '' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="language"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.Select
              label="Language"
              hint="Choose the language you read."
              required
              placeholder="Choose a language"
              options={LANGUAGES.map(({ id, label }) => ({ value: id, label }))}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/select.language.tsx
var select_language_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

const LANGUAGES = [
  { id: 'ja', label: 'Japanese' },
  { id: 'zh', label: 'Chinese' },
  { id: 'ko', label: 'Korean' },
]

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { language: 'ja' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="language"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Choose an option.'
            },
          }}
        >
          {field => (
            <field.Select
              label="Language"
              hint="Choose the language you read."
              required
              placeholder="Choose a language"
              options={LANGUAGES.map(({ id, label }) => ({ value: id, label }))}
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/textarea.reading-notes.tsx
import { useStore as useStore19 } from "@tanstack/react-form";
import { useState as useState30 } from "react";
import { jsx as jsx72, jsxs as jsxs57 } from "react/jsx-runtime";
function Example28() {
  const [savedValues, setSavedValues] = useState30(null);
  const methods = usePaperForm({
    defaultValues: { notes: "Finished chapter two. The pacing is picking up." },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore19(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx72(methods.AppForm, { children: /* @__PURE__ */ jsxs57(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx72(
      methods.AppField,
      {
        name: "notes",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "This field is required.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx72(
          field.TextArea,
          {
            label: "Reading notes",
            hint: "Keep spoilers out of public notes.",
            required: true
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs57("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx72(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx72(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx72(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx72(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs57("details", { children: [
      /* @__PURE__ */ jsx72("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx72("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// src/catalog/examples/textarea.reading-notes.empty.tsx
import { useStore as useStore20 } from "@tanstack/react-form";
import { useState as useState31 } from "react";
import { jsx as jsx73, jsxs as jsxs58 } from "react/jsx-runtime";
function Example29() {
  const [savedValues, setSavedValues] = useState31(null);
  const methods = usePaperForm({
    defaultValues: { notes: "" },
    onSubmit: ({ value: values2 }) => setSavedValues(values2)
  });
  const values = useStore20(methods.store, (state) => state.values);
  return /* @__PURE__ */ jsx73(methods.AppForm, { children: /* @__PURE__ */ jsxs58(methods.Form, { className: "paper-stack", style: { maxWidth: "32rem" }, children: [
    /* @__PURE__ */ jsx73(
      methods.AppField,
      {
        name: "notes",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "This field is required.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx73(
          field.TextArea,
          {
            label: "Reading notes",
            hint: "Keep spoilers out of public notes.",
            required: true
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs58("div", { className: "paper-cluster", children: [
      /* @__PURE__ */ jsx73(Button, { type: "submit", children: "Save entry" }),
      /* @__PURE__ */ jsx73(
        Button,
        {
          variant: "outline",
          onClick: () => {
            methods.reset();
            setSavedValues(null);
          },
          children: "Reset"
        }
      )
    ] }),
    savedValues ? /* @__PURE__ */ jsx73(Flash, { variant: "success", title: "Entry saved", children: /* @__PURE__ */ jsx73(
      "pre",
      {
        style: {
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          margin: 0
        },
        children: JSON.stringify(savedValues, null, 2)
      }
    ) }) : null,
    /* @__PURE__ */ jsxs58("details", { children: [
      /* @__PURE__ */ jsx73("summary", { children: "Current form values" }),
      /* @__PURE__ */ jsx73("pre", { style: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" }, children: JSON.stringify(values, null, 2) })
    ] })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/textarea.reading-notes.empty.tsx
var textarea_reading_notes_empty_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { notes: '' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="notes"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'This field is required.'
            },
          }}
        >
          {field => (
            <field.TextArea
              label="Reading notes"
              hint="Keep spoilers out of public notes."
              required
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// example-source:/app/packages/paper-ui/src/catalog/examples/textarea.reading-notes.tsx
var textarea_reading_notes_default = `import { useStore } from '@tanstack/react-form'
import { Button, Flash, usePaperForm } from 'paper-ui'
import { useState } from 'react'

export default function Example() {
  const [savedValues, setSavedValues] = useState<Record<
    string,
    unknown
  > | null>(null)
  const methods = usePaperForm({
    defaultValues: { notes: 'Finished chapter two. The pacing is picking up.' },
    onSubmit: ({ value: values }) => setSavedValues(values),
  })
  const values = useStore(methods.store, state => state.values)

  return (
    <methods.AppForm>
      <methods.Form className="paper-stack" style={{ maxWidth: '32rem' }}>
        <methods.AppField
          name="notes"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'This field is required.'
            },
          }}
        >
          {field => (
            <field.TextArea
              label="Reading notes"
              hint="Keep spoilers out of public notes."
              required
            />
          )}
        </methods.AppField>
        <div className="paper-cluster">
          <Button type="submit">Save entry</Button>
          <Button
            variant="outline"
            onClick={() => {
              methods.reset()
              setSavedValues(null)
            }}
          >
            Reset
          </Button>
        </div>
        {savedValues ? (
          <Flash variant="success" title="Entry saved">
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                margin: 0,
              }}
            >
              {JSON.stringify(savedValues, null, 2)}
            </pre>
          </Flash>
        ) : null}
        <details>
          <summary>Current form values</summary>
          <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {JSON.stringify(values, null, 2)}
          </pre>
        </details>
      </methods.Form>
    </methods.AppForm>
  )
}
`;

// src/catalog/form-fields.tsx
import { jsx as jsx74 } from "react/jsx-runtime";
var formFieldDetails = {
  "component.textarea": {
    example: "Edit the reading note and save it. The empty example demonstrates required validation: submit blank, then enter a note and resubmit to clear the error.",
    anatomy: "A persistent label, resizable native textarea, optional hint, and inline error share one field relationship.",
    variants: "One multiline presentation. Set rows for the expected starting length; the user can resize vertically. Density changes padding, not the meaning of the field.",
    states: "The populated and empty examples share the same required rule. Invalid submission displays an associated recovery message and focuses the field. Native disabled and readOnly have different purposes: unavailable editing versus selectable text that cannot change.",
    content: "Label the value, such as Reading notes. Use the hint for visibility or spoiler policy. Put examples in a placeholder only when they remain useful after the hint.",
    implementation: formSetup,
    props: [...fieldProps, validationProp, { name: "rows", type: "number", description: "Initial visible text rows; use a larger value for long-form writing." }, { name: "readOnly", type: "boolean", description: "Keeps the text focusable and selectable without allowing edits." }, disabledProp]
  },
  "component.select": {
    example: "Choose a reading language. The second example starts with a placeholder so Save entry demonstrates the required-choice error before selection.",
    anatomy: "A label precedes a native select with a decorative chevron; hint and error text follow the control.",
    variants: "Use options for a flat list or groups for native optgroups. When groups is supplied it replaces options; pass options={[]} for a grouped-only select.",
    states: "An empty string selects the placeholder. Required rejects that value. Individual options and the entire control can be disabled; no custom menu is substituted on mobile.",
    content: "Use parallel labels and an instructional placeholder such as Choose a language. A placeholder is an empty option, not a meaningful saved choice.",
    implementation: formSetup,
    props: [...fieldProps, validationProp, optionsProp, { name: "groups", type: "readonly OptionGroup<string>[]", description: "Named groups containing label and options; takes precedence over options." }, { name: "placeholder", type: "string", description: "Adds an option with an empty-string value before real choices." }, disabledProp]
  },
  "component.checkbox": {
    example: "Toggle profile visibility, then save and inspect the boolean form value. The unchecked example remains valid because this is an optional preference.",
    anatomy: "A native checkbox and its clickable label form one choice, followed by optional hint and error text.",
    variants: "One binary presentation. Checked means the labelled proposition is enabled; this component does not expose a mixed/indeterminate group-selection state.",
    states: "Use true or false in defaultValues. Optional false is valid. required is appropriate for consent that must be checked, not for preferences. Native disabled prevents toggling.",
    content: "Write a proposition that reads naturally when enabled: Show this entry on my profile. Explain who can still access the entry in the hint.",
    implementation: formSetup,
    props: [...fieldProps, validationProp, disabledProp]
  },
  "component.amount-with-unit": {
    example: "Enter positive progress, then choose pages or minutes. Inspect current values to see progressValue as a number and progressUnit as a separate string. Submit the empty example to see required validation.",
    anatomy: "A labelled group joins one numeric input to one native unit picker. The shared hint and first error describe both controls.",
    variants: "One compound field. Use Input with a unit in its label when the unit is fixed; a disabled picker should not be used to fake a fixed suffix.",
    states: "Field or form validators enforce required amounts and numeric bounds on submission. Native required, min, and max describe the control. An empty optional amount becomes NaN through valueAsNumber; normalize it before sending JSON. Disabling the field disables both controls.",
    content: "Label the measurement, such as Progress. Use concise plural unit labels, and state any minimum or rounding policy in the hint.",
    implementation: formSetup,
    props: [{ name: "unitField", type: "Unit field binding", required: true, description: "Bind the amount through form.AppField and pass the separate unit field as unitField." }, ...fieldProps.filter((prop) => prop.name !== "name"), { name: "units", type: "readonly Option<string>[]", required: true, description: "Available units. Each has value, label, and optional disabled." }, { name: "unitsLabel", type: "string", description: "Accessible unit-picker label; defaults to Unit for plus the lowercased field label." }, { name: "min / max / step", type: "number | string", description: "Native number constraints. Add matching min/max rules to AppField validators; use step for the native stepping interval." }, disabledProp]
  }
};
var formFieldCommonMistakes = {
  "component.textarea": "Do not set a fixed height with overflow hidden: users need to read and resize longer notes.",
  "component.select": "Do not store a placeholder as a real language value. Initialize the empty option with an empty string and validate required selections.",
  "component.checkbox": "Do not mark an optional preference required; required means the user must check it to continue.",
  "component.amount-with-unit": "Do not initialize a single progress object: bind progressValue and progressUnit through separate AppFields, and pass the unit field to AmountWithUnit."
};
var textAreaFixtures = [
  defineCatalogFixture({ ...{
    "id": "textarea.reading-notes",
    "name": "Reading notes",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "textarea",
      "reading",
      "notes"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: textarea_reading_notes_default, render: () => /* @__PURE__ */ jsx74(Example28, {}) }),
  defineCatalogFixture({ ...{
    "id": "textarea.reading-notes.empty",
    "name": "Reading notes \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "textarea",
      "reading",
      "notes",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: textarea_reading_notes_empty_default, render: () => /* @__PURE__ */ jsx74(Example29, {}) })
];
var textAreaSpec = { id: "component.textarea", route: "/components/forms/textarea", name: "TextArea", category: "forms", summary: "Collects multiline text with the same deterministic field anatomy as Input.", fixtureId: "textarea.reading-notes", sourcePath: "src/components/forms/controls.tsx", react: ["TextArea"], types: ["TextAreaProps"], css: ["paper-textarea"], when: "Use for prose, notes, and other values that genuinely need multiple lines.", avoid: "Use Input for short single-line values and do not resize away the user's editing space.", choose: "Choose TextArea over Input by content length, not by visual preference.", behavior: "Native text editing, Tab focus, and TanStack Form validation remain intact.", accessibility: "Associate the persistent label, hint, and recoverable error with the native textarea.", migration: "Replace legacy TextArea while retaining the existing field name and TanStack validators." };
var selectFixtures = [
  defineCatalogFixture({ ...{
    "id": "select.language",
    "name": "Language select",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "select",
      "language"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: select_language_default, render: () => /* @__PURE__ */ jsx74(Example26, {}) }),
  defineCatalogFixture({ ...{
    "id": "select.language.empty",
    "name": "Language select \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "select",
      "language",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: select_language_empty_default, render: () => /* @__PURE__ */ jsx74(Example27, {}) })
];
var selectSpec = { id: "component.select", route: "/components/forms/select", name: "Select", category: "forms", summary: "Uses the native platform picker for a known set of concise choices.", fixtureId: "select.language", sourcePath: "src/components/forms/controls.tsx", react: ["Select"], types: ["SelectProps", "Option", "OptionGroup"], when: "Use when one value must be chosen from a stable, reasonably short option set.", avoid: "Use Autocomplete for long searchable collections and RadioSelect when every choice should remain visible.", choose: "Prefer native Select unless a documented requirement needs composite listbox behavior.", behavior: "Platform keyboard, touch, and form submission behavior is preserved.", accessibility: "Provide a visible label and meaningful option labels; disabled options remain programmatically unavailable.", migration: "Map legacy values and groups directly without copying global select selectors." };
var checkboxFixtures = [
  defineCatalogFixture({ ...{
    "id": "checkbox.public-entry",
    "name": "Public entry",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "checkbox",
      "public",
      "entry"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: checkbox_public_entry_default, render: () => /* @__PURE__ */ jsx74(Example24, {}) }),
  defineCatalogFixture({ ...{
    "id": "checkbox.public-entry.empty",
    "name": "Public entry \u2014 unchecked",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "checkbox",
      "public",
      "entry",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: checkbox_public_entry_empty_default, render: () => /* @__PURE__ */ jsx74(Example25, {}) })
];
var checkboxSpec = { id: "component.checkbox", route: "/components/forms/checkbox", name: "Checkbox", category: "forms", summary: "Captures an independent boolean choice with native semantics.", fixtureId: "checkbox.public-entry", sourcePath: "src/components/forms/controls.tsx", react: ["Checkbox"], types: ["CheckboxProps"], when: "Use for an independent yes/no setting or acknowledgement.", avoid: "Use radio choices when exactly one option in a set must be selected.", choose: "A checkbox toggles one proposition; a radio group chooses among mutually exclusive propositions.", behavior: "Space toggles the focused native checkbox and TanStack Form owns its boolean value.", accessibility: "Put the consequence in the visible label or hint and retain the native checkbox input.", migration: "Replace legacy Checkbox without converting it into a custom switch role." };
var amountWithUnitFixtures = [
  defineCatalogFixture({ ...{
    "id": "amount.progress",
    "name": "Progress with unit",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "amount",
      "progress"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: amount_progress_default, render: () => /* @__PURE__ */ jsx74(Example22, {}) }),
  defineCatalogFixture({ ...{
    "id": "amount.progress.empty",
    "name": "Progress with unit \u2014 empty and validation",
    "description": "Edit, save, reset, and inspect the current form values.",
    "tags": [
      "amount",
      "progress",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: amount_progress_empty_default, render: () => /* @__PURE__ */ jsx74(Example23, {}) })
];
var amountWithUnitSpec = { id: "component.amount-with-unit", route: "/components/forms/amount-with-unit", name: "AmountWithUnit", category: "forms", summary: "Groups a numeric amount with the unit that gives it meaning.", fixtureId: "amount.progress", sourcePath: "src/components/forms/AmountWithUnit.tsx", react: ["AmountWithUnit"], types: ["AmountWithUnitProps", "Option"], when: "Use when a numeric value is invalid or ambiguous without one unit from a short set.", avoid: "Use Input alone when the unit is fixed and can be stated in the label.", choose: "Keep the unit fixed in content when users cannot change it; otherwise group amount and native Select.", behavior: "The amount and unit submit as nameValue and nameUnit for legacy-compatible migration.", accessibility: "Name the amount visibly and provide a generated accessible name for the unit picker.", migration: "Retain the legacy paired field names while replacing its implicit layout and error selectors." };

// src/catalog/phase-three-forms-feedback.tsx
var REVIEW_DATE2 = "2026-09-05";
var details = {
  ...formFieldDetails,
  ...choiceGroupDetails,
  ...autocompleteDetails,
  ...buttonGroupAndSurfaceDetails,
  ...feedbackDetails
};
function sections(spec) {
  const required = {
    overview: { heading: "Overview", content: [spec.summary] },
    whenToUse: { heading: "When to use", content: [spec.when] },
    whenNotToUse: { heading: "When not to use", content: [spec.avoid] },
    choosingBetween: { heading: "Choose between", content: [spec.choose] },
    anatomy: { heading: "Anatomy", content: [details[spec.id].anatomy] },
    recommendedExample: { heading: "Recommended example", content: [details[spec.id].example] },
    variants: { heading: "Variants", content: [spec.variants ?? details[spec.id].variants] },
    statesAndAdaptation: { heading: "States and adaptation", content: [spec.states ?? details[spec.id].states] },
    behavior: { heading: "Behavior", content: [spec.behavior] },
    contentGuidance: { heading: "Content guidance", content: [spec.content ?? details[spec.id].content] },
    accessibility: { heading: "Accessibility", content: [spec.accessibility] },
    implementation: { heading: "Implementation", content: [details[spec.id].implementation] },
    apiReference: { heading: "API reference", content: [`Public React APIs: ${spec.react.join(", ")}. Public types: ${spec.types.join(", ") || "none"}.`] },
    relatedPatterns: { heading: "Related patterns", content: [`Combine ${spec.name} with Button and the nearest documented form, feedback, or action pattern rather than inventing local structure.`] },
    migration: { heading: "Migration", content: [spec.migration] },
    lifecycle: { heading: "Lifecycle", content: ["Published in Paper 0.1.0. Review the documented limits before application migration; Stable describes API availability, not exhaustive product coverage."] }
  };
  return {
    required,
    pageSections: spec.pageSections ?? ["usage", "examples", "variantsAndStates", "behavior", "contentGuidance", "accessibility"]
  };
}
var commonMistakes = {
  ...formFieldCommonMistakes,
  ...choiceGroupCommonMistakes,
  ...autocompleteCommonMistakes,
  ...buttonGroupAndSurfaceCommonMistakes,
  ...feedbackCommonMistakes
};
function document2(spec) {
  return defineCatalogDocument({
    id: spec.id,
    route: spec.route,
    name: spec.name,
    kind: "component",
    category: spec.category,
    aliases: [],
    summary: spec.summary,
    keywords: [spec.name.toLocaleLowerCase(), spec.category, "paper"],
    lifecycle: "Stable",
    reviewDate: REVIEW_DATE2,
    sourcePath: spec.sourcePath,
    packageVersion: "0.1.0",
    guidance: {
      whenToUse: [spec.when],
      whenNotToUse: [spec.avoid],
      content: [spec.content ?? details[spec.id].content],
      commonMistakes: [commonMistakes[spec.id]]
    },
    accessibility: {
      requirements: [spec.accessibility],
      keyboard: [spec.behavior],
      knownConstraints: [details[spec.id].states]
    },
    api: {
      react: spec.react,
      props: details[spec.id].props,
      cssClasses: spec.css ?? [],
      publicTypes: spec.types,
      defaults: ["Theme and density inherit from the nearest Paper root."],
      invalidCombinations: [spec.avoid]
    },
    fixtureIds: [spec.fixtureId, ...spec.category === "forms" ? [`${spec.fixtureId}.empty`] : [], ...spec.id === "component.radio-select" ? ["radio-select.native"] : []],
    dependencies: { documents: ["component.button"], packages: spec.category === "forms" ? ["paper-ui", "@tanstack/react-form"] : ["paper-ui"] },
    migration: { legacy: [`ui ${spec.name}`], notes: [spec.migration] },
    changelog: [{ date: REVIEW_DATE2, note: `Revised ${spec.name} with complete runnable examples, state guidance, and concrete prop contracts.` }],
    behaviorTestIds: [`${spec.id}.semantics`, `${spec.id}.behavior`],
    sections: sections(spec)
  });
}
var phaseThreeFormsFeedbackFixtures = [
  ...toggleSelectFixtures,
  ...textAreaFixtures,
  ...selectFixtures,
  ...checkboxFixtures,
  ...radioSelectFixtures,
  ...radioGroupFixtures,
  ...amountWithUnitFixtures,
  ...autocompleteFixtures,
  ...multiAutocompleteFixtures,
  ...tagsInputFixtures,
  ...radioSelectNativeFixtures,
  ...buttonGroupFixtures,
  ...toastFixtures,
  ...flashFixtures,
  ...loadingFixtures,
  ...surfaceFixtures
];
var specs = [
  toggleSelectSpec,
  textAreaSpec,
  selectSpec,
  checkboxSpec,
  radioSelectSpec,
  radioGroupSpec,
  amountWithUnitSpec,
  autocompleteSpec,
  multiAutocompleteSpec,
  tagsInputSpec,
  buttonGroupSpec,
  flashSpec,
  loadingSpec,
  surfaceSpec,
  toastSpec
];
var phaseThreeFormsFeedbackDocuments = specs.map(document2);

// src/catalog/examples/experiment.logging-v2-entry.tsx
import { useEffect as useEffect4, useRef as useRef6, useState as useState32 } from "react";
import { jsx as jsx75, jsxs as jsxs59 } from "react/jsx-runtime";
function LoggingExperimentFixture() {
  const title = useRef6(null);
  const methods = usePaperForm({
    defaultValues: { title: "\u30B3\u30F3\u30D3\u30CB\u4EBA\u9593", pages: 48 },
    onSubmit: () => setReviewing(true)
  });
  const [reviewing, setReviewing] = useState32(false);
  const heading = useRef6(null);
  useEffect4(() => {
    if (reviewing) heading.current?.focus();
    else if (methods.state.submissionAttempts > 0) title.current?.focus();
  }, [reviewing, methods]);
  return /* @__PURE__ */ jsx75(methods.AppForm, { children: reviewing ? /* @__PURE__ */ jsxs59(Surface, { className: "paper-stack", children: [
    /* @__PURE__ */ jsx75("h3", { ref: heading, tabIndex: -1, className: "paper-type-component", children: "Review reading" }),
    /* @__PURE__ */ jsxs59("p", { children: [
      methods.getFieldValue("pages"),
      " pages of",
      " ",
      methods.getFieldValue("title")
    ] }),
    /* @__PURE__ */ jsx75(Flash, { title: "Preview only", children: "Nothing has been saved or submitted to a contest." }),
    /* @__PURE__ */ jsx75("div", { children: /* @__PURE__ */ jsx75(Button, { variant: "outline", onClick: () => setReviewing(false), children: "Edit entry" }) })
  ] }) : /* @__PURE__ */ jsxs59(methods.Form, { className: "paper-stack", children: [
    /* @__PURE__ */ jsx75(Flash, { title: "Experimental flow", children: "Review the entry before considering a separate contest step." }),
    /* @__PURE__ */ jsx75(
      methods.AppField,
      {
        name: "title",
        validators: {
          onChange: ({ value }) => {
            if (!value) return "This field is required.";
            if (!value.trim()) return "Enter a work title.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx75(field.Input, { label: "Work", ref: title, required: true })
      }
    ),
    /* @__PURE__ */ jsx75(
      methods.AppField,
      {
        name: "pages",
        validators: {
          onChange: ({ value }) => {
            if (!Number.isFinite(value)) return "This field is required.";
            if (value < 1) return "Enter at least one page.";
          }
        },
        children: (field) => /* @__PURE__ */ jsx75(
          field.Input,
          {
            label: "Pages",
            type: "number",
            inputMode: "numeric",
            min: 1,
            step: 1,
            required: true
          }
        )
      }
    ),
    /* @__PURE__ */ jsx75("div", { className: "paper-cluster", children: /* @__PURE__ */ jsx75(Button, { type: "submit", children: "Review entry" }) })
  ] }) });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/experiment.logging-v2-entry.tsx
var experiment_logging_v2_entry_default = `import { Button, Flash, Surface, usePaperForm } from 'paper-ui'
import { useEffect, useRef, useState } from 'react'

export default function LoggingExperimentFixture() {
  const title = useRef<HTMLInputElement>(null)
  const methods = usePaperForm({
    defaultValues: { title: '\u30B3\u30F3\u30D3\u30CB\u4EBA\u9593', pages: 48 },
    onSubmit: () => setReviewing(true),
  })
  const [reviewing, setReviewing] = useState(false)
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (reviewing) heading.current?.focus()
    else if (methods.state.submissionAttempts > 0) title.current?.focus()
  }, [reviewing, methods])
  return (
    <methods.AppForm>
      {reviewing ? (
        <Surface className="paper-stack">
          <h3 ref={heading} tabIndex={-1} className="paper-type-component">
            Review reading
          </h3>
          <p>
            {methods.getFieldValue('pages')} pages of{' '}
            {methods.getFieldValue('title')}
          </p>
          <Flash title="Preview only">
            Nothing has been saved or submitted to a contest.
          </Flash>
          <div>
            <Button variant="outline" onClick={() => setReviewing(false)}>
              Edit entry
            </Button>
          </div>
        </Surface>
      ) : (
        <methods.Form className="paper-stack">
          <Flash title="Experimental flow">
            Review the entry before considering a separate contest step.
          </Flash>
          <methods.AppField
            name="title"
            validators={{
              onChange: ({ value }) => {
                if (!value) return 'This field is required.'
                if (!value.trim()) return 'Enter a work title.'
              },
            }}
          >
            {field => <field.Input label="Work" ref={title} required />}
          </methods.AppField>
          <methods.AppField
            name="pages"
            validators={{
              onChange: ({ value }) => {
                if (!Number.isFinite(value)) return 'This field is required.'
                if (value < 1) return 'Enter at least one page.'
              },
            }}
          >
            {field => (
              <field.Input
                label="Pages"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                required
              />
            )}
          </methods.AppField>
          <div className="paper-cluster">
            <Button type="submit">Review entry</Button>
          </div>
        </methods.Form>
      )}
    </methods.AppForm>
  )
}
`;

// src/catalog/examples/pattern.logging-summary.tsx
import { useState as useState33 } from "react";
import { jsx as jsx76, jsxs as jsxs60 } from "react/jsx-runtime";
function ReadingLogPatternFixture() {
  const [details2, setDetails] = useState33(false);
  return /* @__PURE__ */ jsxs60(Surface, { as: "article", accent: true, className: "paper-stack", children: [
    /* @__PURE__ */ jsx76("p", { className: "paper-type-metadata", children: "September 5 \xB7 Japanese" }),
    /* @__PURE__ */ jsx76("h3", { className: "paper-type-component", children: "\u30B3\u30F3\u30D3\u30CB\u4EBA\u9593" }),
    /* @__PURE__ */ jsx76("p", { children: "48 pages read \xB7 Saved to reading history" }),
    /* @__PURE__ */ jsx76("p", { children: "Not submitted to a contest. Notes are private." }),
    /* @__PURE__ */ jsx76(
      ButtonGroup,
      {
        actions: [
          {
            id: "view",
            label: details2 ? "Hide log details" : "View log",
            variant: "outline",
            onSelect: () => setDetails(!details2)
          }
        ]
      }
    ),
    details2 ? /* @__PURE__ */ jsxs60("section", { "aria-label": "Log details", className: "paper-stack", children: [
      /* @__PURE__ */ jsx76("h4", { className: "paper-type-component", children: "Log details" }),
      /* @__PURE__ */ jsx76("p", { children: "Reading date: September 5, 2026. Language: Japanese. Amount: 48 pages." }),
      /* @__PURE__ */ jsx76("p", { children: "Private note: The shop\u2019s routines are becoming familiar." }),
      /* @__PURE__ */ jsx76("p", { children: "This example is local; no reading data has been saved." })
    ] }) : null
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/pattern.logging-summary.tsx
var pattern_logging_summary_default = `import { ButtonGroup, Surface } from 'paper-ui'
import { useState } from 'react'

export default function ReadingLogPatternFixture() {
  const [details, setDetails] = useState(false)
  return (
    <Surface as="article" accent className="paper-stack">
      <p className="paper-type-metadata">September 5 \xB7 Japanese</p>
      <h3 className="paper-type-component">\u30B3\u30F3\u30D3\u30CB\u4EBA\u9593</h3>
      <p>48 pages read \xB7 Saved to reading history</p>
      <p>Not submitted to a contest. Notes are private.</p>
      <ButtonGroup
        actions={[
          {
            id: 'view',
            label: details ? 'Hide log details' : 'View log',
            variant: 'outline',
            onSelect: () => setDetails(!details),
          },
        ]}
      />
      {details ? (
        <section aria-label="Log details" className="paper-stack">
          <h4 className="paper-type-component">Log details</h4>
          <p>
            Reading date: September 5, 2026. Language: Japanese. Amount: 48
            pages.
          </p>
          <p>Private note: The shop\u2019s routines are becoming familiar.</p>
          <p>This example is local; no reading data has been saved.</p>
        </section>
      ) : null}
    </Surface>
  )
}
`;

// src/catalog/guidance-document.ts
var REVIEW_DATE3 = "2026-09-05";
var PACKAGE_VERSION2 = "0.1.0";
function guidanceDocument(options) {
  return defineCatalogDocument({
    ...options,
    category: options.kind === "foundation" ? "foundations" : "governance",
    aliases: [],
    lifecycle: "Experimental",
    reviewDate: REVIEW_DATE3,
    packageVersion: PACKAGE_VERSION2,
    guidance: {
      whenToUse: options.whenToUse,
      whenNotToUse: ["Do not copy legacy presentation values when a semantic Paper role exists."],
      content: options.content,
      commonMistakes: ["Treating a visual sample as a new token or component contract."]
    },
    accessibility: {
      requirements: options.requirements,
      keyboard: [],
      knownConstraints: ["Review these roles in the consuming screen; a foundation sample cannot certify an application\u2019s accessibility."]
    },
    api: {
      react: [],
      cssClasses: [],
      publicTypes: [],
      defaults: options.publicContract,
      invalidCombinations: ["Private palette values and package source paths are not consumer APIs."]
    },
    fixtureIds: [],
    dependencies: { documents: [], packages: ["paper-ui"] },
    migration: {
      legacy: [],
      notes: ["Apply this contract when an application completes its one-time Paper migration."]
    },
    changelog: [{ date: REVIEW_DATE3, note: "Rebuilt from canonical Paper sources for the complete catalogue." }],
    behaviorTestIds: []
  });
}

// src/catalog/visual-foundations.ts
var visualFoundationDocuments = [
  guidanceDocument({
    id: "foundation.color",
    route: "/foundations/color",
    name: "Color",
    kind: "foundation",
    summary: "Semantic surface, text, action, status, rule, focus, and chart roles for light and dark themes.",
    keywords: ["color", "theme", "tokens", "contrast"],
    sourcePath: "src/foundations/tokens.css",
    whenToUse: ["Choose a semantic role for every product color."],
    content: ["Warm paper neutrals carry structure while ink violet is reserved for action, focus, and small annotation-like accents.", "Status and chart colors always need text, shape, pattern, or position as a second cue."],
    requirements: ["Use semantic aliases and preserve text/action contrast in both themes."],
    publicContract: ["Use bg-canvas, bg-paper, text-ink, text-muted and other semantic colors through paper-ui/tailwind-preset. Custom CSS can use the --paper-color-* properties."]
  }),
  guidanceDocument({
    id: "foundation.typography",
    route: "/foundations/typography",
    name: "Typography",
    kind: "foundation",
    summary: "Merriweather editorial hierarchy with Open Sans for interface copy and dense controls.",
    keywords: ["typography", "fonts", "hierarchy", "prose"],
    sourcePath: "src/foundations/fonts.css",
    whenToUse: ["Use named type roles instead of choosing ad hoc sizes or weights."],
    content: ["Merriweather is limited to display, page, and section hierarchy. Open Sans carries component titles, labels, metadata, and body copy.", "Merriweather is shipped at 700; Open Sans at 400, 600, and 700. The full paper-ui/styles.css entry includes the bundled fonts. Other weights and scripts may fall back to browser fonts."],
    requirements: ["Respect zoom, user font settings, and readable line lengths."],
    publicContract: ["Use paper-type-display, paper-type-page, paper-type-section, paper-type-component, paper-type-body, paper-type-label, and paper-type-metadata."]
  }),
  guidanceDocument({
    id: "foundation.spacing-and-density",
    route: "/foundations/spacing-and-density",
    name: "Spacing and density",
    kind: "foundation",
    summary: "A shared spacing rhythm with comfortable 44px and compact 36px control contracts.",
    keywords: ["spacing", "density", "compact", "comfortable"],
    sourcePath: "src/foundations/tokens.css",
    whenToUse: ["Apply one density at an application or bounded preview root."],
    content: ["Use ordinary Tailwind utilities through paper-ui/tailwind-preset: p-4 for 16px padding, gap-2 for 8px between flex/grid children, px-6 for horizontal padding and mt-8 for space before a section. Paper\u2019s preferred 1/2/3/4/6/8/12 values map to semantic tokens; Tailwind\u2019s remaining scale and responsive variants stay available. Comfortable is the product default; compact changes control height, field padding, inline gaps and body type. Page spacing and headings stay fixed.", "Use comfortable for touch-heavy reading flows. Compact controls are 36px minimum; icon-only actions need an explicit 44px target."],
    requirements: ["Density may change spacing and type roles but not semantics, names, or focus visibility."],
    publicContract: ["Use p-*, px-*, m-* and gap-* through the Paper Tailwind preset on composition containers; set data-density to comfortable or compact at a root boundary. The standalone stylesheet exposes CSS variables without duplicating Tailwind spacing classes."]
  }),
  guidanceDocument({
    id: "foundation.shape-and-borders",
    route: "/foundations/shape-and-borders",
    name: "Shape and borders",
    kind: "foundation",
    summary: "Square geometry, quiet rules, straight accent rails, and deliberate lower edges.",
    keywords: ["shape", "borders", "rules", "accent rail"],
    sourcePath: "styles/recipes.css",
    whenToUse: ["Use borders to explain structure or interaction, never to decorate every region."],
    content: ["Static surfaces use a quiet one-pixel rule. Pale controls receive a subtle two-pixel lower edge; filled actions keep a stronger action edge.", "A 3px accent rail covers the leading hairline of a bordered Surface and meets its top and bottom edges squarely. Borderless rails stay within their host\u2019s height."],
    requirements: ["Focus rings remain distinct from borders and status rails."],
    publicContract: ["Use paper-accent-rail and the public field/action recipes."]
  }),
  guidanceDocument({
    id: "foundation.elevation",
    route: "/foundations/elevation",
    name: "Elevation",
    kind: "foundation",
    summary: "Flat ordinary surfaces with hard-offset depth for floating and rare showcase layers.",
    keywords: ["elevation", "shadow", "overlay", "surface"],
    sourcePath: "styles/recipes.css",
    whenToUse: ["Use floating for transient layers and showcase only for deliberate demonstrations."],
    content: ["Ordinary cards remain flat. Floating uses a three-pixel hard offset; showcase uses five pixels and should stay rare."],
    requirements: ["Elevation must not be the only cue for hierarchy or interactivity."],
    publicContract: ["Use paper-elevation-flat, paper-elevation-floating, or paper-elevation-showcase."]
  }),
  guidanceDocument({
    id: "foundation.iconography",
    route: "/foundations/iconography",
    name: "Iconography",
    kind: "foundation",
    summary: "A curated Heroicons grammar for actions, navigation, status, and confirmation.",
    keywords: ["icons", "heroicons", "labels", "size"],
    sourcePath: "src/icons/index.ts",
    whenToUse: ["Use an icon when it improves recognition or saves space without obscuring the action."],
    content: ["Outline icons represent navigation and actions; solid icons represent confirmation and status. Sizes are compact 16px, default 20px, prominent 24px, and 48px only for empty-state illustration."],
    requirements: ["Decorative icons are hidden; icon-only controls have stable accessible names."],
    publicContract: ["Import icons from paper-ui/icons and use paper-icon-compact/default/prominent/empty-state classes. iconClassName is an optional string helper."]
  }),
  guidanceDocument({
    id: "foundation.motion",
    route: "/foundations/motion",
    name: "Motion",
    kind: "foundation",
    summary: "Quick, standard, and deliberate motion roles with reduced-motion safety.",
    keywords: ["motion", "transition", "reduced motion"],
    sourcePath: "src/foundations/tokens.css",
    whenToUse: ["Use motion to explain a state change, not to decorate static content."],
    content: ["Quick feedback is 120ms, standard transitions are 180ms, and deliberate movement is 240ms. Components suppress nonessential animation when reduced motion is requested."],
    requirements: ["No required information may depend on animation completing or being perceived."],
    publicContract: ["Use --paper-motion-quick, --paper-motion-standard, and --paper-motion-deliberate."]
  }),
  guidanceDocument({
    id: "foundation.layout",
    route: "/foundations/layout",
    name: "Layout",
    kind: "foundation",
    summary: "Responsive page width, prose measure, stack, cluster, and overflow guidance.",
    keywords: ["layout", "responsive", "measure", "stack"],
    sourcePath: "styles/utilities.css",
    whenToUse: ["Compose pages from document flow first, then add grids only when relationships require them."],
    content: ["Keep reading copy to a legible measure, let controls wrap before they overflow, and place horizontal data inside an explicitly labelled scroll region.", "Use the same maximum width and responsive gutters on the header, main content and footer. A narrower article or form is an intentional column within that shared frame.", "Fields fill their parent column. Put the maximum width on the form or field group, not on individual controls. Keep labels and controls aligned when adjacent fields have different hints.", "Compose the first screen around its main task: filters and rankings belong together; form instructions belong next to the fields they explain. Give supporting context less weight than the content people came to use."],
    requirements: ["Layouts reflow at 320 CSS pixels without losing content or two-dimensional scrolling."],
    publicContract: ["Public utilities: paper-stack (vertical flow, --paper-stack-gap), paper-cluster (wrapping actions, --paper-cluster-gap), paper-measure (65ch). Applications own page grids, widths, breakpoints and scroll-region semantics. Fixture classes are catalogue-only."]
  }),
  guidanceDocument({
    id: "foundation.brand",
    route: "/foundations/brand",
    name: "Brand",
    kind: "foundation",
    summary: "Canonical Cut Meter, wordmarks, favicons, and monochrome-first lockup rules.",
    keywords: ["brand", "logo", "cut meter", "favicon"],
    sourcePath: "src/assets/brand/README.md",
    whenToUse: ["Use the packaged canonical asset that matches the surface and available width."],
    content: ["The Cut Meter is proven in solid monochrome first, reversed second, and accented only as an optional third expression.", "Never filter, redraw, stretch, recolor individual bars, or reconstruct the diagonal cut."],
    requirements: ["Use meaningful alternate text only when the surrounding wordmark does not already name Tadoku."],
    publicContract: ["Import brand files through paper-ui/assets/brand/*."]
  })
];

// src/catalog/phase-three-content.tsx
import { jsx as jsx77 } from "react/jsx-runtime";
var VIEWPORTS = [
  { id: "phone", label: "Phone", width: 360, height: 720 },
  { id: "tablet", label: "Tablet", width: 768, height: 800 },
  { id: "desktop", label: "Desktop", width: 1280, height: 800 }
];
var phaseThreeFoundationDocuments = [
  guidanceDocument({
    id: "foundation.principles",
    route: "/foundations/principles",
    name: "Principles",
    kind: "foundation",
    summary: "The product and design principles that keep Tadoku calm, legible, and recognizably about reading.",
    keywords: ["principles", "bookplate", "calm", "reading"],
    sourcePath: "src/catalog/phase-three-content.tsx",
    whenToUse: ["Use these principles to resolve component, content, and product-pattern trade-offs."],
    content: ["Paper should feel editorial rather than ornamental: strong hierarchy, quiet structure, and realistic Tadoku language.", "Prefer semantic native behavior, reveal state without relying on color, and introduce complexity only when it helps a reading task."],
    requirements: ["Legibility, keyboard access, and recognizable state outrank decorative novelty."],
    publicContract: ["The decision log and accepted ADRs are the source of truth."]
  }),
  ...visualFoundationDocuments
];
var phaseThreeContentFixtures = [
  defineCatalogFixture({
    id: "pattern.logging-summary",
    name: "Reading-log summary",
    description: "A realistic summary with explicit navigation and continuation actions.",
    tags: ["logging", "pattern", "reading"],
    themes: ["light", "dark"],
    densities: ["comfortable", "compact"],
    viewports: VIEWPORTS,
    deterministic: true,
    code: pattern_logging_summary_default,
    render: () => /* @__PURE__ */ jsx77(ReadingLogPatternFixture, {})
  }),
  defineCatalogFixture({
    id: "experiment.logging-v2-entry",
    name: "Logging v2 entry",
    description: "A deterministic experiment separating saved reading from contest submission.",
    tags: ["logging", "experiment", "form"],
    themes: ["light", "dark"],
    densities: ["comfortable", "compact"],
    viewports: VIEWPORTS,
    deterministic: true,
    code: experiment_logging_v2_entry_default,
    render: () => /* @__PURE__ */ jsx77(LoggingExperimentFixture, {})
  })
];
var phaseThreePatternDocuments = [
  defineCatalogDocument({
    id: "pattern.logging",
    route: "/patterns/logging",
    name: "Logging",
    kind: "pattern",
    category: "patterns",
    aliases: [],
    summary: "Composes Paper primitives into reading summaries, entry actions, and transparent contest state.",
    keywords: ["logging", "reading", "contest", "summary"],
    lifecycle: "Stable",
    reviewDate: REVIEW_DATE3,
    sourcePath: "src/catalog/phase-three-content.tsx",
    packageVersion: PACKAGE_VERSION2,
    guidance: {
      whenToUse: ["Use for product flows that record reading and explain where it contributes."],
      whenNotToUse: ["Do not turn the product flow into a reusable low-level component."],
      content: ["Name the work, amount, language, privacy, and contest state in plain Tadoku language.", "Compose the summary from Paper controls. Keep log data, routes, and save or submission actions in the consuming screen; this example documents a pattern, not an exported component."],
      commonMistakes: ["Hiding whether an entry is merely saved or also submitted to a contest.", "Treating the catalogue fixture's local state as application behavior."]
    },
    accessibility: {
      requirements: ["Keep summaries in document order and expose every action as a named link or button."],
      keyboard: ["All actions follow native link and button operation."],
      knownConstraints: []
    },
    api: { react: ["Surface", "ButtonGroup", "Flash"], cssClasses: [], publicTypes: [], defaults: ["Applications own routing, data, and mutation state."], invalidCombinations: ["Patterns must not import application routers into paper-ui.", "The catalogue example is not a shared logging component."] },
    fixtureIds: ["pattern.logging-summary"],
    dependencies: { documents: ["component.surface", "component.button-group"], packages: ["paper-ui"] },
    migration: { legacy: ["styleguide/pages/logging.tsx"], notes: ["Preserve the realistic information hierarchy, not the legacy local-state implementation."] },
    changelog: [{ date: REVIEW_DATE3, note: "Rebuilt logging as a product pattern." }],
    behaviorTestIds: ["pattern.logging.render"]
  })
];
var phaseThreeExperimentDocuments = [
  defineCatalogDocument({
    id: "experiment.logging-v2",
    route: "/experiments/logging-v2",
    name: "Logging v2",
    kind: "experiment",
    category: "experiments",
    aliases: [],
    summary: "Explores a clearer split between saving reading, reviewing details, and submitting to contests.",
    keywords: ["logging", "experiment", "contest", "form"],
    lifecycle: "Experimental",
    reviewDate: REVIEW_DATE3,
    sourcePath: "src/catalog/phase-three-content.tsx",
    packageVersion: PACKAGE_VERSION2,
    guidance: {
      whenToUse: ["Use only for evaluating the proposed logging flow."],
      whenNotToUse: ["Do not treat this experiment as a Stable product or component contract."],
      content: ["Keep saved, reviewed, and submitted states explicit in labels and confirmation copy."],
      commonMistakes: ["Promoting experimental layout or copy into applications without product review."]
    },
    accessibility: { requirements: ["Retain visible labels, error relationships, and explicit confirmation state."], keyboard: ["Use native form control and button behavior."], knownConstraints: ["Product validation is not complete."] },
    api: { react: ["Flash", "Button"], cssClasses: ["paper-field", "paper-input"], publicTypes: [], defaults: ["The application owns TanStack Form and submission state."], invalidCombinations: ["Do not expose the experiment as a paper-ui component."] },
    fixtureIds: ["experiment.logging-v2-entry"],
    dependencies: { documents: ["pattern.logging"], packages: ["paper-ui", "@tanstack/react-form"] },
    migration: { legacy: ["styleguide/pages/logging-v2.tsx"], notes: ["Keep this visibly Experimental until a product decision promotes or removes it."] },
    changelog: [{ date: REVIEW_DATE3, note: "Preserved logging v2 as a deterministic experiment." }],
    behaviorTestIds: ["experiment.logging-v2.render"]
  })
];
var phaseThreeGovernanceDocuments = [
  guidanceDocument({
    id: "governance.contributing",
    route: "/contributing",
    name: "Contributing",
    kind: "governance",
    summary: "How Paper changes are proposed, implemented, tested, documented, reviewed, and promoted.",
    keywords: ["contributing", "lifecycle", "review", "deprecation"],
    sourcePath: "src/catalog/phase-three-content.tsx",
    whenToUse: ["Use before adding an export, token, fixture, route, or lifecycle change."],
    content: ["Every change has a canonical source, deterministic fixture, behavior evidence, migration note, and review date before Stable promotion.", "Experimental contracts may change. Stable contracts require migration-safe changes. Deprecated contracts name a registered replacement, removal timing, and exit path.", "Design history includes the refinement audit, visual studies, decision log, implementation plan, research packet, and accepted ADRs, archived in Git history and linked from the catalogue design history."],
    requirements: ["Review semantics, keyboard behavior, reflow, both themes, both densities, and realistic content before promotion."],
    publicContract: ["The catalogue registry is the source of lifecycle, route, fixture, source, and migration metadata."]
  }),
  guidanceDocument({
    id: "governance.changelog",
    route: "/changelog",
    name: "Changelog",
    kind: "governance",
    summary: "Meaningful package, component, documentation, deployment, and migration changes.",
    keywords: ["changelog", "release", "version", "migration"],
    sourcePath: "src/catalog/phase-three-content.tsx",
    whenToUse: ["Consult before upgrading Paper or changing an application integration."],
    content: ["0.1.0 establishes semantic foundations, the complete catalogue contract, static delivery at paper.tadoku.app, framework-neutral components, and TypeScript 5.4+-compatible built declarations.", "Current review (unreleased) adds usable foundation examples, a spacing scale and layout utilities, corrects accent-rail perimeter alignment, and adds Experimental page-composition controls (PageHeader, SectionHeading, StatusTag, EmptyState, StatGrid, DescriptionList and AppliedFilters) with the Filter bar pattern. Documentation coverage is not an application migration or release approval."],
    requirements: ["Record behavior, accessibility, API, or migration consequences instead of listing filenames alone."],
    publicContract: ["Release identity is the merged Tadoku commit plus immutable container digest recorded at each deployment gate."]
  })
];
var legacyStyleguideRedirects = [
  { from: "/color", to: "/foundations/color" },
  { from: "/typography", to: "/foundations/typography" },
  { from: "/branding", to: "/foundations/brand" },
  { from: "/templates", to: "/foundations/layout" },
  { from: "/forms", to: "/components/forms/input" },
  { from: "/buttons", to: "/components/actions/button" },
  { from: "/navigation", to: "/components/navigation/navbar" },
  { from: "/toasts", to: "/components/feedback/toast" },
  { from: "/flash", to: "/components/feedback/flash" },
  { from: "/charts", to: "/components/data-display/heatmap-chart" },
  { from: "/modals", to: "/components/overlays/modal" },
  { from: "/tables", to: "/components/data-display/table" },
  { from: "/breadcrumb", to: "/components/navigation/breadcrumb" },
  { from: "/action-menu", to: "/components/actions/action-menu" },
  { from: "/pagination", to: "/components/navigation/pagination" },
  { from: "/logging", to: "/patterns/logging" },
  { from: "/logging-v2", to: "/experiments/logging-v2" }
];

// src/catalog/examples/heatmap-chart.empty.tsx
import { jsx as jsx78, jsxs as jsxs61 } from "react/jsx-runtime";
function Example30() {
  return /* @__PURE__ */ jsxs61("section", { "aria-label": "2023 reading activity", children: [
    /* @__PURE__ */ jsx78("p", { children: "No reading activity recorded in 2023." }),
    /* @__PURE__ */ jsx78(HeatmapChart, { id: "empty-reading-activity", year: 2023, data: [] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/heatmap-chart.empty.tsx
var heatmap_chart_empty_default = `import { HeatmapChart } from 'paper-ui'

export default function Example() {
  return (
    <section aria-label="2023 reading activity">
      <p>No reading activity recorded in 2023.</p>
      <HeatmapChart id="empty-reading-activity" year={2023} data={[]} />
    </section>
  )
}
`;

// src/catalog/examples/heatmap-chart.reading-activity.tsx
import { DateTime as DateTime2, Interval as Interval2 } from "luxon";
import { jsx as jsx79 } from "react/jsx-runtime";
var heatmapYear = 2023;
var heatmapData = Interval2.fromDateTimes(
  DateTime2.fromObject({ year: heatmapYear, month: 1, day: 1 }),
  DateTime2.fromObject({ year: heatmapYear, month: 12, day: 31 }).endOf("day")
).splitBy({ day: 1 }).flatMap((interval, index) => {
  const date = interval.start;
  if (date === null) return [];
  const value = index % 5 === 0 ? 0 : index * 37 % 100 + 1;
  return [
    {
      date: date.toISODate() ?? "",
      value,
      tooltip: `${value} points on ${date.toLocaleString(
        DateTime2.DATE_FULL
      )}`
    }
  ];
});
function Example31() {
  return /* @__PURE__ */ jsx79(HeatmapChart, { id: "reading-activity", year: heatmapYear, data: heatmapData });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/heatmap-chart.reading-activity.tsx
var heatmap_chart_reading_activity_default = "import { DateTime, Interval } from 'luxon'\nimport { HeatmapChart } from 'paper-ui'\n\nconst heatmapYear = 2023\n\nconst heatmapData = Interval.fromDateTimes(\n  DateTime.fromObject({ year: heatmapYear, month: 1, day: 1 }),\n  DateTime.fromObject({ year: heatmapYear, month: 12, day: 31 }).endOf('day'),\n)\n  .splitBy({ day: 1 })\n  .flatMap((interval, index) => {\n    const date = interval.start\n    if (date === null) return []\n    const value = index % 5 === 0 ? 0 : ((index * 37) % 100) + 1\n    return [\n      {\n        date: date.toISODate() ?? '',\n        value,\n        tooltip: `${value} points on ${date.toLocaleString(\n          DateTime.DATE_FULL,\n        )}`,\n      },\n    ]\n  })\n\nexport default function Example() {\n  return (\n    <HeatmapChart id=\"reading-activity\" year={heatmapYear} data={heatmapData} />\n  )\n}\n";

// src/catalog/examples/table.empty.tsx
import { jsx as jsx80 } from "react/jsx-runtime";
var readingColumns = [
  {
    id: "title",
    header: "Title",
    rowHeader: true,
    width: "18rem",
    cell: (row) => row.title
  },
  { id: "language", header: "Language", cell: (row) => row.language },
  { id: "progress", header: "Pages", align: "end", cell: (row) => row.progress },
  { id: "status", header: "Status", cell: (row) => row.status }
];
function Example32() {
  return /* @__PURE__ */ jsx80(
    Table,
    {
      caption: "Recent reading",
      rows: [],
      columns: readingColumns,
      emptyMessage: "No reading logged yet."
    }
  );
}

// example-source:/app/packages/paper-ui/src/catalog/examples/table.empty.tsx
var table_empty_default = `import { Table, type TableColumn } from 'paper-ui'

interface ReadingRow {
  readonly id: string
  readonly title: string
  readonly language: string
  readonly progress: number
  readonly status: string
}

const readingColumns: readonly TableColumn<ReadingRow>[] = [
  {
    id: 'title',
    header: 'Title',
    rowHeader: true,
    width: '18rem',
    cell: row => row.title,
  },
  { id: 'language', header: 'Language', cell: row => row.language },
  { id: 'progress', header: 'Pages', align: 'end', cell: row => row.progress },
  { id: 'status', header: 'Status', cell: row => row.status },
]

export default function Example() {
  return (
    <Table
      caption="Recent reading"
      rows={[]}
      columns={readingColumns}
      emptyMessage="No reading logged yet."
    />
  )
}
`;

// src/catalog/examples/table.reading-log.tsx
import { useId as useId11 } from "react";
import { jsx as jsx81, jsxs as jsxs62 } from "react/jsx-runtime";
var readingRows = [
  {
    id: "1",
    title: "The Housekeeper and the Professor",
    language: "Japanese",
    progress: 184,
    status: "Finished"
  },
  {
    id: "2",
    title: "Convenience Store Woman",
    language: "Japanese",
    progress: 73,
    status: "Reading"
  },
  {
    id: "3",
    title: "The Three-Body Problem",
    language: "Chinese",
    progress: 42,
    status: "Reading"
  }
];
var readingColumns2 = [
  {
    id: "title",
    header: "Title",
    rowHeader: true,
    width: "18rem",
    cell: (row) => row.title
  },
  { id: "language", header: "Language", cell: (row) => row.language },
  { id: "progress", header: "Pages", align: "end", cell: (row) => row.progress },
  { id: "status", header: "Status", cell: (row) => row.status }
];
function Example33() {
  const id = useId11().replace(/:/g, "");
  return /* @__PURE__ */ jsxs62("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx81("div", { children: /* @__PURE__ */ jsx81(Button, { variant: "outline", onClick: (event) => event.currentTarget.ownerDocument.getElementById(`${id}-reading-2`)?.focus(), children: "Find current reading" }) }),
    /* @__PURE__ */ jsx81(
      Table,
      {
        caption: "Recent reading",
        rows: readingRows,
        columns: readingColumns2,
        getRowKey: (row) => row.id,
        getRowProps: (row) => ({
          id: `${id}-reading-${row.id}`,
          tabIndex: -1,
          "aria-current": row.id === "2" ? "true" : void 0,
          className: "paper-focus-ring",
          style: row.id === "2" ? { backgroundColor: "var(--paper-color-action-soft)" } : void 0
        })
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/table.reading-log.tsx
var table_reading_log_default = "import { useId } from 'react'\nimport { Button, Table, type TableColumn } from 'paper-ui'\n\ninterface ReadingRow {\n  readonly id: string\n  readonly title: string\n  readonly language: string\n  readonly progress: number\n  readonly status: string\n}\n\nconst readingRows: readonly ReadingRow[] = [\n  {\n    id: '1',\n    title: 'The Housekeeper and the Professor',\n    language: 'Japanese',\n    progress: 184,\n    status: 'Finished',\n  },\n  {\n    id: '2',\n    title: 'Convenience Store Woman',\n    language: 'Japanese',\n    progress: 73,\n    status: 'Reading',\n  },\n  {\n    id: '3',\n    title: 'The Three-Body Problem',\n    language: 'Chinese',\n    progress: 42,\n    status: 'Reading',\n  },\n]\n\nconst readingColumns: readonly TableColumn<ReadingRow>[] = [\n  {\n    id: 'title',\n    header: 'Title',\n    rowHeader: true,\n    width: '18rem',\n    cell: row => row.title,\n  },\n  { id: 'language', header: 'Language', cell: row => row.language },\n  { id: 'progress', header: 'Pages', align: 'end', cell: row => row.progress },\n  { id: 'status', header: 'Status', cell: row => row.status },\n]\n\nexport default function Example() {\n  const id = useId().replace(/:/g, '')\n  return (\n    <div className=\"paper-stack\">\n      <div><Button variant=\"outline\" onClick={event => event.currentTarget.ownerDocument.getElementById(`${id}-reading-2`)?.focus()}>Find current reading</Button></div>\n    <Table\n      caption=\"Recent reading\"\n      rows={readingRows}\n      columns={readingColumns}\n      getRowKey={row => row.id}\n      getRowProps={row => ({\n        id: `${id}-reading-${row.id}`,\n        tabIndex: -1,\n        'aria-current': row.id === '2' ? 'true' : undefined,\n        className: 'paper-focus-ring',\n        style: row.id === '2' ? { backgroundColor: 'var(--paper-color-action-soft)' } : undefined,\n      })}\n    />\n    </div>\n  )\n}\n";

// src/catalog/phase-three-data-layout.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
var REVIEW_DATE4 = "2026-09-05";
var PACKAGE_VERSION3 = "0.1.0";
var phaseThreeDataLayoutFixtures = [
  defineCatalogFixture({ ...{
    "id": "table.reading-log",
    "name": "Reading log table",
    "description": "A responsive reading log with native column and row headers.",
    "tags": [
      "table",
      "responsive",
      "row headers",
      "reading log"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: table_reading_log_default, render: () => /* @__PURE__ */ jsx82(Example33, {}) }),
  defineCatalogFixture({ ...{
    "id": "table.empty",
    "name": "Empty reading log",
    "description": "An empty table keeps its caption, headers, and explicit state message.",
    "tags": [
      "table",
      "empty",
      "status"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: table_empty_default, render: () => /* @__PURE__ */ jsx82(Example32, {}) }),
  defineCatalogFixture({ ...{
    "id": "heatmap-chart.reading-activity",
    "name": "2023 annual reading activity",
    "description": "A full-year calendar of daily activity using the original compact week-by-week geometry.",
    "tags": [
      "heatmap",
      "chart",
      "calendar",
      "annual activity"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: heatmap_chart_reading_activity_default, render: () => /* @__PURE__ */ jsx82(Example31, {}) }),
  defineCatalogFixture({ ...{
    "id": "heatmap-chart.empty",
    "name": "Year without activity",
    "description": "Missing dates use the zero-activity baseline; provide an explicit explanation alongside the chart.",
    "tags": [
      "heatmap",
      "empty"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: heatmap_chart_empty_default, render: () => /* @__PURE__ */ jsx82(Example30, {}) })
];
function completeSections(values) {
  return { required: values, pageSections: COMPONENT_PAGE_SECTION_KEYS };
}
var tableSections = completeSections({
  overview: { heading: "Overview", content: ["Table presents comparable records in native rows and columns while Paper supplies restrained rules, density, and responsive overflow."] },
  whenToUse: { heading: "When to use", content: ["Use Table when readers need to scan or compare several records across the same fields."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use Table for prose, a single record, or layouts whose cells do not have meaningful row and column relationships."] },
  choosingBetween: { heading: "Choosing between", content: ["Use a list for one-dimensional content, Surface for one grouped summary, and HeatmapChart when a compact annual calendar communicates daily activity."] },
  anatomy: { heading: "Anatomy", content: ["A labelled scroll region contains a native caption, column-header row, body rows, optional row headers, and an explicit empty state."] },
  recommendedExample: { heading: "Recommended example", content: ["The reading log identifies each row by title, aligns pages to the end, and uses stable IDs. Switch to Empty reading log to compare the preserved headers and empty message. On a phone, focus the labelled table region and scroll horizontally to inspect all columns."] },
  variants: { heading: "Variants", content: ["Captions may be visible or screen-reader-only; columns may align start, center, or end and one identifying column may become row headers."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Empty data retains the table structure. Narrow viewports preserve native semantics and expose horizontal keyboard scrolling instead of converting cells into ambiguous cards."] },
  behavior: { heading: "Behavior", content: ["The region receives focus for keyboard scrolling; Table does not own sorting, selection, pagination, loading, or data fetching."] },
  contentGuidance: { heading: "Content guidance", content: ["Write concise noun headers, use a caption that names the data set, format values consistently, and state an actionable empty message."] },
  accessibility: { heading: "Accessibility", content: ["Always provide a meaningful caption, mark the identifying column with rowHeader when rows need names, and never communicate state with alignment or color alone."] },
  implementation: { heading: "Implementation", content: ["Pass immutable rows and column definitions. Load paper-ui/styles.css once and keep interactive controls inside cells natively labelled."] },
  apiReference: { heading: "API reference", content: ["TableProps<Row> accepts caption, columns, rows, getRowKey, getRowProps, emptyMessage, captionVisibility, minWidth, and native region attributes; TableColumn<Row> defines header and cell rendering. Use getRowProps for a row ID, tabIndex, aria-current or custom class; getRowKey continues to own stable React identity."] },
  relatedPatterns: { heading: "Related patterns", content: ["Related contracts include Surface for summaries, HeatmapChart for magnitude matrices, and ButtonGroup for a small visible set of row actions."] },
  migration: { heading: "Migration", content: ["Replace copied responsive-table wrappers, preserve native table markup, move data operations to the application, and explicitly identify row headers."] },
  lifecycle: { heading: "Lifecycle", content: ["Stable in Paper 0.1.0; changes to native semantics, responsive overflow, or generic column typing require compatibility review."] }
});
var heatmapSections = completeSections({
  overview: { heading: "Overview", content: ["HeatmapChart is Tadoku's compact annual activity calendar, with weeks across the horizontal axis and weekdays down the vertical axis."] },
  whenToUse: { heading: "When to use", content: ["Use HeatmapChart to show the rhythm, gaps, and relative intensity of dated activity across one calendar year."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for exact record comparison, multiple unrelated series, negative-versus-positive direction, or trends that need a continuous axis."] },
  choosingBetween: { heading: "Choosing between", content: ["Use Table for exact multi-field records, a line chart for continuous trends, and HeatmapChart for a familiar contribution-calendar overview of daily activity."] },
  anatomy: { heading: "Anatomy", content: ["Sparse weekday labels and month labels frame a full year of 10 by 10 pixel cells separated by 3 pixel gaps. An SVG tooltip layer stays above every cell."] },
  recommendedExample: { heading: "Recommended example", content: ["Hover, touch, or Tab to a populated date to inspect its full date and points. At a narrow width, scroll the chart horizontally. Compare with Year without activity to see why empty data also needs explanatory text."] },
  variants: { heading: "Variants", content: ["The calendar geometry is intentionally fixed. Zero activity uses the neutral cell color, while positive values occupy four theme-aware intensity bands relative to the year's maximum."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Days without a record count as zero. Cells outside the requested year remain transparent, and an all-zero year stays on the neutral baseline."] },
  behavior: { heading: "Behavior", content: ["HeatmapChart maps ISO dates into week and weekday positions, uses the last repeated date value, scales positive activity against the annual maximum, and reveals supplied copy on hover, touch, or keyboard focus."] },
  contentGuidance: { heading: "Content guidance", content: ["Use ISO calendar dates and consistent numeric units. Write tooltips as short standalone descriptions, such as 42 points on January 5, 2023."] },
  accessibility: { heading: "Accessibility", content: ["The SVG has a year-specific accessible name, dated cells expose their date and value, and cells with tooltip copy can receive keyboard focus. Forced-colors mode outlines the cells."] },
  implementation: { heading: "Implementation", content: ["Pass the original id, year, and data contract and load paper-ui/styles.css once. Keep ids unique when more than one heatmap appears on a page."] },
  apiReference: { heading: "API reference", content: ["HeatmapChartProps accepts id, year, and data. Each HeatmapChartDatum contains an ISO date, numeric value, and optional tooltip string."] },
  relatedPatterns: { heading: "Related patterns", content: ["Related contracts include Table for exact record comparison and the Color foundation for the semantic chart and forced-color tokens used by the calendar."] },
  migration: { heading: "Migration", content: ["Replace the incompatible Paper matrix props with the original ui HeatmapChart contract unchanged: id, year, and dated data records."] },
  lifecycle: { heading: "Lifecycle", content: ["Stable in Paper 0.1.0; calendar geometry, five-level intensity mapping, and tooltip placement preserve the original ui component contract."] }
});
function componentDocument2(options) {
  return defineCatalogDocument({
    ...options,
    kind: "component",
    category: "data-display",
    aliases: [],
    lifecycle: "Stable",
    reviewDate: REVIEW_DATE4,
    packageVersion: PACKAGE_VERSION3,
    dependencies: { documents: [], packages: ["paper-ui", "react"] },
    changelog: [{ date: REVIEW_DATE4, note: "Published the Stable Phase 3 contract." }]
  });
}
var phaseThreeDataLayoutDocuments = [
  componentDocument2({
    id: "component.table",
    route: "/components/data-display/table",
    name: "Table",
    summary: "Presents comparable records with native headers and keyboard-scrollable responsive behavior.",
    keywords: ["table", "data", "responsive", "row header"],
    sourcePath: "src/components/data-display/Table.tsx",
    fixtureIds: ["table.reading-log", "table.empty"],
    behaviorTestIds: ["table.native-semantics", "table.responsive-region", "table.empty-state"],
    guidance: {
      whenToUse: ["Compare records that share a stable set of fields."],
      whenNotToUse: ["Do not use a table only to position unrelated content."],
      content: ["Use concise headers, consistent value formats, and a specific caption."],
      commonMistakes: ["Do not replace native table semantics with a grid of generic elements."]
    },
    accessibility: {
      requirements: ["Provide a caption and mark identifying cells as row headers."],
      keyboard: ["Tab focuses the overflow region; arrow keys scroll it when content is wider than the viewport."],
      knownConstraints: ["Application-owned interactive cell controls need their own accessible names."]
    },
    api: {
      react: ["Table"],
      props: [
        {
          "name": "caption",
          "type": "ReactNode",
          "description": "Names the data and its labelled scrolling region.",
          "required": true
        },
        {
          "name": "rows",
          "type": "readonly Row[]",
          "description": "Records to compare; supply an empty array for the empty state.",
          "required": true
        },
        {
          "name": "columns",
          "type": "readonly TableColumn<Row>[]",
          "description": "At least one column, each with id, header and cell(row, index).",
          "required": true
        },
        {
          "name": "columns[].rowHeader",
          "type": "boolean",
          "description": "Marks the cell that identifies the record as a row header.",
          "defaultValue": "false"
        },
        {
          "name": "columns[].align",
          "type": '"start" | "center" | "end"',
          "description": "Align numeric quantities to the end for comparison.",
          "defaultValue": "start"
        },
        {
          "name": "columns[].width",
          "type": "string",
          "description": "Optional CSS width hint; content can still influence native table sizing."
        },
        {
          "name": "getRowKey",
          "type": "(row, index) => Key",
          "description": "Use stable data IDs when records can reorder.",
          "defaultValue": "row index"
        },
        {
          "name": "getRowProps",
          "type": "(row, index) => HTMLAttributes<HTMLTableRowElement>",
          "description": "Set a stable row ID and tabIndex=-1 for a Find my row action, or add aria-current and a class to identify the current record. Keep navigation on real links in cells; this does not turn a row into an interactive control."
        },
        {
          "name": "captionVisibility",
          "type": '"visible" | "screen-reader"',
          "description": "Hide only if nearby visible context already identifies the data.",
          "defaultValue": "visible"
        },
        {
          "name": "emptyMessage",
          "type": "ReactNode",
          "description": "Specific empty state and useful next action.",
          "defaultValue": "No data to display."
        },
        {
          "name": "minWidth",
          "type": "string",
          "description": "Minimum table width before its own region scrolls; choose for the data.",
          "defaultValue": "max(30, columns.length \xD7 9) rem"
        },
        {
          "name": "tableClassName",
          "type": "string",
          "description": "Optional class on the native table; className applies to the outer region."
        }
      ],
      cssClasses: ["paper-table-region", "paper-table", "paper-table__*"],
      publicTypes: ["TableProps<Row>", "TableColumn<Row>", "TableColumnAlignment"],
      defaults: ["Caption is visible, cells align to start, and minWidth derives from the column count."],
      invalidCombinations: ["At least one column is required; a rowHeader column should identify rather than merely describe a row."]
    },
    migration: {
      legacy: ["ui/components/Table", "application-owned responsive table wrappers"],
      notes: ["Preserve native headers and move sorting, selection, and pagination to application composition."]
    },
    sections: tableSections
  }),
  componentDocument2({
    id: "component.heatmap-chart",
    route: "/components/data-display/heatmap-chart",
    name: "HeatmapChart",
    summary: "Shows a full year of dated activity in the original compact week-by-week calendar.",
    keywords: ["heatmap", "chart", "calendar", "activity", "year"],
    sourcePath: "src/components/data-display/HeatmapChart.tsx",
    fixtureIds: ["heatmap-chart.reading-activity", "heatmap-chart.empty"],
    behaviorTestIds: ["heatmap.calendar-geometry", "heatmap.intensity-bands", "heatmap.tooltip-interaction"],
    guidance: {
      whenToUse: ["Show the rhythm and intensity of daily activity across one calendar year."],
      whenNotToUse: ["Do not use it when exact values or continuous trends matter more than the annual pattern."],
      content: ["Include the value, unit, and full date in each supplied tooltip."],
      commonMistakes: ["Do not reshape the dated records into generic rows and columns."]
    },
    accessibility: {
      requirements: ["Provide tooltip text for meaningful activity so its exact date and value are exposed alongside the visual intensity."],
      keyboard: ["Tab reaches cells with tooltip content; focus reveals the same tooltip shown by hover and touch."],
      knownConstraints: ["The compact annual overview supplements rather than replaces an exact activity log."]
    },
    api: {
      react: ["HeatmapChart"],
      props: [
        {
          "name": "id",
          "type": "string",
          "description": "Unique per mounted chart for its tooltip layer.",
          "required": true
        },
        {
          "name": "year",
          "type": "number",
          "description": "Calendar year to display.",
          "required": true
        },
        {
          "name": "data",
          "type": "readonly HeatmapChartDatum[]",
          "description": "ISO date, nonnegative value and optional tooltip for each activity record.",
          "required": true
        },
        {
          "name": "data[].date",
          "type": "string",
          "description": "YYYY-MM-DD calendar date within the year. Missing dates show zero.",
          "required": true
        },
        {
          "name": "data[].value",
          "type": "number",
          "description": "Activity magnitude in a consistent unit. Colors scale to the year\u2019s maximum.",
          "required": true
        },
        {
          "name": "data[].tooltip",
          "type": "string",
          "description": "Full date, quantity and unit. Enables keyboard-focusable cell detail."
        }
      ],
      cssClasses: ["paper-heatmap", "paper-heatmap__*"],
      publicTypes: ["HeatmapChartProps", "HeatmapChartDatum"],
      defaults: ["Missing dates have value zero; positive values use four bands relative to the maximum value in the requested year."],
      invalidCombinations: ["Ids must be unique per page, dates use ISO calendar strings, and consumers should keep records within the requested year."]
    },
    migration: {
      legacy: ["ui/components/HeatmapChart", "the temporary Paper generic matrix HeatmapChart"],
      notes: ["Keep the original id, year, and data props; remove temporary columns, rows, domain, and formatting props."]
    },
    sections: heatmapSections
  })
];

// src/catalog/examples/navigation.breadcrumb.contest.tsx
import { jsx as jsx83 } from "react/jsx-runtime";
function Example34() {
  return /* @__PURE__ */ jsx83(
    Breadcrumb,
    {
      items: [
        { id: "home", label: "Home", href: "#home" },
        { id: "contests", label: "Contests", href: "#contests" },
        { id: "round", label: "August Japanese", href: "#august" },
        { id: "entry", label: "Reading entry 124" }
      ]
    }
  );
}

// example-source:/app/packages/paper-ui/src/catalog/examples/navigation.breadcrumb.contest.tsx
var navigation_breadcrumb_contest_default = "import { Breadcrumb } from 'paper-ui'\n\nexport default function Example() {\n  return (\n    <Breadcrumb\n      items={[\n        { id: 'home', label: 'Home', href: '#home' },\n        { id: 'contests', label: 'Contests', href: '#contests' },\n        { id: 'round', label: 'August Japanese', href: '#august' },\n        { id: 'entry', label: 'Reading entry 124' },\n      ]}\n    />\n  )\n}\n";

// src/catalog/examples/navigation.navbar.primary.tsx
import { useState as useState34 } from "react";
import { jsx as jsx84, jsxs as jsxs63 } from "react/jsx-runtime";
var navbarNavigation = [
  { type: "link", id: "home", label: "Home", href: "#home" },
  { type: "link", id: "contests", label: "Contests", href: "#contests" },
  {
    type: "dropdown",
    id: "guides",
    label: "Guides",
    links: [
      { id: "start", label: "Start here", href: "#start" },
      { id: "scoring", label: "Scoring and rules", href: "#scoring" }
    ]
  }
];
function NavbarExample() {
  const [path, setPath] = useState34("#home");
  return /* @__PURE__ */ jsxs63("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx84(
      Navbar,
      {
        brand: "Tadoku",
        brandHref: "#home",
        currentPath: path,
        navigation: navbarNavigation,
        mobileFooter: (closeMenu) => /* @__PURE__ */ jsx84(
          "a",
          {
            className: "paper-navbar__mobile-link",
            href: "#profile",
            "aria-current": path === "#profile" ? "page" : void 0,
            onClick: (event) => {
              event.preventDefault();
              setPath("#profile");
              closeMenu();
            },
            children: "Your profile"
          }
        ),
        renderLink: (props) => /* @__PURE__ */ jsx84(
          "a",
          {
            ...props,
            onClick: (event) => {
              props.onClick?.(event);
              if (!event.defaultPrevented) setPath(props.href);
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs63("p", { role: "status", children: [
      "Selected destination: ",
      path.slice(1),
      ". These fragment links keep this example on the page."
    ] }),
    /* @__PURE__ */ jsx84("h3", { children: "Route loading" }),
    /* @__PURE__ */ jsx84(
      Navbar,
      {
        brand: "Tadoku",
        brandHref: "#home",
        navigation: [],
        mobileNavigation: false,
        isLoading: true
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/navigation.navbar.primary.tsx
var navigation_navbar_primary_default = `import { useState } from 'react'
import { Navbar } from 'paper-ui'

const navbarNavigation = [
  { type: 'link', id: 'home', label: 'Home', href: '#home' },
  { type: 'link', id: 'contests', label: 'Contests', href: '#contests' },
  {
    type: 'dropdown',
    id: 'guides',
    label: 'Guides',
    links: [
      { id: 'start', label: 'Start here', href: '#start' },
      { id: 'scoring', label: 'Scoring and rules', href: '#scoring' },
    ],
  },
] as const

export default function NavbarExample() {
  const [path, setPath] = useState('#home')
  return (
    <div className="paper-stack">
      <Navbar
        brand="Tadoku"
        brandHref="#home"
        currentPath={path}
        navigation={navbarNavigation}
        mobileFooter={closeMenu => (
          <a
            className="paper-navbar__mobile-link"
            href="#profile"
            aria-current={path === '#profile' ? 'page' : undefined}
            onClick={event => {
              event.preventDefault()
              setPath('#profile')
              closeMenu()
            }}
          >
            Your profile
          </a>
        )}
        renderLink={props => (
          <a
            {...props}
            onClick={event => {
              props.onClick?.(event)
              if (!event.defaultPrevented) setPath(props.href)
            }}
          />
        )}
      />
      <p role="status">
        Selected destination: {path.slice(1)}. These fragment links keep this
        example on the page.
      </p>
      <h3>Route loading</h3>
      <Navbar
        brand="Tadoku"
        brandHref="#home"
        navigation={[]}
        mobileNavigation={false}
        isLoading
      />
    </div>
  )
}
`;

// src/catalog/examples/navigation.sidebar.admin.tsx
import { useState as useState35 } from "react";
import { jsx as jsx85, jsxs as jsxs64 } from "react/jsx-runtime";
var sidebarSections = [
  {
    id: "content",
    title: "Content",
    links: [
      { id: "logs", label: "Reading logs", href: "#logs" },
      { id: "users", label: "Users", href: "#users" }
    ]
  },
  {
    id: "system",
    title: "System",
    links: [
      { id: "imports", label: "Imports", href: "#imports" },
      { id: "jobs", label: "Background jobs", href: "#jobs", disabled: true }
    ]
  }
];
function SidebarExample() {
  const [path, setPath] = useState35("#logs");
  return /* @__PURE__ */ jsxs64("div", { className: "paper-stack", style: { maxWidth: "22rem" }, children: [
    /* @__PURE__ */ jsx85(
      Sidebar,
      {
        label: "Admin sections",
        currentPath: path,
        sections: sidebarSections.map((section2) => ({
          ...section2,
          links: section2.links.map((link) => ({
            ...link,
            onSelect: () => setPath(link.href)
          }))
        }))
      }
    ),
    /* @__PURE__ */ jsxs64("p", { role: "status", children: [
      "Selected: ",
      path.slice(1),
      ". Background jobs are unavailable while the worker is offline."
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/navigation.sidebar.admin.tsx
var navigation_sidebar_admin_default = `import { Sidebar } from 'paper-ui'
import { useState } from 'react'

const sidebarSections = [
  {
    id: 'content',
    title: 'Content',
    links: [
      { id: 'logs', label: 'Reading logs', href: '#logs' },
      { id: 'users', label: 'Users', href: '#users' },
    ],
  },
  {
    id: 'system',
    title: 'System',
    links: [
      { id: 'imports', label: 'Imports', href: '#imports' },
      { id: 'jobs', label: 'Background jobs', href: '#jobs', disabled: true },
    ],
  },
] as const

export default function SidebarExample() {
  const [path, setPath] = useState('#logs')
  return (
    <div className="paper-stack" style={{ maxWidth: '22rem' }}>
      <Sidebar
        label="Admin sections"
        currentPath={path}
        sections={sidebarSections.map(section => ({
          ...section,
          links: section.links.map(link => ({
            ...link,
            onSelect: () => setPath(link.href),
          })),
        }))}
      />
      <p role="status">
        Selected: {path.slice(1)}. Background jobs are unavailable while the
        worker is offline.
      </p>
    </div>
  )
}
`;

// src/catalog/site-navigation.tsx
import { jsx as jsx86 } from "react/jsx-runtime";
var siteNavigationFixtures = [
  defineCatalogFixture({ ...{
    "id": "navigation.navbar.primary",
    "name": "Primary Tadoku navigation",
    "description": "Choose a destination, open Guides, or switch to Phone and open Menu. Your profile appears at the bottom of the mobile menu. A second bar demonstrates loading.",
    "tags": [
      "navigation",
      "navbar",
      "primary"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: navigation_navbar_primary_default, render: () => /* @__PURE__ */ jsx86(NavbarExample, {}) }),
  defineCatalogFixture({ ...{
    "id": "navigation.sidebar.admin",
    "name": "Admin sidebar",
    "description": "Switch current destination within grouped links; Background jobs demonstrates an explained unavailable destination.",
    "tags": [
      "navigation",
      "sidebar",
      "admin"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: navigation_sidebar_admin_default, render: () => /* @__PURE__ */ jsx86(SidebarExample, {}) }),
  defineCatalogFixture({ ...{
    "id": "navigation.breadcrumb.contest",
    "name": "Contest breadcrumb",
    "description": "A four-level trail collapses to parent and current location below 48rem. The current page is plain text.",
    "tags": [
      "navigation",
      "breadcrumb",
      "contest"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: navigation_breadcrumb_contest_default, render: () => /* @__PURE__ */ jsx86(Example34, {}) })
];
var siteNavigationSpecs = [
  {
    id: "component.navbar",
    route: "/components/navigation/navbar",
    name: "Navbar",
    example: "Choose a destination to see the active route change. Open Guides on Desktop; choose Phone and open Menu to see the same destinations plus Your profile at the bottom. The mobileFooter callback supplies closeMenu so account links and commands can close the disclosure after selection. A separate loading bar illustrates progress without starting a timer.",
    props: [{ "name": "brand / brandHref", "type": "ReactNode / string", "required": true, "description": "Accessible brand content and its home destination." }, { "name": "navigation", "type": "readonly NavbarItem[]", "required": true, "description": 'Direct {type:"link", id, label, href} destinations or {type:"dropdown", id, label, links} destination groups.' }, { "name": "actions", "type": "ReactNode", "description": "End-aligned controls visible at every breakpoint; keep them short enough to fit beside the brand and mobile menu trigger." }, { "name": "mobileFooter", "type": "(closeMenu: () => void) => ReactNode", "description": "Optional account links or commands at the bottom of the mobile disclosure. Call closeMenu after choosing a destination or completing an action; this content is hidden on desktop." }, { "name": "mobileNavigation", "type": "boolean", "defaultValue": "true", "description": "Show disclosure below 48rem. Disable only when the shell supplies an alternate narrow navigation." }, { "name": "menuLabel", "type": "string", "defaultValue": "Menu", "description": "Used in the narrow trigger accessible name: Open menu / Close menu." }, { "name": "isLoading", "type": "boolean", "defaultValue": "false", "description": "Show the named indeterminate route-loading rail; the application controls its duration." }, { "name": "currentPath", "type": "string", "description": "Exact href match for current state. An item\u2019s explicit current boolean takes precedence." }, { "name": "renderLink", "type": "NavigationLinkRenderer", "description": "Optional router adapter returning an anchor. Forward every supplied prop, including handlers and accessibility attributes.", "defaultValue": "native <a>" }, { "name": "label", "type": "string", "description": "Accessible navigation landmark name; distinguish multiple navigation regions." }],
    summary: "Provides responsive global navigation, account destinations, current-route state, and progress feedback without owning application routing.",
    fixtureId: "navigation.navbar.primary",
    sourcePath: "src/components/navigation/Navbar.tsx",
    react: ["Navbar"],
    types: ["NavbarProps", "NavbarItem", "NavigationDirectLink", "NavigationDropdown", "NavigationLinkRenderer"],
    css: ["paper-navbar", "paper-navbar__link", "paper-navbar__dropdown", "paper-navbar__mobile-link"],
    when: "Use once as the application's primary global navigation landmark.",
    avoid: "Do not use for local page sections, contextual actions, or authorization decisions.",
    choose: "Use Sidebar for grouped local destinations and ActionMenu for contextual commands.",
    anatomy: "A brand link, direct destination links, optional Base UI dropdowns, an optional end-actions slot, a narrow disclosure with an optional mobile footer, and an optional named loading rail.",
    variants: "Direct links and account dropdowns share the same current-path and renderLink contract. Set mobileNavigation to false when an application shell supplies a Drawer or another narrow navigation pattern.",
    states: "Below 48rem, direct links and dropdown destinations move into the Menu disclosure, followed by mobileFooter when supplied. Use this footer for account links or commands that belong inside the mobile menu. The application decides which links exist for each account role. Loading adds a status rail without disabling navigation.",
    behavior: "Tab reaches links, end actions, and triggers; Enter or Space opens disclosures; Base UI supplies menu arrows, Home/End, Escape, outside dismissal, and focus return.",
    content: "Use short destination nouns and a specific account-group label; brand content must retain an accessible name.",
    accessibility: "Keep one named navigation landmark, aria-current on the active destination, expanded/control relationships on the narrow trigger, and owner-document portals.",
    migration: "Move Next route comparison and Link rendering into the application adapter; replace Headless UI disclosure/menu imports with Paper Navbar data.",
    packages: ["paper-ui", "@base-ui/react"]
  },
  {
    id: "component.sidebar",
    route: "/components/navigation/sidebar",
    name: "Sidebar",
    example: "Choose Reading logs, Users or Imports to move the current rail. Background jobs stays unavailable with an explanation. The example bounds the sidebar to 22rem; position it beside page content in the application shell.",
    props: [{ "name": "sections", "type": "readonly SidebarSection[]", "required": true, "description": "Groups with unique id, visible title and NavigationItem[] links." }, { "name": "links[].id / label / href / current / disabled / icon / onSelect", "type": "NavigationItem", "description": "Stable id, visible label and destination href; optional current state, disabled state, decorative icon and selection callback. onSelect does not update currentPath for you." }, { "name": "currentPath", "type": "string", "description": "Exact href match for current state. An item\u2019s explicit current boolean takes precedence." }, { "name": "renderLink", "type": "NavigationLinkRenderer", "description": "Optional router adapter returning an anchor. Forward every supplied prop, including handlers and accessibility attributes.", "defaultValue": "native <a>" }, { "name": "label", "type": "string", "description": "Accessible navigation landmark name; distinguish multiple navigation regions." }],
    summary: "Groups local application destinations under visible section headings with a straight current-route accent rail.",
    fixtureId: "navigation.sidebar.admin",
    sourcePath: "src/components/navigation/Sidebar.tsx",
    react: ["Sidebar"],
    types: ["SidebarProps", "SidebarSection", "NavigationItem", "NavigationLinkRenderer"],
    css: ["paper-sidebar", "paper-sidebar__section", "paper-sidebar__link"],
    when: "Use for persistent local navigation with several meaningfully named groups.",
    avoid: "Do not use as a second global Navbar or for commands that do not navigate.",
    choose: "Use VerticalTabbar for peer views of one object and Sidebar for broader grouped destinations.",
    anatomy: "A named navigation landmark containing labelled sections, native lists, and router-rendered anchors.",
    variants: "Sections may contain current, default, icon-bearing, and unavailable destinations.",
    states: "Current destinations have a side rail and stronger text. Disabled destinations remain visible but cannot activate. Comfortable and compact density inherit the corresponding control height; place the Sidebar above content or in a Drawer when a side column no longer fits.",
    behavior: "Native links participate in sequential focus; disabled destinations are removed from Tab order and block activation.",
    content: "Section headings name coherent destination groups; link labels use parallel destination nouns.",
    accessibility: "Use unique section IDs, aria-current page, aria-disabled with blocked activation, and icons hidden from the accessible name.",
    migration: "Replace Next Link with renderLink and pass currentPath or explicit current state from the application."
  },
  {
    id: "component.breadcrumb",
    route: "/components/navigation/breadcrumb",
    name: "Breadcrumb",
    example: "Follow the four-level contest trail. Below 48rem only the immediate parent and current entry remain. The final item stays plain text; hrefs on ancestors preserve native navigation.",
    props: [{ "name": "items", "type": "readonly BreadcrumbItem[]", "required": true, "description": "Ordered {id, label, href?, icon?} ancestors followed by the current page. Last item always renders as current text, even if href is supplied." }, { "name": "renderLink", "type": "NavigationLinkRenderer", "description": "Optional router adapter returning an anchor. Forward every supplied prop, including handlers and accessibility attributes.", "defaultValue": "native <a>" }, { "name": "label", "type": "string", "defaultValue": "Breadcrumb", "description": "Accessible name for this location trail." }],
    summary: "Communicates hierarchical location with an ordered trail whose current page is not an unnecessary link.",
    fixtureId: "navigation.breadcrumb.contest",
    sourcePath: "src/components/navigation/Breadcrumb.tsx",
    react: ["Breadcrumb"],
    types: ["BreadcrumbProps", "BreadcrumbItem", "NavigationLinkRenderer"],
    css: ["paper-breadcrumb", "paper-breadcrumb__list", "paper-breadcrumb__current"],
    when: "Use when users can arrive deep in a hierarchy and benefit from parent destinations.",
    avoid: "Do not mirror flat history, workflow steps, or primary navigation.",
    choose: "Breadcrumb shows hierarchy; Pagination moves through result pages and Tabbar switches peer views.",
    anatomy: "A named nav, ordered list, parent links, hidden separator icons, and one aria-current terminal item.",
    variants: "Items may carry decorative icons; narrow layouts retain the parent and current location.",
    states: "Below 48rem, all but the immediate parent and current location are hidden. A single item remains current text. Missing ancestor hrefs render as text; supply hrefs when users can navigate upward.",
    behavior: "Parent anchors use native link keyboard behavior; separators and the current item are not interactive.",
    content: "Use the same concise labels as destination headings and omit redundant words such as page.",
    accessibility: "Keep ordered-list structure, hide separators, render one aria-current page, and never disable a current-page anchor as a substitute for text.",
    migration: "Replace legacy Next links and the mobile back-link branch with one semantic responsive trail and renderLink adapter."
  }
];

// src/catalog/examples/navigation.pagination.entries.tsx
import { useState as useState36 } from "react";
import { jsx as jsx87, jsxs as jsxs65 } from "react/jsx-runtime";
function PaginationExample() {
  const [page, setPage] = useState36(8);
  return /* @__PURE__ */ jsxs65("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsxs65("p", { role: "status", children: [
      "Page ",
      page,
      " of 24 \xB7 Entries ",
      (page - 1) * 10 + 1,
      "\u2013",
      page * 10
    ] }),
    /* @__PURE__ */ jsx87(Pagination, { totalPages: 24, currentPage: page, onPageChange: setPage }),
    /* @__PURE__ */ jsx87("h3", { children: "One page" }),
    /* @__PURE__ */ jsx87("p", { children: "Keep pagination out of an empty result. This boundary example has no available previous or next page." }),
    /* @__PURE__ */ jsx87(
      Pagination,
      {
        label: "Single-page results",
        totalPages: 1,
        currentPage: 1,
        getHref: () => "#results"
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/navigation.pagination.entries.tsx
var navigation_pagination_entries_default = `import { Pagination } from 'paper-ui'
import { useState } from 'react'

export default function PaginationExample() {
  const [page, setPage] = useState(8)
  return (
    <div className="paper-stack">
      <p role="status">
        Page {page} of 24 \xB7 Entries {(page - 1) * 10 + 1}\u2013{page * 10}
      </p>
      <Pagination totalPages={24} currentPage={page} onPageChange={setPage} />
      <h3>One page</h3>
      <p>
        Keep pagination out of an empty result. This boundary example has no
        available previous or next page.
      </p>
      <Pagination
        label="Single-page results"
        totalPages={1}
        currentPage={1}
        getHref={() => '#results'}
      />
    </div>
  )
}
`;

// src/catalog/examples/navigation.tabbar.contests.tsx
import { useState as useState37 } from "react";
import { jsx as jsx88, jsxs as jsxs66 } from "react/jsx-runtime";
var contestLinks = [
  { id: "official", label: "Official contests", href: "#official" },
  { id: "community", label: "Community contests", href: "#community" },
  { id: "mine", label: "My contests", href: "#mine" }
];
function TabbarExample() {
  const [path, setPath] = useState37("#mine");
  return /* @__PURE__ */ jsxs66("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsx88(
      Tabbar,
      {
        label: "Contest views",
        currentPath: path,
        links: contestLinks.map((link) => ({
          ...link,
          onSelect: () => setPath(link.href)
        }))
      }
    ),
    /* @__PURE__ */ jsxs66("p", { role: "status", children: [
      "Selected destination: ",
      path.slice(1),
      "."
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/navigation.tabbar.contests.tsx
var navigation_tabbar_contests_default = `import { Tabbar } from 'paper-ui'
import { useState } from 'react'

const contestLinks = [
  { id: 'official', label: 'Official contests', href: '#official' },
  { id: 'community', label: 'Community contests', href: '#community' },
  { id: 'mine', label: 'My contests', href: '#mine' },
] as const

export default function TabbarExample() {
  const [path, setPath] = useState('#mine')
  return (
    <div className="paper-stack">
      <Tabbar
        label="Contest views"
        currentPath={path}
        links={contestLinks.map(link => ({
          ...link,
          onSelect: () => setPath(link.href),
        }))}
      />
      <p role="status">Selected destination: {path.slice(1)}.</p>
    </div>
  )
}
`;

// src/catalog/examples/navigation.vertical-tabbar.profile.tsx
import { useState as useState38 } from "react";
import { jsx as jsx89, jsxs as jsxs67 } from "react/jsx-runtime";
var profileLinks = [
  { id: "entries", label: "Reading entries", href: "#entries" },
  { id: "statistics", label: "Statistics", href: "#statistics" },
  { id: "contests", label: "Contests", href: "#profile-contests" }
];
function VerticalTabbarExample() {
  const [path, setPath] = useState38("#statistics");
  return /* @__PURE__ */ jsxs67("div", { className: "paper-stack", style: { maxWidth: "22rem" }, children: [
    /* @__PURE__ */ jsx89(
      VerticalTabbar,
      {
        label: "Profile views",
        currentPath: path,
        links: profileLinks.map((link) => ({
          ...link,
          onSelect: () => setPath(link.href)
        }))
      }
    ),
    /* @__PURE__ */ jsxs67("p", { role: "status", children: [
      "Selected destination: ",
      path.slice(1),
      "."
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/navigation.vertical-tabbar.profile.tsx
var navigation_vertical_tabbar_profile_default = `import { VerticalTabbar } from 'paper-ui'
import { useState } from 'react'

const profileLinks = [
  { id: 'entries', label: 'Reading entries', href: '#entries' },
  { id: 'statistics', label: 'Statistics', href: '#statistics' },
  { id: 'contests', label: 'Contests', href: '#profile-contests' },
] as const

export default function VerticalTabbarExample() {
  const [path, setPath] = useState('#statistics')
  return (
    <div className="paper-stack" style={{ maxWidth: '22rem' }}>
      <VerticalTabbar
        label="Profile views"
        currentPath={path}
        links={profileLinks.map(link => ({
          ...link,
          onSelect: () => setPath(link.href),
        }))}
      />
      <p role="status">Selected destination: {path.slice(1)}.</p>
    </div>
  )
}
`;

// src/catalog/view-navigation.tsx
import { jsx as jsx90 } from "react/jsx-runtime";
var viewNavigationFixtures = [
  defineCatalogFixture({ ...{
    "id": "navigation.tabbar.contests",
    "name": "Contest views",
    "description": "Choose a linked view and observe its current rail. On Phone, scroll the list without hiding destinations.",
    "tags": [
      "navigation",
      "tabbar",
      "contests"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: navigation_tabbar_contests_default, render: () => /* @__PURE__ */ jsx90(TabbarExample, {}) }),
  defineCatalogFixture({ ...{
    "id": "navigation.vertical-tabbar.profile",
    "name": "Profile views",
    "description": "Choose peer destinations within a bounded navigation column. Parent layout supplies its width.",
    "tags": [
      "navigation",
      "vertical",
      "tabbar",
      "profile"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: navigation_vertical_tabbar_profile_default, render: () => /* @__PURE__ */ jsx90(VerticalTabbarExample, {}) }),
  defineCatalogFixture({ ...{
    "id": "navigation.pagination.entries",
    "name": "Reading-entry pages",
    "description": "Move the controlled result page; compare the middle-page window with the single-page boundary.",
    "tags": [
      "navigation",
      "pagination",
      "entries"
    ],
    "themes": [
      "light",
      "dark"
    ],
    "densities": [
      "comfortable",
      "compact"
    ],
    "viewports": [
      {
        "id": "phone",
        "label": "Phone",
        "width": 360,
        "height": 720
      },
      {
        "id": "tablet",
        "label": "Tablet",
        "width": 768,
        "height": 800
      },
      {
        "id": "desktop",
        "label": "Desktop",
        "width": 1280,
        "height": 800
      }
    ],
    "deterministic": true
  }, code: navigation_pagination_entries_default, render: () => /* @__PURE__ */ jsx90(PaginationExample, {}) })
];
var viewNavigationSpecs = [
  {
    id: "component.tabbar",
    route: "/components/navigation/tabbar",
    name: "Tabbar",
    example: "Select Official contests, Community contests or My contests. The example updates currentPath through onSelect and uses real fragment hrefs; replace them with application routes. Phone demonstrates horizontal overflow.",
    props: [{ "name": "links", "type": "readonly TabbarItem[]", "required": true, "description": "Peer destinations with unique id, label and href. Avoid more items than users can compare." }, { "name": "links[].id / label / href / current / disabled / icon / onSelect", "type": "NavigationItem", "description": "Stable id, visible label and destination href; optional current state, disabled state, decorative icon and selection callback. onSelect does not update currentPath for you." }, { "name": "currentPath", "type": "string", "description": "Exact href match for current state. An item\u2019s explicit current boolean takes precedence." }, { "name": "renderLink", "type": "NavigationLinkRenderer", "description": "Optional router adapter returning an anchor. Forward every supplied prop, including handlers and accessibility attributes.", "defaultValue": "native <a>" }, { "name": "label", "type": "string", "description": "Accessible navigation landmark name; distinguish multiple navigation regions." }],
    summary: "Displays peer destination views as horizontal linked navigation that remains scrollable at narrow widths.",
    fixtureId: "navigation.tabbar.contests",
    sourcePath: "src/components/navigation/Tabbar.tsx",
    react: ["Tabbar"],
    types: ["TabbarProps", "TabbarItem", "NavigationLinkRenderer"],
    css: ["paper-tabbar", "paper-tabbar__list--horizontal", "paper-tabbar__link"],
    when: "Use for a small set of peer routes representing different views of the same context.",
    avoid: "Do not apply tab roles to links that navigate and do not hide primary destinations in a menu solely for narrow widths.",
    choose: "Use VerticalTabbar when vertical space and labels fit better; use native in-page tabs only for panels that do not navigate.",
    anatomy: "A named navigation landmark, native list, linked destinations, and a straight current-route lower rail.",
    variants: "Horizontal linked navigation supports current, default, disabled, icon-bearing, and overflow states.",
    states: "The current lower rail stays distinct from keyboard focus. Below the available width the list scrolls horizontally; Tab brings offscreen links into view. Density changes target height, not the number of destinations.",
    behavior: "Links remain in normal Tab order and activate with native link behavior; horizontal overflow remains touch and keyboard scrollable.",
    content: "Use short parallel view labels and keep the number of peer destinations small.",
    accessibility: "Use navigation and aria-current rather than tablist/tab roles because activation changes route.",
    migration: "Replace Next Link and responsive ActionMenu coupling with renderLink and a visible overflow-safe list."
  },
  {
    id: "component.vertical-tabbar",
    route: "/components/navigation/vertical-tabbar",
    name: "VerticalTabbar",
    example: "Select a profile view and watch the left rail follow currentPath. The 22rem wrapper is a layout choice, not part of the component API. Keep the content region beside it on wide layouts and below it on narrow layouts.",
    props: [{ "name": "links", "type": "readonly TabbarItem[]", "required": true, "description": "Peer destinations with unique id, label and href. Width and placement beside content belong to the surrounding layout." }, { "name": "links[].id / label / href / current / disabled / icon / onSelect", "type": "NavigationItem", "description": "Stable id, visible label and destination href; optional current state, disabled state, decorative icon and selection callback. onSelect does not update currentPath for you." }, { "name": "currentPath", "type": "string", "description": "Exact href match for current state. An item\u2019s explicit current boolean takes precedence." }, { "name": "renderLink", "type": "NavigationLinkRenderer", "description": "Optional router adapter returning an anchor. Forward every supplied prop, including handlers and accessibility attributes.", "defaultValue": "native <a>" }, { "name": "label", "type": "string", "description": "Accessible navigation landmark name; distinguish multiple navigation regions." }],
    summary: "Presents peer destination views vertically while preserving linked-navigation semantics.",
    fixtureId: "navigation.vertical-tabbar.profile",
    sourcePath: "src/components/navigation/Tabbar.tsx",
    react: ["VerticalTabbar"],
    types: ["TabbarProps", "TabbarItem", "NavigationLinkRenderer"],
    css: ["paper-tabbar--vertical", "paper-tabbar__list--vertical", "paper-tabbar__link"],
    when: "Use when peer route labels benefit from vertical measure beside their content.",
    avoid: "Do not use for broad multi-section application navigation or non-routing panel controls.",
    choose: "Use Tabbar for compact horizontal peer routes and Sidebar for grouped application areas.",
    anatomy: "A named navigation landmark, vertical list, linked destinations, and a straight current-route side rail.",
    variants: "Vertical linked navigation shares the same current, disabled, icon, and router adapter contract as Tabbar.",
    states: "Current state uses a left rail. The component remains vertical at every breakpoint; the application moves the navigation above content when the side column no longer fits. Use short labels and test localized content.",
    behavior: "Native link focus and activation remain unchanged; no arrow-key tab composite is introduced.",
    content: "Use concise parallel labels describing peer views of one object or task.",
    accessibility: "Expose navigation plus aria-current and do not use tab roles for route changes.",
    migration: "Replace the undocumented legacy VerticalTabbar and Next Link coupling while retaining destination hrefs."
  },
  {
    id: "component.pagination",
    route: "/components/navigation/pagination",
    name: "Pagination",
    example: "Previous, Next and numbered buttons update the displayed page and entry range. Compare the one-page example, whose edge controls are unavailable. Real data fetching, loading announcements and moving focus to results belong to the application.",
    props: [{ "name": "totalPages", "type": "number", "required": true, "description": "Positive integer. Omit the component when there are no result pages." }, { "name": "currentPage", "type": "number", "required": true, "description": "One-based controlled page between 1 and totalPages. Invalid boundaries throw RangeError." }, { "name": "getHref", "type": "(page: number) => string", "description": "Creates native destination anchors. At least getHref or onPageChange is required." }, { "name": "onPageChange", "type": "(page: number) => void", "description": "Update your page state and result data. Alone: buttons. With getHref: intercept unmodified primary activation; modified clicks keep browser behavior." }, { "name": "siblingCount", "type": "number", "defaultValue": "2", "description": "Non-negative integer number of neighboring pages on each side; first and last are retained." }, { "name": "renderLink", "type": "NavigationLinkRenderer", "description": "Optional router adapter returning an anchor. Forward every supplied prop, including handlers and accessibility attributes.", "defaultValue": "native <a>" }, { "name": "label", "type": "string", "defaultValue": "Pagination", "description": "Accessible landmark name. Distinguish multiple result sets." }],
    summary: "Moves through a known bounded page set with href, callback, or combined router integration.",
    fixtureId: "navigation.pagination.entries",
    sourcePath: "src/components/navigation/Pagination.tsx",
    react: ["Pagination"],
    types: ["PaginationProps", "NavigationLinkRenderer"],
    css: ["paper-pagination", "paper-pagination__control", "paper-pagination__page"],
    when: "Use when a result collection is split into stable numbered pages and direct page URLs or controlled page state exist.",
    avoid: "Do not use for unknown unbounded feeds, carousel slides, or step-by-step forms.",
    choose: "Use getHref for crawlable/direct routes, onPageChange for controlled local state, or both when an app router intercepts anchors.",
    anatomy: "A named nav, native list, previous/next controls, current-page text, bounded numbered destinations, and non-interactive gaps.",
    variants: "Href, callback, and combined modes share siblingCount and compact responsive behavior.",
    states: "Below 48rem the numbered links and gaps are hidden; Previous and Next remain. Pair them with a visible Page X of Y summary, as this example does. At the first and last page the unavailable edge is non-interactive.",
    behavior: "Native anchors preserve modified-click browser behavior. With onPageChange, ordinary activation updates controlled state; callback-only mode uses buttons. Current-page display does not update until the application passes the new currentPage.",
    content: "Keep Previous and Next labels visible and expose each numeric destination as Page plus its number.",
    accessibility: "Name the landmark, identify the current page with aria-current, label page destinations, hide decorative gaps, and remove unavailable edges from interaction.",
    migration: "Replace Next router/query access and the page-jump modal with explicit getHref or onPageChange supplied by the application."
  }
];

// src/catalog/phase-three-navigation.tsx
var REVIEW_DATE5 = "2026-09-05";
function navigationSections(spec) {
  const required = {
    overview: { heading: "Overview", content: [spec.summary] },
    whenToUse: { heading: "When to use", content: [spec.when] },
    whenNotToUse: { heading: "When not to use", content: [spec.avoid] },
    choosingBetween: { heading: "Choose between", content: [spec.choose] },
    anatomy: { heading: "Anatomy", content: [spec.anatomy] },
    recommendedExample: { heading: "Recommended example", content: [spec.example] },
    variants: { heading: "Variants", content: [spec.variants] },
    statesAndAdaptation: { heading: "States and adaptation", content: [spec.states] },
    behavior: { heading: "Behavior", content: [spec.behavior] },
    contentGuidance: { heading: "Content guidance", content: [spec.content] },
    accessibility: { heading: "Accessibility", content: [spec.accessibility] },
    implementation: { heading: "Implementation", content: [`Import ${spec.react.join(", ")} from paper-ui and load paper-ui/styles.css once at the application entry. Examples use native fragment links so they run without a router. For an application router, pass renderLink={(props) => <AppLink {...props} />}; forward href, className, children, tabIndex, event handlers and aria attributes to the final anchor. Derive currentPath from your router; current on an item explicitly overrides exact href matching.`] },
    apiReference: { heading: "API reference", content: [`See API / Props for required data, defaults, and routing contracts. Public types: ${spec.types.join(", ")}.`] },
    relatedPatterns: { heading: "Related patterns", content: ["Combine navigation components with the application shell and real destination data; do not duplicate routing or authorization policy inside Paper."] },
    migration: { heading: "Migration", content: [spec.migration] },
    lifecycle: { heading: "Lifecycle", content: ["Stable in Paper 0.1.0 with deterministic fixtures and rendered semantic, keyboard, and router-boundary tests."] }
  };
  return { required, pageSections: COMPONENT_PAGE_SECTION_KEYS };
}
function navigationDocument(spec) {
  return defineCatalogDocument({
    id: spec.id,
    route: spec.route,
    name: spec.name,
    kind: "component",
    category: "navigation",
    aliases: [],
    summary: spec.summary,
    keywords: [spec.name.toLocaleLowerCase(), "navigation", "router neutral", "paper"],
    lifecycle: "Stable",
    reviewDate: REVIEW_DATE5,
    sourcePath: spec.sourcePath,
    packageVersion: "0.1.0",
    guidance: {
      whenToUse: [spec.when],
      whenNotToUse: [spec.avoid],
      content: [spec.content],
      commonMistakes: [spec.choose]
    },
    accessibility: {
      requirements: [spec.accessibility],
      keyboard: [spec.behavior],
      knownConstraints: [spec.states]
    },
    api: {
      react: spec.react,
      props: spec.props,
      cssClasses: spec.css,
      publicTypes: spec.types,
      defaults: [
        "Links render as native anchors; renderLink may replace them without changing semantics.",
        ...spec.name === "Navbar" ? ["actions renders end-aligned utilities; mobileNavigation defaults to true and may be disabled when the application provides another narrow navigation pattern."] : []
      ],
      invalidCombinations: [spec.avoid]
    },
    fixtureIds: [spec.fixtureId],
    dependencies: { documents: ["foundation.iconography"], packages: spec.packages ?? ["paper-ui"] },
    migration: { legacy: [`ui ${spec.name}`], notes: [spec.migration] },
    changelog: [{ date: REVIEW_DATE5, note: `Published router-neutral ${spec.name} as Stable.` }],
    behaviorTestIds: [`${spec.id}.semantics`, `${spec.id}.keyboard`, `${spec.id}.router-boundary`],
    sections: navigationSections(spec)
  });
}
var phaseThreeNavigationFixtures = [
  ...siteNavigationFixtures,
  ...viewNavigationFixtures
];
var specs2 = [
  ...siteNavigationSpecs,
  ...viewNavigationSpecs
];
var phaseThreeNavigationDocuments = specs2.map(navigationDocument);

// src/catalog/examples/drawer.filters.tsx
import { useId as useId12, useState as useState39 } from "react";
import { Fragment as Fragment9, jsx as jsx91, jsxs as jsxs68 } from "react/jsx-runtime";
function DrawerExample() {
  const [open, setOpen] = useState39(false);
  const [language, setLanguage] = useState39("All languages");
  const formId = useId12();
  const methods = usePaperForm({
    defaultValues: { language: "All languages" },
    onSubmit: ({ value: values }) => {
      setLanguage(values.language);
      setOpen(false);
    }
  });
  return /* @__PURE__ */ jsxs68("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsxs68("p", { role: "status", children: [
      "Showing: ",
      language
    ] }),
    /* @__PURE__ */ jsx91(methods.AppForm, { children: /* @__PURE__ */ jsx91(
      Drawer,
      {
        trigger: /* @__PURE__ */ jsx91(Button, { variant: "outline", children: "Review filters" }),
        title: "Entry filters",
        description: "Choose which reading entries to show.",
        open,
        onOpenChange: (nextOpen) => {
          if (nextOpen)
            methods.reset({ language }, { keepDefaultValues: true });
          setOpen(nextOpen);
        },
        footer: /* @__PURE__ */ jsxs68(Fragment9, { children: [
          /* @__PURE__ */ jsx91(Button, { variant: "ghost", onClick: () => setOpen(false), children: "Cancel" }),
          /* @__PURE__ */ jsx91(Button, { type: "submit", form: formId, children: "Apply filters" })
        ] }),
        children: /* @__PURE__ */ jsx91(methods.Form, { className: "paper-stack", id: formId, children: /* @__PURE__ */ jsx91(methods.AppField, { name: "language", children: (field) => /* @__PURE__ */ jsx91(
          field.Select,
          {
            label: "Language",
            options: [
              { value: "All languages", label: "All languages" },
              { value: "Japanese", label: "Japanese" },
              { value: "French", label: "French" }
            ],
            hint: "Changes take effect only when you apply filters."
          }
        ) }) })
      }
    ) }),
    /* @__PURE__ */ jsx91("h3", { children: "Start placement, without a footer" }),
    /* @__PURE__ */ jsxs68(
      Drawer,
      {
        placement: "start",
        trigger: /* @__PURE__ */ jsx91(Button, { variant: "outline", children: "Reading help" }),
        title: "Reading help",
        description: "How entries contribute to your progress.",
        children: [
          /* @__PURE__ */ jsx91("p", { children: "Record the amount you read in the original unit. Your reading history stays available even when an entry is not part of a contest." }),
          /* @__PURE__ */ jsx91("p", { children: "Close this sheet with the close button or Escape to return to your entries." })
        ]
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/drawer.filters.tsx
var drawer_filters_default = `import { Button, Drawer, usePaperForm } from 'paper-ui'
import { useId, useState } from 'react'

export default function DrawerExample() {
  const [open, setOpen] = useState(false)
  const [language, setLanguage] = useState('All languages')
  const formId = useId()
  const methods = usePaperForm({
    defaultValues: { language: 'All languages' },
    onSubmit: ({ value: values }) => {
      setLanguage(values.language)
      setOpen(false)
    },
  })
  return (
    <div className="paper-stack">
      <p role="status">Showing: {language}</p>
      <methods.AppForm>
        <Drawer
          trigger={<Button variant="outline">Review filters</Button>}
          title="Entry filters"
          description="Choose which reading entries to show."
          open={open}
          onOpenChange={nextOpen => {
            if (nextOpen)
              methods.reset({ language }, { keepDefaultValues: true })
            setOpen(nextOpen)
          }}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" form={formId}>
                Apply filters
              </Button>
            </>
          }
        >
          <methods.Form className="paper-stack" id={formId}>
            <methods.AppField name="language">
              {field => (
                <field.Select
                  label="Language"
                  options={[
                    { value: 'All languages', label: 'All languages' },
                    { value: 'Japanese', label: 'Japanese' },
                    { value: 'French', label: 'French' },
                  ]}
                  hint="Changes take effect only when you apply filters."
                />
              )}
            </methods.AppField>
          </methods.Form>
        </Drawer>
      </methods.AppForm>
      <h3>Start placement, without a footer</h3>
      <Drawer
        placement="start"
        trigger={<Button variant="outline">Reading help</Button>}
        title="Reading help"
        description="How entries contribute to your progress."
      >
        <p>
          Record the amount you read in the original unit. Your reading history
          stays available even when an entry is not part of a contest.
        </p>
        <p>
          Close this sheet with the close button or Escape to return to your
          entries.
        </p>
      </Drawer>
    </div>
  )
}
`;

// src/catalog/examples/tabs.content.tsx
import { useState as useState40 } from "react";
import { jsx as jsx92, jsxs as jsxs69 } from "react/jsx-runtime";
function TabsExample() {
  const [view, setView] = useState40("summary");
  return /* @__PURE__ */ jsxs69("div", { className: "paper-stack", children: [
    /* @__PURE__ */ jsxs69("section", { children: [
      /* @__PURE__ */ jsx92("h3", { children: "Automatic selection" }),
      /* @__PURE__ */ jsxs69(Tabs.Root, { defaultValue: "summary", children: [
        /* @__PURE__ */ jsxs69(Tabs.List, { "aria-label": "Reading log views", children: [
          /* @__PURE__ */ jsx92(Tabs.Tab, { value: "summary", children: "Summary" }),
          /* @__PURE__ */ jsx92(Tabs.Tab, { value: "entries", children: "Entries" }),
          /* @__PURE__ */ jsx92(Tabs.Tab, { value: "moderation", disabled: true, children: "Moderation" })
        ] }),
        /* @__PURE__ */ jsx92(Tabs.Panel, { value: "summary", children: "1,240 pages read in Japanese." }),
        /* @__PURE__ */ jsx92(Tabs.Panel, { value: "entries", children: "12 reading entries." }),
        /* @__PURE__ */ jsx92(Tabs.Panel, { value: "moderation", children: "No moderation notes." })
      ] }),
      /* @__PURE__ */ jsx92("p", { children: "Use Left and Right arrows to select. Moderation is unavailable for this reader account." })
    ] }),
    /* @__PURE__ */ jsxs69("section", { children: [
      /* @__PURE__ */ jsx92("h3", { children: "Controlled vertical tabs with manual selection" }),
      /* @__PURE__ */ jsxs69(Tabs.Root, { orientation: "vertical", value: view, onValueChange: setView, children: [
        /* @__PURE__ */ jsxs69(Tabs.List, { "aria-label": "Reading details", activateOnFocus: false, children: [
          /* @__PURE__ */ jsx92(Tabs.Tab, { value: "summary", children: "Summary" }),
          /* @__PURE__ */ jsx92(Tabs.Tab, { value: "entries", children: "Entries" })
        ] }),
        /* @__PURE__ */ jsx92(Tabs.Panel, { value: "summary", children: "1,240 pages read in Japanese." }),
        /* @__PURE__ */ jsx92(Tabs.Panel, { value: "entries", children: "12 reading entries." })
      ] }),
      /* @__PURE__ */ jsxs69("p", { children: [
        "Use Up and Down arrows to move focus, then Enter or Space to select. Selected view: ",
        view,
        "."
      ] })
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/tabs.content.tsx
var tabs_content_default = `import { Tabs } from 'paper-ui'
import { useState } from 'react'

export default function TabsExample() {
  const [view, setView] = useState('summary')
  return (
    <div className="paper-stack">
      <section>
        <h3>Automatic selection</h3>
        <Tabs.Root defaultValue="summary">
          <Tabs.List aria-label="Reading log views">
            <Tabs.Tab value="summary">Summary</Tabs.Tab>
            <Tabs.Tab value="entries">Entries</Tabs.Tab>
            <Tabs.Tab value="moderation" disabled>
              Moderation
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="summary">1,240 pages read in Japanese.</Tabs.Panel>
          <Tabs.Panel value="entries">12 reading entries.</Tabs.Panel>
          <Tabs.Panel value="moderation">No moderation notes.</Tabs.Panel>
        </Tabs.Root>
        <p>
          Use Left and Right arrows to select. Moderation is unavailable for
          this reader account.
        </p>
      </section>
      <section>
        <h3>Controlled vertical tabs with manual selection</h3>
        <Tabs.Root orientation="vertical" value={view} onValueChange={setView}>
          <Tabs.List aria-label="Reading details" activateOnFocus={false}>
            <Tabs.Tab value="summary">Summary</Tabs.Tab>
            <Tabs.Tab value="entries">Entries</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="summary">1,240 pages read in Japanese.</Tabs.Panel>
          <Tabs.Panel value="entries">12 reading entries.</Tabs.Panel>
        </Tabs.Root>
        <p>
          Use Up and Down arrows to move focus, then Enter or Space to select.
          Selected view: {view}.
        </p>
      </section>
    </div>
  )
}
`;

// src/catalog/published-primitives.tsx
import { jsx as jsx93 } from "react/jsx-runtime";
var REVIEW_DATE6 = "2026-09-05";
var VIEWPORTS2 = [
  { id: "phone", label: "Phone", width: 360, height: 720 },
  { id: "tablet", label: "Tablet", width: 768, height: 800 },
  { id: "desktop", label: "Desktop", width: 1280, height: 800 }
];
function sections2(required) {
  return { required, pageSections: COMPONENT_PAGE_SECTION_KEYS };
}
function document3(input) {
  return defineCatalogDocument({
    ...input,
    kind: "component",
    aliases: [],
    lifecycle: "Stable",
    reviewDate: REVIEW_DATE6,
    packageVersion: "0.1.0",
    dependencies: { documents: [], packages: ["paper-ui"] },
    changelog: [{ date: REVIEW_DATE6, note: "Published the Paper primitive." }]
  });
}
function fixture(input) {
  return defineCatalogFixture({
    ...input,
    themes: ["light", "dark"],
    densities: ["comfortable", "compact"],
    viewports: VIEWPORTS2,
    deterministic: true
  });
}
var publishedPrimitiveFixtures = [
  fixture({ id: "tabs.content", name: "Reading log views", description: "Compare automatic horizontal selection with a controlled vertical list that waits for Enter or Space.", tags: ["tabs", "keyboard", "controlled"], code: tabs_content_default, render: () => /* @__PURE__ */ jsx93(TabsExample, {}) }),
  fixture({ id: "drawer.filters", name: "Reading-entry filters", description: "Apply a real language filter, cancel without saving, and compare end/start placement and optional footer.", tags: ["drawer", "keyboard", "controlled"], code: drawer_filters_default, render: () => /* @__PURE__ */ jsx93(DrawerExample, {}) })
];
var tabsSections = {
  overview: { heading: "Overview", content: ["Tabs switches among peer content views while keeping one view active."] },
  whenToUse: { heading: "When to use", content: ["Use Tabs to switch between peer views of the same local context, such as the summary and entries for one reading log."] },
  whenNotToUse: { heading: "When not to use", content: ["Use Tabbar for linked destinations with their own URLs, and use ordinary headings when readers should see every section together."] },
  choosingBetween: { heading: "Choose between", content: ["Tabs changes content in place. Tabbar and VerticalTabbar navigate to linked destinations and preserve native link behavior."] },
  anatomy: { heading: "Anatomy", content: ["A Tabs root contains one labelled list, two or more tabs, and one panel for each value."] },
  recommendedExample: { heading: "Recommended example", content: ["Compare automatic horizontal selection with controlled vertical selection. The horizontal example skips disabled Moderation. The vertical example uses activateOnFocus={false}: arrow keys move focus without changing the panel until Enter or Space."] },
  variants: { heading: "Variants", content: ["Use horizontal orientation by default. The second example uses vertical orientation and controlled value/onValueChange. Vertical orientation does not collapse automatically; choose horizontal when the available width cannot fit a side list and readable panel."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Selected, focused, hovered, and disabled tabs retain distinct non-color cues. Horizontal lists scroll at narrow widths."] },
  behavior: { heading: "Behavior", content: ["Horizontal lists use Left/Right; vertical lists use Up/Down. Home and End reach the first/last enabled tab; loopFocus defaults to true. activateOnFocus defaults to true; set false when switching would cause expensive work. Inactive panels stay mounted by default, retaining local state. Set keepMounted={false} on a panel only when discarding that state is intentional."] },
  contentGuidance: { heading: "Content guidance", content: ["Use short parallel nouns such as Summary and Entries. The tab-list label should name the set, not repeat the word tabs."] },
  accessibility: { heading: "Accessibility", content: ["Give Tabs.List an accessible label, keep a panel for every tab value, and do not place linked page navigation in a tab role."] },
  implementation: { heading: "Implementation", content: ["Import Tabs and paper-ui/styles.css. Pair every Tab value with a Panel value, and supply defaultValue for an uncontrolled root or value plus onValueChange for controlled state. Do not mix these state models. Give List an aria-label or aria-labelledby."] },
  apiReference: { heading: "API reference", content: ["TabsRootProps controls value and orientation; TabsListProps controls activation and focus looping; TabsTabProps and TabsPanelProps share string values."] },
  relatedPatterns: { heading: "Related patterns", content: ["Use Tabbar for URL-backed destinations and Drawer for supporting work that temporarily covers the current view."] },
  migration: { heading: "Migration", content: ["Replace application-owned role, aria-selected, roving-tabindex, and panel visibility logic with the matching Tabs parts."] },
  lifecycle: { heading: "Lifecycle", content: ["Stable in Paper 0.1.0 with horizontal, vertical, controlled, disabled, and mounted-panel behavior covered."] }
};
var drawerSections = {
  overview: { heading: "Overview", content: ["Drawer presents a modal side sheet without discarding the page behind it."] },
  whenToUse: { heading: "When to use", content: ["Use Drawer for a supporting task such as filters, compact editing, or contextual navigation that should preserve the current page."] },
  whenNotToUse: { heading: "When not to use", content: ["Use Modal for a small focused decision and a dedicated page for long, multi-step, or linkable work."] },
  choosingBetween: { heading: "Choose between", content: ["Drawer allows more vertical content and page context than Modal; Sidebar remains persistent navigation rather than a modal task surface."] },
  anatomy: { heading: "Anatomy", content: ["An application-owned trigger opens a titled sheet with an optional description, scrolling body, footer, and labelled close action."] },
  recommendedExample: { heading: "Recommended example", content: ["Open Review filters, choose a language, then Apply filters: the sheet closes and the result summary updates. Cancel, Escape and outside dismissal discard the draft on next open. Reading help demonstrates start placement and a footerless sheet."] },
  variants: { heading: "Variants", content: ["Placement is start or end in logical reading direction. Prefer end for supporting tasks unless navigation hierarchy calls for start."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["End placement opens from the reading-direction end; start opens from the beginning. The sheet is at most 28rem wide and leaves one control-width of backdrop visible on narrow screens. Its body scrolls independently, while header and footer stay visible. Reduced-motion removes transitions."] },
  behavior: { heading: "Behavior", content: ["Opening contains focus and blocks background interaction. Escape, outside press and Close dismiss and restore trigger focus. Use defaultOpen for an uncontrolled sheet or open/onOpenChange for a controlled one. Drawer does not save data or confirm unsaved changes; decide the draft policy in the application, as the example does."] },
  contentGuidance: { heading: "Content guidance", content: ["Use a brief noun title, add a description only when it clarifies scope, and keep persistent commit or cancel actions in the footer."] },
  accessibility: { heading: "Accessibility", content: ["Keep a visible title, supply an accurate trigger name, and preserve the close button. Tab and Shift+Tab stay within the sheet; Escape returns to the opener. Use the Phone preview to verify close and footer actions remain reachable while the body scrolls."] },
  implementation: { heading: "Implementation", content: ["Import Drawer, Button and the form controls from paper-ui; load paper-ui/styles.css once. Wrap the form in form.AppForm. The footer lives outside the body form, so give the form a unique id from useId and the submit button a matching form attribute. Update controlled open state after a successful submit; retain the draft and display an error if saving fails."] },
  apiReference: { heading: "API reference", content: ["DrawerProps accepts trigger, title, description, children, footer, placement, closeLabel, and controlled or uncontrolled open state. DrawerPlacement is start or end."] },
  relatedPatterns: { heading: "Related patterns", content: ["Use Modal for compact decisions, Sidebar for persistent local navigation, and a page for durable workflows."] },
  migration: { heading: "Migration", content: ["Replace application-owned portal, backdrop, focus trap, and slide animation code while retaining the application trigger and body content."] },
  lifecycle: { heading: "Lifecycle", content: ["Stable in Paper 0.1.0 with start/end placement, focus containment, dismissal, restoration, and owner-document behavior covered."] }
};
var publishedPrimitiveDocuments = [
  document3({
    id: "component.tabs",
    route: "/components/navigation/tabs",
    name: "Tabs",
    category: "navigation",
    summary: "Switches among related content views with one keyboard-operable selection model.",
    keywords: ["tabs", "tabpanel", "content", "selection", "keyboard"],
    sourcePath: "src/components/navigation/Tabs/Tabs.tsx",
    guidance: {
      whenToUse: tabsSections.whenToUse.content,
      whenNotToUse: tabsSections.whenNotToUse.content,
      content: tabsSections.contentGuidance.content,
      commonMistakes: tabsSections.choosingBetween.content
    },
    accessibility: {
      requirements: tabsSections.accessibility.content,
      keyboard: tabsSections.behavior.content,
      knownConstraints: tabsSections.statesAndAdaptation.content
    },
    api: {
      react: ["Tabs", "TabsRoot", "TabsList", "TabsTab", "TabsPanel"],
      props: [{ "name": "Root.defaultValue", "type": "string", "description": "Initial active Tab value for an uncontrolled root. Choose an enabled tab." }, { "name": "Root.value / onValueChange", "type": "string / (value: string) => void", "description": "Controlled selection. Update value from the callback; use instead of defaultValue." }, { "name": "Root.orientation", "type": '"horizontal" | "vertical"', "defaultValue": "horizontal", "description": "List layout and arrow-key axis. No automatic breakpoint switching." }, { "name": "List.aria-label / aria-labelledby", "type": "string", "required": true, "description": "Name the content-view group with one of these attributes." }, { "name": "List.activateOnFocus", "type": "boolean", "defaultValue": "true", "description": "Select as keyboard focus moves. Set false to require Enter/Space before changing panels." }, { "name": "List.loopFocus", "type": "boolean", "defaultValue": "true", "description": "Wrap focus at the first and last enabled tab." }, { "name": "Tab.value / Panel.value", "type": "string", "required": true, "description": "Matching unique value associates a tab and its panel." }, { "name": "Tab.disabled", "type": "boolean", "defaultValue": "false", "description": "Prevent selection and skip this tab during keyboard navigation. Explain unavailability nearby." }, { "name": "Panel.keepMounted", "type": "boolean", "defaultValue": "true", "description": "Keep hidden panel state in the DOM. False unmounts inactive panels." }, { "name": "className / ref / native attributes", "type": "per-part HTML props", "description": "Root/List/Panel forward div props and refs; Tab forwards button props and ref." }],
      cssClasses: ["paper-tabs", "paper-tabs__list", "paper-tabs__tab", "paper-tabs__panel"],
      publicTypes: ["TabsValue", "TabsOrientation", "TabsRootProps", "TabsListProps", "TabsTabProps", "TabsPanelProps"],
      defaults: ["orientation=horizontal", "activateOnFocus=true", "loopFocus=true", "keepMounted=true"],
      invalidCombinations: ["Do not use Tabs for navigation to another URL."]
    },
    fixtureIds: ["tabs.content"],
    migration: { legacy: ["application-owned tab roles"], notes: tabsSections.migration.content },
    behaviorTestIds: ["tabs.semantics", "tabs.keyboard", "tabs.controlled", "tabs.mounting"],
    sections: sections2(tabsSections)
  }),
  document3({
    id: "component.drawer",
    route: "/components/overlays/drawer",
    name: "Drawer",
    category: "overlays",
    summary: "Opens a modal side sheet for supporting work while preserving page context.",
    keywords: ["drawer", "sheet", "overlay", "focus", "filters"],
    sourcePath: "src/components/overlays/Drawer/Drawer.tsx",
    guidance: {
      whenToUse: drawerSections.whenToUse.content,
      whenNotToUse: drawerSections.whenNotToUse.content,
      content: drawerSections.contentGuidance.content,
      commonMistakes: drawerSections.choosingBetween.content
    },
    accessibility: {
      requirements: drawerSections.accessibility.content,
      keyboard: drawerSections.behavior.content,
      knownConstraints: drawerSections.statesAndAdaptation.content
    },
    api: {
      react: ["Drawer", "DRAWER_PLACEMENTS"],
      props: [{ "name": "trigger", "type": "ReactElement", "required": true, "description": "A focusable button that accepts merged props and a ref; Paper Button works directly." }, { "name": "title", "type": "ReactNode", "required": true, "description": "Visible heading that names the modal dialog." }, { "name": "children", "type": "ReactNode", "required": true, "description": "Scrollable body. The application owns content and form state." }, { "name": "description", "type": "ReactNode", "description": "Optional supporting copy associated with the dialog." }, { "name": "footer", "type": "ReactNode", "description": "Optional persistent actions outside the body. Link submit buttons to a form by id." }, { "name": "placement", "type": '"start" | "end"', "defaultValue": "end", "description": "Logical side of the viewport, following reading direction." }, { "name": "open / onOpenChange", "type": "boolean / (open: boolean) => void", "description": "Controlled open state. Apply the callback so Escape, outside press and close work." }, { "name": "defaultOpen", "type": "boolean", "defaultValue": "false", "description": "Initial open state for an uncontrolled drawer; do not combine with open." }, { "name": "closeLabel", "type": "string", "defaultValue": "Close", "description": "Accessible label for the built-in close button. Localize when needed." }],
      cssClasses: ["paper-drawer", "paper-drawer__body", "paper-drawer__footer"],
      publicTypes: ["DrawerProps", "DrawerPlacement"],
      defaults: ["placement=end", "closeLabel=Close", "modal=true"],
      invalidCombinations: ["Do not nest Drawer or Modal instances."]
    },
    fixtureIds: ["drawer.filters"],
    migration: { legacy: ["application-owned side sheet"], notes: drawerSections.migration.content },
    behaviorTestIds: ["drawer.semantics", "drawer.focus", "drawer.dismissal", "drawer.owner-document"],
    sections: sections2(drawerSections)
  })
];

// src/catalog/examples/pattern.filter-bar.tsx
import { useStore as useStore21 } from "@tanstack/react-form";
import { useId as useId13, useRef as useRef7, useState as useState41 } from "react";
import { jsx as jsx94, jsxs as jsxs70 } from "react/jsx-runtime";
var standings = [
  { name: "Kai", language: "Japanese", activity: "Reading", score: 1400 },
  { name: "Mei", language: "Japanese", activity: "Listening", score: 1120 },
  { name: "Sora", language: "Spanish", activity: "Reading", score: 860 },
  { name: "Anton", language: "Japanese", activity: "Reading", score: 450 },
  { name: "Lina", language: "Spanish", activity: "Listening", score: 320 }
];
var noFilters = { language: "all", activity: "all", query: "" };
function FilterBarExample() {
  const [open, setOpen] = useState41(false);
  const toggle = useRef7(null);
  const panelId = useId13();
  const headingId = useId13();
  const methods = usePaperForm({ defaultValues: noFilters });
  const {
    language = "all",
    activity = "all",
    query = ""
  } = useStore21(methods.store, (state) => state.values);
  const search = query.trim().toLocaleLowerCase();
  const applied = [
    language !== "all" ? language : "",
    activity !== "all" ? activity : "",
    search ? `Name: ${query.trim()}` : ""
  ].filter(Boolean);
  const rows = standings.filter(
    (row) => (language === "all" || row.language === language) && (activity === "all" || row.activity === activity) && row.name.toLocaleLowerCase().includes(search)
  );
  const clearFilters = () => {
    methods.reset(noFilters, { keepDefaultValues: true });
    toggle.current?.focus();
  };
  return /* @__PURE__ */ jsxs70("section", { "aria-labelledby": headingId, style: { maxWidth: "44rem" }, children: [
    /* @__PURE__ */ jsx94(
      SectionHeading,
      {
        level: 2,
        id: headingId,
        title: "2026 Round 5 standings",
        actions: /* @__PURE__ */ jsxs70(
          Button,
          {
            ref: toggle,
            variant: "ghost",
            "aria-expanded": open,
            "aria-controls": panelId,
            onClick: () => setOpen(!open),
            trailingIcon: /* @__PURE__ */ jsx94(
              ChevronDownIcon,
              {
                className: "paper-icon-compact",
                style: open ? { transform: "rotate(180deg)" } : void 0
              }
            ),
            children: [
              "Filters",
              applied.length ? ` (${applied.length})` : ""
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx94("div", { id: panelId, hidden: !open, children: /* @__PURE__ */ jsx94(methods.AppForm, { children: /* @__PURE__ */ jsxs70(
      "form",
      {
        onSubmit: (event) => event.preventDefault(),
        style: {
          display: "grid",
          gap: "0.75rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))",
          paddingBlockEnd: "1rem"
        },
        children: [
          /* @__PURE__ */ jsx94(methods.AppField, { name: "language", children: (field) => /* @__PURE__ */ jsx94(
            field.Select,
            {
              label: "Language",
              options: [
                { value: "all", label: "All languages" },
                { value: "Japanese", label: "Japanese" },
                { value: "Spanish", label: "Spanish" }
              ]
            }
          ) }),
          /* @__PURE__ */ jsx94(methods.AppField, { name: "activity", children: (field) => /* @__PURE__ */ jsx94(
            field.Select,
            {
              label: "Activity",
              options: [
                { value: "all", label: "All activities" },
                { value: "Reading", label: "Reading" },
                { value: "Listening", label: "Listening" }
              ]
            }
          ) }),
          /* @__PURE__ */ jsx94(methods.AppField, { name: "query", children: (field) => /* @__PURE__ */ jsx94(field.Input, { label: "Find a participant", type: "search" }) })
        ]
      }
    ) }) }),
    /* @__PURE__ */ jsxs70(
      "div",
      {
        className: "paper-cluster",
        style: { justifyContent: "space-between" },
        children: [
          /* @__PURE__ */ jsx94(AppliedFilters, { filters: applied, onClear: clearFilters }),
          /* @__PURE__ */ jsxs70("p", { className: "paper-type-metadata", role: "status", children: [
            rows.length,
            " of ",
            standings.length,
            " participants"
          ] })
        ]
      }
    ),
    rows.length ? /* @__PURE__ */ jsx94(
      Table,
      {
        caption: "Standings",
        captionVisibility: "screen-reader",
        rows,
        getRowKey: (row) => row.name,
        minWidth: "17rem",
        columns: [
          {
            id: "participant",
            header: "Participant",
            rowHeader: true,
            cell: (row) => row.name
          },
          { id: "language", header: "Language", cell: (row) => row.language },
          {
            id: "score",
            header: "Score",
            align: "end",
            cell: (row) => row.score.toLocaleString("en")
          }
        ]
      }
    ) : /* @__PURE__ */ jsx94(
      EmptyState,
      {
        variant: "plain",
        headingLevel: 3,
        title: "No participants match these filters.",
        description: "Filtering and searching never change anyone\u2019s rank.",
        actions: /* @__PURE__ */ jsx94(Button, { variant: "outline", onClick: clearFilters, children: "Clear filters" })
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/pattern.filter-bar.tsx
var pattern_filter_bar_default = `import { useStore } from '@tanstack/react-form'
import {
  AppliedFilters,
  Button,
  EmptyState,
  SectionHeading,
  Table,
  usePaperForm,
} from 'paper-ui'
import { ChevronDownIcon } from 'paper-ui/icons'
import { useId, useRef, useState } from 'react'

interface Standing {
  readonly name: string
  readonly language: string
  readonly activity: string
  readonly score: number
}

const standings: readonly Standing[] = [
  { name: 'Kai', language: 'Japanese', activity: 'Reading', score: 1400 },
  { name: 'Mei', language: 'Japanese', activity: 'Listening', score: 1120 },
  { name: 'Sora', language: 'Spanish', activity: 'Reading', score: 860 },
  { name: 'Anton', language: 'Japanese', activity: 'Reading', score: 450 },
  { name: 'Lina', language: 'Spanish', activity: 'Listening', score: 320 },
]
const noFilters = { language: 'all', activity: 'all', query: '' }

export default function FilterBarExample() {
  const [open, setOpen] = useState(false)
  const toggle = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const headingId = useId()
  const methods = usePaperForm({ defaultValues: noFilters })
  const {
    language = 'all',
    activity = 'all',
    query = '',
  } = useStore(methods.store, state => state.values)
  const search = query.trim().toLocaleLowerCase()
  const applied = [
    language !== 'all' ? language : '',
    activity !== 'all' ? activity : '',
    search ? \`Name: \${query.trim()}\` : '',
  ].filter(Boolean)
  const rows = standings.filter(
    row =>
      (language === 'all' || row.language === language) &&
      (activity === 'all' || row.activity === activity) &&
      row.name.toLocaleLowerCase().includes(search),
  )
  const clearFilters = () => {
    methods.reset(noFilters, { keepDefaultValues: true })
    toggle.current?.focus()
  }

  return (
    <section aria-labelledby={headingId} style={{ maxWidth: '44rem' }}>
      <SectionHeading
        level={2}
        id={headingId}
        title="2026 Round 5 standings"
        actions={
          <Button
            ref={toggle}
            variant="ghost"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen(!open)}
            trailingIcon={
              <ChevronDownIcon
                className="paper-icon-compact"
                style={open ? { transform: 'rotate(180deg)' } : undefined}
              />
            }
          >
            Filters{applied.length ? \` (\${applied.length})\` : ''}
          </Button>
        }
      />
      <div id={panelId} hidden={!open}>
        <methods.AppForm>
          <form
            onSubmit={event => event.preventDefault()}
            style={{
              display: 'grid',
              gap: '0.75rem',
              gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
              paddingBlockEnd: '1rem',
            }}
          >
            <methods.AppField name="language">
              {field => (
                <field.Select
                  label="Language"
                  options={[
                    { value: 'all', label: 'All languages' },
                    { value: 'Japanese', label: 'Japanese' },
                    { value: 'Spanish', label: 'Spanish' },
                  ]}
                />
              )}
            </methods.AppField>
            <methods.AppField name="activity">
              {field => (
                <field.Select
                  label="Activity"
                  options={[
                    { value: 'all', label: 'All activities' },
                    { value: 'Reading', label: 'Reading' },
                    { value: 'Listening', label: 'Listening' },
                  ]}
                />
              )}
            </methods.AppField>
            <methods.AppField name="query">
              {field => (
                <field.Input label="Find a participant" type="search" />
              )}
            </methods.AppField>
          </form>
        </methods.AppForm>
      </div>
      <div
        className="paper-cluster"
        style={{ justifyContent: 'space-between' }}
      >
        <AppliedFilters filters={applied} onClear={clearFilters} />
        <p className="paper-type-metadata" role="status">
          {rows.length} of {standings.length} participants
        </p>
      </div>
      {rows.length ? (
        <Table
          caption="Standings"
          captionVisibility="screen-reader"
          rows={rows}
          getRowKey={row => row.name}
          minWidth="17rem"
          columns={[
            {
              id: 'participant',
              header: 'Participant',
              rowHeader: true,
              cell: row => row.name,
            },
            { id: 'language', header: 'Language', cell: row => row.language },
            {
              id: 'score',
              header: 'Score',
              align: 'end',
              cell: row => row.score.toLocaleString('en'),
            },
          ]}
        />
      ) : (
        <EmptyState
          variant="plain"
          headingLevel={3}
          title="No participants match these filters."
          description="Filtering and searching never change anyone\u2019s rank."
          actions={
            <Button variant="outline" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      )}
    </section>
  )
}
`;

// src/catalog/page-composition-document.ts
var REVIEW_DATE7 = "2026-10-12";
var VIEWPORTS3 = [
  { id: "phone", label: "Phone", width: 360, height: 720 },
  { id: "tablet", label: "Tablet", width: 768, height: 800 },
  { id: "desktop", label: "Desktop", width: 1280, height: 800 }
];
var EXPERIMENTAL_LIFECYCLE = "Experimental in Paper 0.1.0. Extracted from repeated paper-playground screens; no production application consumes it yet.";
function fixture2(input) {
  return defineCatalogFixture({
    ...input,
    themes: ["light", "dark"],
    densities: ["comfortable", "compact"],
    viewports: VIEWPORTS3,
    deterministic: true
  });
}
function componentDocument3({
  sections: sections3,
  commonMistakes: commonMistakes2,
  dependencies = [],
  legacy,
  ...input
}) {
  return defineCatalogDocument({
    ...input,
    kind: "component",
    aliases: [],
    lifecycle: "Experimental",
    reviewDate: REVIEW_DATE7,
    packageVersion: "0.1.0",
    guidance: {
      whenToUse: sections3.whenToUse.content,
      whenNotToUse: sections3.whenNotToUse.content,
      content: sections3.contentGuidance.content,
      commonMistakes: commonMistakes2
    },
    accessibility: {
      requirements: sections3.accessibility.content,
      keyboard: sections3.behavior.content,
      knownConstraints: sections3.statesAndAdaptation.content
    },
    dependencies: { documents: dependencies, packages: ["paper-ui"] },
    migration: { legacy, notes: sections3.migration.content },
    changelog: [{ date: REVIEW_DATE7, note: "Added as an Experimental page-composition control." }],
    sections: { required: sections3, pageSections: COMPONENT_PAGE_SECTION_KEYS }
  });
}

// src/catalog/examples/page-header.contest-detail.tsx
import { Fragment as Fragment10, jsx as jsx95, jsxs as jsxs71 } from "react/jsx-runtime";
function Example35() {
  return /* @__PURE__ */ jsx95(
    PageHeader,
    {
      backLink: { href: "#official-contests", label: "Back to official contests" },
      title: "2026 Round 5",
      status: /* @__PURE__ */ jsxs71(Fragment10, { children: [
        /* @__PURE__ */ jsx95(StatusTag, { tone: "success", children: "Live now" }),
        /* @__PURE__ */ jsx95(StatusTag, { children: "Registration open" })
      ] }),
      lead: "Two weeks of reading and listening with the Tadoku community. Join with up to three languages.",
      actions: /* @__PURE__ */ jsxs71(Fragment10, { children: [
        /* @__PURE__ */ jsx95("a", { className: buttonClassName(), href: "#join", children: "Join contest" }),
        /* @__PURE__ */ jsx95("a", { className: buttonClassName({ variant: "outline" }), href: "#standings", children: "View standings" })
      ] })
    }
  );
}

// example-source:/app/packages/paper-ui/src/catalog/examples/page-header.contest-detail.tsx
var page_header_contest_detail_default = `import { PageHeader, StatusTag, buttonClassName } from 'paper-ui'

export default function Example() {
  return (
    <PageHeader
      backLink={{ href: '#official-contests', label: 'Back to official contests' }}
      title="2026 Round 5"
      status={
        <>
          <StatusTag tone="success">Live now</StatusTag>
          <StatusTag>Registration open</StatusTag>
        </>
      }
      lead="Two weeks of reading and listening with the Tadoku community. Join with up to three languages."
      actions={
        <>
          <a className={buttonClassName()} href="#join">
            Join contest
          </a>
          <a className={buttonClassName({ variant: 'outline' })} href="#standings">
            View standings
          </a>
        </>
      }
    />
  )
}
`;

// src/catalog/examples/section-heading.profile.tsx
import { jsx as jsx96, jsxs as jsxs72 } from "react/jsx-runtime";
function Example36() {
  return /* @__PURE__ */ jsxs72("section", { "aria-labelledby": "activity-heading", children: [
    /* @__PURE__ */ jsx96(
      SectionHeading,
      {
        level: 2,
        id: "activity-heading",
        title: "Anton\u2019s activity",
        description: "Reading and listening recorded in 2026.",
        actions: /* @__PURE__ */ jsx96("a", { className: buttonClassName({ variant: "outline" }), href: "#activity-log", children: "View activity log" })
      }
    ),
    /* @__PURE__ */ jsx96("p", { children: "1,240.5 personal points across 18 entries." }),
    /* @__PURE__ */ jsxs72("section", { "aria-labelledby": "rhythm-heading", children: [
      /* @__PURE__ */ jsx96(
        SectionHeading,
        {
          level: 3,
          id: "rhythm-heading",
          title: "Immersion rhythm",
          actions: /* @__PURE__ */ jsx96("a", { className: buttonClassName({ variant: "link" }), href: "#scoring", children: "How scoring works" })
        }
      ),
      /* @__PURE__ */ jsx96("p", { children: "12 active days \xB7 Longest streak: 5 days" })
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/section-heading.profile.tsx
var section_heading_profile_default = `import { SectionHeading, buttonClassName } from 'paper-ui'

export default function Example() {
  return (
    <section aria-labelledby="activity-heading">
      <SectionHeading
        level={2}
        id="activity-heading"
        title="Anton\u2019s activity"
        description="Reading and listening recorded in 2026."
        actions={
          <a className={buttonClassName({ variant: 'outline' })} href="#activity-log">
            View activity log
          </a>
        }
      />
      <p>1,240.5 personal points across 18 entries.</p>
      <section aria-labelledby="rhythm-heading">
        <SectionHeading
          level={3}
          id="rhythm-heading"
          title="Immersion rhythm"
          actions={
            <a className={buttonClassName({ variant: 'link' })} href="#scoring">
              How scoring works
            </a>
          }
        />
        <p>12 active days \xB7 Longest streak: 5 days</p>
      </section>
    </section>
  )
}
`;

// src/catalog/page-structure.tsx
import { jsx as jsx97 } from "react/jsx-runtime";
var pageStructureFixtures = [
  fixture2({ id: "page-header.contest-detail", name: "Contest page header", description: "Back link, title, status tags, lead and page actions for one contest.", tags: ["page header", "status", "actions"], code: page_header_contest_detail_default, render: () => /* @__PURE__ */ jsx97(Example35, {}) }),
  fixture2({ id: "section-heading.profile", name: "Profile sections", description: "A level-two section with a description and action, and a nested level-three subsection.", tags: ["section heading", "outline", "actions"], code: section_heading_profile_default, render: () => /* @__PURE__ */ jsx97(Example36, {}) })
];
var pageHeaderSections = {
  overview: { heading: "Overview", content: ["PageHeader names a page with its only level-one heading and gathers the context and actions that apply to the whole page."] },
  whenToUse: { heading: "When to use", content: ["Use PageHeader at the top of every application page: a title, an optional lead sentence, an optional way back, status for the page\u2019s subject and the page\u2019s main actions."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for sections inside a page; use SectionHeading. Do not use it for a promotional hero whose layout is part of the page design, or for a page that is only an empty or error state; use EmptyState with headingLevel 1."] },
  choosingBetween: { heading: "Choosing between", content: ["PageHeader owns the page\u2019s h1. SectionHeading starts at h2 for regions within the page. Breadcrumb shows a hierarchy; the back link returns to the single place the reader most likely came from."] },
  anatomy: { heading: "Anatomy", content: ["In reading order: an optional back link with a leading chevron, the title, optional status tags beside it, an optional lead, optional supporting content such as a step indicator, then actions. Actions sit at the end of the title row when the text and actions fit, and wrap below the text when space runs out."] },
  recommendedExample: { heading: "Recommended example", content: ["The contest header returns to the official contests, names the round, shows that it is live and open for registration, and offers joining as the primary action with standings as the secondary one."] },
  variants: { heading: "Variants", content: ["Every part except the title is optional. Pass a lead when the title alone does not explain the page. Pass children for page-level supporting content that must stay inside the header."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["At every width, the text keeps its natural width and actions wrap below it when the whole row cannot fit. Status tags stay beside the title when they fit; otherwise they wrap below it. Long titles wrap rather than overflow, with individual words breaking only when necessary. The title keeps the page type role in both densities; density changes only the actions\u2019 control height."] },
  behavior: { heading: "Behavior", content: ["The back link, actions and any links in the content follow native link and button behavior in reading order. PageHeader has no interactive state of its own."] },
  contentGuidance: { heading: "Content guidance", content: ["Use the subject\u2019s name as the title, such as a contest or log title. Keep the lead to one or two sentences. Label the back link with its destination, such as Back to official contests. Put one default-variant action first when there is a primary task."] },
  accessibility: { heading: "Accessibility", content: ["Render exactly one PageHeader per page so the page has one h1. Status tags render outside the heading so the heading\u2019s name stays the subject\u2019s name. The chevron is decorative; the back link\u2019s text is its name."] },
  implementation: { heading: "Implementation", content: ["Import PageHeader from paper-ui. Pass renderLink to render the back link with your router, for example renderLink={({ href, ...props }) => <Link to={href} {...props} />}; the renderer must forward className and children. Without it a native link is rendered."] },
  apiReference: { heading: "API reference", content: ["PageHeaderProps accepts title, lead, status, actions, backLink ({ href, label }), renderLink, children and className."] },
  relatedPatterns: { heading: "Related patterns", content: ["Use StatusTag inside status, Button or buttonClassName links inside actions, and SectionHeading for each region that follows."] },
  migration: { heading: "Migration", content: ["Replace application page-header, page-lead and back-link markup and styles. Keep page-specific title typography, such as a display-size guide title, as an application class on PageHeader."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var sectionHeadingSections = {
  overview: { heading: "Overview", content: ["SectionHeading titles a region of a page at an explicit heading level, with an optional description, status and actions."] },
  whenToUse: { heading: "When to use", content: ["Use SectionHeading at the start of each named region of a page, such as Contest history with an All contests link or Activity log with a Filters toggle."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for the page title; PageHeader owns the h1. Do not use it for list-item titles inside a row or card, where heading spacing and actions would crowd the item."] },
  choosingBetween: { heading: "Choosing between", content: ["Choose the level from the document outline, not the visual size. Level 2 uses the section type role; levels 3 to 6 use the component type role."] },
  anatomy: { heading: "Anatomy", content: ["A heading with optional status tags beside it and an optional description below, followed by actions at the end of the row. Actions wrap below the text when the row is too narrow. The heading ends with 1rem of space; inside paper-stack the stack gap replaces it."] },
  recommendedExample: { heading: "Recommended example", content: ["Anton\u2019s activity is a level-two section with a description and an action. Immersion rhythm is a level-three subsection inside it, with a link to the scoring guide."] },
  variants: { heading: "Variants", content: ["The level selects the element and type role. Description, status and actions are independent and optional."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["At every width, the text keeps its natural width and actions wrap below it when the whole row cannot fit. Status tags stay beside the title when they fit; otherwise they wrap below it. Long titles wrap rather than overflow, with individual words breaking only when necessary. The heading\u2019s hierarchy does not change with density."] },
  behavior: { heading: "Behavior", content: ["SectionHeading has no interactive state. Actions keep native link and button behavior and follow the heading in the tab order."] },
  contentGuidance: { heading: "Content guidance", content: ["Use a short noun phrase for the title. Use the description for scope, such as a date range, not for instructions. Keep actions to the one or two tasks that apply to the whole section."] },
  accessibility: { heading: "Accessibility", content: ["Pass id and label the enclosing section with aria-labelledby so it becomes a named region. Do not skip levels. Status tags stay outside the heading\u2019s accessible name."] },
  implementation: { heading: "Implementation", content: ["Import SectionHeading from paper-ui and pass level explicitly; TypeScript rejects level 1."] },
  apiReference: { heading: "API reference", content: ["SectionHeadingProps accepts level (2\u20136), title, id, description, status, actions and className."] },
  relatedPatterns: { heading: "Related patterns", content: ["Use PageHeader above it, StatusTag for status, and the Filter bar pattern for a Filters toggle in actions."] },
  migration: { heading: "Migration", content: ["Replace application section-heading wrappers that pair a heading with a link or buttons. Remove heading font overrides that only reproduce the section or component type roles."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var pageStructureDocuments = [
  componentDocument3({
    id: "component.page-header",
    route: "/components/page-structure/page-header",
    name: "PageHeader",
    category: "page-structure",
    summary: "Names a page with its only h1, with an optional back link, lead, status and page actions.",
    keywords: ["page header", "title", "h1", "back link", "lead", "actions"],
    sourcePath: "src/components/page-structure/PageHeader/PageHeader.tsx",
    fixtureIds: ["page-header.contest-detail"],
    behaviorTestIds: ["page-header.single-h1", "page-header.status-outside-name", "page-header.back-link-renderer", "page-header.actions-keyboard"],
    sections: pageHeaderSections,
    commonMistakes: ["Rendering a second h1 for a page section.", "Placing status text inside the heading, which changes its accessible name."],
    api: {
      react: ["PageHeader"],
      props: [
        { name: "title", type: "ReactNode", required: true, description: "The page\u2019s only level-one heading." },
        { name: "lead", type: "ReactNode", description: "One or two sentences that explain the page." },
        { name: "status", type: "ReactNode", description: "StatusTags beside the title, outside its accessible name." },
        { name: "actions", type: "ReactNode", description: "Page-level links or buttons at the end of the title row." },
        { name: "backLink", type: "{ href: string; label: string }", description: "The destination the reader most likely came from." },
        { name: "renderLink", type: "NavigationLinkRenderer", defaultValue: "native <a>", description: "Renders the back link with the application router; forward className and children." },
        { name: "children", type: "ReactNode", description: "Supporting content after the lead, such as a step indicator." }
      ],
      cssClasses: ["paper-page-header", "paper-page-header__*"],
      publicTypes: ["PageHeaderProps", "NavigationLinkRenderer"],
      defaults: ["Renders an h1 with the page type role and 2rem of space after the header."],
      invalidCombinations: ["Use one PageHeader per page."]
    },
    dependencies: ["component.section-heading", "component.status-tag", "component.breadcrumb"],
    legacy: ["paper-playground page-header, page-lead and page-back-link"]
  }),
  componentDocument3({
    id: "component.section-heading",
    route: "/components/page-structure/section-heading",
    name: "SectionHeading",
    category: "page-structure",
    summary: "Titles a page region at an explicit heading level, with optional description, status and actions.",
    keywords: ["section heading", "heading level", "h2", "h3", "outline", "actions"],
    sourcePath: "src/components/page-structure/SectionHeading/SectionHeading.tsx",
    fixtureIds: ["section-heading.profile", "description-list.contest-facts"],
    behaviorTestIds: ["section-heading.level", "section-heading.region-label", "section-heading.status-outside-name", "section-heading.actions-keyboard"],
    sections: sectionHeadingSections,
    commonMistakes: ["Choosing a level for its size instead of its place in the outline.", "Forgetting aria-labelledby, which leaves the section unnamed."],
    api: {
      react: ["SectionHeading"],
      props: [
        { name: "level", type: "2 | 3 | 4 | 5 | 6", required: true, description: "Heading element. Level 2 uses the section type role; 3\u20136 use the component role." },
        { name: "title", type: "ReactNode", required: true, description: "Short name of the region." },
        { name: "id", type: "string", description: "Applied to the heading so the section can use aria-labelledby." },
        { name: "description", type: "ReactNode", description: "Muted scope line below the title." },
        { name: "status", type: "ReactNode", description: "StatusTags beside the title, outside its accessible name." },
        { name: "actions", type: "ReactNode", description: "Links or buttons that apply to the whole section." }
      ],
      cssClasses: ["paper-section-heading", "paper-section-heading__*"],
      publicTypes: ["SectionHeadingProps"],
      defaults: ["1rem of space after the heading; paper-stack gaps replace it."],
      invalidCombinations: ["level 1 is rejected; use PageHeader."]
    },
    dependencies: ["component.page-header", "component.status-tag"],
    legacy: ["paper-playground section-heading and records-section-heading"]
  })
];

// src/catalog/examples/empty-state.first-activity.tsx
import { jsx as jsx98, jsxs as jsxs73 } from "react/jsx-runtime";
function Example37() {
  return /* @__PURE__ */ jsxs73(Surface, { as: "section", "aria-labelledby": "year-heading", children: [
    /* @__PURE__ */ jsx98(SectionHeading, { level: 2, id: "year-heading", title: "Anton\u2019s activity" }),
    /* @__PURE__ */ jsx98(
      EmptyState,
      {
        variant: "plain",
        headingLevel: 3,
        title: "No activity recorded in 2026",
        description: "A few pages or minutes are a good place to start. Your personal record grows with every entry, whether you join a contest or read on your own.",
        actions: /* @__PURE__ */ jsx98("a", { className: buttonClassName(), href: "#log-activity", children: "Log activity" })
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/empty-state.first-activity.tsx
var empty_state_first_activity_default = `import { EmptyState, SectionHeading, Surface, buttonClassName } from 'paper-ui'

export default function Example() {
  return (
    <Surface as="section" aria-labelledby="year-heading">
      <SectionHeading level={2} id="year-heading" title="Anton\u2019s activity" />
      <EmptyState
        variant="plain"
        headingLevel={3}
        title="No activity recorded in 2026"
        description="A few pages or minutes are a good place to start. Your personal record grows with every entry, whether you join a contest or read on your own."
        actions={
          <a className={buttonClassName()} href="#log-activity">
            Log activity
          </a>
        }
      />
    </Surface>
  )
}
`;

// src/catalog/examples/empty-state.no-results.tsx
import { useStore as useStore22 } from "@tanstack/react-form";
import { useRef as useRef8 } from "react";
import { jsx as jsx99, jsxs as jsxs74 } from "react/jsx-runtime";
var contests = ["2026 Round 5", "Japanese novel club", "Autumn in French"];
function Example38() {
  const search = useRef8(null);
  const methods = usePaperForm({ defaultValues: { query: "Klingon" } });
  const query = useStore22(methods.store, (state) => state.values["query"]);
  const matches = contests.filter(
    (title) => title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  );
  const clearSearch = () => {
    methods.setFieldValue("query", "");
    search.current?.focus();
  };
  return /* @__PURE__ */ jsxs74("div", { className: "paper-stack", style: { maxWidth: "40rem" }, children: [
    /* @__PURE__ */ jsx99(methods.AppForm, { children: /* @__PURE__ */ jsx99("form", { role: "search", onSubmit: (event) => event.preventDefault(), children: /* @__PURE__ */ jsx99(methods.AppField, { name: "query", children: (field) => /* @__PURE__ */ jsx99(field.Input, { label: "Find a contest", type: "search", ref: search }) }) }) }),
    /* @__PURE__ */ jsxs74("p", { role: "status", children: [
      matches.length,
      " ",
      matches.length === 1 ? "contest" : "contests"
    ] }),
    matches.length ? /* @__PURE__ */ jsx99("ul", { children: matches.map((title) => /* @__PURE__ */ jsx99("li", { children: title }, title)) }) : /* @__PURE__ */ jsx99(
      EmptyState,
      {
        headingLevel: 2,
        title: `No contests match \u201C${query.trim()}\u201D.`,
        description: "Try another title or language, or clear the search to see every contest.",
        actions: /* @__PURE__ */ jsx99(Button, { variant: "outline", onClick: clearSearch, children: "Clear search" })
      }
    )
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/empty-state.no-results.tsx
var empty_state_no_results_default = `import { useStore } from '@tanstack/react-form'
import { Button, EmptyState, usePaperForm } from 'paper-ui'
import { useRef } from 'react'

const contests = ['2026 Round 5', 'Japanese novel club', 'Autumn in French']

export default function Example() {
  const search = useRef<HTMLInputElement>(null)
  const methods = usePaperForm({ defaultValues: { query: 'Klingon' } })
  const query = useStore(methods.store, state => state.values['query'])
  const matches = contests.filter(title =>
    title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  )
  const clearSearch = () => {
    methods.setFieldValue('query', '')
    search.current?.focus()
  }

  return (
    <div className="paper-stack" style={{ maxWidth: '40rem' }}>
      <methods.AppForm>
        <form role="search" onSubmit={event => event.preventDefault()}>
          <methods.AppField name="query">
            {field => (
              <field.Input label="Find a contest" type="search" ref={search} />
            )}
          </methods.AppField>
        </form>
      </methods.AppForm>
      <p role="status">
        {matches.length} {matches.length === 1 ? 'contest' : 'contests'}
      </p>
      {matches.length ? (
        <ul>
          {matches.map(title => (
            <li key={title}>{title}</li>
          ))}
        </ul>
      ) : (
        <EmptyState
          headingLevel={2}
          title={\`No contests match \u201C\${query.trim()}\u201D.\`}
          description="Try another title or language, or clear the search to see every contest."
          actions={
            <Button variant="outline" onClick={clearSearch}>
              Clear search
            </Button>
          }
        />
      )}
    </div>
  )
}
`;

// src/catalog/examples/status-tag.tones.tsx
import { jsx as jsx100, jsxs as jsxs75 } from "react/jsx-runtime";
function Example39() {
  return /* @__PURE__ */ jsxs75("div", { className: "paper-cluster", children: [
    /* @__PURE__ */ jsx100(StatusTag, { children: "Unlisted" }),
    /* @__PURE__ */ jsx100(StatusTag, { tone: "information", children: "Scheduled" }),
    /* @__PURE__ */ jsx100(StatusTag, { tone: "success", children: "Live now" }),
    /* @__PURE__ */ jsx100(StatusTag, { tone: "warning", children: "Draft" }),
    /* @__PURE__ */ jsx100(StatusTag, { tone: "danger", children: "Unavailable" })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/status-tag.tones.tsx
var status_tag_tones_default = `import { StatusTag } from 'paper-ui'

export default function Example() {
  return (
    <div className="paper-cluster">
      <StatusTag>Unlisted</StatusTag>
      <StatusTag tone="information">Scheduled</StatusTag>
      <StatusTag tone="success">Live now</StatusTag>
      <StatusTag tone="warning">Draft</StatusTag>
      <StatusTag tone="danger">Unavailable</StatusTag>
    </div>
  )
}
`;

// src/catalog/status-tag-and-empty-state.tsx
import { jsx as jsx101 } from "react/jsx-runtime";
var statusTagAndEmptyStateFixtures = [
  fixture2({ id: "status-tag.tones", name: "Status tones", description: "Every tone with the visible text that carries its meaning.", tags: ["status", "tag", "tones"], code: status_tag_tones_default, render: () => /* @__PURE__ */ jsx101(Example39, {}) }),
  fixture2({ id: "empty-state.no-results", name: "Search without results", description: "A standalone empty state whose action clears the search and returns focus to it.", tags: ["empty", "search", "recovery"], code: empty_state_no_results_default, render: () => /* @__PURE__ */ jsx101(Example38, {}) }),
  fixture2({ id: "empty-state.first-activity", name: "Empty year", description: "A plain empty state inside an existing section, offering the first entry.", tags: ["empty", "plain", "first use"], code: empty_state_first_activity_default, render: () => /* @__PURE__ */ jsx101(Example37, {}) })
];
var statusTagSections = {
  overview: { heading: "Overview", content: ["StatusTag labels the current state of a contest, record or service with short visible text and a tone."] },
  whenToUse: { heading: "When to use", content: ["Use StatusTag beside a title or in a row to show a state that is true right now, such as Live now, Unlisted, Draft or Published."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for messages that need explanation or action; use Flash. Do not use it for counts, categories or filters, and do not make it interactive."] },
  choosingBetween: { heading: "Choosing between", content: ["StatusTag is a static label. Flash explains a change or problem. Toast confirms an action that just happened."] },
  anatomy: { heading: "Anatomy", content: ["A single line of text inside a quiet rule. Tone colours the text and, except for neutral, the rule."] },
  recommendedExample: { heading: "Recommended example", content: ["The example shows every tone with a realistic Tadoku state. Read each label without its colour: the text alone must still carry the meaning."] },
  variants: { heading: "Variants", content: ["Neutral is the default for descriptive states such as Unlisted. Information marks scheduled or upcoming states, success marks a live or healthy state, warning marks a state that needs attention soon, and danger marks an unavailable or failed state."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Tags do not wrap internally; a row of tags wraps between tags. Tones use the semantic status colours of the active light or dark theme."] },
  behavior: { heading: "Behavior", content: ["StatusTag has no keyboard behavior and is not a live region. Announce a change in status through the screen\u2019s own status message."] },
  contentGuidance: { heading: "Content guidance", content: ["Use one to three words in sentence case, such as Live now or Registration closed. Prefer the reader\u2019s perspective over system terms."] },
  accessibility: { heading: "Accessibility", content: ['Visible text is required by the API, so status never relies on colour alone. Do not add role="status" to a static tag.'] },
  implementation: { heading: "Implementation", content: ["Import StatusTag from paper-ui and pass the label as its text child. Non-React consumers can use the paper-status-tag class with one paper-status-tag--{tone} modifier."] },
  apiReference: { heading: "API reference", content: ["StatusTagProps accepts tone (neutral, information, success, warning or danger; default neutral), the required string children and className."] },
  relatedPatterns: { heading: "Related patterns", content: ["Pass StatusTag to the status slot of PageHeader or SectionHeading, or place it in a Table cell or DescriptionList value."] },
  migration: { heading: "Migration", content: ["Replace application status-tag spans and the legacy admin StatusBadge colour classes with StatusTag tones: Draft as warning, Scheduled as information and Published as success."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var emptyStateSections = {
  overview: { heading: "Overview", content: ["EmptyState explains why there is nothing to show and offers the next useful step."] },
  whenToUse: { heading: "When to use", content: ["Use EmptyState when a collection has no items yet, when filters or a search exclude every item, when data could not be loaded, or when a page cannot be shown, such as a missing contest."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for loading; use Loading. Do not use it for a failure that leaves the rest of the content usable; use Flash. Inside Table, prefer its emptyMessage while the table structure still helps."] },
  choosingBetween: { heading: "Choosing between", content: ["Use the surface variant when the state stands alone in the page. Use plain inside an existing Surface, sheet or dialog that already provides a boundary."] },
  anatomy: { heading: "Anatomy", content: ["A heading, an optional description and optional actions. The heading level is required because the state replaces content at a particular place in the outline."] },
  recommendedExample: { heading: "Recommended example", content: ["Search without results clears the search and returns focus to the search field. Empty year places a plain state inside a profile section and offers the first entry."] },
  variants: { heading: "Variants", content: ["surface (default) adds paper, a rule and padding. plain keeps only vertical spacing. headingLevel 1 uses the page type role, 2 the section role and 3 to 6 the component role."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ['Content stacks and actions wrap at narrow widths; the description keeps a readable measure. Pass role="alert" for a failure the reader caused or must notice, and aria-live="polite" when results can become empty while the reader types.'] },
  behavior: { heading: "Behavior", content: ["Actions follow native link and button behavior. After an action removes the content, pass headingRef and focus the heading so the reader learns what happened; the heading is focusable only when headingRef is supplied."] },
  contentGuidance: { heading: "Content guidance", content: ["Title the state plainly, such as No activity yet or This log couldn\u2019t be loaded. Use the description for the reason and what will make content appear. Label the action with the outcome, such as Clear filters or Log activity."] },
  accessibility: { heading: "Accessibility", content: ["Choose headingLevel from the surrounding outline. Use role or aria-live only for states that appear in response to the reader. Return focus to the control that can change the result after clearing."] },
  implementation: { heading: "Implementation", content: ["Import EmptyState from paper-ui. The screen decides which state applies and supplies the actions; EmptyState does not fetch, retry or check permissions."] },
  apiReference: { heading: "API reference", content: ["EmptyStateProps accepts title, headingLevel (1\u20136), description, actions, variant (surface or plain), headingRef and native div attributes such as role and aria-live."] },
  relatedPatterns: { heading: "Related patterns", content: ["The Filter bar pattern uses a plain EmptyState for filtered results. Use PageHeader instead when the page still has content."] },
  migration: { heading: "Migration", content: ["Replace application empty-state boxes and their heading and paragraph overrides. Move any focus target to headingRef."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var statusTagAndEmptyStateDocuments = [
  componentDocument3({
    id: "component.status-tag",
    route: "/components/feedback/status-tag",
    name: "StatusTag",
    category: "feedback",
    summary: "Labels the current state of a contest, record or service with visible text and a status tone.",
    keywords: ["status", "tag", "badge", "label", "tone", "live"],
    sourcePath: "src/components/feedback/StatusTag/StatusTag.tsx",
    fixtureIds: ["status-tag.tones"],
    behaviorTestIds: ["status-tag.visible-text", "status-tag.not-live-region", "status-tag.recipe"],
    sections: statusTagSections,
    commonMistakes: ["Using colour without a meaningful label.", "Turning a tag into a filter or button."],
    api: {
      react: ["StatusTag"],
      props: [
        { name: "children", type: "string", required: true, description: "Visible status text. Required so status never relies on colour alone." },
        { name: "tone", type: '"neutral" | "information" | "success" | "warning" | "danger"', defaultValue: "neutral", description: "Maps to the semantic status colours of the active theme." }
      ],
      cssClasses: ["paper-status-tag", "paper-status-tag--neutral", "paper-status-tag--information", "paper-status-tag--success", "paper-status-tag--warning", "paper-status-tag--danger"],
      publicTypes: ["StatusTagProps", "StatusTagTone"],
      defaults: ["tone=neutral"],
      invalidCombinations: ["A tag without text does not compile."]
    },
    dependencies: ["component.flash"],
    legacy: ["paper-playground status-tag", "admin StatusBadge"]
  }),
  componentDocument3({
    id: "component.empty-state",
    route: "/components/feedback/empty-state",
    name: "EmptyState",
    category: "feedback",
    summary: "Explains why there is nothing to show and offers the next useful step.",
    keywords: ["empty", "no results", "not found", "error", "zero state", "first use"],
    sourcePath: "src/components/feedback/EmptyState/EmptyState.tsx",
    fixtureIds: ["empty-state.no-results", "empty-state.first-activity", "pattern.filter-bar"],
    behaviorTestIds: ["empty-state.heading-level", "empty-state.actions", "empty-state.alert", "empty-state.focus-heading"],
    sections: emptyStateSections,
    commonMistakes: ["Using a fixed heading level that breaks the page outline.", "Leaving focus on an action that disappears after clearing."],
    api: {
      react: ["EmptyState"],
      props: [
        { name: "title", type: "ReactNode", required: true, description: "Plain statement of the state." },
        { name: "headingLevel", type: "1 | 2 | 3 | 4 | 5 | 6", required: true, description: "Place in the outline; 1 when the state replaces the page." },
        { name: "description", type: "ReactNode", description: "Reason and what will make content appear." },
        { name: "actions", type: "ReactNode", description: "Recovery or first-use actions." },
        { name: "variant", type: '"surface" | "plain"', defaultValue: "surface", description: "Standalone paper surface, or plain inside an existing container." },
        { name: "headingRef", type: "Ref<HTMLHeadingElement>", description: "Makes the heading programmatically focusable for focus moves." },
        { name: "role / aria-live / className", type: "HTMLAttributes<HTMLDivElement>", description: 'Announce failures with role="alert" or result changes with aria-live.' }
      ],
      cssClasses: ["paper-empty-state", "paper-empty-state--surface", "paper-empty-state--plain", "paper-empty-state__*"],
      publicTypes: ["EmptyStateProps"],
      defaults: ["variant=surface"],
      invalidCombinations: ["headingLevel is required."]
    },
    dependencies: ["component.loading", "component.flash", "component.table"],
    legacy: ["paper-playground empty-state, records-empty-year and records-submission-empty"]
  })
];

// src/catalog/examples/description-list.contest-facts.tsx
import { jsx as jsx102, jsxs as jsxs76 } from "react/jsx-runtime";
function Example40() {
  return /* @__PURE__ */ jsxs76("div", { className: "paper-stack", style: { maxWidth: "36rem" }, children: [
    /* @__PURE__ */ jsxs76("section", { "aria-labelledby": "dates-heading", children: [
      /* @__PURE__ */ jsx102(SectionHeading, { level: 3, id: "dates-heading", title: "Dates & registration" }),
      /* @__PURE__ */ jsx102(
        DescriptionList,
        {
          items: [
            { label: "Round begins", value: "Sep 1, 2026, 12:00 AM UTC" },
            { label: "Round ends", value: "Sep 14, 2026, 11:59 PM UTC" },
            { label: "Joining this round", value: "Open until Sep 7, 2026, 11:59 PM UTC" }
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs76("section", { "aria-labelledby": "current-heading", children: [
      /* @__PURE__ */ jsx102(SectionHeading, { level: 3, id: "current-heading", title: "Current contest" }),
      /* @__PURE__ */ jsx102(
        DescriptionList,
        {
          layout: "inline",
          items: [
            { label: "Period", value: "Sep 1 \u2013 14, 2026" },
            { label: "Registration deadline", value: "Sep 7, 2026" },
            { label: "Submitted entries", value: "42" }
          ]
        }
      )
    ] })
  ] });
}

// example-source:/app/packages/paper-ui/src/catalog/examples/description-list.contest-facts.tsx
var description_list_contest_facts_default = `import { DescriptionList, SectionHeading } from 'paper-ui'

export default function Example() {
  return (
    <div className="paper-stack" style={{ maxWidth: '36rem' }}>
      <section aria-labelledby="dates-heading">
        <SectionHeading level={3} id="dates-heading" title="Dates & registration" />
        <DescriptionList
          items={[
            { label: 'Round begins', value: 'Sep 1, 2026, 12:00 AM UTC' },
            { label: 'Round ends', value: 'Sep 14, 2026, 11:59 PM UTC' },
            { label: 'Joining this round', value: 'Open until Sep 7, 2026, 11:59 PM UTC' },
          ]}
        />
      </section>
      <section aria-labelledby="current-heading">
        <SectionHeading level={3} id="current-heading" title="Current contest" />
        <DescriptionList
          layout="inline"
          items={[
            { label: 'Period', value: 'Sep 1 \u2013 14, 2026' },
            { label: 'Registration deadline', value: 'Sep 7, 2026' },
            { label: 'Submitted entries', value: '42' },
          ]}
        />
      </section>
    </div>
  )
}
`;

// src/catalog/examples/stat-grid.reading-year.tsx
import { jsx as jsx103 } from "react/jsx-runtime";
function Example41() {
  return /* @__PURE__ */ jsx103(
    StatGrid,
    {
      stats: [
        { label: "Personal score", value: "1,240.5" },
        { label: "Entries", value: "18", detail: "12 active days" },
        { label: "Languages", value: "2", detail: "Japanese leads" },
        { label: "Contests", value: "1" }
      ]
    }
  );
}

// example-source:/app/packages/paper-ui/src/catalog/examples/stat-grid.reading-year.tsx
var stat_grid_reading_year_default = "import { StatGrid } from 'paper-ui'\n\nexport default function Example() {\n  return (\n    <StatGrid\n      stats={[\n        { label: 'Personal score', value: '1,240.5' },\n        { label: 'Entries', value: '18', detail: '12 active days' },\n        { label: 'Languages', value: '2', detail: 'Japanese leads' },\n        { label: 'Contests', value: '1' },\n      ]}\n    />\n  )\n}\n";

// src/catalog/stat-grid-and-description-list.tsx
import { jsx as jsx104 } from "react/jsx-runtime";
var statGridAndDescriptionListFixtures = [
  fixture2({ id: "stat-grid.reading-year", name: "Reading year figures", description: "Four key figures for one profile year, two with supporting detail.", tags: ["statistics", "profile", "figures"], code: stat_grid_reading_year_default, render: () => /* @__PURE__ */ jsx104(Example41, {}) }),
  fixture2({ id: "description-list.contest-facts", name: "Contest facts", description: "Stacked dates beside an inline summary of the current contest.", tags: ["facts", "description list", "contest"], code: description_list_contest_facts_default, render: () => /* @__PURE__ */ jsx104(Example40, {}) })
];
var statGridSections = {
  overview: { heading: "Overview", content: ["StatGrid presents a few key figures as labelled values in a responsive grid."] },
  whenToUse: { heading: "When to use", content: ["Use StatGrid for two to six headline figures that summarize a page or section, such as a profile year\u2019s personal score, entries, languages and contests."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for facts whose values are sentences or dates; use DescriptionList. Do not use it to compare many records; use Table."] },
  choosingBetween: { heading: "Choosing between", content: ["StatGrid emphasises the value. DescriptionList emphasises the relationship between a label and a longer value."] },
  anatomy: { heading: "Anatomy", content: ["A description list. Each figure has a muted label, a large tabular value and optional detail read with the value, separated from its neighbour by a quiet leading rule."] },
  recommendedExample: { heading: "Recommended example", content: ["Reading year figures shows four profile figures; Entries and Languages add detail that explains them."] },
  variants: { heading: "Variants", content: ["There is one appearance. Detail is optional per figure."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Columns are at least 9rem wide and fill the available width, so four figures become two or one column on narrow screens without breakpoints. Values wrap anywhere rather than overflow."] },
  behavior: { heading: "Behavior", content: ["StatGrid is static and has no keyboard behavior."] },
  contentGuidance: { heading: "Content guidance", content: ["Use short noun labels and formatted values with their units in the label or detail. Show zero rather than hiding a figure when zero is meaningful."] },
  accessibility: { heading: "Accessibility", content: ["Each label is a term and each value, with its detail, is its definition, so assistive technology reads them as pairs."] },
  implementation: { heading: "Implementation", content: ["Import StatGrid from paper-ui and pass formatted values; StatGrid does not format numbers."] },
  apiReference: { heading: "API reference", content: ["StatGridProps accepts stats (an array of { label, value, detail? }) and className."] },
  relatedPatterns: { heading: "Related patterns", content: ["Pair StatGrid with SectionHeading, and with HeatmapChart or Table for the detail behind a figure."] },
  migration: { heading: "Migration", content: ["Replace application stat-grid and stat markup, including divs that paired labels and values without list semantics."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var descriptionListSections = {
  overview: { heading: "Overview", content: ["DescriptionList presents named facts about one subject as term and description pairs."] },
  whenToUse: { heading: "When to use", content: ["Use DescriptionList for the facts of one contest, log or service: dates, eligibility, moderators or status."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it for headline figures; use StatGrid. Do not use it for records that share columns; use Table. Do not use it for unlabelled metadata lines such as Japanese \xB7 Reading \xB7 48 pages."] },
  choosingBetween: { heading: "Choosing between", content: ["Use stacked when values are long or the list sits in a narrow aside. Use inline for short values that readers scan down a column."] },
  anatomy: { heading: "Anatomy", content: ["Each pair is grouped. Stacked places a muted label above its value. Inline places the label at the start and the value at the end of one ruled row."] },
  recommendedExample: { heading: "Recommended example", content: ["Contest facts shows round dates stacked and a current-contest summary inline."] },
  variants: { heading: "Variants", content: ["layout is stacked (default) or inline. Arrange stacked pairs in columns with an application grid on className when the page needs it."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Labels and values wrap anywhere. Inline rows wrap the value below the label when both do not fit."] },
  behavior: { heading: "Behavior", content: ["DescriptionList has no keyboard behavior; links and StatusTags in values keep their own behavior."] },
  contentGuidance: { heading: "Content guidance", content: ["Use short, parallel labels. Keep each value to the answer, such as Japanese, Korean only, and put explanations in nearby text."] },
  accessibility: { heading: "Accessibility", content: ["Pairs render as dt and dd inside a grouping div, which assistive technology reads as term and definition."] },
  implementation: { heading: "Implementation", content: ["Import DescriptionList from paper-ui and pass items in display order."] },
  apiReference: { heading: "API reference", content: ["DescriptionListProps accepts items (an array of { label, value }), layout (stacked or inline) and className."] },
  relatedPatterns: { heading: "Related patterns", content: ["Use StatusTag in a value for a service or contest state, and SectionHeading to name the list."] },
  migration: { heading: "Migration", content: ["Replace application dl styles for contest facts, eligibility and summary rows."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var statGridAndDescriptionListDocuments = [
  componentDocument3({
    id: "component.stat-grid",
    route: "/components/data-display/stat-grid",
    name: "StatGrid",
    category: "data-display",
    summary: "Presents a few key figures as labelled values in a responsive grid.",
    keywords: ["statistics", "stats", "figures", "metrics", "summary", "numbers"],
    sourcePath: "src/components/data-display/StatGrid/StatGrid.tsx",
    fixtureIds: ["stat-grid.reading-year"],
    behaviorTestIds: ["stat-grid.description-list", "stat-grid.detail"],
    sections: statGridSections,
    commonMistakes: ["Using StatGrid for long values or dates.", "Hiding units the reader needs to interpret a value."],
    api: {
      react: ["StatGrid"],
      props: [
        { name: "stats", type: "readonly StatGridItem[]", required: true, description: "Figures in display order: { label, value, detail? }." },
        { name: "className", type: "string", description: "Application spacing or rules around the grid." }
      ],
      cssClasses: ["paper-stat-grid", "paper-stat-grid__*"],
      publicTypes: ["StatGridProps", "StatGridItem"],
      defaults: ["Columns are at least 9rem wide."],
      invalidCombinations: ["Values are not formatted by StatGrid."]
    },
    dependencies: ["component.description-list"],
    legacy: ["paper-playground stat-grid, home-ledger-stats, records-profile-stats and records-admin-stats"]
  }),
  componentDocument3({
    id: "component.description-list",
    route: "/components/data-display/description-list",
    name: "DescriptionList",
    category: "data-display",
    summary: "Presents named facts about one subject as grouped term and description pairs.",
    keywords: ["description list", "facts", "key value", "dl", "details"],
    sourcePath: "src/components/data-display/DescriptionList/DescriptionList.tsx",
    fixtureIds: ["description-list.contest-facts"],
    behaviorTestIds: ["description-list.structure", "description-list.linked-values"],
    sections: descriptionListSections,
    commonMistakes: ["Using a description list for unlabelled metadata.", "Choosing inline for long values that then wrap awkwardly."],
    api: {
      react: ["DescriptionList"],
      props: [
        { name: "items", type: "readonly DescriptionListItem[]", required: true, description: "Facts in display order: { label, value }." },
        { name: "layout", type: '"stacked" | "inline"', defaultValue: "stacked", description: "Label above value, or label and value on one ruled row." },
        { name: "className", type: "string", description: "Application grid or spacing, such as two stacked columns." }
      ],
      cssClasses: ["paper-description-list", "paper-description-list--stacked", "paper-description-list--inline", "paper-description-list__*"],
      publicTypes: ["DescriptionListProps", "DescriptionListItem"],
      defaults: ["layout=stacked"],
      invalidCombinations: []
    },
    dependencies: ["component.stat-grid", "component.table"],
    legacy: ["paper-playground contest-facts, contest-eligibility, records-admin-contest and records-activity-mix"]
  })
];

// src/catalog/page-composition.tsx
import { jsx as jsx105 } from "react/jsx-runtime";
var pageCompositionFixtures = [
  ...pageStructureFixtures,
  ...statusTagAndEmptyStateFixtures,
  ...statGridAndDescriptionListFixtures,
  fixture2({ id: "pattern.filter-bar", name: "Standings filter bar", description: "Collapsible filters, applied-filter summary, result count and a filtered empty state.", tags: ["filters", "pattern", "standings"], code: pattern_filter_bar_default, render: () => /* @__PURE__ */ jsx105(FilterBarExample, {}) })
];
var appliedFiltersSections = {
  overview: { heading: "Overview", content: ["AppliedFilters summarizes the filters narrowing a result and clears them in one action."] },
  whenToUse: { heading: "When to use", content: ["Use AppliedFilters below a collapsible filter panel so readers can see and reset what still narrows the results after the panel closes."] },
  whenNotToUse: { heading: "When not to use", content: ["Do not use it when every filter field stays visible beside the results; a Clear filters button next to the fields is enough. Do not use it for navigation between collections; use Tabbar."] },
  choosingBetween: { heading: "Choosing between", content: ["AppliedFilters is a summary with one reset action. TagsInput edits a list of values."] },
  anatomy: { heading: "Anatomy", content: ["A named group with the applied filter labels joined in one line, followed by a link-style Clear all button. It renders nothing when no filter applies."] },
  recommendedExample: { heading: "Recommended example", content: ["Open Filters, choose Spanish and close the panel: the summary keeps Spanish visible and the count updates. Clear all resets the filters and returns focus to the Filters toggle."] },
  variants: { heading: "Variants", content: ["There is one appearance. label and clearLabel localize the group name and action."] },
  statesAndAdaptation: { heading: "States and adaptation", content: ["Labels wrap anywhere; the action wraps below them at narrow widths."] },
  behavior: { heading: "Behavior", content: ["Clear all calls onClear. The screen resets its filter state and moves focus to the filter toggle or first field, because the summary disappears once nothing is applied."] },
  contentGuidance: { heading: "Content guidance", content: ["Use the value a reader chose, such as Japanese, and prefix free text with its field, such as Name: Anton."] },
  accessibility: { heading: "Accessibility", content: ['The group is named Applied filters. Announce result counts in a separate role="status" element owned by the screen.'] },
  implementation: { heading: "Implementation", content: ["Import AppliedFilters from paper-ui. Derive filters from the same form state that filters the results."] },
  apiReference: { heading: "API reference", content: ["AppliedFiltersProps accepts filters (readonly string[]), onClear, label (default Applied filters), clearLabel (default Clear all) and className."] },
  relatedPatterns: { heading: "Related patterns", content: ["The Filter bar pattern composes AppliedFilters with a Filters toggle, the filter panel, a result count and EmptyState."] },
  migration: { heading: "Migration", content: ["Replace application applied-filter summaries that join labels and add a Clear all link button."] },
  lifecycle: { heading: "Lifecycle", content: [EXPERIMENTAL_LIFECYCLE] }
};
var pageCompositionDocuments = [
  ...pageStructureDocuments,
  ...statusTagAndEmptyStateDocuments,
  ...statGridAndDescriptionListDocuments,
  componentDocument3({
    id: "component.applied-filters",
    route: "/components/forms/applied-filters",
    name: "AppliedFilters",
    category: "forms",
    summary: "Summarizes the filters narrowing a result and clears them in one action.",
    keywords: ["filters", "applied filters", "clear all", "summary", "search"],
    sourcePath: "src/components/forms/AppliedFilters/AppliedFilters.tsx",
    fixtureIds: ["pattern.filter-bar"],
    behaviorTestIds: ["applied-filters.empty", "applied-filters.group-name", "applied-filters.clear", "applied-filters.localized"],
    sections: appliedFiltersSections,
    commonMistakes: ["Leaving focus on Clear all, which disappears after clearing.", "Showing a summary next to filter fields that are already visible."],
    api: {
      react: ["AppliedFilters"],
      props: [
        { name: "filters", type: "readonly string[]", required: true, description: "Readable labels of the applied filters. Empty renders nothing." },
        { name: "onClear", type: "() => void", required: true, description: "Reset every filter and move focus to the toggle or first field." },
        { name: "label", type: "string", defaultValue: "Applied filters", description: "Accessible name of the group." },
        { name: "clearLabel", type: "string", defaultValue: "Clear all", description: "Text of the reset action." }
      ],
      cssClasses: ["paper-applied-filters", "paper-applied-filters__list"],
      publicTypes: ["AppliedFiltersProps"],
      defaults: ["label=Applied filters", "clearLabel=Clear all"],
      invalidCombinations: []
    },
    dependencies: ["pattern.filter-bar", "component.button"],
    legacy: ["paper-playground records-applied-filters and standings-filter-summary"]
  })
];
var pageCompositionPatternDocuments = [
  defineCatalogDocument({
    id: "pattern.filter-bar",
    route: "/patterns/filter-bar",
    name: "Filter bar",
    kind: "pattern",
    category: "patterns",
    aliases: [],
    summary: "Narrows a list or board with collapsible filters, a visible summary, a live result count and a filtered empty state.",
    keywords: ["filters", "filter bar", "search", "standings", "activity log"],
    lifecycle: "Experimental",
    reviewDate: REVIEW_DATE7,
    sourcePath: "src/catalog/page-composition.tsx",
    packageVersion: "0.1.0",
    guidance: {
      whenToUse: ["Use when readers narrow a long list or board by a few fields, such as standings by language and activity or an activity log by period and title."],
      whenNotToUse: ["When one search field or two selects fit beside the list, keep them visible with a Clear filters button and skip the toggle and summary.", "Do not use filters to switch between collections with their own addresses; use Tabbar."],
      content: ["Order: a Filters toggle in the section heading\u2019s actions, the filter panel, AppliedFilters with the result count, then the results or a plain EmptyState.", "The toggle reads Filters, or Filters (n) when n filters apply, and uses aria-expanded and aria-controls for the panel. Keep the panel mounted while collapsed so selections persist.", "Filters apply as fields change. AppliedFilters stays visible while the panel is collapsed; Clear all and the empty state\u2019s Clear filters reset every field and return focus to the toggle.", 'Announce the result count in a role="status" element, such as 2 of 5 participants.'],
      commonMistakes: ["Hiding which filters still apply after the panel closes.", "Losing focus when the Clear all button disappears.", "Treating a filtered empty result like an empty collection: explain the filters, not how to add content."]
    },
    accessibility: {
      requirements: ["The toggle exposes its expanded state and controls the panel by id.", "Every field has a visible label; the summary is a named group; the count is a polite status message."],
      keyboard: ["Tab reaches the toggle, then the panel fields when expanded, then Clear all and the results. Enter or Space toggles the panel."],
      knownConstraints: ["The screen owns the filter state, the query string if filters are linkable, and any default-open rule, such as opening yearly standings filters so the year is reachable."]
    },
    api: {
      react: ["SectionHeading", "Button", "Select", "Input", "AppliedFilters", "EmptyState", "Table"],
      cssClasses: [],
      publicTypes: [],
      defaults: ["The panel starts collapsed unless a required filter, such as a year, must be reachable."],
      invalidCombinations: ["The pattern is guidance; only AppliedFilters is an exported control."]
    },
    fixtureIds: ["pattern.filter-bar"],
    dependencies: { documents: ["component.applied-filters", "component.section-heading", "component.empty-state", "component.button", "component.select", "component.input", "component.table"], packages: ["paper-ui", "@tanstack/react-form"] },
    migration: { legacy: ["paper-playground standings and activity-log filters"], notes: ["Keep each screen\u2019s filter state and field layout; replace hand-built summaries with AppliedFilters and empty results with EmptyState."] },
    changelog: [{ date: REVIEW_DATE7, note: "Documented from the paper-playground standings and activity-log filters." }],
    behaviorTestIds: ["pattern.filter-bar.summary", "pattern.filter-bar.empty"]
  })
];

// src/catalog/registry.ts
var foundationDocuments = phaseThreeFoundationDocuments;
var governanceDocuments = phaseThreeGovernanceDocuments;
function createCatalogRegistry(input) {
  const registry = {
    documents: input.documents,
    fixtures: input.fixtures,
    redirects: input.redirects ?? []
  };
  const result = validateCatalogRegistry(registry);
  if (!result.valid) throw new CatalogValidationError(result.issues);
  return registry;
}
var catalogRegistry = createCatalogRegistry({
  documents: [
    ...foundationDocuments,
    ...phaseTwoDocuments,
    ...phaseThreeFormsFeedbackDocuments,
    ...phaseThreeNavigationDocuments,
    ...publishedPrimitiveDocuments,
    ...phaseThreeDataLayoutDocuments,
    ...pageCompositionDocuments,
    ...phaseThreePatternDocuments,
    ...pageCompositionPatternDocuments,
    ...phaseThreeExperimentDocuments,
    ...governanceDocuments
  ],
  fixtures: [
    ...phaseTwoFixtures,
    ...phaseThreeFormsFeedbackFixtures,
    ...phaseThreeNavigationFixtures,
    ...publishedPrimitiveFixtures,
    ...phaseThreeDataLayoutFixtures,
    ...phaseThreeContentFixtures,
    ...pageCompositionFixtures
  ],
  redirects: legacyStyleguideRedirects
});
export {
  CATALOG_CATEGORIES,
  CATALOG_DOCUMENT_KINDS,
  CATALOG_LIFECYCLES,
  COMPONENT_CATEGORIES,
  COMPONENT_PAGE_SECTION_KEYS,
  CatalogValidationError,
  FIXTURE_DENSITIES,
  FIXTURE_THEMES,
  REQUIRED_COMPONENT_SECTION_KEYS,
  catalogRegistry,
  createCatalogRegistry,
  defineCatalogDocument,
  defineCatalogFixture,
  foundationDocuments,
  governanceDocuments,
  phaseThreeDataLayoutDocuments,
  phaseThreeDataLayoutFixtures,
  phaseThreeNavigationDocuments,
  phaseThreeNavigationFixtures,
  publishedPrimitiveDocuments,
  publishedPrimitiveFixtures,
  validateCatalogFixture,
  validateCatalogRegistry
};
//# sourceMappingURL=catalog.js.map